var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);

// src/alpine/ime-enter-guard.ts
var installed = false;
function installImeEnterGuard() {
  if (installed || typeof document === "undefined") {
    return;
  }
  installed = true;
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") {
      return;
    }
    const composing = event.isComposing || event.keyCode === 229;
    if (!composing) {
      return;
    }
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      event.stopImmediatePropagation();
    }
  }, true);
}
__name(installImeEnterGuard, "installImeEnterGuard");

// src/alpine/plugin.ts
import { resolveTerminology } from "@hereby/core/constants";
import { formatDate, formatDateCustom, formatDateISO, formatDateTime, formatDateTimeLong, formatTime, todayLocal } from "@hereby/core/date";
import { createI18n, getLocaleBundle } from "@hereby/core/locales";

// src/device-login/device-key.ts
var subtle = /* @__PURE__ */ __name(() => {
  const s = globalThis.crypto?.subtle;
  if (!s) {
    throw new Error("\uC774 \uD658\uACBD\uC740 WebCrypto \uB97C \uC9C0\uC6D0\uD558\uC9C0 \uC54A\uC544\uC694.");
  }
  return s;
}, "subtle");
function toBase64Url(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = "";
  for (const b of u8) {
    bin += String.fromCharCode(b);
  }
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
__name(toBase64Url, "toBase64Url");
function fromBase64Url(text) {
  const pad = text.length % 4 === 0 ? "" : "=".repeat(4 - text.length % 4);
  const bin = atob(text.replace(/-/g, "+").replace(/_/g, "/") + pad);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}
__name(fromBase64Url, "fromBase64Url");
function memoizeKey(load) {
  let pending = null;
  return () => {
    if (!pending) {
      pending = load().catch((e) => {
        pending = null;
        throw e;
      });
    }
    return pending;
  };
}
__name(memoizeKey, "memoizeKey");
var _WebWrapKeyProvider = class _WebWrapKeyProvider {
  constructor(store) {
    __publicField(this, "store");
    __publicField(this, "load");
    this.store = store;
    this.load = memoizeKey(async () => {
      const existing = await this.store.getWrapKey();
      if (existing) {
        return existing;
      }
      const key = await subtle().generateKey({
        name: "AES-GCM",
        length: 256
      }, false, [
        "encrypt",
        "decrypt"
      ]);
      await this.store.setWrapKey(key);
      return key;
    });
  }
  getWrappingKey() {
    return this.load();
  }
};
__name(_WebWrapKeyProvider, "WebWrapKeyProvider");
var WebWrapKeyProvider = _WebWrapKeyProvider;
var _SecretBytesProvider = class _SecretBytesProvider {
  constructor(readSecret) {
    __publicField(this, "readSecret");
    __publicField(this, "load");
    this.readSecret = readSecret;
    this.load = memoizeKey(async () => {
      const raw = await this.readSecret();
      if (raw.length !== 32) {
        throw new Error("\uAE30\uAE30 \uBCF4\uD638 \uBE44\uBC00\uC758 \uAE38\uC774\uAC00 \uC62C\uBC14\uB974\uC9C0 \uC54A\uC544\uC694.");
      }
      const copy2 = new Uint8Array(new ArrayBuffer(32));
      copy2.set(raw);
      return subtle().importKey("raw", copy2, {
        name: "AES-GCM"
      }, false, [
        "encrypt",
        "decrypt"
      ]);
    });
  }
  getWrappingKey() {
    return this.load();
  }
};
__name(_SecretBytesProvider, "SecretBytesProvider");
var SecretBytesProvider = _SecretBytesProvider;
async function generateDeviceKey(provider) {
  const pair = await subtle().generateKey({
    name: "ECDSA",
    namedCurve: "P-256"
  }, true, [
    "sign",
    "verify"
  ]);
  const spki = await subtle().exportKey("spki", pair.publicKey);
  const pkcs8 = await subtle().exportKey("pkcs8", pair.privateKey);
  const wrap = await provider.getWrappingKey();
  const iv = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(12)));
  const ct = await subtle().encrypt({
    name: "AES-GCM",
    iv
  }, wrap, pkcs8);
  return {
    publicKey: toBase64Url(spki),
    wrappedPrivateKey: {
      iv: toBase64Url(iv),
      ct: toBase64Url(ct)
    }
  };
}
__name(generateDeviceKey, "generateDeviceKey");
async function signChallenge(provider, wrapped, challenge) {
  const wrap = await provider.getWrappingKey();
  const pkcs8 = await subtle().decrypt({
    name: "AES-GCM",
    iv: fromBase64Url(wrapped.iv)
  }, wrap, fromBase64Url(wrapped.ct));
  const key = await subtle().importKey("pkcs8", pkcs8, {
    name: "ECDSA",
    namedCurve: "P-256"
  }, false, [
    "sign"
  ]);
  const sig = await subtle().sign({
    name: "ECDSA",
    hash: "SHA-256"
  }, key, fromBase64Url(challenge));
  return toBase64Url(sig);
}
__name(signChallenge, "signChallenge");

// src/device-login/device-key-store.ts
var DB_NAME = "hereby-device";
var DB_VERSION = 1;
var STORE_DEVICE_KEY = "deviceKey";
var STORE_WRAP_KEY = "wrapKey";
var ACTIVE = "active";
var DEFAULT = "default";
function request(req) {
  return new Promise((resolve2, reject) => {
    req.onsuccess = () => resolve2(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB \uC694\uCCAD\uC774 \uC2E4\uD328\uD588\uC5B4\uC694."));
  });
}
__name(request, "request");
var _IndexedDbDeviceKeyStore = class _IndexedDbDeviceKeyStore {
  constructor() {
    __publicField(this, "dbPromise", null);
  }
  open() {
    if (!this.dbPromise) {
      this.dbPromise = new Promise((resolve2, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_DEVICE_KEY)) {
            db.createObjectStore(STORE_DEVICE_KEY);
          }
          if (!db.objectStoreNames.contains(STORE_WRAP_KEY)) {
            db.createObjectStore(STORE_WRAP_KEY);
          }
        };
        req.onsuccess = () => {
          const db = req.result;
          db.onversionchange = () => {
            db.close();
            this.dbPromise = null;
          };
          resolve2(db);
        };
        req.onerror = () => {
          this.dbPromise = null;
          reject(req.error ?? new Error("IndexedDB \uB97C \uC5F4 \uC218 \uC5C6\uC5B4\uC694."));
        };
      });
    }
    return this.dbPromise;
  }
  async run(store, mode, fn) {
    const db = await this.open();
    const tx = db.transaction(store, mode);
    const result = await request(fn(tx.objectStore(store)));
    await new Promise((resolve2, reject) => {
      tx.oncomplete = () => resolve2();
      tx.onerror = () => reject(tx.error ?? new Error("IndexedDB \uD2B8\uB79C\uC7AD\uC158\uC774 \uC2E4\uD328\uD588\uC5B4\uC694."));
      tx.onabort = () => reject(tx.error ?? new Error("IndexedDB \uD2B8\uB79C\uC7AD\uC158\uC774 \uC911\uB2E8\uB410\uC5B4\uC694."));
    });
    return result;
  }
  async getActive() {
    const row = await this.run(STORE_DEVICE_KEY, "readonly", (s) => s.get(ACTIVE));
    return row ?? null;
  }
  async setActive(record) {
    await this.run(STORE_DEVICE_KEY, "readwrite", (s) => s.put(record, ACTIVE));
  }
  async clearActive() {
    await this.run(STORE_DEVICE_KEY, "readwrite", (s) => s.delete(ACTIVE));
  }
  async getWrapKey() {
    const row = await this.run(STORE_WRAP_KEY, "readonly", (s) => s.get(DEFAULT));
    return row ?? null;
  }
  async setWrapKey(key) {
    await this.run(STORE_WRAP_KEY, "readwrite", (s) => s.put(key, DEFAULT));
  }
};
__name(_IndexedDbDeviceKeyStore, "IndexedDbDeviceKeyStore");
var IndexedDbDeviceKeyStore = _IndexedDbDeviceKeyStore;
var _MemoryDeviceKeyStore = class _MemoryDeviceKeyStore {
  constructor() {
    __publicField(this, "active", null);
    __publicField(this, "wrapKey", null);
  }
  async getActive() {
    return this.active;
  }
  async setActive(record) {
    this.active = record;
  }
  async clearActive() {
    this.active = null;
  }
  async getWrapKey() {
    return this.wrapKey;
  }
  async setWrapKey(key) {
    this.wrapKey = key;
  }
};
__name(_MemoryDeviceKeyStore, "MemoryDeviceKeyStore");
var MemoryDeviceKeyStore = _MemoryDeviceKeyStore;
function createDefaultDeviceKeyStore() {
  return typeof indexedDB !== "undefined" ? new IndexedDbDeviceKeyStore() : new MemoryDeviceKeyStore();
}
__name(createDefaultDeviceKeyStore, "createDefaultDeviceKeyStore");

// src/device-login/provider-registry.ts
var current = null;
function setDeviceSecretProvider(provider) {
  current = provider;
}
__name(setDeviceSecretProvider, "setDeviceSecretProvider");
function getDeviceSecretProvider() {
  return current;
}
__name(getDeviceSecretProvider, "getDeviceSecretProvider");

// src/chat-crypto/at-rest.ts
var _FallbackWrapProvider = class _FallbackWrapProvider {
  constructor(providers) {
    __publicField(this, "providers");
    this.providers = providers;
  }
  async getWrappingKey() {
    let lastError = new Error("\uBCF4\uD638 \uD0A4 \uACF5\uAE09\uC790\uAC00 \uC5C6\uC5B4\uC694.");
    for (const p of this.providers) {
      try {
        return await p.getWrappingKey();
      } catch (e) {
        lastError = e;
      }
    }
    throw lastError;
  }
  /** 풀기용 후보 전부 — 못 읽는 공급자는 건너뛴다 */
  async getWrappingKeys() {
    const keys = [];
    for (const p of this.providers) {
      try {
        keys.push(await p.getWrappingKey());
      } catch {
      }
    }
    return keys;
  }
};
__name(_FallbackWrapProvider, "FallbackWrapProvider");
var FallbackWrapProvider = _FallbackWrapProvider;
async function candidateKeys(provider) {
  const multi = provider;
  if (typeof multi.getWrappingKeys === "function") {
    const keys = await multi.getWrappingKeys();
    if (keys.length === 0) {
      throw new Error("\uBCF4\uD638 \uD0A4\uB97C \uC77D\uC744 \uC218 \uC5C6\uC5B4\uC694.");
    }
    return keys;
  }
  return [
    await provider.getWrappingKey()
  ];
}
__name(candidateKeys, "candidateKeys");
async function decryptWithAny(provider, iv, ct) {
  let lastError = null;
  for (const key of await candidateKeys(provider)) {
    try {
      return await crypto.subtle.decrypt({
        name: "AES-GCM",
        iv
      }, key, ct);
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError ?? new Error("\uBCF4\uD638 \uD0A4\uB85C \uD480 \uC218 \uC5C6\uC5B4\uC694.");
}
__name(decryptWithAny, "decryptWithAny");
function isWrapped(value) {
  return !!value && typeof value === "object" && value.v === 1 && typeof value.iv === "string" && typeof value.ct === "string";
}
__name(isWrapped, "isWrapped");
async function wrapBytes(provider, bytes) {
  const key = await provider.getWrappingKey();
  const iv = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(12)));
  const copy2 = new Uint8Array(new ArrayBuffer(bytes.length));
  copy2.set(bytes);
  const ct = await crypto.subtle.encrypt({
    name: "AES-GCM",
    iv
  }, key, copy2);
  return {
    v: 1,
    iv: toBase64Url(iv),
    ct: toBase64Url(ct)
  };
}
__name(wrapBytes, "wrapBytes");
async function unwrapBytes(provider, enc) {
  const plain = await decryptWithAny(provider, fromBase64Url(enc.iv), fromBase64Url(enc.ct));
  return new Uint8Array(plain);
}
__name(unwrapBytes, "unwrapBytes");
async function wrapBinary(provider, bytes) {
  const key = await provider.getWrappingKey();
  const iv = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(12)));
  const copy2 = new Uint8Array(new ArrayBuffer(bytes.length));
  copy2.set(bytes);
  const ct = new Uint8Array(await crypto.subtle.encrypt({
    name: "AES-GCM",
    iv
  }, key, copy2));
  return {
    iv,
    ct
  };
}
__name(wrapBinary, "wrapBinary");
async function unwrapBinary(provider, enc) {
  const iv = new Uint8Array(new ArrayBuffer(enc.iv.length));
  iv.set(enc.iv);
  const ct = new Uint8Array(new ArrayBuffer(enc.ct.length));
  ct.set(enc.ct);
  return new Uint8Array(await decryptWithAny(provider, iv, ct));
}
__name(unwrapBinary, "unwrapBinary");
async function wrapJson(provider, value) {
  return wrapBytes(provider, new TextEncoder().encode(JSON.stringify(value)));
}
__name(wrapJson, "wrapJson");
async function unwrapJson(provider, enc) {
  return JSON.parse(new TextDecoder().decode(await unwrapBytes(provider, enc)));
}
__name(unwrapJson, "unwrapJson");

// src/chat-crypto/key-store.ts
var epochKeySink = null;
function setEpochKeySink(sink) {
  epochKeySink = sink;
}
__name(setEpochKeySink, "setEpochKeySink");
function mirrorEpochKey(conversationId, epoch, key) {
  epochKeySink?.put(conversationId, epoch, key).catch(() => void 0);
}
__name(mirrorEpochKey, "mirrorEpochKey");
function mirrorClear() {
  epochKeySink?.clear().catch(() => void 0);
}
__name(mirrorClear, "mirrorClear");
var MESSAGE_CACHE_PER_ROOM = 300;
var ATTACHMENT_CACHE_MAX_FILE = 8 * 1024 * 1024;
var ATTACHMENT_CACHE_MAX_TOTAL = 100 * 1024 * 1024;
function matchesIndex(e, params) {
  if (params.fromSenderId && e.senderId !== params.fromSenderId) {
    return false;
  }
  if (params.conversationId && e.conversationId !== params.conversationId) {
    return false;
  }
  const k = params.keyword.trim().toLowerCase();
  return k.length === 0 || e.text.toLowerCase().includes(k);
}
__name(matchesIndex, "matchesIndex");
var _MemoryChatKeyStore = class _MemoryChatKeyStore {
  constructor() {
    __publicField(this, "cache", /* @__PURE__ */ new Map());
    __publicField(this, "attachments", /* @__PURE__ */ new Map());
    __publicField(this, "accounts", /* @__PURE__ */ new Map());
    __publicField(this, "epochKeys", /* @__PURE__ */ new Map());
    __publicField(this, "peers", /* @__PURE__ */ new Map());
    __publicField(this, "search", /* @__PURE__ */ new Map());
  }
  async getCachedMessages(employeeId, conversationId, limit) {
    return [
      ...this.cache.values()
    ].filter((c) => c.employeeId === employeeId && c.conversationId === conversationId).sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, limit).reverse().map(({ employeeId: _e, ...c }) => structuredClone(c));
  }
  async putCachedMessages(employeeId, items) {
    for (const c of items) {
      this.cache.set(c.id, {
        ...structuredClone(c),
        employeeId
      });
    }
    for (const conversationId of new Set(items.map((i) => i.conversationId))) {
      const rows = [
        ...this.cache.values()
      ].filter((c) => c.employeeId === employeeId && c.conversationId === conversationId).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      for (const stale of rows.slice(MESSAGE_CACHE_PER_ROOM)) {
        this.cache.delete(stale.id);
      }
    }
  }
  async removeCachedMessages(ids) {
    for (const id of ids) {
      this.cache.delete(id);
    }
  }
  async clearMessageCache() {
    this.cache.clear();
  }
  async getCachedAttachment(employeeId, path) {
    const row = this.attachments.get(path);
    if (!row || row.employeeId !== employeeId) {
      return null;
    }
    row.lastUsedAt = Date.now();
    return {
      path: row.path,
      mode: row.mode,
      data: row.data
    };
  }
  async putCachedAttachment(employeeId, item, opts = {}) {
    if (item.data.byteLength > ATTACHMENT_CACHE_MAX_FILE) {
      return;
    }
    this.attachments.set(item.path, {
      ...item,
      employeeId,
      lastUsedAt: Date.now()
    });
    if (opts.trim !== false) {
      await this.trimAttachmentCache(employeeId);
    }
  }
  async trimAttachmentCache(employeeId) {
    const mine = [
      ...this.attachments.values()
    ].filter((a) => a.employeeId === employeeId).sort((a, b) => a.lastUsedAt - b.lastUsedAt);
    let total = mine.reduce((sum, a) => sum + a.data.byteLength, 0);
    for (const old of mine) {
      if (total <= ATTACHMENT_CACHE_MAX_TOTAL) {
        break;
      }
      this.attachments.delete(old.path);
      total -= old.data.byteLength;
    }
  }
  async clearAttachmentCache() {
    this.attachments.clear();
  }
  async getPeerPin(employeeId) {
    return this.peers.get(employeeId) ?? null;
  }
  async putPeerPin(pin) {
    this.peers.set(pin.employeeId, structuredClone(pin));
  }
  async getAccount(employeeId) {
    return this.accounts.get(employeeId) ?? null;
  }
  async putAccount(record) {
    this.accounts.set(record.employeeId, structuredClone(record));
  }
  async deleteAccount(employeeId) {
    this.accounts.delete(employeeId);
  }
  async getEpochKey(conversationId, epoch) {
    return this.epochKeys.get(`${conversationId}:${epoch}`) ?? null;
  }
  async putEpochKey(conversationId, epoch, key) {
    this.epochKeys.set(`${conversationId}:${epoch}`, key);
    mirrorEpochKey(conversationId, epoch, key);
  }
  async listEpochKeys() {
    return [
      ...this.epochKeys.entries()
    ].map(([k, key]) => parseEpochKeyId(k, key));
  }
  async clearEpochKeys() {
    this.epochKeys.clear();
    mirrorClear();
  }
  async putSearchEntries(entries) {
    for (const e of entries) {
      this.search.set(e.messageId, e);
    }
  }
  async searchIndex(params) {
    return [
      ...this.search.values()
    ].filter((e) => matchesIndex(e, params)).sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, params.limit);
  }
  async clearSearchIndex() {
    this.search.clear();
  }
};
__name(_MemoryChatKeyStore, "MemoryChatKeyStore");
var MemoryChatKeyStore = _MemoryChatKeyStore;
function parseEpochKeyId(id, key) {
  const at = id.lastIndexOf(":");
  return {
    conversationId: id.slice(0, at),
    epoch: Number(id.slice(at + 1)),
    key
  };
}
__name(parseEpochKeyId, "parseEpochKeyId");
var DB_NAME2 = "hereby-chat";
var DB_VERSION2 = 5;
var STORE_MESSAGES = "messages";
var INDEX_MESSAGES_ROOM = "room";
var STORE_ATTACHMENTS = "attachments";
var INDEX_ATTACHMENTS_OWNER = "owner";
var STORE_ACCOUNT = "account";
var STORE_EPOCH = "epochKey";
var STORE_SEARCH = "search";
var STORE_PEER = "peerKey";
function request2(req) {
  return new Promise((resolve2, reject) => {
    req.onsuccess = () => resolve2(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB \uC694\uCCAD\uC774 \uC2E4\uD328\uD588\uC5B4\uC694."));
  });
}
__name(request2, "request");
function isIndexedDbAvailable() {
  return typeof indexedDB !== "undefined";
}
__name(isIndexedDbAvailable, "isIndexedDbAvailable");
var _IndexedDbChatKeyStore = class _IndexedDbChatKeyStore {
  constructor(opts = {}) {
    __publicField(this, "dbPromise", null);
    __publicField(this, "wrap");
    __publicField(this, "legacySearchMigrated", false);
    const web = new WebWrapKeyProvider(createDefaultDeviceKeyStore());
    this.wrap = opts.wrap ? new FallbackWrapProvider([
      opts.wrap,
      web
    ]) : web;
  }
  open() {
    if (!this.dbPromise) {
      this.dbPromise = new Promise((resolve2, reject) => {
        const req = indexedDB.open(DB_NAME2, DB_VERSION2);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_ACCOUNT)) {
            db.createObjectStore(STORE_ACCOUNT, {
              keyPath: "employeeId"
            });
          }
          if (!db.objectStoreNames.contains(STORE_EPOCH)) {
            db.createObjectStore(STORE_EPOCH);
          }
          if (!db.objectStoreNames.contains(STORE_SEARCH)) {
            db.createObjectStore(STORE_SEARCH, {
              keyPath: "messageId"
            });
          }
          if (!db.objectStoreNames.contains(STORE_PEER)) {
            db.createObjectStore(STORE_PEER, {
              keyPath: "employeeId"
            });
          }
          if (!db.objectStoreNames.contains(STORE_MESSAGES)) {
            const messages = db.createObjectStore(STORE_MESSAGES, {
              keyPath: "id"
            });
            messages.createIndex(INDEX_MESSAGES_ROOM, [
              "employeeId",
              "conversationId"
            ], {
              unique: false
            });
          }
          if (!db.objectStoreNames.contains(STORE_ATTACHMENTS)) {
            const attachments = db.createObjectStore(STORE_ATTACHMENTS, {
              keyPath: "path"
            });
            attachments.createIndex(INDEX_ATTACHMENTS_OWNER, "employeeId", {
              unique: false
            });
          }
        };
        req.onsuccess = () => {
          const db = req.result;
          db.onversionchange = () => {
            db.close();
            this.dbPromise = null;
          };
          resolve2(db);
        };
        req.onerror = () => {
          this.dbPromise = null;
          reject(req.error ?? new Error("IndexedDB \uB97C \uC5F4 \uC218 \uC5C6\uC5B4\uC694."));
        };
      });
    }
    return this.dbPromise;
  }
  async run(store, mode, fn) {
    const db = await this.open();
    const tx = db.transaction(store, mode);
    const result = await request2(fn(tx.objectStore(store)));
    await new Promise((resolve2, reject) => {
      tx.oncomplete = () => resolve2();
      tx.onerror = () => reject(tx.error ?? new Error("IndexedDB \uD2B8\uB79C\uC7AD\uC158\uC774 \uC2E4\uD328\uD588\uC5B4\uC694."));
      tx.onabort = () => reject(tx.error ?? new Error("IndexedDB \uD2B8\uB79C\uC7AD\uC158\uC774 \uC911\uB2E8\uB410\uC5B4\uC694."));
    });
    return result;
  }
  async getAccount(employeeId) {
    const row = await this.run(STORE_ACCOUNT, "readonly", (s) => s.get(employeeId));
    if (!row) {
      return null;
    }
    if (row.enc) {
      const bundle = await unwrapJson(this.wrap, row.enc);
      return {
        employeeId: row.employeeId,
        organizationId: row.organizationId,
        version: row.version,
        savedAt: row.savedAt,
        bundle
      };
    }
    if (row.bundle) {
      const record = {
        employeeId: row.employeeId,
        organizationId: row.organizationId,
        version: row.version,
        savedAt: row.savedAt,
        bundle: row.bundle
      };
      this.putAccount(record).catch(() => void 0);
      return record;
    }
    return null;
  }
  async getPeerPin(employeeId) {
    const row = await this.run(STORE_PEER, "readonly", (s) => s.get(employeeId));
    return row ?? null;
  }
  async putPeerPin(pin) {
    await this.run(STORE_PEER, "readwrite", (s) => s.put(pin));
  }
  async putAccount(record) {
    const row = {
      employeeId: record.employeeId,
      organizationId: record.organizationId,
      version: record.version,
      savedAt: record.savedAt,
      enc: await wrapJson(this.wrap, record.bundle)
    };
    await this.run(STORE_ACCOUNT, "readwrite", (s) => s.put(row));
  }
  async deleteAccount(employeeId) {
    await this.run(STORE_ACCOUNT, "readwrite", (s) => s.delete(employeeId));
  }
  /** 세대 키 행 — 감싼 것(enc) 또는 예전 평문 바이트 */
  async decodeEpochRow(conversationId, epoch, value) {
    if (isWrapped(value)) {
      return unwrapBytes(this.wrap, value);
    }
    if (value instanceof Uint8Array || value instanceof ArrayBuffer) {
      const raw = new Uint8Array(value);
      this.putEpochKey(conversationId, epoch, raw).catch(() => void 0);
      return raw;
    }
    return null;
  }
  async getEpochKey(conversationId, epoch) {
    const row = await this.run(STORE_EPOCH, "readonly", (s) => s.get(`${conversationId}:${epoch}`));
    return row ? this.decodeEpochRow(conversationId, epoch, row) : null;
  }
  async putEpochKey(conversationId, epoch, key) {
    const enc = await wrapBytes(this.wrap, key);
    await this.run(STORE_EPOCH, "readwrite", (s) => s.put(enc, `${conversationId}:${epoch}`));
    mirrorEpochKey(conversationId, epoch, key);
  }
  async listEpochKeys() {
    const db = await this.open();
    const tx = db.transaction(STORE_EPOCH, "readonly");
    const store = tx.objectStore(STORE_EPOCH);
    const [ids, values] = await Promise.all([
      request2(store.getAllKeys()),
      request2(store.getAll())
    ]);
    const out = [];
    for (let i = 0; i < ids.length; i++) {
      const parsed = parseEpochKeyId(String(ids[i]), new Uint8Array());
      const key = await this.decodeEpochRow(parsed.conversationId, parsed.epoch, values[i]);
      if (key) {
        out.push({
          conversationId: parsed.conversationId,
          epoch: parsed.epoch,
          key
        });
      }
    }
    return out;
  }
  async clearEpochKeys() {
    await this.run(STORE_EPOCH, "readwrite", (s) => s.clear());
    mirrorClear();
  }
  async putRows(store, rows) {
    if (rows.length === 0) {
      return;
    }
    const db = await this.open();
    const tx = db.transaction(store, "readwrite");
    const os = tx.objectStore(store);
    for (const r of rows) {
      os.put(r);
    }
    await new Promise((resolve2, reject) => {
      tx.oncomplete = () => resolve2();
      tx.onerror = () => reject(tx.error ?? new Error("IndexedDB \uD2B8\uB79C\uC7AD\uC158\uC774 \uC2E4\uD328\uD588\uC5B4\uC694."));
      tx.onabort = () => reject(tx.error ?? new Error("IndexedDB \uD2B8\uB79C\uC7AD\uC158\uC774 \uC911\uB2E8\uB410\uC5B4\uC694."));
    });
  }
  async encodeSearchRow(e) {
    return {
      messageId: e.messageId,
      conversationId: e.conversationId,
      senderId: e.senderId,
      createdAt: e.createdAt,
      type: e.type,
      enc: await wrapJson(this.wrap, {
        senderName: e.senderName,
        text: e.text
      })
    };
  }
  async decodeSearchRow(row) {
    if (row.enc) {
      const { senderName, text } = await unwrapJson(this.wrap, row.enc);
      return {
        messageId: row.messageId,
        conversationId: row.conversationId,
        senderId: row.senderId,
        senderName,
        text,
        createdAt: row.createdAt,
        type: row.type
      };
    }
    if (typeof row.text === "string") {
      return {
        messageId: row.messageId,
        conversationId: row.conversationId,
        senderId: row.senderId,
        senderName: row.senderName ?? "",
        text: row.text,
        createdAt: row.createdAt,
        type: row.type
      };
    }
    return null;
  }
  /** 예전 평문 색인 행을 한 번에 감싸 다시 쓴다 — 저장소 인스턴스당 한 번, 처음 색인을 건드릴 때. */
  async migrateLegacySearchRows(rows) {
    if (this.legacySearchMigrated) {
      return;
    }
    this.legacySearchMigrated = true;
    const legacy = rows.filter((r) => !r.enc && typeof r.text === "string");
    if (legacy.length === 0) {
      return;
    }
    const encoded = [];
    for (const r of legacy) {
      const entry = await this.decodeSearchRow(r);
      if (entry) {
        encoded.push(await this.encodeSearchRow(entry));
      }
    }
    await this.putRows(STORE_SEARCH, encoded).catch(() => void 0);
  }
  async putSearchEntries(entries) {
    if (entries.length === 0) {
      return;
    }
    const rows = [];
    for (const e of entries) {
      rows.push(await this.encodeSearchRow(e));
    }
    await this.putRows(STORE_SEARCH, rows);
  }
  /** 색인 전체를 읽어 푼 뒤 거른다 — 한 사람의 대화량(수만 건)에서는 충분히 빠르고, 전문 검색 색인은 두지 않는다. */
  async searchIndex(params) {
    const all = await this.run(STORE_SEARCH, "readonly", (s) => s.getAll());
    this.migrateLegacySearchRows(all).catch(() => void 0);
    const decoded = [];
    for (const row of all) {
      if (params.conversationId && row.conversationId !== params.conversationId) {
        continue;
      }
      if (params.fromSenderId && row.senderId !== params.fromSenderId) {
        continue;
      }
      const entry = await this.decodeSearchRow(row).catch(() => null);
      if (entry) {
        decoded.push(entry);
      }
    }
    return decoded.filter((e) => matchesIndex(e, params)).sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, params.limit);
  }
  /* ── 메시지 캐시(2026-10-13) ── */
  async getCachedMessages(employeeId, conversationId, limit) {
    const db = await this.open();
    const tx = db.transaction(STORE_MESSAGES, "readonly");
    const index = tx.objectStore(STORE_MESSAGES).index(INDEX_MESSAGES_ROOM);
    const rows = await request2(index.getAll(IDBKeyRange.only([
      employeeId,
      conversationId
    ])));
    rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const out = [];
    for (const row of rows.slice(0, limit)) {
      try {
        const payload = await unwrapJson(this.wrap, row.enc);
        out.push({
          id: row.id,
          conversationId: row.conversationId,
          createdAt: row.createdAt,
          payload
        });
      } catch {
      }
    }
    return out.reverse();
  }
  async putCachedMessages(employeeId, items) {
    if (items.length === 0) {
      return;
    }
    const rows = [];
    for (const c of items) {
      rows.push({
        id: c.id,
        employeeId,
        conversationId: c.conversationId,
        createdAt: c.createdAt,
        enc: await wrapJson(this.wrap, c.payload)
      });
    }
    await this.putRows(STORE_MESSAGES, rows);
    const db = await this.open();
    for (const conversationId of new Set(items.map((i) => i.conversationId))) {
      const tx = db.transaction(STORE_MESSAGES, "readwrite");
      const os = tx.objectStore(STORE_MESSAGES);
      const all = await request2(os.index(INDEX_MESSAGES_ROOM).getAll(IDBKeyRange.only([
        employeeId,
        conversationId
      ])));
      all.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      for (const stale of all.slice(MESSAGE_CACHE_PER_ROOM)) {
        os.delete(stale.id);
      }
      await new Promise((resolve2) => {
        tx.oncomplete = () => resolve2();
        tx.onerror = () => resolve2();
        tx.onabort = () => resolve2();
      });
    }
  }
  async removeCachedMessages(ids) {
    for (const id of ids) {
      await this.run(STORE_MESSAGES, "readwrite", (s) => s.delete(id));
    }
  }
  async clearMessageCache() {
    await this.run(STORE_MESSAGES, "readwrite", (s) => s.clear());
  }
  /* ── 첨부 캐시(2026-10-13) ── */
  async getCachedAttachment(employeeId, path) {
    const row = await this.run(STORE_ATTACHMENTS, "readonly", (s) => s.get(path));
    if (!row || row.employeeId !== employeeId) {
      return null;
    }
    this.run(STORE_ATTACHMENTS, "readwrite", (s) => s.put({
      ...row,
      lastUsedAt: Date.now()
    })).catch(() => void 0);
    if (row.mode === "wrapped") {
      const plain = await unwrapBinary(this.wrap, {
        iv: row.iv,
        ct: row.data
      });
      return {
        path: row.path,
        mode: "wrapped",
        data: plain
      };
    }
    return {
      path: row.path,
      mode: "cipher",
      data: new Uint8Array(row.data)
    };
  }
  async putCachedAttachment(employeeId, item, opts = {}) {
    if (item.data.byteLength > ATTACHMENT_CACHE_MAX_FILE) {
      return;
    }
    const base = {
      path: item.path,
      employeeId,
      size: item.data.byteLength,
      lastUsedAt: Date.now()
    };
    const row = item.mode === "wrapped" ? {
      ...base,
      mode: "wrapped",
      ...await wrapBinary(this.wrap, item.data).then((e) => ({
        iv: e.iv,
        data: e.ct
      }))
    } : {
      ...base,
      mode: "cipher",
      iv: null,
      data: item.data
    };
    await this.run(STORE_ATTACHMENTS, "readwrite", (s) => s.put(row));
    if (opts.trim !== false) {
      await this.trimAttachmentCache(employeeId);
    }
  }
  /** 총량 상한 — 가장 오래 안 쓴 것부터. 전체 행을 읽으므로 묶음 저장 뒤 한 번만 부른다(put 마다 부르면 100MB 를 N 번 읽는다). */
  async trimAttachmentCache(employeeId) {
    const db = await this.open();
    const tx = db.transaction(STORE_ATTACHMENTS, "readwrite");
    const os = tx.objectStore(STORE_ATTACHMENTS);
    const mine = await request2(os.index(INDEX_ATTACHMENTS_OWNER).getAll(IDBKeyRange.only(employeeId)));
    mine.sort((a, b) => a.lastUsedAt - b.lastUsedAt);
    let total = mine.reduce((sum, a) => sum + a.size, 0);
    for (const old of mine) {
      if (total <= ATTACHMENT_CACHE_MAX_TOTAL) {
        break;
      }
      os.delete(old.path);
      total -= old.size;
    }
    await new Promise((resolve2) => {
      tx.oncomplete = () => resolve2();
      tx.onerror = () => resolve2();
      tx.onabort = () => resolve2();
    });
  }
  async clearAttachmentCache() {
    await this.run(STORE_ATTACHMENTS, "readwrite", (s) => s.clear());
  }
  async clearSearchIndex() {
    await this.run(STORE_SEARCH, "readwrite", (s) => s.clear());
  }
};
__name(_IndexedDbChatKeyStore, "IndexedDbChatKeyStore");
var IndexedDbChatKeyStore = _IndexedDbChatKeyStore;
function createDefaultChatKeyStore() {
  return isIndexedDbAvailable() ? new IndexedDbChatKeyStore({
    wrap: getDeviceSecretProvider() ?? void 0
  }) : new MemoryChatKeyStore();
}
__name(createDefaultChatKeyStore, "createDefaultChatKeyStore");

// src/client/auth-failure.ts
function isAbortError(error) {
  if (!error || typeof error !== "object") {
    return false;
  }
  const e = error;
  if (e.name === "AbortError") {
    return true;
  }
  const msg = (e.message || "").toLowerCase();
  if (msg.includes("aborted") || msg.includes("cancelled") || msg.includes("canceled") || // Chrome/Safari/Firefox가 네비게이션 중 끊긴 fetch에 내는 문구들
  msg.includes("failed to fetch") || msg.includes("network error") || msg.includes("load failed")) {
    return true;
  }
  return e.cause ? isAbortError(e.cause) : false;
}
__name(isAbortError, "isAbortError");
function isDefinitiveAuthFailure(error) {
  if (isAbortError(error)) {
    return false;
  }
  if (!error || typeof error !== "object") {
    return false;
  }
  const data = error.data;
  if (!data) {
    return false;
  }
  return data.code === "UNAUTHORIZED" || data.httpStatus === 401;
}
__name(isDefinitiveAuthFailure, "isDefinitiveAuthFailure");
var unloading = false;
function installUnloadGuard() {
  if (typeof window === "undefined" || typeof window.addEventListener !== "function") {
    return;
  }
  const mark = /* @__PURE__ */ __name(() => {
    unloading = true;
  }, "mark");
  window.addEventListener("pagehide", mark);
  window.addEventListener("beforeunload", mark);
  window.addEventListener("pageshow", () => {
    unloading = false;
  });
}
__name(installUnloadGuard, "installUnloadGuard");
function isUnloading() {
  return unloading;
}
__name(isUnloading, "isUnloading");
function isTopLevelFrame() {
  if (typeof window === "undefined") {
    return true;
  }
  try {
    return window.top === window.self;
  } catch {
    return false;
  }
}
__name(isTopLevelFrame, "isTopLevelFrame");

// ../../node_modules/.pnpm/@trpc+client@11.11.0_@trpc+server@11.11.0_typescript@5.9.3__typescript@5.9.3/node_modules/@trpc/client/dist/objectSpread2-BvkFp-_Y.mjs
var __create = Object.create;
var __defProp2 = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __getProtoOf = Object.getPrototypeOf;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __commonJS = /* @__PURE__ */ __name((cb, mod) => function() {
  return mod || (0, cb[__getOwnPropNames(cb)[0]])((mod = { exports: {} }).exports, mod), mod.exports;
}, "__commonJS");
var __copyProps = /* @__PURE__ */ __name((to, from, except, desc) => {
  if (from && typeof from === "object" || typeof from === "function") for (var keys = __getOwnPropNames(from), i = 0, n = keys.length, key; i < n; i++) {
    key = keys[i];
    if (!__hasOwnProp.call(to, key) && key !== except) __defProp2(to, key, {
      get: ((k) => from[k]).bind(null, key),
      enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable
    });
  }
  return to;
}, "__copyProps");
var __toESM = /* @__PURE__ */ __name((mod, isNodeMode, target) => (target = mod != null ? __create(__getProtoOf(mod)) : {}, __copyProps(isNodeMode || !mod || !mod.__esModule ? __defProp2(target, "default", {
  value: mod,
  enumerable: true
}) : target, mod)), "__toESM");
var require_typeof = __commonJS({ "../../node_modules/.pnpm/@oxc-project+runtime@0.72.2/node_modules/@oxc-project/runtime/src/helpers/typeof.js"(exports, module) {
  function _typeof$2(o) {
    "@babel/helpers - typeof";
    return module.exports = _typeof$2 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function(o$1) {
      return typeof o$1;
    } : function(o$1) {
      return o$1 && "function" == typeof Symbol && o$1.constructor === Symbol && o$1 !== Symbol.prototype ? "symbol" : typeof o$1;
    }, module.exports.__esModule = true, module.exports["default"] = module.exports, _typeof$2(o);
  }
  __name(_typeof$2, "_typeof$2");
  module.exports = _typeof$2, module.exports.__esModule = true, module.exports["default"] = module.exports;
} });
var require_toPrimitive = __commonJS({ "../../node_modules/.pnpm/@oxc-project+runtime@0.72.2/node_modules/@oxc-project/runtime/src/helpers/toPrimitive.js"(exports, module) {
  var _typeof$1 = require_typeof()["default"];
  function toPrimitive$1(t, r) {
    if ("object" != _typeof$1(t) || !t) return t;
    var e = t[Symbol.toPrimitive];
    if (void 0 !== e) {
      var i = e.call(t, r || "default");
      if ("object" != _typeof$1(i)) return i;
      throw new TypeError("@@toPrimitive must return a primitive value.");
    }
    return ("string" === r ? String : Number)(t);
  }
  __name(toPrimitive$1, "toPrimitive$1");
  module.exports = toPrimitive$1, module.exports.__esModule = true, module.exports["default"] = module.exports;
} });
var require_toPropertyKey = __commonJS({ "../../node_modules/.pnpm/@oxc-project+runtime@0.72.2/node_modules/@oxc-project/runtime/src/helpers/toPropertyKey.js"(exports, module) {
  var _typeof = require_typeof()["default"];
  var toPrimitive = require_toPrimitive();
  function toPropertyKey$1(t) {
    var i = toPrimitive(t, "string");
    return "symbol" == _typeof(i) ? i : i + "";
  }
  __name(toPropertyKey$1, "toPropertyKey$1");
  module.exports = toPropertyKey$1, module.exports.__esModule = true, module.exports["default"] = module.exports;
} });
var require_defineProperty = __commonJS({ "../../node_modules/.pnpm/@oxc-project+runtime@0.72.2/node_modules/@oxc-project/runtime/src/helpers/defineProperty.js"(exports, module) {
  var toPropertyKey = require_toPropertyKey();
  function _defineProperty(e, r, t) {
    return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
      value: t,
      enumerable: true,
      configurable: true,
      writable: true
    }) : e[r] = t, e;
  }
  __name(_defineProperty, "_defineProperty");
  module.exports = _defineProperty, module.exports.__esModule = true, module.exports["default"] = module.exports;
} });
var require_objectSpread2 = __commonJS({ "../../node_modules/.pnpm/@oxc-project+runtime@0.72.2/node_modules/@oxc-project/runtime/src/helpers/objectSpread2.js"(exports, module) {
  var defineProperty = require_defineProperty();
  function ownKeys(e, r) {
    var t = Object.keys(e);
    if (Object.getOwnPropertySymbols) {
      var o = Object.getOwnPropertySymbols(e);
      r && (o = o.filter(function(r$1) {
        return Object.getOwnPropertyDescriptor(e, r$1).enumerable;
      })), t.push.apply(t, o);
    }
    return t;
  }
  __name(ownKeys, "ownKeys");
  function _objectSpread2(e) {
    for (var r = 1; r < arguments.length; r++) {
      var t = null != arguments[r] ? arguments[r] : {};
      r % 2 ? ownKeys(Object(t), true).forEach(function(r$1) {
        defineProperty(e, r$1, t[r$1]);
      }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function(r$1) {
        Object.defineProperty(e, r$1, Object.getOwnPropertyDescriptor(t, r$1));
      });
    }
    return e;
  }
  __name(_objectSpread2, "_objectSpread2");
  module.exports = _objectSpread2, module.exports.__esModule = true, module.exports["default"] = module.exports;
} });

// ../../node_modules/.pnpm/@trpc+client@11.11.0_@trpc+server@11.11.0_typescript@5.9.3__typescript@5.9.3/node_modules/@trpc/client/dist/splitLink-B7Cuf2c_.mjs
import { observable } from "@trpc/server/observable";
function createChain(opts) {
  return observable((observer) => {
    function execute(index = 0, op = opts.op) {
      const next = opts.links[index];
      if (!next) throw new Error("No more links to execute - did you forget to add an ending link?");
      const subscription = next({
        op,
        next(nextOp) {
          const nextObserver = execute(index + 1, nextOp);
          return nextObserver;
        }
      });
      return subscription;
    }
    __name(execute, "execute");
    const obs$ = execute();
    return obs$.subscribe(observer);
  });
}
__name(createChain, "createChain");
function asArray(value) {
  return Array.isArray(value) ? value : [value];
}
__name(asArray, "asArray");
function splitLink(opts) {
  return (runtime) => {
    const yes = asArray(opts.true).map((link) => link(runtime));
    const no = asArray(opts.false).map((link) => link(runtime));
    return (props) => {
      return observable((observer) => {
        const links = opts.condition(props.op) ? yes : no;
        return createChain({
          op: props.op,
          links
        }).subscribe(observer);
      });
    };
  };
}
__name(splitLink, "splitLink");

// ../../node_modules/.pnpm/@trpc+client@11.11.0_@trpc+server@11.11.0_typescript@5.9.3__typescript@5.9.3/node_modules/@trpc/client/dist/TRPCClientError-apv8gw59.mjs
import { isObject } from "@trpc/server/unstable-core-do-not-import";
var import_defineProperty = __toESM(require_defineProperty(), 1);
var import_objectSpread2 = __toESM(require_objectSpread2(), 1);
function isTRPCClientError(cause) {
  return cause instanceof TRPCClientError;
}
__name(isTRPCClientError, "isTRPCClientError");
function isTRPCErrorResponse(obj) {
  return isObject(obj) && isObject(obj["error"]) && typeof obj["error"]["code"] === "number" && typeof obj["error"]["message"] === "string";
}
__name(isTRPCErrorResponse, "isTRPCErrorResponse");
function getMessageFromUnknownError(err, fallback) {
  if (typeof err === "string") return err;
  if (isObject(err) && typeof err["message"] === "string") return err["message"];
  return fallback;
}
__name(getMessageFromUnknownError, "getMessageFromUnknownError");
var _a;
var TRPCClientError = (_a = class extends Error {
  constructor(message, opts) {
    var _opts$result, _opts$result2;
    const cause = opts === null || opts === void 0 ? void 0 : opts.cause;
    super(message, { cause });
    (0, import_defineProperty.default)(this, "cause", void 0);
    (0, import_defineProperty.default)(this, "shape", void 0);
    (0, import_defineProperty.default)(this, "data", void 0);
    (0, import_defineProperty.default)(this, "meta", void 0);
    this.meta = opts === null || opts === void 0 ? void 0 : opts.meta;
    this.cause = cause;
    this.shape = opts === null || opts === void 0 || (_opts$result = opts.result) === null || _opts$result === void 0 ? void 0 : _opts$result.error;
    this.data = opts === null || opts === void 0 || (_opts$result2 = opts.result) === null || _opts$result2 === void 0 ? void 0 : _opts$result2.error.data;
    this.name = "TRPCClientError";
    Object.setPrototypeOf(this, _a.prototype);
  }
  static from(_cause, opts = {}) {
    const cause = _cause;
    if (isTRPCClientError(cause)) {
      if (opts.meta) cause.meta = (0, import_objectSpread2.default)((0, import_objectSpread2.default)({}, cause.meta), opts.meta);
      return cause;
    }
    if (isTRPCErrorResponse(cause)) return new _a(cause.error.message, (0, import_objectSpread2.default)((0, import_objectSpread2.default)({}, opts), {}, {
      result: cause,
      cause: opts.cause
    }));
    return new _a(getMessageFromUnknownError(cause, "Unknown error"), (0, import_objectSpread2.default)((0, import_objectSpread2.default)({}, opts), {}, { cause }));
  }
}, __name(_a, "TRPCClientError"), _a);

// ../../node_modules/.pnpm/@trpc+client@11.11.0_@trpc+server@11.11.0_typescript@5.9.3__typescript@5.9.3/node_modules/@trpc/client/dist/unstable-internals-Bg7n9BBj.mjs
function getTransformer(transformer) {
  const _transformer = transformer;
  if (!_transformer) return {
    input: {
      serialize: /* @__PURE__ */ __name((data) => data, "serialize"),
      deserialize: /* @__PURE__ */ __name((data) => data, "deserialize")
    },
    output: {
      serialize: /* @__PURE__ */ __name((data) => data, "serialize"),
      deserialize: /* @__PURE__ */ __name((data) => data, "deserialize")
    }
  };
  if ("input" in _transformer) return _transformer;
  return {
    input: _transformer,
    output: _transformer
  };
}
__name(getTransformer, "getTransformer");

// ../../node_modules/.pnpm/@trpc+client@11.11.0_@trpc+server@11.11.0_typescript@5.9.3__typescript@5.9.3/node_modules/@trpc/client/dist/httpUtils-Dv57hbOd.mjs
var isFunction = /* @__PURE__ */ __name((fn) => typeof fn === "function", "isFunction");
function getFetch(customFetchImpl) {
  if (customFetchImpl) return customFetchImpl;
  if (typeof window !== "undefined" && isFunction(window.fetch)) return window.fetch;
  if (typeof globalThis !== "undefined" && isFunction(globalThis.fetch)) return globalThis.fetch;
  throw new Error("No fetch implementation found");
}
__name(getFetch, "getFetch");
var import_objectSpread22 = __toESM(require_objectSpread2(), 1);
function resolveHTTPLinkOptions(opts) {
  return {
    url: opts.url.toString(),
    fetch: opts.fetch,
    transformer: getTransformer(opts.transformer),
    methodOverride: opts.methodOverride
  };
}
__name(resolveHTTPLinkOptions, "resolveHTTPLinkOptions");
function arrayToDict(array) {
  const dict = {};
  for (let index = 0; index < array.length; index++) {
    const element = array[index];
    dict[index] = element;
  }
  return dict;
}
__name(arrayToDict, "arrayToDict");
var METHOD = {
  query: "GET",
  mutation: "POST",
  subscription: "PATCH"
};
function getInput(opts) {
  return "input" in opts ? opts.transformer.input.serialize(opts.input) : arrayToDict(opts.inputs.map((_input) => opts.transformer.input.serialize(_input)));
}
__name(getInput, "getInput");
var getUrl = /* @__PURE__ */ __name((opts) => {
  const parts = opts.url.split("?");
  const base = parts[0].replace(/\/$/, "");
  let url = base + "/" + opts.path;
  const queryParts = [];
  if (parts[1]) queryParts.push(parts[1]);
  if ("inputs" in opts) queryParts.push("batch=1");
  if (opts.type === "query" || opts.type === "subscription") {
    const input = getInput(opts);
    if (input !== void 0 && opts.methodOverride !== "POST") queryParts.push(`input=${encodeURIComponent(JSON.stringify(input))}`);
  }
  if (queryParts.length) url += "?" + queryParts.join("&");
  return url;
}, "getUrl");
var getBody = /* @__PURE__ */ __name((opts) => {
  if (opts.type === "query" && opts.methodOverride !== "POST") return void 0;
  const input = getInput(opts);
  return input !== void 0 ? JSON.stringify(input) : void 0;
}, "getBody");
var jsonHttpRequester = /* @__PURE__ */ __name((opts) => {
  return httpRequest((0, import_objectSpread22.default)((0, import_objectSpread22.default)({}, opts), {}, {
    contentTypeHeader: "application/json",
    getUrl,
    getBody
  }));
}, "jsonHttpRequester");
var _a2;
var AbortError = (_a2 = class extends Error {
  constructor() {
    const name = "AbortError";
    super(name);
    this.name = name;
    this.message = name;
  }
}, __name(_a2, "AbortError"), _a2);
var throwIfAborted = /* @__PURE__ */ __name((signal) => {
  var _signal$throwIfAborte;
  if (!(signal === null || signal === void 0 ? void 0 : signal.aborted)) return;
  (_signal$throwIfAborte = signal.throwIfAborted) === null || _signal$throwIfAborte === void 0 || _signal$throwIfAborte.call(signal);
  if (typeof DOMException !== "undefined") throw new DOMException("AbortError", "AbortError");
  throw new AbortError();
}, "throwIfAborted");
async function fetchHTTPResponse(opts) {
  var _opts$methodOverride;
  throwIfAborted(opts.signal);
  const url = opts.getUrl(opts);
  const body = opts.getBody(opts);
  const method = (_opts$methodOverride = opts.methodOverride) !== null && _opts$methodOverride !== void 0 ? _opts$methodOverride : METHOD[opts.type];
  const resolvedHeaders = await (async () => {
    const heads = await opts.headers();
    if (Symbol.iterator in heads) return Object.fromEntries(heads);
    return heads;
  })();
  const headers = (0, import_objectSpread22.default)((0, import_objectSpread22.default)((0, import_objectSpread22.default)({}, opts.contentTypeHeader && method !== "GET" ? { "content-type": opts.contentTypeHeader } : {}), opts.trpcAcceptHeader ? { "trpc-accept": opts.trpcAcceptHeader } : void 0), resolvedHeaders);
  return getFetch(opts.fetch)(url, {
    method,
    signal: opts.signal,
    body,
    headers
  });
}
__name(fetchHTTPResponse, "fetchHTTPResponse");
async function httpRequest(opts) {
  const meta = {};
  const res = await fetchHTTPResponse(opts);
  meta.response = res;
  const json = await res.json();
  meta.responseJSON = json;
  return {
    json,
    meta
  };
}
__name(httpRequest, "httpRequest");

// ../../node_modules/.pnpm/@trpc+client@11.11.0_@trpc+server@11.11.0_typescript@5.9.3__typescript@5.9.3/node_modules/@trpc/client/dist/httpLink-Cz9h1Qgh.mjs
import { observable as observable2 } from "@trpc/server/observable";
import { transformResult } from "@trpc/server/unstable-core-do-not-import";
var import_objectSpread23 = __toESM(require_objectSpread2(), 1);

// ../../node_modules/.pnpm/@trpc+client@11.11.0_@trpc+server@11.11.0_typescript@5.9.3__typescript@5.9.3/node_modules/@trpc/client/dist/httpBatchLink-fQ1NAi-e.mjs
import { observable as observable3 } from "@trpc/server/observable";
import { transformResult as transformResult2 } from "@trpc/server/unstable-core-do-not-import";
var throwFatalError = /* @__PURE__ */ __name(() => {
  throw new Error("Something went wrong. Please submit an issue at https://github.com/trpc/trpc/issues/new");
}, "throwFatalError");
function dataLoader(batchLoader) {
  let pendingItems = null;
  let dispatchTimer = null;
  const destroyTimerAndPendingItems = /* @__PURE__ */ __name(() => {
    clearTimeout(dispatchTimer);
    dispatchTimer = null;
    pendingItems = null;
  }, "destroyTimerAndPendingItems");
  function groupItems(items) {
    const groupedItems = [[]];
    let index = 0;
    while (true) {
      const item = items[index];
      if (!item) break;
      const lastGroup = groupedItems[groupedItems.length - 1];
      if (item.aborted) {
        var _item$reject;
        (_item$reject = item.reject) === null || _item$reject === void 0 || _item$reject.call(item, new Error("Aborted"));
        index++;
        continue;
      }
      const isValid = batchLoader.validate(lastGroup.concat(item).map((it) => it.key));
      if (isValid) {
        lastGroup.push(item);
        index++;
        continue;
      }
      if (lastGroup.length === 0) {
        var _item$reject2;
        (_item$reject2 = item.reject) === null || _item$reject2 === void 0 || _item$reject2.call(item, new Error("Input is too big for a single dispatch"));
        index++;
        continue;
      }
      groupedItems.push([]);
    }
    return groupedItems;
  }
  __name(groupItems, "groupItems");
  function dispatch() {
    const groupedItems = groupItems(pendingItems);
    destroyTimerAndPendingItems();
    for (const items of groupedItems) {
      if (!items.length) continue;
      const batch = { items };
      for (const item of items) item.batch = batch;
      const promise = batchLoader.fetch(batch.items.map((_item) => _item.key));
      promise.then(async (result) => {
        await Promise.all(result.map(async (valueOrPromise, index) => {
          const item = batch.items[index];
          try {
            var _item$resolve;
            const value = await Promise.resolve(valueOrPromise);
            (_item$resolve = item.resolve) === null || _item$resolve === void 0 || _item$resolve.call(item, value);
          } catch (cause) {
            var _item$reject3;
            (_item$reject3 = item.reject) === null || _item$reject3 === void 0 || _item$reject3.call(item, cause);
          }
          item.batch = null;
          item.reject = null;
          item.resolve = null;
        }));
        for (const item of batch.items) {
          var _item$reject4;
          (_item$reject4 = item.reject) === null || _item$reject4 === void 0 || _item$reject4.call(item, new Error("Missing result"));
          item.batch = null;
        }
      }).catch((cause) => {
        for (const item of batch.items) {
          var _item$reject5;
          (_item$reject5 = item.reject) === null || _item$reject5 === void 0 || _item$reject5.call(item, cause);
          item.batch = null;
        }
      });
    }
  }
  __name(dispatch, "dispatch");
  function load(key) {
    var _dispatchTimer;
    const item = {
      aborted: false,
      key,
      batch: null,
      resolve: throwFatalError,
      reject: throwFatalError
    };
    const promise = new Promise((resolve2, reject) => {
      var _pendingItems;
      item.reject = reject;
      item.resolve = resolve2;
      (_pendingItems = pendingItems) !== null && _pendingItems !== void 0 || (pendingItems = []);
      pendingItems.push(item);
    });
    (_dispatchTimer = dispatchTimer) !== null && _dispatchTimer !== void 0 || (dispatchTimer = setTimeout(dispatch));
    return promise;
  }
  __name(load, "load");
  return { load };
}
__name(dataLoader, "dataLoader");
function allAbortSignals(...signals) {
  const ac = new AbortController();
  const count = signals.length;
  let abortedCount = 0;
  const onAbort = /* @__PURE__ */ __name(() => {
    if (++abortedCount === count) ac.abort();
  }, "onAbort");
  for (const signal of signals) if (signal === null || signal === void 0 ? void 0 : signal.aborted) onAbort();
  else signal === null || signal === void 0 || signal.addEventListener("abort", onAbort, { once: true });
  return ac.signal;
}
__name(allAbortSignals, "allAbortSignals");
var import_objectSpread24 = __toESM(require_objectSpread2(), 1);
function httpBatchLink(opts) {
  var _opts$maxURLLength, _opts$maxItems;
  const resolvedOpts = resolveHTTPLinkOptions(opts);
  const maxURLLength = (_opts$maxURLLength = opts.maxURLLength) !== null && _opts$maxURLLength !== void 0 ? _opts$maxURLLength : Infinity;
  const maxItems = (_opts$maxItems = opts.maxItems) !== null && _opts$maxItems !== void 0 ? _opts$maxItems : Infinity;
  return () => {
    const batchLoader = /* @__PURE__ */ __name((type) => {
      return {
        validate(batchOps) {
          if (maxURLLength === Infinity && maxItems === Infinity) return true;
          if (batchOps.length > maxItems) return false;
          const path = batchOps.map((op) => op.path).join(",");
          const inputs = batchOps.map((op) => op.input);
          const url = getUrl((0, import_objectSpread24.default)((0, import_objectSpread24.default)({}, resolvedOpts), {}, {
            type,
            path,
            inputs,
            signal: null
          }));
          return url.length <= maxURLLength;
        },
        async fetch(batchOps) {
          const path = batchOps.map((op) => op.path).join(",");
          const inputs = batchOps.map((op) => op.input);
          const signal = allAbortSignals(...batchOps.map((op) => op.signal));
          const res = await jsonHttpRequester((0, import_objectSpread24.default)((0, import_objectSpread24.default)({}, resolvedOpts), {}, {
            path,
            inputs,
            type,
            headers() {
              if (!opts.headers) return {};
              if (typeof opts.headers === "function") return opts.headers({ opList: batchOps });
              return opts.headers;
            },
            signal
          }));
          const resJSON = Array.isArray(res.json) ? res.json : batchOps.map(() => res.json);
          const result = resJSON.map((item) => ({
            meta: res.meta,
            json: item
          }));
          return result;
        }
      };
    }, "batchLoader");
    const query = dataLoader(batchLoader("query"));
    const mutation = dataLoader(batchLoader("mutation"));
    const loaders = {
      query,
      mutation
    };
    return ({ op }) => {
      return observable3((observer) => {
        if (op.type === "subscription") throw new Error("Subscriptions are unsupported by `httpLink` - use `httpSubscriptionLink` or `wsLink`");
        const loader = loaders[op.type];
        const promise = loader.load(op);
        let _res = void 0;
        promise.then((res) => {
          _res = res;
          const transformed = transformResult2(res.json, resolvedOpts.transformer.output);
          if (!transformed.ok) {
            observer.error(TRPCClientError.from(transformed.error, { meta: res.meta }));
            return;
          }
          observer.next({
            context: res.meta,
            result: transformed.result
          });
          observer.complete();
        }).catch((err) => {
          observer.error(TRPCClientError.from(err, { meta: _res === null || _res === void 0 ? void 0 : _res.meta }));
        });
        return () => {
        };
      });
    };
  };
}
__name(httpBatchLink, "httpBatchLink");

// ../../node_modules/.pnpm/@trpc+client@11.11.0_@trpc+server@11.11.0_typescript@5.9.3__typescript@5.9.3/node_modules/@trpc/client/dist/loggerLink-ineCN1PO.mjs
import { observable as observable4, tap } from "@trpc/server/observable";
var import_objectSpread25 = __toESM(require_objectSpread2(), 1);

// ../../node_modules/.pnpm/@trpc+client@11.11.0_@trpc+server@11.11.0_typescript@5.9.3__typescript@5.9.3/node_modules/@trpc/client/dist/wsLink-DSf4KOdW.mjs
import { behaviorSubject, observable as observable5 } from "@trpc/server/observable";
import { run, sleep, transformResult as transformResult3 } from "@trpc/server/unstable-core-do-not-import";
var jsonEncoder = {
  encode: /* @__PURE__ */ __name((data) => JSON.stringify(data), "encode"),
  decode: /* @__PURE__ */ __name((data) => {
    if (typeof data !== "string") throw new Error("jsonEncoder received binary data. JSON uses text frames. Use a binary encoder for binary data.");
    return JSON.parse(data);
  }, "decode")
};
var lazyDefaults = {
  enabled: false,
  closeMs: 0
};
var keepAliveDefaults = {
  enabled: false,
  pongTimeoutMs: 1e3,
  intervalMs: 5e3
};
var exponentialBackoff = /* @__PURE__ */ __name((attemptIndex) => {
  return attemptIndex === 0 ? 0 : Math.min(1e3 * 2 ** attemptIndex, 3e4);
}, "exponentialBackoff");
var resultOf = /* @__PURE__ */ __name((value, ...args) => {
  return typeof value === "function" ? value(...args) : value;
}, "resultOf");
var import_defineProperty$3 = __toESM(require_defineProperty(), 1);
var _a3;
var TRPCWebSocketClosedError = (_a3 = class extends Error {
  constructor(opts) {
    super(opts.message, { cause: opts.cause });
    this.name = "TRPCWebSocketClosedError";
    Object.setPrototypeOf(this, _a3.prototype);
  }
}, __name(_a3, "TRPCWebSocketClosedError"), _a3);
var _a4;
var ResettableTimeout = (_a4 = class {
  constructor(onTimeout, timeoutMs) {
    this.onTimeout = onTimeout;
    this.timeoutMs = timeoutMs;
    (0, import_defineProperty$3.default)(this, "timeout", void 0);
  }
  /**
  * Resets the current timeout, restarting it with the same duration.
  * Does nothing if no timeout is active.
  */
  reset() {
    if (!this.timeout) return;
    clearTimeout(this.timeout);
    this.timeout = setTimeout(this.onTimeout, this.timeoutMs);
  }
  start() {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(this.onTimeout, this.timeoutMs);
  }
  stop() {
    clearTimeout(this.timeout);
    this.timeout = void 0;
  }
}, __name(_a4, "ResettableTimeout"), _a4);
function withResolvers() {
  let resolve2;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve2 = res;
    reject = rej;
  });
  return {
    promise,
    resolve: resolve2,
    reject
  };
}
__name(withResolvers, "withResolvers");
async function prepareUrl(urlOptions) {
  const url = await resultOf(urlOptions.url);
  if (!urlOptions.connectionParams) return url;
  const prefix = url.includes("?") ? "&" : "?";
  const connectionParams = `${prefix}connectionParams=1`;
  return url + connectionParams;
}
__name(prepareUrl, "prepareUrl");
async function buildConnectionMessage(connectionParams, encoder) {
  const message = {
    method: "connectionParams",
    data: await resultOf(connectionParams)
  };
  return encoder.encode(message);
}
__name(buildConnectionMessage, "buildConnectionMessage");
var import_defineProperty$2 = __toESM(require_defineProperty(), 1);
var _a5;
var RequestManager = (_a5 = class {
  constructor() {
    (0, import_defineProperty$2.default)(this, "outgoingRequests", new Array());
    (0, import_defineProperty$2.default)(this, "pendingRequests", {});
  }
  /**
  * Registers a new request by adding it to the outgoing queue and setting up
  * callbacks for lifecycle events such as completion or error.
  *
  * @param message - The outgoing message to be sent.
  * @param callbacks - Callback functions to observe the request's state.
  * @returns A cleanup function to manually remove the request.
  */
  register(message, callbacks) {
    const { promise: end, resolve: resolve2 } = withResolvers();
    this.outgoingRequests.push({
      id: String(message.id),
      message,
      end,
      callbacks: {
        next: callbacks.next,
        complete: /* @__PURE__ */ __name(() => {
          callbacks.complete();
          resolve2();
        }, "complete"),
        error: /* @__PURE__ */ __name((e) => {
          callbacks.error(e);
          resolve2();
        }, "error")
      }
    });
    return () => {
      this.delete(message.id);
      callbacks.complete();
      resolve2();
    };
  }
  /**
  * Deletes a request from both the outgoing and pending collections, if it exists.
  */
  delete(messageId) {
    if (messageId === null) return;
    this.outgoingRequests = this.outgoingRequests.filter(({ id }) => id !== String(messageId));
    delete this.pendingRequests[String(messageId)];
  }
  /**
  * Moves all outgoing requests to the pending state and clears the outgoing queue.
  *
  * The caller is expected to handle the actual sending of the requests
  * (e.g., sending them over the network) after this method is called.
  *
  * @returns The list of requests that were transitioned to the pending state.
  */
  flush() {
    const requests = this.outgoingRequests;
    this.outgoingRequests = [];
    for (const request3 of requests) this.pendingRequests[request3.id] = request3;
    return requests;
  }
  /**
  * Retrieves all currently pending requests, which are in flight awaiting responses
  * or handling ongoing subscriptions.
  */
  getPendingRequests() {
    return Object.values(this.pendingRequests);
  }
  /**
  * Retrieves a specific pending request by its message ID.
  */
  getPendingRequest(messageId) {
    if (messageId === null) return null;
    return this.pendingRequests[String(messageId)];
  }
  /**
  * Retrieves all outgoing requests, which are waiting to be sent.
  */
  getOutgoingRequests() {
    return this.outgoingRequests;
  }
  /**
  * Retrieves all requests, both outgoing and pending, with their respective states.
  *
  * @returns An array of all requests with their state ("outgoing" or "pending").
  */
  getRequests() {
    return [...this.getOutgoingRequests().map((request3) => ({
      state: "outgoing",
      message: request3.message,
      end: request3.end,
      callbacks: request3.callbacks
    })), ...this.getPendingRequests().map((request3) => ({
      state: "pending",
      message: request3.message,
      end: request3.end,
      callbacks: request3.callbacks
    }))];
  }
  /**
  * Checks if there are any pending requests, including ongoing subscriptions.
  */
  hasPendingRequests() {
    return this.getPendingRequests().length > 0;
  }
  /**
  * Checks if there are any pending subscriptions
  */
  hasPendingSubscriptions() {
    return this.getPendingRequests().some((request3) => request3.message.method === "subscription");
  }
  /**
  * Checks if there are any outgoing requests waiting to be sent.
  */
  hasOutgoingRequests() {
    return this.outgoingRequests.length > 0;
  }
}, __name(_a5, "RequestManager"), _a5);
var import_defineProperty$1 = __toESM(require_defineProperty(), 1);
function asyncWsOpen(ws) {
  const { promise, resolve: resolve2, reject } = withResolvers();
  ws.addEventListener("open", () => {
    ws.removeEventListener("error", reject);
    resolve2();
  });
  ws.addEventListener("error", reject);
  return promise;
}
__name(asyncWsOpen, "asyncWsOpen");
function setupPingInterval(ws, { intervalMs, pongTimeoutMs }) {
  let pingTimeout;
  let pongTimeout;
  function start() {
    pingTimeout = setTimeout(() => {
      ws.send("PING");
      pongTimeout = setTimeout(() => {
        ws.close();
      }, pongTimeoutMs);
    }, intervalMs);
  }
  __name(start, "start");
  function reset() {
    clearTimeout(pingTimeout);
    start();
  }
  __name(reset, "reset");
  function pong() {
    clearTimeout(pongTimeout);
    reset();
  }
  __name(pong, "pong");
  ws.addEventListener("open", start);
  ws.addEventListener("message", ({ data }) => {
    clearTimeout(pingTimeout);
    start();
    if (data === "PONG") pong();
  });
  ws.addEventListener("close", () => {
    clearTimeout(pingTimeout);
    clearTimeout(pongTimeout);
  });
}
__name(setupPingInterval, "setupPingInterval");
var _a6;
var WsConnection = (_a6 = class {
  constructor(opts) {
    var _opts$WebSocketPonyfi;
    (0, import_defineProperty$1.default)(this, "id", ++_a6.connectCount);
    (0, import_defineProperty$1.default)(this, "WebSocketPonyfill", void 0);
    (0, import_defineProperty$1.default)(this, "urlOptions", void 0);
    (0, import_defineProperty$1.default)(this, "keepAliveOpts", void 0);
    (0, import_defineProperty$1.default)(this, "encoder", void 0);
    (0, import_defineProperty$1.default)(this, "wsObservable", behaviorSubject(null));
    (0, import_defineProperty$1.default)(this, "openPromise", null);
    this.WebSocketPonyfill = (_opts$WebSocketPonyfi = opts.WebSocketPonyfill) !== null && _opts$WebSocketPonyfi !== void 0 ? _opts$WebSocketPonyfi : WebSocket;
    if (!this.WebSocketPonyfill) throw new Error("No WebSocket implementation found - you probably don't want to use this on the server, but if you do you need to pass a `WebSocket`-ponyfill");
    this.urlOptions = opts.urlOptions;
    this.keepAliveOpts = opts.keepAlive;
    this.encoder = opts.encoder;
  }
  get ws() {
    return this.wsObservable.get();
  }
  set ws(ws) {
    this.wsObservable.next(ws);
  }
  /**
  * Checks if the WebSocket connection is open and ready to communicate.
  */
  isOpen() {
    return !!this.ws && this.ws.readyState === this.WebSocketPonyfill.OPEN && !this.openPromise;
  }
  /**
  * Checks if the WebSocket connection is closed or in the process of closing.
  */
  isClosed() {
    return !!this.ws && (this.ws.readyState === this.WebSocketPonyfill.CLOSING || this.ws.readyState === this.WebSocketPonyfill.CLOSED);
  }
  async open() {
    var _this = this;
    if (_this.openPromise) return _this.openPromise;
    _this.id = ++_a6.connectCount;
    const wsPromise = prepareUrl(_this.urlOptions).then((url) => new _this.WebSocketPonyfill(url));
    _this.openPromise = wsPromise.then(async (ws) => {
      _this.ws = ws;
      ws.binaryType = "arraybuffer";
      ws.addEventListener("message", function({ data }) {
        if (data === "PING") this.send("PONG");
      });
      if (_this.keepAliveOpts.enabled) setupPingInterval(ws, _this.keepAliveOpts);
      ws.addEventListener("close", () => {
        if (_this.ws === ws) _this.ws = null;
      });
      await asyncWsOpen(ws);
      if (_this.urlOptions.connectionParams) ws.send(await buildConnectionMessage(_this.urlOptions.connectionParams, _this.encoder));
    });
    try {
      await _this.openPromise;
    } finally {
      _this.openPromise = null;
    }
  }
  /**
  * Closes the WebSocket connection gracefully.
  * Waits for any ongoing open operation to complete before closing.
  */
  async close() {
    var _this2 = this;
    try {
      await _this2.openPromise;
    } finally {
      var _this$ws;
      (_this$ws = _this2.ws) === null || _this$ws === void 0 || _this$ws.close();
    }
  }
}, __name(_a6, "WsConnection"), _a6);
(0, import_defineProperty$1.default)(WsConnection, "connectCount", 0);
function backwardCompatibility(connection) {
  if (connection.isOpen()) return {
    id: connection.id,
    state: "open",
    ws: connection.ws
  };
  if (connection.isClosed()) return {
    id: connection.id,
    state: "closed",
    ws: connection.ws
  };
  if (!connection.ws) return null;
  return {
    id: connection.id,
    state: "connecting",
    ws: connection.ws
  };
}
__name(backwardCompatibility, "backwardCompatibility");
var import_defineProperty2 = __toESM(require_defineProperty(), 1);
var import_objectSpread26 = __toESM(require_objectSpread2(), 1);
var _a7;
var WsClient = (_a7 = class {
  constructor(opts) {
    var _opts$experimental_en, _opts$retryDelayMs;
    (0, import_defineProperty2.default)(this, "connectionState", void 0);
    (0, import_defineProperty2.default)(this, "allowReconnect", false);
    (0, import_defineProperty2.default)(this, "requestManager", new RequestManager());
    (0, import_defineProperty2.default)(this, "activeConnection", void 0);
    (0, import_defineProperty2.default)(this, "reconnectRetryDelay", void 0);
    (0, import_defineProperty2.default)(this, "inactivityTimeout", void 0);
    (0, import_defineProperty2.default)(this, "callbacks", void 0);
    (0, import_defineProperty2.default)(this, "lazyMode", void 0);
    (0, import_defineProperty2.default)(this, "encoder", void 0);
    (0, import_defineProperty2.default)(this, "reconnecting", null);
    this.encoder = (_opts$experimental_en = opts.experimental_encoder) !== null && _opts$experimental_en !== void 0 ? _opts$experimental_en : jsonEncoder;
    this.callbacks = {
      onOpen: opts.onOpen,
      onClose: opts.onClose,
      onError: opts.onError
    };
    const lazyOptions = (0, import_objectSpread26.default)((0, import_objectSpread26.default)({}, lazyDefaults), opts.lazy);
    this.inactivityTimeout = new ResettableTimeout(() => {
      if (this.requestManager.hasOutgoingRequests() || this.requestManager.hasPendingRequests()) {
        this.inactivityTimeout.reset();
        return;
      }
      this.close().catch(() => null);
    }, lazyOptions.closeMs);
    this.activeConnection = new WsConnection({
      WebSocketPonyfill: opts.WebSocket,
      urlOptions: opts,
      keepAlive: (0, import_objectSpread26.default)((0, import_objectSpread26.default)({}, keepAliveDefaults), opts.keepAlive),
      encoder: this.encoder
    });
    this.activeConnection.wsObservable.subscribe({ next: /* @__PURE__ */ __name((ws) => {
      if (!ws) return;
      this.setupWebSocketListeners(ws);
    }, "next") });
    this.reconnectRetryDelay = (_opts$retryDelayMs = opts.retryDelayMs) !== null && _opts$retryDelayMs !== void 0 ? _opts$retryDelayMs : exponentialBackoff;
    this.lazyMode = lazyOptions.enabled;
    this.connectionState = behaviorSubject({
      type: "state",
      state: lazyOptions.enabled ? "idle" : "connecting",
      error: null
    });
    if (!this.lazyMode) this.open().catch(() => null);
  }
  /**
  * Opens the WebSocket connection. Handles reconnection attempts and updates
  * the connection state accordingly.
  */
  async open() {
    var _this = this;
    _this.allowReconnect = true;
    if (_this.connectionState.get().state === "idle") _this.connectionState.next({
      type: "state",
      state: "connecting",
      error: null
    });
    try {
      await _this.activeConnection.open();
    } catch (error) {
      _this.reconnect(new TRPCWebSocketClosedError({
        message: "Initialization error",
        cause: error
      }));
      return _this.reconnecting;
    }
  }
  /**
  * Closes the WebSocket connection and stops managing requests.
  * Ensures all outgoing and pending requests are properly finalized.
  */
  async close() {
    var _this2 = this;
    _this2.allowReconnect = false;
    _this2.inactivityTimeout.stop();
    const requestsToAwait = [];
    for (const request3 of _this2.requestManager.getRequests()) if (request3.message.method === "subscription") request3.callbacks.complete();
    else if (request3.state === "outgoing") request3.callbacks.error(TRPCClientError.from(new TRPCWebSocketClosedError({ message: "Closed before connection was established" })));
    else requestsToAwait.push(request3.end);
    await Promise.all(requestsToAwait).catch(() => null);
    await _this2.activeConnection.close().catch(() => null);
    _this2.connectionState.next({
      type: "state",
      state: "idle",
      error: null
    });
  }
  /**
  * Method to request the server.
  * Handles data transformation, batching of requests, and subscription lifecycle.
  *
  * @param op - The operation details including id, type, path, input and signal
  * @param transformer - Data transformer for serializing requests and deserializing responses
  * @param lastEventId - Optional ID of the last received event for subscriptions
  *
  * @returns An observable that emits operation results and handles cleanup
  */
  request({ op: { id, type, path, input, signal }, transformer, lastEventId }) {
    return observable5((observer) => {
      const abort = this.batchSend({
        id,
        method: type,
        params: {
          input: transformer.input.serialize(input),
          path,
          lastEventId
        }
      }, (0, import_objectSpread26.default)((0, import_objectSpread26.default)({}, observer), {}, { next(event) {
        const transformed = transformResult3(event, transformer.output);
        if (!transformed.ok) {
          observer.error(TRPCClientError.from(transformed.error));
          return;
        }
        observer.next({ result: transformed.result });
      } }));
      return () => {
        abort();
        if (type === "subscription" && this.activeConnection.isOpen()) this.send({
          id,
          method: "subscription.stop"
        });
        signal === null || signal === void 0 || signal.removeEventListener("abort", abort);
      };
    });
  }
  get connection() {
    return backwardCompatibility(this.activeConnection);
  }
  reconnect(closedError) {
    var _this3 = this;
    this.connectionState.next({
      type: "state",
      state: "connecting",
      error: TRPCClientError.from(closedError)
    });
    if (this.reconnecting) return;
    const tryReconnect = /* @__PURE__ */ __name(async (attemptIndex) => {
      try {
        await sleep(_this3.reconnectRetryDelay(attemptIndex));
        if (_this3.allowReconnect) {
          await _this3.activeConnection.close();
          await _this3.activeConnection.open();
          if (_this3.requestManager.hasPendingRequests()) _this3.send(_this3.requestManager.getPendingRequests().map(({ message }) => message));
        }
        _this3.reconnecting = null;
      } catch (_unused) {
        await tryReconnect(attemptIndex + 1);
      }
    }, "tryReconnect");
    this.reconnecting = tryReconnect(0);
  }
  setupWebSocketListeners(ws) {
    var _this4 = this;
    const handleCloseOrError = /* @__PURE__ */ __name((cause) => {
      const reqs = this.requestManager.getPendingRequests();
      for (const { message, callbacks } of reqs) {
        if (message.method === "subscription") continue;
        callbacks.error(TRPCClientError.from(cause !== null && cause !== void 0 ? cause : new TRPCWebSocketClosedError({
          message: "WebSocket closed",
          cause
        })));
        this.requestManager.delete(message.id);
      }
    }, "handleCloseOrError");
    ws.addEventListener("open", () => {
      run(async () => {
        var _this$callbacks$onOpe, _this$callbacks;
        if (_this4.lazyMode) _this4.inactivityTimeout.start();
        (_this$callbacks$onOpe = (_this$callbacks = _this4.callbacks).onOpen) === null || _this$callbacks$onOpe === void 0 || _this$callbacks$onOpe.call(_this$callbacks);
        _this4.connectionState.next({
          type: "state",
          state: "pending",
          error: null
        });
      }).catch((error) => {
        ws.close(3e3);
        handleCloseOrError(error);
      });
    });
    ws.addEventListener("message", ({ data }) => {
      this.inactivityTimeout.reset();
      if (["PING", "PONG"].includes(data)) return;
      const incomingMessage = this.encoder.decode(data);
      if ("method" in incomingMessage) {
        this.handleIncomingRequest(incomingMessage);
        return;
      }
      this.handleResponseMessage(incomingMessage);
    });
    ws.addEventListener("close", (event) => {
      var _this$callbacks$onClo, _this$callbacks2;
      handleCloseOrError(event);
      (_this$callbacks$onClo = (_this$callbacks2 = this.callbacks).onClose) === null || _this$callbacks$onClo === void 0 || _this$callbacks$onClo.call(_this$callbacks2, event);
      if (!this.lazyMode || this.requestManager.hasPendingSubscriptions()) this.reconnect(new TRPCWebSocketClosedError({
        message: "WebSocket closed",
        cause: event
      }));
    });
    ws.addEventListener("error", (event) => {
      var _this$callbacks$onErr, _this$callbacks3;
      handleCloseOrError(event);
      (_this$callbacks$onErr = (_this$callbacks3 = this.callbacks).onError) === null || _this$callbacks$onErr === void 0 || _this$callbacks$onErr.call(_this$callbacks3, event);
      this.reconnect(new TRPCWebSocketClosedError({
        message: "WebSocket closed",
        cause: event
      }));
    });
  }
  handleResponseMessage(message) {
    const request3 = this.requestManager.getPendingRequest(message.id);
    if (!request3) return;
    request3.callbacks.next(message);
    let completed = true;
    if ("result" in message && request3.message.method === "subscription") {
      if (message.result.type === "data") request3.message.params.lastEventId = message.result.id;
      if (message.result.type !== "stopped") completed = false;
    }
    if (completed) {
      request3.callbacks.complete();
      this.requestManager.delete(message.id);
    }
  }
  handleIncomingRequest(message) {
    if (message.method === "reconnect") this.reconnect(new TRPCWebSocketClosedError({ message: "Server requested reconnect" }));
  }
  /**
  * Sends a message or batch of messages directly to the server.
  */
  send(messageOrMessages) {
    if (!this.activeConnection.isOpen()) throw new Error("Active connection is not open");
    const messages = messageOrMessages instanceof Array ? messageOrMessages : [messageOrMessages];
    this.activeConnection.ws.send(this.encoder.encode(messages.length === 1 ? messages[0] : messages));
  }
  /**
  * Groups requests for batch sending.
  *
  * @returns A function to abort the batched request.
  */
  batchSend(message, callbacks) {
    var _this5 = this;
    this.inactivityTimeout.reset();
    run(async () => {
      if (!_this5.activeConnection.isOpen()) await _this5.open();
      await sleep(0);
      if (!_this5.requestManager.hasOutgoingRequests()) return;
      _this5.send(_this5.requestManager.flush().map(({ message: message$1 }) => message$1));
    }).catch((err) => {
      this.requestManager.delete(message.id);
      callbacks.error(TRPCClientError.from(err));
    });
    return this.requestManager.register(message, callbacks);
  }
}, __name(_a7, "WsClient"), _a7);
function createWSClient(opts) {
  return new WsClient(opts);
}
__name(createWSClient, "createWSClient");
function wsLink(opts) {
  const { client } = opts;
  const transformer = getTransformer(opts.transformer);
  return () => {
    return ({ op }) => {
      return observable5((observer) => {
        const connStateSubscription = op.type === "subscription" ? client.connectionState.subscribe({ next(result) {
          observer.next({
            result,
            context: op.context
          });
        } }) : null;
        const requestSubscription = client.request({
          op,
          transformer
        }).subscribe(observer);
        return () => {
          requestSubscription.unsubscribe();
          connStateSubscription === null || connStateSubscription === void 0 || connStateSubscription.unsubscribe();
        };
      });
    };
  };
}
__name(wsLink, "wsLink");

// ../../node_modules/.pnpm/@trpc+client@11.11.0_@trpc+server@11.11.0_typescript@5.9.3__typescript@5.9.3/node_modules/@trpc/client/dist/index.mjs
import { behaviorSubject as behaviorSubject2, observable as observable6, observableToPromise, share } from "@trpc/server/observable";
import { callProcedure, createFlatProxy, createRecursiveProxy, isAbortError as isAbortError2, isAsyncIterable, iteratorResource, jsonlStreamConsumer, makeResource, retryableRpcCodes, run as run2, sseStreamConsumer } from "@trpc/server/unstable-core-do-not-import";
import { getTRPCErrorFromUnknown, getTRPCErrorShape, isTrackedEnvelope } from "@trpc/server";
import { TRPC_ERROR_CODES_BY_KEY } from "@trpc/server/rpc";
var import_defineProperty3 = __toESM(require_defineProperty(), 1);
var import_objectSpread2$4 = __toESM(require_objectSpread2(), 1);
var _a8;
var TRPCUntypedClient = (_a8 = class {
  constructor(opts) {
    (0, import_defineProperty3.default)(this, "links", void 0);
    (0, import_defineProperty3.default)(this, "runtime", void 0);
    (0, import_defineProperty3.default)(this, "requestId", void 0);
    this.requestId = 0;
    this.runtime = {};
    this.links = opts.links.map((link) => link(this.runtime));
  }
  $request(opts) {
    var _opts$context;
    const chain$ = createChain({
      links: this.links,
      op: (0, import_objectSpread2$4.default)((0, import_objectSpread2$4.default)({}, opts), {}, {
        context: (_opts$context = opts.context) !== null && _opts$context !== void 0 ? _opts$context : {},
        id: ++this.requestId
      })
    });
    return chain$.pipe(share());
  }
  async requestAsPromise(opts) {
    var _this = this;
    try {
      const req$ = _this.$request(opts);
      const envelope = await observableToPromise(req$);
      const data = envelope.result.data;
      return data;
    } catch (err) {
      throw TRPCClientError.from(err);
    }
  }
  query(path, input, opts) {
    return this.requestAsPromise({
      type: "query",
      path,
      input,
      context: opts === null || opts === void 0 ? void 0 : opts.context,
      signal: opts === null || opts === void 0 ? void 0 : opts.signal
    });
  }
  mutation(path, input, opts) {
    return this.requestAsPromise({
      type: "mutation",
      path,
      input,
      context: opts === null || opts === void 0 ? void 0 : opts.context,
      signal: opts === null || opts === void 0 ? void 0 : opts.signal
    });
  }
  subscription(path, input, opts) {
    const observable$ = this.$request({
      type: "subscription",
      path,
      input,
      context: opts.context,
      signal: opts.signal
    });
    return observable$.subscribe({
      next(envelope) {
        switch (envelope.result.type) {
          case "state": {
            var _opts$onConnectionSta;
            (_opts$onConnectionSta = opts.onConnectionStateChange) === null || _opts$onConnectionSta === void 0 || _opts$onConnectionSta.call(opts, envelope.result);
            break;
          }
          case "started": {
            var _opts$onStarted;
            (_opts$onStarted = opts.onStarted) === null || _opts$onStarted === void 0 || _opts$onStarted.call(opts, { context: envelope.context });
            break;
          }
          case "stopped": {
            var _opts$onStopped;
            (_opts$onStopped = opts.onStopped) === null || _opts$onStopped === void 0 || _opts$onStopped.call(opts);
            break;
          }
          case "data":
          case void 0: {
            var _opts$onData;
            (_opts$onData = opts.onData) === null || _opts$onData === void 0 || _opts$onData.call(opts, envelope.result.data);
            break;
          }
        }
      },
      error(err) {
        var _opts$onError;
        (_opts$onError = opts.onError) === null || _opts$onError === void 0 || _opts$onError.call(opts, err);
      },
      complete() {
        var _opts$onComplete;
        (_opts$onComplete = opts.onComplete) === null || _opts$onComplete === void 0 || _opts$onComplete.call(opts);
      }
    });
  }
}, __name(_a8, "TRPCUntypedClient"), _a8);
var untypedClientSymbol = Symbol.for("trpc_untypedClient");
var clientCallTypeMap = {
  query: "query",
  mutate: "mutation",
  subscribe: "subscription"
};
var clientCallTypeToProcedureType = /* @__PURE__ */ __name((clientCallType) => {
  return clientCallTypeMap[clientCallType];
}, "clientCallTypeToProcedureType");
function createTRPCClientProxy(client) {
  const proxy = createRecursiveProxy(({ path, args }) => {
    const pathCopy = [...path];
    const procedureType = clientCallTypeToProcedureType(pathCopy.pop());
    const fullPath = pathCopy.join(".");
    return client[procedureType](fullPath, ...args);
  });
  return createFlatProxy((key) => {
    if (key === untypedClientSymbol) return client;
    return proxy[key];
  });
}
__name(createTRPCClientProxy, "createTRPCClientProxy");
function createTRPCClient(opts) {
  const client = new TRPCUntypedClient(opts);
  const proxy = createTRPCClientProxy(client);
  return proxy;
}
__name(createTRPCClient, "createTRPCClient");
var import_objectSpread2$3 = __toESM(require_objectSpread2(), 1);
var import_objectSpread2$2 = __toESM(require_objectSpread2(), 1);
var require_asyncIterator = __commonJS({ "../../node_modules/.pnpm/@oxc-project+runtime@0.72.2/node_modules/@oxc-project/runtime/src/helpers/asyncIterator.js"(exports, module) {
  function _asyncIterator$1(r) {
    var n, t, o, e = 2;
    for ("undefined" != typeof Symbol && (t = Symbol.asyncIterator, o = Symbol.iterator); e--; ) {
      if (t && null != (n = r[t])) return n.call(r);
      if (o && null != (n = r[o])) return new AsyncFromSyncIterator(n.call(r));
      t = "@@asyncIterator", o = "@@iterator";
    }
    throw new TypeError("Object is not async iterable");
  }
  __name(_asyncIterator$1, "_asyncIterator$1");
  function AsyncFromSyncIterator(r) {
    function AsyncFromSyncIteratorContinuation(r$1) {
      if (Object(r$1) !== r$1) return Promise.reject(new TypeError(r$1 + " is not an object."));
      var n = r$1.done;
      return Promise.resolve(r$1.value).then(function(r$2) {
        return {
          value: r$2,
          done: n
        };
      });
    }
    __name(AsyncFromSyncIteratorContinuation, "AsyncFromSyncIteratorContinuation");
    return AsyncFromSyncIterator = /* @__PURE__ */ __name(function AsyncFromSyncIterator$1(r$1) {
      this.s = r$1, this.n = r$1.next;
    }, "AsyncFromSyncIterator$1"), AsyncFromSyncIterator.prototype = {
      s: null,
      n: null,
      next: /* @__PURE__ */ __name(function next() {
        return AsyncFromSyncIteratorContinuation(this.n.apply(this.s, arguments));
      }, "next"),
      "return": /* @__PURE__ */ __name(function _return(r$1) {
        var n = this.s["return"];
        return void 0 === n ? Promise.resolve({
          value: r$1,
          done: true
        }) : AsyncFromSyncIteratorContinuation(n.apply(this.s, arguments));
      }, "_return"),
      "throw": /* @__PURE__ */ __name(function _throw(r$1) {
        var n = this.s["return"];
        return void 0 === n ? Promise.reject(r$1) : AsyncFromSyncIteratorContinuation(n.apply(this.s, arguments));
      }, "_throw")
    }, new AsyncFromSyncIterator(r);
  }
  __name(AsyncFromSyncIterator, "AsyncFromSyncIterator");
  module.exports = _asyncIterator$1, module.exports.__esModule = true, module.exports["default"] = module.exports;
} });
var import_asyncIterator = __toESM(require_asyncIterator(), 1);
var import_objectSpread2$1 = __toESM(require_objectSpread2(), 1);
var require_usingCtx = __commonJS({ "../../node_modules/.pnpm/@oxc-project+runtime@0.72.2/node_modules/@oxc-project/runtime/src/helpers/usingCtx.js"(exports, module) {
  function _usingCtx() {
    var r = "function" == typeof SuppressedError ? SuppressedError : function(r$1, e$1) {
      var n$1 = Error();
      return n$1.name = "SuppressedError", n$1.error = r$1, n$1.suppressed = e$1, n$1;
    }, e = {}, n = [];
    function using(r$1, e$1) {
      if (null != e$1) {
        if (Object(e$1) !== e$1) throw new TypeError("using declarations can only be used with objects, functions, null, or undefined.");
        if (r$1) var o = e$1[Symbol.asyncDispose || Symbol["for"]("Symbol.asyncDispose")];
        if (void 0 === o && (o = e$1[Symbol.dispose || Symbol["for"]("Symbol.dispose")], r$1)) var t = o;
        if ("function" != typeof o) throw new TypeError("Object is not disposable.");
        t && (o = /* @__PURE__ */ __name(function o$1() {
          try {
            t.call(e$1);
          } catch (r$2) {
            return Promise.reject(r$2);
          }
        }, "o$1")), n.push({
          v: e$1,
          d: o,
          a: r$1
        });
      } else r$1 && n.push({
        d: e$1,
        a: r$1
      });
      return e$1;
    }
    __name(using, "using");
    return {
      e,
      u: using.bind(null, false),
      a: using.bind(null, true),
      d: /* @__PURE__ */ __name(function d() {
        var o, t = this.e, s = 0;
        function next() {
          for (; o = n.pop(); ) try {
            if (!o.a && 1 === s) return s = 0, n.push(o), Promise.resolve().then(next);
            if (o.d) {
              var r$1 = o.d.call(o.v);
              if (o.a) return s |= 2, Promise.resolve(r$1).then(next, err);
            } else s |= 1;
          } catch (r$2) {
            return err(r$2);
          }
          if (1 === s) return t !== e ? Promise.reject(t) : Promise.resolve();
          if (t !== e) throw t;
        }
        __name(next, "next");
        function err(n$1) {
          return t = t !== e ? new r(n$1, t) : n$1, next();
        }
        __name(err, "err");
        return next();
      }, "d")
    };
  }
  __name(_usingCtx, "_usingCtx");
  module.exports = _usingCtx, module.exports.__esModule = true, module.exports["default"] = module.exports;
} });
var require_OverloadYield = __commonJS({ "../../node_modules/.pnpm/@oxc-project+runtime@0.72.2/node_modules/@oxc-project/runtime/src/helpers/OverloadYield.js"(exports, module) {
  function _OverloadYield(e, d) {
    this.v = e, this.k = d;
  }
  __name(_OverloadYield, "_OverloadYield");
  module.exports = _OverloadYield, module.exports.__esModule = true, module.exports["default"] = module.exports;
} });
var require_awaitAsyncGenerator = __commonJS({ "../../node_modules/.pnpm/@oxc-project+runtime@0.72.2/node_modules/@oxc-project/runtime/src/helpers/awaitAsyncGenerator.js"(exports, module) {
  var OverloadYield$1 = require_OverloadYield();
  function _awaitAsyncGenerator$1(e) {
    return new OverloadYield$1(e, 0);
  }
  __name(_awaitAsyncGenerator$1, "_awaitAsyncGenerator$1");
  module.exports = _awaitAsyncGenerator$1, module.exports.__esModule = true, module.exports["default"] = module.exports;
} });
var require_wrapAsyncGenerator = __commonJS({ "../../node_modules/.pnpm/@oxc-project+runtime@0.72.2/node_modules/@oxc-project/runtime/src/helpers/wrapAsyncGenerator.js"(exports, module) {
  var OverloadYield = require_OverloadYield();
  function _wrapAsyncGenerator$1(e) {
    return function() {
      return new AsyncGenerator(e.apply(this, arguments));
    };
  }
  __name(_wrapAsyncGenerator$1, "_wrapAsyncGenerator$1");
  function AsyncGenerator(e) {
    var r, t;
    function resume(r$1, t$1) {
      try {
        var n = e[r$1](t$1), o = n.value, u = o instanceof OverloadYield;
        Promise.resolve(u ? o.v : o).then(function(t$2) {
          if (u) {
            var i = "return" === r$1 ? "return" : "next";
            if (!o.k || t$2.done) return resume(i, t$2);
            t$2 = e[i](t$2).value;
          }
          settle(n.done ? "return" : "normal", t$2);
        }, function(e$1) {
          resume("throw", e$1);
        });
      } catch (e$1) {
        settle("throw", e$1);
      }
    }
    __name(resume, "resume");
    function settle(e$1, n) {
      switch (e$1) {
        case "return":
          r.resolve({
            value: n,
            done: true
          });
          break;
        case "throw":
          r.reject(n);
          break;
        default:
          r.resolve({
            value: n,
            done: false
          });
      }
      (r = r.next) ? resume(r.key, r.arg) : t = null;
    }
    __name(settle, "settle");
    this._invoke = function(e$1, n) {
      return new Promise(function(o, u) {
        var i = {
          key: e$1,
          arg: n,
          resolve: o,
          reject: u,
          next: null
        };
        t ? t = t.next = i : (r = t = i, resume(e$1, n));
      });
    }, "function" != typeof e["return"] && (this["return"] = void 0);
  }
  __name(AsyncGenerator, "AsyncGenerator");
  AsyncGenerator.prototype["function" == typeof Symbol && Symbol.asyncIterator || "@@asyncIterator"] = function() {
    return this;
  }, AsyncGenerator.prototype.next = function(e) {
    return this._invoke("next", e);
  }, AsyncGenerator.prototype["throw"] = function(e) {
    return this._invoke("throw", e);
  }, AsyncGenerator.prototype["return"] = function(e) {
    return this._invoke("return", e);
  };
  module.exports = _wrapAsyncGenerator$1, module.exports.__esModule = true, module.exports["default"] = module.exports;
} });
var import_usingCtx = __toESM(require_usingCtx(), 1);
var import_awaitAsyncGenerator = __toESM(require_awaitAsyncGenerator(), 1);
var import_wrapAsyncGenerator = __toESM(require_wrapAsyncGenerator(), 1);
var import_objectSpread27 = __toESM(require_objectSpread2(), 1);

// src/client/HerebyClient.ts
import { observable as observable7 } from "@trpc/server/observable";

// ../../node_modules/.pnpm/superjson@2.2.5/node_modules/superjson/dist/double-indexed-kv.js
var _DoubleIndexedKV = class _DoubleIndexedKV {
  constructor() {
    this.keyToValue = /* @__PURE__ */ new Map();
    this.valueToKey = /* @__PURE__ */ new Map();
  }
  set(key, value) {
    this.keyToValue.set(key, value);
    this.valueToKey.set(value, key);
  }
  getByKey(key) {
    return this.keyToValue.get(key);
  }
  getByValue(value) {
    return this.valueToKey.get(value);
  }
  clear() {
    this.keyToValue.clear();
    this.valueToKey.clear();
  }
};
__name(_DoubleIndexedKV, "DoubleIndexedKV");
var DoubleIndexedKV = _DoubleIndexedKV;

// ../../node_modules/.pnpm/superjson@2.2.5/node_modules/superjson/dist/registry.js
var _Registry = class _Registry {
  constructor(generateIdentifier) {
    this.generateIdentifier = generateIdentifier;
    this.kv = new DoubleIndexedKV();
  }
  register(value, identifier) {
    if (this.kv.getByValue(value)) {
      return;
    }
    if (!identifier) {
      identifier = this.generateIdentifier(value);
    }
    this.kv.set(identifier, value);
  }
  clear() {
    this.kv.clear();
  }
  getIdentifier(value) {
    return this.kv.getByValue(value);
  }
  getValue(identifier) {
    return this.kv.getByKey(identifier);
  }
};
__name(_Registry, "Registry");
var Registry = _Registry;

// ../../node_modules/.pnpm/superjson@2.2.5/node_modules/superjson/dist/class-registry.js
var _ClassRegistry = class _ClassRegistry extends Registry {
  constructor() {
    super((c) => c.name);
    this.classToAllowedProps = /* @__PURE__ */ new Map();
  }
  register(value, options) {
    if (typeof options === "object") {
      if (options.allowProps) {
        this.classToAllowedProps.set(value, options.allowProps);
      }
      super.register(value, options.identifier);
    } else {
      super.register(value, options);
    }
  }
  getAllowedProps(value) {
    return this.classToAllowedProps.get(value);
  }
};
__name(_ClassRegistry, "ClassRegistry");
var ClassRegistry = _ClassRegistry;

// ../../node_modules/.pnpm/superjson@2.2.5/node_modules/superjson/dist/util.js
function valuesOfObj(record) {
  if ("values" in Object) {
    return Object.values(record);
  }
  const values = [];
  for (const key in record) {
    if (record.hasOwnProperty(key)) {
      values.push(record[key]);
    }
  }
  return values;
}
__name(valuesOfObj, "valuesOfObj");
function find(record, predicate) {
  const values = valuesOfObj(record);
  if ("find" in values) {
    return values.find(predicate);
  }
  const valuesNotNever = values;
  for (let i = 0; i < valuesNotNever.length; i++) {
    const value = valuesNotNever[i];
    if (predicate(value)) {
      return value;
    }
  }
  return void 0;
}
__name(find, "find");
function forEach(record, run3) {
  Object.entries(record).forEach(([key, value]) => run3(value, key));
}
__name(forEach, "forEach");
function includes(arr, value) {
  return arr.indexOf(value) !== -1;
}
__name(includes, "includes");
function findArr(record, predicate) {
  for (let i = 0; i < record.length; i++) {
    const value = record[i];
    if (predicate(value)) {
      return value;
    }
  }
  return void 0;
}
__name(findArr, "findArr");

// ../../node_modules/.pnpm/superjson@2.2.5/node_modules/superjson/dist/custom-transformer-registry.js
var _CustomTransformerRegistry = class _CustomTransformerRegistry {
  constructor() {
    this.transfomers = {};
  }
  register(transformer) {
    this.transfomers[transformer.name] = transformer;
  }
  findApplicable(v) {
    return find(this.transfomers, (transformer) => transformer.isApplicable(v));
  }
  findByName(name) {
    return this.transfomers[name];
  }
};
__name(_CustomTransformerRegistry, "CustomTransformerRegistry");
var CustomTransformerRegistry = _CustomTransformerRegistry;

// ../../node_modules/.pnpm/superjson@2.2.5/node_modules/superjson/dist/is.js
var getType = /* @__PURE__ */ __name((payload) => Object.prototype.toString.call(payload).slice(8, -1), "getType");
var isUndefined = /* @__PURE__ */ __name((payload) => typeof payload === "undefined", "isUndefined");
var isNull = /* @__PURE__ */ __name((payload) => payload === null, "isNull");
var isPlainObject = /* @__PURE__ */ __name((payload) => {
  if (typeof payload !== "object" || payload === null) return false;
  if (payload === Object.prototype) return false;
  if (Object.getPrototypeOf(payload) === null) return true;
  return Object.getPrototypeOf(payload) === Object.prototype;
}, "isPlainObject");
var isEmptyObject = /* @__PURE__ */ __name((payload) => isPlainObject(payload) && Object.keys(payload).length === 0, "isEmptyObject");
var isArray = /* @__PURE__ */ __name((payload) => Array.isArray(payload), "isArray");
var isString = /* @__PURE__ */ __name((payload) => typeof payload === "string", "isString");
var isNumber = /* @__PURE__ */ __name((payload) => typeof payload === "number" && !isNaN(payload), "isNumber");
var isBoolean = /* @__PURE__ */ __name((payload) => typeof payload === "boolean", "isBoolean");
var isRegExp = /* @__PURE__ */ __name((payload) => payload instanceof RegExp, "isRegExp");
var isMap = /* @__PURE__ */ __name((payload) => payload instanceof Map, "isMap");
var isSet = /* @__PURE__ */ __name((payload) => payload instanceof Set, "isSet");
var isSymbol = /* @__PURE__ */ __name((payload) => getType(payload) === "Symbol", "isSymbol");
var isDate = /* @__PURE__ */ __name((payload) => payload instanceof Date && !isNaN(payload.valueOf()), "isDate");
var isError = /* @__PURE__ */ __name((payload) => payload instanceof Error, "isError");
var isNaNValue = /* @__PURE__ */ __name((payload) => typeof payload === "number" && isNaN(payload), "isNaNValue");
var isPrimitive = /* @__PURE__ */ __name((payload) => isBoolean(payload) || isNull(payload) || isUndefined(payload) || isNumber(payload) || isString(payload) || isSymbol(payload), "isPrimitive");
var isBigint = /* @__PURE__ */ __name((payload) => typeof payload === "bigint", "isBigint");
var isInfinite = /* @__PURE__ */ __name((payload) => payload === Infinity || payload === -Infinity, "isInfinite");
var isTypedArray = /* @__PURE__ */ __name((payload) => ArrayBuffer.isView(payload) && !(payload instanceof DataView), "isTypedArray");
var isURL = /* @__PURE__ */ __name((payload) => payload instanceof URL, "isURL");

// ../../node_modules/.pnpm/superjson@2.2.5/node_modules/superjson/dist/pathstringifier.js
var escapeKey = /* @__PURE__ */ __name((key) => key.replace(/\\/g, "\\\\").replace(/\./g, "\\."), "escapeKey");
var stringifyPath = /* @__PURE__ */ __name((path) => path.map(String).map(escapeKey).join("."), "stringifyPath");
var parsePath = /* @__PURE__ */ __name((string, legacyPaths) => {
  const result = [];
  let segment = "";
  for (let i = 0; i < string.length; i++) {
    let char = string.charAt(i);
    if (!legacyPaths && char === "\\") {
      const escaped = string.charAt(i + 1);
      if (escaped === "\\") {
        segment += "\\";
        i++;
        continue;
      } else if (escaped !== ".") {
        throw Error("invalid path");
      }
    }
    const isEscapedDot = char === "\\" && string.charAt(i + 1) === ".";
    if (isEscapedDot) {
      segment += ".";
      i++;
      continue;
    }
    const isEndOfSegment = char === ".";
    if (isEndOfSegment) {
      result.push(segment);
      segment = "";
      continue;
    }
    segment += char;
  }
  const lastSegment = segment;
  result.push(lastSegment);
  return result;
}, "parsePath");

// ../../node_modules/.pnpm/superjson@2.2.5/node_modules/superjson/dist/transformer.js
function simpleTransformation(isApplicable, annotation, transform, untransform) {
  return {
    isApplicable,
    annotation,
    transform,
    untransform
  };
}
__name(simpleTransformation, "simpleTransformation");
var simpleRules = [
  simpleTransformation(isUndefined, "undefined", () => null, () => void 0),
  simpleTransformation(isBigint, "bigint", (v) => v.toString(), (v) => {
    if (typeof BigInt !== "undefined") {
      return BigInt(v);
    }
    console.error("Please add a BigInt polyfill.");
    return v;
  }),
  simpleTransformation(isDate, "Date", (v) => v.toISOString(), (v) => new Date(v)),
  simpleTransformation(isError, "Error", (v, superJson) => {
    const baseError = {
      name: v.name,
      message: v.message
    };
    if ("cause" in v) {
      baseError.cause = v.cause;
    }
    superJson.allowedErrorProps.forEach((prop) => {
      baseError[prop] = v[prop];
    });
    return baseError;
  }, (v, superJson) => {
    const e = new Error(v.message, {
      cause: v.cause
    });
    e.name = v.name;
    e.stack = v.stack;
    superJson.allowedErrorProps.forEach((prop) => {
      e[prop] = v[prop];
    });
    return e;
  }),
  simpleTransformation(isRegExp, "regexp", (v) => "" + v, (regex) => {
    const body = regex.slice(1, regex.lastIndexOf("/"));
    const flags = regex.slice(regex.lastIndexOf("/") + 1);
    return new RegExp(body, flags);
  }),
  simpleTransformation(
    isSet,
    "set",
    // (sets only exist in es6+)
    // eslint-disable-next-line es5/no-es6-methods
    (v) => [
      ...v.values()
    ],
    (v) => new Set(v)
  ),
  simpleTransformation(isMap, "map", (v) => [
    ...v.entries()
  ], (v) => new Map(v)),
  simpleTransformation((v) => isNaNValue(v) || isInfinite(v), "number", (v) => {
    if (isNaNValue(v)) {
      return "NaN";
    }
    if (v > 0) {
      return "Infinity";
    } else {
      return "-Infinity";
    }
  }, Number),
  simpleTransformation((v) => v === 0 && 1 / v === -Infinity, "number", () => {
    return "-0";
  }, Number),
  simpleTransformation(isURL, "URL", (v) => v.toString(), (v) => new URL(v))
];
function compositeTransformation(isApplicable, annotation, transform, untransform) {
  return {
    isApplicable,
    annotation,
    transform,
    untransform
  };
}
__name(compositeTransformation, "compositeTransformation");
var symbolRule = compositeTransformation((s, superJson) => {
  if (isSymbol(s)) {
    const isRegistered = !!superJson.symbolRegistry.getIdentifier(s);
    return isRegistered;
  }
  return false;
}, (s, superJson) => {
  const identifier = superJson.symbolRegistry.getIdentifier(s);
  return [
    "symbol",
    identifier
  ];
}, (v) => v.description, (_, a, superJson) => {
  const value = superJson.symbolRegistry.getValue(a[1]);
  if (!value) {
    throw new Error("Trying to deserialize unknown symbol");
  }
  return value;
});
var constructorToName = [
  Int8Array,
  Uint8Array,
  Int16Array,
  Uint16Array,
  Int32Array,
  Uint32Array,
  Float32Array,
  Float64Array,
  Uint8ClampedArray
].reduce((obj, ctor) => {
  obj[ctor.name] = ctor;
  return obj;
}, {});
var typedArrayRule = compositeTransformation(isTypedArray, (v) => [
  "typed-array",
  v.constructor.name
], (v) => [
  ...v
], (v, a) => {
  const ctor = constructorToName[a[1]];
  if (!ctor) {
    throw new Error("Trying to deserialize unknown typed array");
  }
  return new ctor(v);
});
function isInstanceOfRegisteredClass(potentialClass, superJson) {
  if (potentialClass?.constructor) {
    const isRegistered = !!superJson.classRegistry.getIdentifier(potentialClass.constructor);
    return isRegistered;
  }
  return false;
}
__name(isInstanceOfRegisteredClass, "isInstanceOfRegisteredClass");
var classRule = compositeTransformation(isInstanceOfRegisteredClass, (clazz, superJson) => {
  const identifier = superJson.classRegistry.getIdentifier(clazz.constructor);
  return [
    "class",
    identifier
  ];
}, (clazz, superJson) => {
  const allowedProps = superJson.classRegistry.getAllowedProps(clazz.constructor);
  if (!allowedProps) {
    return {
      ...clazz
    };
  }
  const result = {};
  allowedProps.forEach((prop) => {
    result[prop] = clazz[prop];
  });
  return result;
}, (v, a, superJson) => {
  const clazz = superJson.classRegistry.getValue(a[1]);
  if (!clazz) {
    throw new Error(`Trying to deserialize unknown class '${a[1]}' - check https://github.com/blitz-js/superjson/issues/116#issuecomment-773996564`);
  }
  return Object.assign(Object.create(clazz.prototype), v);
});
var customRule = compositeTransformation((value, superJson) => {
  return !!superJson.customTransformerRegistry.findApplicable(value);
}, (value, superJson) => {
  const transformer = superJson.customTransformerRegistry.findApplicable(value);
  return [
    "custom",
    transformer.name
  ];
}, (value, superJson) => {
  const transformer = superJson.customTransformerRegistry.findApplicable(value);
  return transformer.serialize(value);
}, (v, a, superJson) => {
  const transformer = superJson.customTransformerRegistry.findByName(a[1]);
  if (!transformer) {
    throw new Error("Trying to deserialize unknown custom value");
  }
  return transformer.deserialize(v);
});
var compositeRules = [
  classRule,
  symbolRule,
  customRule,
  typedArrayRule
];
var transformValue = /* @__PURE__ */ __name((value, superJson) => {
  const applicableCompositeRule = findArr(compositeRules, (rule) => rule.isApplicable(value, superJson));
  if (applicableCompositeRule) {
    return {
      value: applicableCompositeRule.transform(value, superJson),
      type: applicableCompositeRule.annotation(value, superJson)
    };
  }
  const applicableSimpleRule = findArr(simpleRules, (rule) => rule.isApplicable(value, superJson));
  if (applicableSimpleRule) {
    return {
      value: applicableSimpleRule.transform(value, superJson),
      type: applicableSimpleRule.annotation
    };
  }
  return void 0;
}, "transformValue");
var simpleRulesByAnnotation = {};
simpleRules.forEach((rule) => {
  simpleRulesByAnnotation[rule.annotation] = rule;
});
var untransformValue = /* @__PURE__ */ __name((json, type, superJson) => {
  if (isArray(type)) {
    switch (type[0]) {
      case "symbol":
        return symbolRule.untransform(json, type, superJson);
      case "class":
        return classRule.untransform(json, type, superJson);
      case "custom":
        return customRule.untransform(json, type, superJson);
      case "typed-array":
        return typedArrayRule.untransform(json, type, superJson);
      default:
        throw new Error("Unknown transformation: " + type);
    }
  } else {
    const transformation = simpleRulesByAnnotation[type];
    if (!transformation) {
      throw new Error("Unknown transformation: " + type);
    }
    return transformation.untransform(json, superJson);
  }
}, "untransformValue");

// ../../node_modules/.pnpm/superjson@2.2.5/node_modules/superjson/dist/accessDeep.js
var getNthKey = /* @__PURE__ */ __name((value, n) => {
  if (n > value.size) throw new Error("index out of bounds");
  const keys = value.keys();
  while (n > 0) {
    keys.next();
    n--;
  }
  return keys.next().value;
}, "getNthKey");
function validatePath(path) {
  if (includes(path, "__proto__")) {
    throw new Error("__proto__ is not allowed as a property");
  }
  if (includes(path, "prototype")) {
    throw new Error("prototype is not allowed as a property");
  }
  if (includes(path, "constructor")) {
    throw new Error("constructor is not allowed as a property");
  }
}
__name(validatePath, "validatePath");
var getDeep = /* @__PURE__ */ __name((object, path) => {
  validatePath(path);
  for (let i = 0; i < path.length; i++) {
    const key = path[i];
    if (isSet(object)) {
      object = getNthKey(object, +key);
    } else if (isMap(object)) {
      const row = +key;
      const type = +path[++i] === 0 ? "key" : "value";
      const keyOfRow = getNthKey(object, row);
      switch (type) {
        case "key":
          object = keyOfRow;
          break;
        case "value":
          object = object.get(keyOfRow);
          break;
      }
    } else {
      object = object[key];
    }
  }
  return object;
}, "getDeep");
var setDeep = /* @__PURE__ */ __name((object, path, mapper) => {
  validatePath(path);
  if (path.length === 0) {
    return mapper(object);
  }
  let parent = object;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    if (isArray(parent)) {
      const index = +key;
      parent = parent[index];
    } else if (isPlainObject(parent)) {
      parent = parent[key];
    } else if (isSet(parent)) {
      const row = +key;
      parent = getNthKey(parent, row);
    } else if (isMap(parent)) {
      const isEnd = i === path.length - 2;
      if (isEnd) {
        break;
      }
      const row = +key;
      const type = +path[++i] === 0 ? "key" : "value";
      const keyOfRow = getNthKey(parent, row);
      switch (type) {
        case "key":
          parent = keyOfRow;
          break;
        case "value":
          parent = parent.get(keyOfRow);
          break;
      }
    }
  }
  const lastKey = path[path.length - 1];
  if (isArray(parent)) {
    parent[+lastKey] = mapper(parent[+lastKey]);
  } else if (isPlainObject(parent)) {
    parent[lastKey] = mapper(parent[lastKey]);
  }
  if (isSet(parent)) {
    const oldValue = getNthKey(parent, +lastKey);
    const newValue = mapper(oldValue);
    if (oldValue !== newValue) {
      parent.delete(oldValue);
      parent.add(newValue);
    }
  }
  if (isMap(parent)) {
    const row = +path[path.length - 2];
    const keyToRow = getNthKey(parent, row);
    const type = +lastKey === 0 ? "key" : "value";
    switch (type) {
      case "key": {
        const newKey = mapper(keyToRow);
        parent.set(newKey, parent.get(keyToRow));
        if (newKey !== keyToRow) {
          parent.delete(keyToRow);
        }
        break;
      }
      case "value": {
        parent.set(keyToRow, mapper(parent.get(keyToRow)));
        break;
      }
    }
  }
  return object;
}, "setDeep");

// ../../node_modules/.pnpm/superjson@2.2.5/node_modules/superjson/dist/plainer.js
var enableLegacyPaths = /* @__PURE__ */ __name((version) => version < 1, "enableLegacyPaths");
function traverse(tree, walker2, version, origin = []) {
  if (!tree) {
    return;
  }
  const legacyPaths = enableLegacyPaths(version);
  if (!isArray(tree)) {
    forEach(tree, (subtree, key) => traverse(subtree, walker2, version, [
      ...origin,
      ...parsePath(key, legacyPaths)
    ]));
    return;
  }
  const [nodeValue, children] = tree;
  if (children) {
    forEach(children, (child, key) => {
      traverse(child, walker2, version, [
        ...origin,
        ...parsePath(key, legacyPaths)
      ]);
    });
  }
  walker2(nodeValue, origin);
}
__name(traverse, "traverse");
function applyValueAnnotations(plain, annotations, version, superJson) {
  traverse(annotations, (type, path) => {
    plain = setDeep(plain, path, (v) => untransformValue(v, type, superJson));
  }, version);
  return plain;
}
__name(applyValueAnnotations, "applyValueAnnotations");
function applyReferentialEqualityAnnotations(plain, annotations, version) {
  const legacyPaths = enableLegacyPaths(version);
  function apply(identicalPaths, path) {
    const object = getDeep(plain, parsePath(path, legacyPaths));
    identicalPaths.map((path2) => parsePath(path2, legacyPaths)).forEach((identicalObjectPath) => {
      plain = setDeep(plain, identicalObjectPath, () => object);
    });
  }
  __name(apply, "apply");
  if (isArray(annotations)) {
    const [root, other] = annotations;
    root.forEach((identicalPath) => {
      plain = setDeep(plain, parsePath(identicalPath, legacyPaths), () => plain);
    });
    if (other) {
      forEach(other, apply);
    }
  } else {
    forEach(annotations, apply);
  }
  return plain;
}
__name(applyReferentialEqualityAnnotations, "applyReferentialEqualityAnnotations");
var isDeep = /* @__PURE__ */ __name((object, superJson) => isPlainObject(object) || isArray(object) || isMap(object) || isSet(object) || isError(object) || isInstanceOfRegisteredClass(object, superJson), "isDeep");
function addIdentity(object, path, identities) {
  const existingSet = identities.get(object);
  if (existingSet) {
    existingSet.push(path);
  } else {
    identities.set(object, [
      path
    ]);
  }
}
__name(addIdentity, "addIdentity");
function generateReferentialEqualityAnnotations(identitites, dedupe) {
  const result = {};
  let rootEqualityPaths = void 0;
  identitites.forEach((paths) => {
    if (paths.length <= 1) {
      return;
    }
    if (!dedupe) {
      paths = paths.map((path) => path.map(String)).sort((a, b) => a.length - b.length);
    }
    const [representativePath, ...identicalPaths] = paths;
    if (representativePath.length === 0) {
      rootEqualityPaths = identicalPaths.map(stringifyPath);
    } else {
      result[stringifyPath(representativePath)] = identicalPaths.map(stringifyPath);
    }
  });
  if (rootEqualityPaths) {
    if (isEmptyObject(result)) {
      return [
        rootEqualityPaths
      ];
    } else {
      return [
        rootEqualityPaths,
        result
      ];
    }
  } else {
    return isEmptyObject(result) ? void 0 : result;
  }
}
__name(generateReferentialEqualityAnnotations, "generateReferentialEqualityAnnotations");
var walker = /* @__PURE__ */ __name((object, identities, superJson, dedupe, path = [], objectsInThisPath = [], seenObjects = /* @__PURE__ */ new Map()) => {
  const primitive = isPrimitive(object);
  if (!primitive) {
    addIdentity(object, path, identities);
    const seen = seenObjects.get(object);
    if (seen) {
      return dedupe ? {
        transformedValue: null
      } : seen;
    }
  }
  if (!isDeep(object, superJson)) {
    const transformed2 = transformValue(object, superJson);
    const result2 = transformed2 ? {
      transformedValue: transformed2.value,
      annotations: [
        transformed2.type
      ]
    } : {
      transformedValue: object
    };
    if (!primitive) {
      seenObjects.set(object, result2);
    }
    return result2;
  }
  if (includes(objectsInThisPath, object)) {
    return {
      transformedValue: null
    };
  }
  const transformationResult = transformValue(object, superJson);
  const transformed = transformationResult?.value ?? object;
  const transformedValue = isArray(transformed) ? [] : {};
  const innerAnnotations = {};
  forEach(transformed, (value, index) => {
    if (index === "__proto__" || index === "constructor" || index === "prototype") {
      throw new Error(`Detected property ${index}. This is a prototype pollution risk, please remove it from your object.`);
    }
    const recursiveResult = walker(value, identities, superJson, dedupe, [
      ...path,
      index
    ], [
      ...objectsInThisPath,
      object
    ], seenObjects);
    transformedValue[index] = recursiveResult.transformedValue;
    if (isArray(recursiveResult.annotations)) {
      innerAnnotations[escapeKey(index)] = recursiveResult.annotations;
    } else if (isPlainObject(recursiveResult.annotations)) {
      forEach(recursiveResult.annotations, (tree, key) => {
        innerAnnotations[escapeKey(index) + "." + key] = tree;
      });
    }
  });
  const result = isEmptyObject(innerAnnotations) ? {
    transformedValue,
    annotations: !!transformationResult ? [
      transformationResult.type
    ] : void 0
  } : {
    transformedValue,
    annotations: !!transformationResult ? [
      transformationResult.type,
      innerAnnotations
    ] : innerAnnotations
  };
  if (!primitive) {
    seenObjects.set(object, result);
  }
  return result;
}, "walker");

// ../../node_modules/.pnpm/is-what@5.5.0/node_modules/is-what/dist/getType.js
function getType2(payload) {
  return Object.prototype.toString.call(payload).slice(8, -1);
}
__name(getType2, "getType");

// ../../node_modules/.pnpm/is-what@5.5.0/node_modules/is-what/dist/isArray.js
function isArray2(payload) {
  return getType2(payload) === "Array";
}
__name(isArray2, "isArray");

// ../../node_modules/.pnpm/is-what@5.5.0/node_modules/is-what/dist/isPlainObject.js
function isPlainObject2(payload) {
  if (getType2(payload) !== "Object") return false;
  const prototype = Object.getPrototypeOf(payload);
  return !!prototype && prototype.constructor === Object && prototype === Object.prototype;
}
__name(isPlainObject2, "isPlainObject");

// ../../node_modules/.pnpm/copy-anything@4.0.5/node_modules/copy-anything/dist/index.js
function assignProp(carry, key, newVal, originalObject, includeNonenumerable) {
  const propType = {}.propertyIsEnumerable.call(originalObject, key) ? "enumerable" : "nonenumerable";
  if (propType === "enumerable") carry[key] = newVal;
  if (includeNonenumerable && propType === "nonenumerable") {
    Object.defineProperty(carry, key, {
      value: newVal,
      enumerable: false,
      writable: true,
      configurable: true
    });
  }
}
__name(assignProp, "assignProp");
function copy(target, options = {}) {
  if (isArray2(target)) {
    return target.map((item) => copy(item, options));
  }
  if (!isPlainObject2(target)) {
    return target;
  }
  const props = Object.getOwnPropertyNames(target);
  const symbols = Object.getOwnPropertySymbols(target);
  return [
    ...props,
    ...symbols
  ].reduce((carry, key) => {
    if (key === "__proto__") return carry;
    if (isArray2(options.props) && !options.props.includes(key)) {
      return carry;
    }
    const val = target[key];
    const newVal = copy(val, options);
    assignProp(carry, key, newVal, target, options.nonenumerable);
    return carry;
  }, {});
}
__name(copy, "copy");

// ../../node_modules/.pnpm/superjson@2.2.5/node_modules/superjson/dist/index.js
var _SuperJSON = class _SuperJSON {
  /**
   * @param dedupeReferentialEqualities  If true, SuperJSON will make sure only one instance of referentially equal objects are serialized and the rest are replaced with `null`.
   */
  constructor({ dedupe = false } = {}) {
    this.classRegistry = new ClassRegistry();
    this.symbolRegistry = new Registry((s) => s.description ?? "");
    this.customTransformerRegistry = new CustomTransformerRegistry();
    this.allowedErrorProps = [];
    this.dedupe = dedupe;
  }
  serialize(object) {
    const identities = /* @__PURE__ */ new Map();
    const output = walker(object, identities, this, this.dedupe);
    const res = {
      json: output.transformedValue
    };
    if (output.annotations) {
      res.meta = {
        ...res.meta,
        values: output.annotations
      };
    }
    const equalityAnnotations = generateReferentialEqualityAnnotations(identities, this.dedupe);
    if (equalityAnnotations) {
      res.meta = {
        ...res.meta,
        referentialEqualities: equalityAnnotations
      };
    }
    if (res.meta) res.meta.v = 1;
    return res;
  }
  deserialize(payload, options) {
    const { json, meta } = payload;
    let result = options?.inPlace ? json : copy(json);
    if (meta?.values) {
      result = applyValueAnnotations(result, meta.values, meta.v ?? 0, this);
    }
    if (meta?.referentialEqualities) {
      result = applyReferentialEqualityAnnotations(result, meta.referentialEqualities, meta.v ?? 0);
    }
    return result;
  }
  stringify(object) {
    return JSON.stringify(this.serialize(object));
  }
  parse(string) {
    return this.deserialize(JSON.parse(string), {
      inPlace: true
    });
  }
  registerClass(v, options) {
    this.classRegistry.register(v, options);
  }
  registerSymbol(v, identifier) {
    this.symbolRegistry.register(v, identifier);
  }
  registerCustom(transformer, name) {
    this.customTransformerRegistry.register({
      name,
      ...transformer
    });
  }
  allowErrorProps(...props) {
    this.allowedErrorProps.push(...props);
  }
};
__name(_SuperJSON, "SuperJSON");
var SuperJSON = _SuperJSON;
SuperJSON.defaultInstance = new SuperJSON();
SuperJSON.serialize = SuperJSON.defaultInstance.serialize.bind(SuperJSON.defaultInstance);
SuperJSON.deserialize = SuperJSON.defaultInstance.deserialize.bind(SuperJSON.defaultInstance);
SuperJSON.stringify = SuperJSON.defaultInstance.stringify.bind(SuperJSON.defaultInstance);
SuperJSON.parse = SuperJSON.defaultInstance.parse.bind(SuperJSON.defaultInstance);
SuperJSON.registerClass = SuperJSON.defaultInstance.registerClass.bind(SuperJSON.defaultInstance);
SuperJSON.registerSymbol = SuperJSON.defaultInstance.registerSymbol.bind(SuperJSON.defaultInstance);
SuperJSON.registerCustom = SuperJSON.defaultInstance.registerCustom.bind(SuperJSON.defaultInstance);
SuperJSON.allowErrorProps = SuperJSON.defaultInstance.allowErrorProps.bind(SuperJSON.defaultInstance);
var serialize = SuperJSON.serialize;
var deserialize = SuperJSON.deserialize;
var stringify = SuperJSON.stringify;
var parse = SuperJSON.parse;
var registerClass = SuperJSON.registerClass;
var registerCustom = SuperJSON.registerCustom;
var registerSymbol = SuperJSON.registerSymbol;
var allowErrorProps = SuperJSON.allowErrorProps;

// src/cs-report/collector.ts
import { redactText, redactValue } from "@hereby/core/redact";
var MAX_ENTRIES = 200;
var MAX_OK_API_ENTRIES = 20;
var MAX_STRING = 2e3;
var MAX_SERIALIZED_BYTES = 256 * 1024;
var ERROR_SNAPSHOT_TTL_MS = 5e3;
var MAX_STACK_LINES = 20;
function clip(value) {
  return value.length > MAX_STRING ? `${value.slice(0, MAX_STRING)}\u2026` : value;
}
__name(clip, "clip");
function utf8Bytes(value) {
  return new TextEncoder().encode(value).length;
}
__name(utf8Bytes, "utf8Bytes");
var _CsReportCollector = class _CsReportCollector {
  constructor() {
    __publicField(this, "entries", []);
    __publicField(this, "lastError", null);
  }
  push(entry) {
    this.entries.push(entry);
    if (this.entries.length > MAX_ENTRIES) {
      this.entries.splice(0, this.entries.length - MAX_ENTRIES);
    }
    const okApi = this.entries.filter((e) => e.kind === "api" && e.level === "debug");
    if (okApi.length > MAX_OK_API_ENTRIES) {
      const drop = new Set(okApi.slice(0, okApi.length - MAX_OK_API_ENTRIES));
      this.entries = this.entries.filter((e) => !drop.has(e));
    }
  }
  recordLog(level, msg, data) {
    this.push({
      ts: Date.now(),
      level,
      kind: "log",
      msg: clip(redactText(msg)),
      ...data === void 0 ? {} : {
        data: redactValue(data, {
          maxStringLength: 500
        })
      }
    });
  }
  /** 전역 오류 훅·Alpine 표현식 오류 — 링버퍼 기록 + 마지막 오류 스냅샷 보관 */
  recordError(input) {
    const stack = input.stack ? redactText(input.stack.split("\n").slice(0, MAX_STACK_LINES).join("\n")) : void 0;
    const message = clip(redactText(input.message));
    this.push({
      ts: Date.now(),
      level: "error",
      kind: "error",
      msg: message,
      ...stack ? {
        data: {
          stack
        }
      } : {}
    });
    this.lastError = {
      at: Date.now(),
      snapshot: {
        message,
        occurredAt: (/* @__PURE__ */ new Date()).toISOString(),
        ...input.code ? {
          code: input.code
        } : {},
        ...input.requestId ? {
          requestId: input.requestId
        } : {},
        ...stack ? {
          stack
        } : {}
      }
    };
  }
  /**
  * API 호출 요약. 실패(ok=false)는 상태·코드·requestId 까지, 성공은 경로·ms 만
  * level=debug 로 기록해 별도 상한(20건)을 받는다.
  */
  recordApi(input) {
    this.push({
      ts: Date.now(),
      level: input.ok ? "debug" : "error",
      kind: "api",
      msg: clip(redactText(input.path)),
      data: input.ok ? {
        ms: input.ms
      } : {
        status: input.status,
        code: input.code,
        requestId: input.requestId,
        ms: input.ms
      }
    });
  }
  /** 화면 이동 — "어디서 어디로 가다가"를 운영자가 읽을 수 있게 (§5.2) */
  recordNav(from, to) {
    this.push({
      ts: Date.now(),
      level: "info",
      kind: "nav",
      msg: `${redactText(from)} \u2192 ${redactText(to)}`
    });
  }
  /** 마지막 오류 스냅샷 — TTL(5초) 안일 때만 돌려준다 (§4.1 A 자동 연결) */
  peekLastError(now = Date.now()) {
    if (!this.lastError || now - this.lastError.at > ERROR_SNAPSHOT_TTL_MS) {
      return null;
    }
    return this.lastError.snapshot;
  }
  /** 오류 다이얼로그가 명시적으로 스냅샷을 밀어 넣을 때 (tRPC 오류 등) */
  setLastError(snapshot) {
    this.lastError = {
      at: Date.now(),
      snapshot
    };
  }
  /** 리포트 첨부용 스냅샷 — 직렬화 상한을 넘으면 오래된 항목부터 버린다 */
  snapshot() {
    let list = [
      ...this.entries
    ];
    while (list.length > 0 && utf8Bytes(JSON.stringify(list)) > MAX_SERIALIZED_BYTES) {
      list = list.slice(Math.max(1, Math.floor(list.length / 10)));
    }
    return list;
  }
  clear() {
    this.entries = [];
    this.lastError = null;
  }
};
__name(_CsReportCollector, "CsReportCollector");
var CsReportCollector = _CsReportCollector;
var csCollector = new CsReportCollector();
function collectEnvContext() {
  if (typeof window === "undefined") {
    return {};
  }
  const cap = globalThis.Capacitor;
  const nav = navigator;
  let timezone;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = void 0;
  }
  const platformRaw = cap?.getPlatform?.();
  return {
    userAgent: nav.userAgent?.slice(0, 512),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    devicePixelRatio: window.devicePixelRatio,
    language: nav.language,
    timezone,
    platform: platformRaw === "ios" || platformRaw === "android" ? platformRaw : "web",
    buildVersion: typeof __HEREBY_BUILD__ !== "undefined" ? __HEREBY_BUILD__ : void 0,
    online: nav.onLine,
    connectionType: nav.connection?.effectiveType,
    embedded: window.self !== window.top,
    parentUrl: window.self !== window.top ? document.referrer?.slice(0, 512) : void 0
  };
}
__name(collectEnvContext, "collectEnvContext");
var hooksInstalled = false;
function installCsReportHooks() {
  if (hooksInstalled || typeof window === "undefined") {
    return;
  }
  hooksInstalled = true;
  window.addEventListener("error", (event) => {
    const err = event.error;
    csCollector.recordError({
      message: event.message || String(err?.message ?? "unknown error"),
      stack: err?.stack
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    csCollector.recordError({
      message: String(reason?.message ?? reason ?? "unhandled rejection"),
      stack: reason?.stack
    });
  });
  let lastRoute = `${window.location.pathname}${window.location.hash}`;
  const onNavigate = /* @__PURE__ */ __name(() => {
    const next = `${window.location.pathname}${window.location.hash}`;
    if (next !== lastRoute) {
      csCollector.recordNav(lastRoute, next);
      lastRoute = next;
    }
  }, "onNavigate");
  window.addEventListener("hashchange", onNavigate);
  window.addEventListener("popstate", onNavigate);
}
__name(installCsReportHooks, "installCsReportHooks");

// src/utils/logger.ts
var LOG_LEVELS = {
  silent: Number.POSITIVE_INFINITY,
  fatal: 60,
  error: 50,
  warn: 40,
  info: 30,
  debug: 20,
  trace: 10
};
function createConsoleLogger(level, bindings = {}) {
  const threshold = LOG_LEVELS[level] ?? LOG_LEVELS.warn;
  const prefix = bindings.service ? `[${bindings.service}]` : "[sdk]";
  function makeLogFn(methodLevel, consoleFn) {
    return (obj, msg, ...args) => {
      if (methodLevel < threshold) {
        return;
      }
      if (typeof obj === "string") {
        consoleFn(prefix, obj, msg, ...args);
      } else {
        consoleFn(prefix, msg || "", obj, ...args);
      }
    };
  }
  __name(makeLogFn, "makeLogFn");
  return {
    level,
    // biome-ignore lint/suspicious/noConsole: This IS the logger implementation — console is the output target
    trace: makeLogFn(LOG_LEVELS.trace, console.debug),
    // biome-ignore lint/suspicious/noConsole: This IS the logger implementation
    debug: makeLogFn(LOG_LEVELS.debug, console.debug),
    // biome-ignore lint/suspicious/noConsole: This IS the logger implementation
    info: makeLogFn(LOG_LEVELS.info, console.info),
    // biome-ignore lint/suspicious/noConsole: This IS the logger implementation
    warn: makeLogFn(LOG_LEVELS.warn, console.warn),
    // biome-ignore lint/suspicious/noConsole: This IS the logger implementation
    error: makeLogFn(LOG_LEVELS.error, console.error),
    // biome-ignore lint/suspicious/noConsole: This IS the logger implementation
    fatal: makeLogFn(LOG_LEVELS.fatal, console.error),
    child(childBindings) {
      return createConsoleLogger(level, {
        ...bindings,
        ...childBindings
      });
    }
  };
}
__name(createConsoleLogger, "createConsoleLogger");
var _a9;
var SDKLogger = (_a9 = class {
  constructor() {
    __publicField(this, "logger");
    __publicField(this, "config", {});
    this.logger = createConsoleLogger("warn");
  }
  configure(config) {
    this.config = config;
    if (config.customLogger) {
      this.logger = config.customLogger;
    } else if (config.silent) {
      this.logger = createConsoleLogger("silent");
    } else if (config.debug) {
      this.logger = createConsoleLogger("debug");
    } else {
      this.logger = createConsoleLogger("warn");
    }
  }
  getLogger() {
    return this.logger;
  }
  createServiceLogger(serviceName) {
    return this.logger.child({
      service: serviceName
    });
  }
  debug(...args) {
    if (!this.config.silent) {
      this.logger.debug(...args);
    }
  }
  info(...args) {
    if (!this.config.silent) {
      this.logger.info(...args);
    }
  }
  warn(...args) {
    if (!this.config.silent) {
      this.logger.warn(...args);
    }
  }
  error(...args) {
    if (!this.config.silent) {
      this.logger.error(...args);
    }
  }
}, __name(_a9, "SDKLogger"), _a9);
var sdkLogger = new SDKLogger();
var logger = sdkLogger.getLogger();

// src/utils/password-encrypt.ts
var PREFIX = "enc:v1:";
var cachedKey = null;
var cachedPem = null;
function getSubtle() {
  const subtle2 = globalThis.crypto?.subtle;
  if (!subtle2) {
    throw new Error("Web Crypto API (crypto.subtle) is not available. Requires a browser or Node 20+.");
  }
  return subtle2;
}
__name(getSubtle, "getSubtle");
function pemToDer(pem) {
  const body = pem.replace(/-----BEGIN [^-]+-----/, "").replace(/-----END [^-]+-----/, "").replace(/\s+/g, "");
  const binary = typeof atob === "function" ? atob(body) : Buffer.from(body, "base64").toString("binary");
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return buffer;
}
__name(pemToDer, "pemToDer");
function bytesToBase64(bytes) {
  if (typeof btoa === "function") {
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
  return Buffer.from(bytes).toString("base64");
}
__name(bytesToBase64, "bytesToBase64");
async function importKey(pem) {
  const der = pemToDer(pem);
  return getSubtle().importKey("spki", der, {
    name: "RSA-OAEP",
    hash: "SHA-256"
  }, false, [
    "encrypt"
  ]);
}
__name(importKey, "importKey");
async function encryptPassword(plaintext, publicKeyPem) {
  if (!plaintext) {
    return plaintext;
  }
  if (!cachedKey || cachedPem !== publicKeyPem) {
    cachedKey = await importKey(publicKeyPem);
    cachedPem = publicKeyPem;
  }
  const encoded = new TextEncoder().encode(plaintext);
  const data = new ArrayBuffer(encoded.byteLength);
  new Uint8Array(data).set(encoded);
  const ciphertext = await getSubtle().encrypt({
    name: "RSA-OAEP"
  }, cachedKey, data);
  return PREFIX + bytesToBase64(new Uint8Array(ciphertext));
}
__name(encryptPassword, "encryptPassword");

// src/services/AccountService.ts
var _AccountService = class _AccountService {
  constructor(client) {
    __publicField(this, "client");
    __publicField(this, "logger");
    __publicField(this, "publicKeyPem", null);
    this.client = client;
    this.logger = sdkLogger.createServiceLogger("AccountService");
  }
  async encrypt(password) {
    try {
      if (!this.publicKeyPem) {
        const result = await this.client.trpc.auth.publicKey.query();
        this.publicKeyPem = result.publicKeyPem;
      }
      return await encryptPassword(password, this.publicKeyPem);
    } catch (error) {
      this.logger.warn({
        error
      }, "Password encryption unavailable, falling back to plaintext");
      return password;
    }
  }
  /** 통합 로그인 (Account 우선 + 레거시 Employee 폴백) */
  async login(params) {
    const encryptedParams = {
      ...params,
      password: await this.encrypt(params.password)
    };
    const result = await this.client.trpc.account.login.mutate(encryptedParams);
    if (result.mode === "two_factor" || result.mode === "captcha") {
      return result;
    }
    this.client.setTokens(result.accessToken, result.refreshToken);
    this.logger.info({
      mode: result.mode
    }, "Account login successful");
    return result;
  }
  /** 회원 정보 + 소속 워크스페이스 목록 (account 세션 필요) */
  async me() {
    return this.client.trpc.account.me.query();
  }
  /** 소속 워크스페이스 목록 (account 세션 필요) */
  async workspaces() {
    return this.client.trpc.account.workspaces.query();
  }
  /** 워크스페이스 입장 — 조직 스코프 세션으로 전환 */
  async selectWorkspace(organizationId) {
    const result = await this.client.trpc.account.selectWorkspace.mutate({
      organizationId
    });
    this.client.setTokens(result.accessToken, result.refreshToken);
    return result;
  }
  /**
  * owner 암묵 grant 입장 — Employee 멤버십 없이 소유 조직에 진입.
  * (meta account — 허브의 ownerEntry 항목 전용)
  */
  async enterOwnedWorkspace(organizationId) {
    const result = await this.client.trpc.account.enterOwnedWorkspace.mutate({
      organizationId
    });
    this.client.setTokens(result.accessToken, result.refreshToken);
    return result;
  }
  /** 플랫폼 스태프: 내 유효 grant 목록 — 허브 "지원 중인 조직" 섹션 */
  async myGrants() {
    return this.client.trpc.platform.myGrants.query();
  }
  /** OrganizationGrant 입장 — support 대행/impersonation 세션 (meta account Phase B) */
  async enterGrantedWorkspace(grantId) {
    const result = await this.client.trpc.account.enterGrantedWorkspace.mutate({
      grantId
    });
    this.client.setTokens(result.accessToken, result.refreshToken);
    return result;
  }
  /** 워크스페이스 생성 후 바로 입장 (조직 스코프 세션 반환) */
  async createWorkspace(params) {
    const result = await this.client.trpc.account.createWorkspace.mutate(params);
    this.client.setTokens(result.accessToken, result.refreshToken);
    return result;
  }
  /** 허브 초대 인박스 — 내 계정 이메일로 온 유효한 초대 목록 (account 세션 필요) */
  async pendingInvites() {
    return this.client.trpc.account.pendingInvites.query();
  }
  /** 허브에서 초대 수락 — account 세션 기반, 비밀번호 재입력 없음 */
  async acceptInvite(inviteId) {
    return this.client.trpc.account.acceptInvite.mutate({
      inviteId
    });
  }
  /** 레거시 직원 셀프 업그레이드 — 본인 이메일로 계정 연결 인증 메일 발송 */
  async requestSelfUpgrade() {
    return this.client.trpc.account.requestSelfUpgrade.mutate();
  }
  /** 조직 세션 → account 세션 전환 (워크스페이스 허브로 돌아가기) */
  async toAccountSession() {
    const result = await this.client.trpc.account.toAccountSession.mutate();
    this.client.setTokens(result.accessToken, result.refreshToken);
    return result;
  }
  /** account 세션 로그아웃 */
  async logout() {
    try {
      await this.client.trpc.account.logout.mutate();
    } catch (error) {
      this.logger.warn({
        error
      }, "Account logout API call failed, clearing tokens locally");
    } finally {
      this.client.clearTokens();
    }
  }
};
__name(_AccountService, "AccountService");
var AccountService = _AccountService;

// src/services/AiService.ts
var _AiService = class _AiService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 조직 AI 플래그 — 위젯 노출 판단용 */
  async status() {
    return this.client.trpc.ai.status.query();
  }
  async guidanceCatalog(input) {
    return this.client.trpc.ai.guidanceCatalog.query(input);
  }
  async resolveGuidanceAction(input) {
    return this.client.trpc.ai.resolveGuidanceAction.query(input);
  }
  async createConversation(input = {}) {
    return this.client.trpc.ai.createConversation.mutate(input);
  }
  async chat(input) {
    return this.client.trpc.ai.chat.mutate(input);
  }
  async history(input) {
    return this.client.trpc.ai.history.query(input);
  }
  async listConversations(input = {}) {
    return this.client.trpc.ai.listConversations.query(input);
  }
  /** 병원 관리자가 직원 앱 AI 어시스턴트를 켜고 끈다(ai:manage) — 마스터 플래그가 꺼진 조직은 서버가 거절한다 */
  async updateEmployeeAppSettings(input) {
    return this.client.trpc.ai.updateEmployeeAppSettings.mutate(input);
  }
  async feedback(input) {
    return this.client.trpc.ai.feedback.mutate(input);
  }
  async usageSummary(input) {
    return this.client.trpc.ai.usageSummary.query(input);
  }
  /** 채팅 스트리밍 구독 — chat 뮤테이션 호출 전에 구독해야 delta를 놓치지 않는다 */
  subscribeToStream(conversationId, onData, onError) {
    const subscription = this.client.trpc.ai.onStream.subscribe({
      conversationId
    }, {
      onData: /* @__PURE__ */ __name((data) => onData(data), "onData"),
      onError: /* @__PURE__ */ __name((err) => onError?.(err), "onError")
    });
    return subscription;
  }
};
__name(_AiService, "AiService");
var AiService = _AiService;

// src/services/AllowanceService.ts
var _AllowanceService = class _AllowanceService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.allowance.list.query({
      filter,
      pagination
    });
  }
  async get(id) {
    return this.client.trpc.allowance.get.query(id);
  }
  async create(input) {
    return this.client.trpc.allowance.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.allowance.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.allowance.delete.mutate(id);
  }
  async approve(ids) {
    return this.client.trpc.allowance.approve.mutate({
      ids
    });
  }
  async cancel(ids) {
    return this.client.trpc.allowance.cancel.mutate({
      ids
    });
  }
  async getSourceDetail(id) {
    return this.client.trpc.allowance.getSourceDetail.query(id);
  }
  async batchCreate(input) {
    return this.client.trpc.allowance.batchCreate.mutate(input);
  }
};
__name(_AllowanceService, "AllowanceService");
var AllowanceService = _AllowanceService;

// src/services/AnnualLeavePromotionService.ts
var _AnnualLeavePromotionService = class _AnnualLeavePromotionService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async openSigner(input) {
    return this.client.trpc.annualLeavePromotion.openSigner.mutate(input);
  }
  async list(input = {}) {
    return this.client.trpc.annualLeavePromotion.list.query(input);
  }
  async firstNotice(input) {
    return this.client.trpc.annualLeavePromotion.firstNotice.mutate(input);
  }
  async retryFirstNotice(promotionId) {
    return this.client.trpc.annualLeavePromotion.retryFirstNotice.mutate({
      promotionId
    });
  }
  async templates() {
    return this.client.trpc.annualLeavePromotion.templates.query();
  }
  async templateManageUrl() {
    return this.client.trpc.annualLeavePromotion.templateManageUrl.query();
  }
  async batchPreflight(input) {
    return this.client.trpc.annualLeavePromotion.batchPreflight.mutate(input);
  }
  async batchCreate(input) {
    return this.client.trpc.annualLeavePromotion.batchCreate.mutate(input);
  }
  async batchStatus(batchId) {
    return this.client.trpc.annualLeavePromotion.batchStatus.query({
      batchId
    });
  }
  async batchList(input = {}) {
    return this.client.trpc.annualLeavePromotion.batchList.query(input);
  }
  async batchCancel(batchId, reason) {
    return this.client.trpc.annualLeavePromotion.batchCancel.mutate({
      batchId,
      reason
    });
  }
  async batchRetryFailed(batchId) {
    return this.client.trpc.annualLeavePromotion.batchRetryFailed.mutate({
      batchId
    });
  }
  async reminder(input) {
    return this.client.trpc.annualLeavePromotion.reminder.mutate(input);
  }
  async reissue(input) {
    return this.client.trpc.annualLeavePromotion.reissue.mutate(input);
  }
  async designateSecond(input) {
    return this.client.trpc.annualLeavePromotion.designateSecond.mutate(input);
  }
};
__name(_AnnualLeavePromotionService, "AnnualLeavePromotionService");
var AnnualLeavePromotionService = _AnnualLeavePromotionService;

// src/services/AppOnboardingService.ts
var _AppOnboardingService = class _AppOnboardingService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  // ── 관리자 ──
  /** 체크리스트 7단계 상태 + 집계(전체·팀별) */
  async summary(input) {
    return this.client.trpc.appOnboarding.summary.query(input ?? {});
  }
  /** 직원별 단계 목록(필터·페이지) */
  async listStatus(input) {
    return this.client.trpc.appOnboarding.listStatus.query(input);
  }
  /** 안내 문구 미리보기(직원을 주면 그 직원 값으로) */
  async previewGuide(input) {
    return this.client.trpc.appOnboarding.previewGuide.query(input ?? {});
  }
  /** 관리자 본인에게 테스트 발송 */
  async sendGuideTest(input) {
    return this.client.trpc.appOnboarding.sendGuideTest.mutate(input);
  }
  /** 앱 안내 발송(선택·전체·미안내·미완료·실패) */
  async sendGuide(input) {
    return this.client.trpc.appOnboarding.sendGuide.mutate(input);
  }
  async sendHistory(input) {
    return this.client.trpc.appOnboarding.sendHistory.query(input ?? {});
  }
  /** 온보딩 안내 공지 게시(6단계) */
  async postNotice(input) {
    return this.client.trpc.appOnboarding.postNotice.mutate(input ?? {});
  }
  /** 배포 완료 보고용 행(직원별 + 팀별) */
  async report() {
    return this.client.trpc.appOnboarding.report.query();
  }
  /** 관리자가 「완료」/「다시 열기」 */
  async setCompleted(input) {
    return this.client.trpc.appOnboarding.setCompleted.mutate(input);
  }
  // ── 직원 앱 ──
  /** 로그인 뒤 앱이 자기 정보를 남긴다(설치·첫 로그인·버전) */
  async heartbeat(input) {
    return this.client.trpc.appOnboarding.heartbeat.mutate(input);
  }
  async myTutorial() {
    return this.client.trpc.appOnboarding.myTutorial.query();
  }
  async completeTutorial(input) {
    return this.client.trpc.appOnboarding.completeTutorial.mutate(input);
  }
};
__name(_AppOnboardingService, "AppOnboardingService");
var AppOnboardingService = _AppOnboardingService;

// src/services/ApprovalFlowService.ts
var _ApprovalFlowService = class _ApprovalFlowService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 한 건의 결재 단계(결재자 이름·직책·시각·사유·대신 처리) */
  async steps(requestType, requestId) {
    return this.client.trpc.approvalFlow.steps.query({
      requestType,
      requestId
    });
  }
  /** 여러 건의 결재 단계 — 목록 화면의 「내 차례」 표시용 */
  async stepsByRequests(requestType, requestIds) {
    return this.client.trpc.approvalFlow.stepsByRequests.query({
      requestType,
      requestIds
    });
  }
  /** 결재함 숫자 — 내가 결재자인 대기 단계 수(종류별)와 가장 오래된 신청일 */
  async inboxSummary() {
    return this.client.trpc.approvalFlow.inboxSummary.query();
  }
  /** 결재할 문서 — 내가 현 단계 결재자인 대기 건(신청자 · 종류 · 핵심 내용 · 단계), 오래된 것부터 */
  async inbox(input) {
    return this.client.trpc.approvalFlow.inbox.query(input ?? {});
  }
  /** 결재한 문서 — 내가 처리한 단계(본인 단계 + 대신 처리)와 문서의 최종 상태, 최근 것부터. from/to = 결재한 날(조직 시간대) */
  async decided(input) {
    return this.client.trpc.approvalFlow.decided.query(input ?? {});
  }
  /** 신청 폼이 열릴 때 — 후보(이름·팀·직책) · 기본 결재선 · 내 결재선(재검증) · 지금 적용될 결재선 · 안내 */
  async lineContext(requestType) {
    return this.client.trpc.approvalFlow.lineContext.query({
      requestType
    });
  }
  /** 내 결재선 저장 — 그 종류의 다음 신청부터 자동 적용 */
  async saveMyLine(requestType, approverIds) {
    return this.client.trpc.approvalFlow.saveMyLine.mutate({
      requestType,
      approverIds
    });
  }
  /** 내 결재선 삭제 — 다음 신청부터 기본 결재선 */
  async clearMyLine(requestType) {
    return this.client.trpc.approvalFlow.clearMyLine.mutate({
      requestType
    });
  }
  /* ── 5단계 관리자 설정(setting:update) ── */
  /** 병원 설정 「결재선」 카드 — 정책 · 종류별 템플릿 · 종류별 템플릿 후보(조직 전체 범위 승인자) */
  async templates() {
    return this.client.trpc.approvalFlow.templates.query();
  }
  /** 병원 기본 결재선 템플릿 저장(종류별 1개) */
  async saveTemplate(requestType, approverIds) {
    return this.client.trpc.approvalFlow.saveTemplate.mutate({
      requestType,
      approverIds
    });
  }
  /** 병원 기본 결재선 템플릿 삭제 → 자동 기본 */
  async clearTemplate(requestType) {
    return this.client.trpc.approvalFlow.clearTemplate.mutate({
      requestType
    });
  }
  /** 결재자 바꾸기 후보 — 이 신청의 후보 − 신청자 − 아직 처리되지 않은 단계의 결재자(바꿀 권한이 있을 때만) */
  async replaceCandidates(requestType, requestId) {
    return this.client.trpc.approvalFlow.replaceCandidates.query({
      requestType,
      requestId
    });
  }
  /** 결재자 바꾸기 — 아직 처리되지 않은 단계의 결재자를 같은 후보 안의 다른 사람으로(관리자) */
  async replaceApprover(input) {
    return this.client.trpc.approvalFlow.replaceApprover.mutate(input);
  }
  /* ── 결재선 후속(approvalLine 라우터) — 나의 결재선 여러 개 · 팀 기본 결재선 · 변경 이력 ── */
  /** 나의 결재선 전부(네 종류, 재검증) + 실제 정책(병원 AND 팀) */
  async myLines() {
    return this.client.trpc.approvalLine.myLines.query();
  }
  /** 나의 결재선 만들기·수정 — id 가 있으면 수정. isDefault 를 켜면 같은 종류의 다른 기본은 꺼진다 */
  async saveMyNamedLine(input) {
    return this.client.trpc.approvalLine.saveMyLine.mutate(input);
  }
  /** 나의 결재선 삭제 */
  async deleteMyLine(id) {
    return this.client.trpc.approvalLine.deleteMyLine.mutate({
      id
    });
  }
  /** 나의 결재선 기본 지정 켜기·끄기 */
  async setMyLineDefault(id, isDefault) {
    return this.client.trpc.approvalLine.setMyLineDefault.mutate({
      id,
      isDefault
    });
  }
  /** 내 결재선 변경 이력 */
  async myChangeLogs(limit) {
    return this.client.trpc.approvalLine.myChangeLogs.query(limit ? {
      limit
    } : void 0);
  }
  /** 결재선을 관리할 수 있는 팀(팀장은 자기 팀, 시스템 관리자는 전부) */
  async managedTeams() {
    return this.client.trpc.approvalLine.managedTeams.query();
  }
  /** 팀 결재선 설정 화면 — 팀 · 경로 · 정책 · 종류별 템플릿/상속/후보 */
  /** 팀별 결재선 표 — 관리할 수 있는 팀 전부(트리 순서)의 종류별 결재선 · 출처 · 직원 직접 지정 */
  async teamOverview() {
    return this.client.trpc.approvalLine.teamOverview.query();
  }
  async teamContext(teamId) {
    return this.client.trpc.approvalLine.teamContext.query({
      teamId
    });
  }
  /** 팀 기본 결재선 저장(종류별 1개) */
  async saveTeamTemplate(teamId, requestType, approverIds) {
    return this.client.trpc.approvalLine.saveTeamTemplate.mutate({
      teamId,
      requestType,
      approverIds
    });
  }
  /** 팀 기본 결재선 비우기 */
  async clearTeamTemplate(teamId, requestType) {
    return this.client.trpc.approvalLine.clearTeamTemplate.mutate({
      teamId,
      requestType
    });
  }
  /** 팀의 직원 수정 허용 — true/false/null(정하지 않음) */
  async saveTeamSettings(teamId, allowCustom) {
    return this.client.trpc.approvalLine.saveTeamSettings.mutate({
      teamId,
      allowCustom
    });
  }
  /** 한 팀의 결재선 변경 이력 */
  async teamChangeLogs(teamId, limit) {
    return this.client.trpc.approvalLine.teamChangeLogs.query({
      teamId,
      limit
    });
  }
  /** 병원 전체 변경 이력(시스템 관리자) */
  async orgChangeLogs(input) {
    return this.client.trpc.approvalLine.orgChangeLogs.query(input);
  }
};
__name(_ApprovalFlowService, "ApprovalFlowService");
var ApprovalFlowService = _ApprovalFlowService;

// src/services/AttendanceCorrectionService.ts
var _AttendanceCorrectionService = class _AttendanceCorrectionService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async request(input) {
    return this.client.trpc.attendanceCorrection.request.mutate(input);
  }
  async mine(input = {}) {
    return this.client.trpc.attendanceCorrection.mine.query(input);
  }
  async cancel(id) {
    return this.client.trpc.attendanceCorrection.cancel.mutate({
      id
    });
  }
  /** 이날 정정 요청을 낼 수 있는가 — 앞날 · 잠김 사유 · 검토 중인 내 요청 */
  async dayStatus(date) {
    return this.client.trpc.attendanceCorrection.dayStatus.query({
      date
    });
  }
  async list(input = {}) {
    return this.client.trpc.attendanceCorrection.list.query(input);
  }
  /** 검토 팝업 — 승인하면 바뀔 기록 · 근무시간 미리 보기(저장하지 않는다) */
  async preview(id) {
    return this.client.trpc.attendanceCorrection.preview.query({
      id
    });
  }
  async approve(id) {
    return this.client.trpc.attendanceCorrection.approve.mutate({
      id
    });
  }
  async reject(id, rejectionReason) {
    return this.client.trpc.attendanceCorrection.reject.mutate({
      id,
      rejectionReason
    });
  }
};
__name(_AttendanceCorrectionService, "AttendanceCorrectionService");
var AttendanceCorrectionService = _AttendanceCorrectionService;

// src/services/AttendanceRateService.ts
var _AttendanceRateService = class _AttendanceRateService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 산정연도 직원별 출근율 목록 */
  async list(input) {
    return this.client.trpc.attendanceRate.list.query(input);
  }
  /** 단일 직원 소정근로일수 설정(행 [수정]) */
  async getSetting(input) {
    return this.client.trpc.attendanceRate.getSetting.query(input);
  }
  /** 소정근로일수 설정 저장(일괄/개별) */
  async saveSettings(input) {
    return this.client.trpc.attendanceRate.saveSettings.mutate(input);
  }
};
__name(_AttendanceRateService, "AttendanceRateService");
var AttendanceRateService = _AttendanceRateService;

// src/services/AttendanceService.ts
var _AttendanceService = class _AttendanceService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /**
  * Check in (clock in)
  * @param params - Optional parameters for check-in
  */
  async checkIn(params) {
    return this.client.trpc.attendance.checkIn.mutate(params || {});
  }
  /**
  * Check out (clock out)
  * @param params - Optional parameters for check-out
  */
  async checkOut(params) {
    return this.client.trpc.attendance.checkOut.mutate(params || {});
  }
  /**
  * Create attendance record (manual entry)
  * @param params - Attendance creation parameters
  */
  async create(params) {
    return this.client.trpc.attendance.create.mutate(params);
  }
  /**
  * Update attendance record
  * @param params - Attendance update parameters
  */
  async update(params) {
    return this.client.trpc.attendance.update.mutate(params);
  }
  /**
  * 근태 수정 — 시간 구분 직접 입력(수동 오버라이드). 출퇴근 시간 + 8개 구분 + 휴게를
  * 직접 저장한다. 소정 4종은 Attendance 에, 초과 4종은 manual OvertimeRecord 로 반영된다.
  */
  async saveManualHours(params) {
    return this.client.trpc.attendance.saveManualHours.mutate(params);
  }
  /**
  * Get single attendance record by ID
  * @param attendanceId - Attendance record ID
  */
  async get(attendanceId) {
    return this.client.trpc.attendance.get.query(attendanceId);
  }
  /**
  * Get attendance records with filtering and pagination
  * @param params - Query parameters
  */
  async list(params) {
    return this.client.trpc.attendance.list.query(params || {});
  }
  /**
  * Delete attendance record (soft delete)
  * @param attendanceId - Attendance record ID
  */
  async delete(attendanceId) {
    return this.client.trpc.attendance.delete.mutate(attendanceId);
  }
  /**
  * Bulk create attendance records
  * @param params - Bulk creation parameters
  */
  async bulkCreate(params) {
    return this.client.trpc.attendance.bulkCreate.mutate(params);
  }
  /**
  * Get work hours summary with 52-hour compliance check
  * @param params - Summary query parameters
  */
  async getWorkHoursSummary(params) {
    return this.client.trpc.attendance.workHoursSummary.query(params);
  }
  /**
  * 전체 근무기록 — 출퇴근·연장근무·외근을 일별 타임라인으로 합친 조회.
  * from/to 는 조직 로컬 "YYYY-MM-DD", employeeId 를 비우면 본인.
  */
  /** 팀 근태 — 하루(MED-966). 팀 범위 이상 근태 조회 권한자 */
  async teamDaily(date) {
    return this.client.trpc.attendance.teamDaily.query({
      date
    });
  }
  /** 팀 근태 — 한 달 요약(MED-966) */
  async teamMonthly(year, month) {
    return this.client.trpc.attendance.teamMonthly.query({
      year,
      month
    });
  }
  async workLog(params) {
    return this.client.trpc.attendance.workLog.query(params);
  }
  /**
  * Get today's attendance status for an employee
  * @param employeeId - Optional employee ID (defaults to current user)
  */
  async getTodayStatus(employeeId) {
    return this.client.trpc.attendance.todayStatus.query({
      employeeId
    });
  }
  /** 직원의 날짜 D 근무형태(서버 판정) — 근태 수정 창의 듀티(3교대) 표시 등. date 를 비우면 조직의 오늘. */
  async getEffectiveShift(employeeId, date) {
    return this.client.trpc.attendance.effectiveShift.query({
      employeeId,
      date
    });
  }
  // Helper methods for common operations
  /**
  * Check if employee has checked in today
  * @param employeeId - Optional employee ID
  */
  async hasCheckedInToday(employeeId) {
    const status = await this.getTodayStatus(employeeId);
    return status.hasCheckedIn;
  }
  /**
  * Check if employee has checked out today
  * @param employeeId - Optional employee ID
  */
  async hasCheckedOutToday(employeeId) {
    const status = await this.getTodayStatus(employeeId);
    return status.hasCheckedOut;
  }
  /**
  * Get attendance records for a specific date range
  * @param startDate - Start date
  * @param endDate - End date
  * @param employeeId - Optional employee ID
  */
  async getByDateRange(startDate, endDate, employeeId) {
    return this.list({
      filter: {
        employeeId,
        dateRange: {
          from: startDate,
          to: endDate
        }
      }
    });
  }
  /**
  * Get attendance records for current month
  * @param employeeId - Optional employee ID
  */
  async getCurrentMonthAttendance(employeeId) {
    const now = /* @__PURE__ */ new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return this.getByDateRange(startOfMonth, endOfMonth, employeeId);
  }
  /**
  * Get attendance records for current week
  * @param employeeId - Optional employee ID
  */
  async getCurrentWeekAttendance(employeeId) {
    const now = /* @__PURE__ */ new Date();
    const dayOfWeek = now.getDay();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    return this.getByDateRange(startOfWeek, endOfWeek, employeeId);
  }
  /**
  * Get late attendance records
  * @param params - Query parameters
  */
  async getLateAttendance(params) {
    return this.list({
      filter: {
        ...params,
        isLate: true
      }
    });
  }
  /**
  * Get overtime attendance records
  * @param params - Query parameters
  */
  async getOvertimeAttendance(params) {
    return this.list({
      filter: {
        ...params,
        hasOvertime: true
      }
    });
  }
  /**
  * Check weekly 52-hour compliance for an employee
  * @param employeeId - Employee ID
  * @param year - Year
  * @param week - Week number
  */
  async checkWeeklyCompliance(employeeId, year, week) {
    const summary = await this.getWorkHoursSummary({
      employeeId,
      period: {
        year,
        week
      }
    });
    return {
      isCompliant: summary.complianceStatus.weeklyLimit,
      violations: summary.complianceStatus.violations,
      totalHours: summary.totalRegularHours + summary.totalOvertimeHours + summary.totalNightShiftHours + summary.totalWeekendHours + summary.totalHolidayHours
    };
  }
  /**
  * Get monthly work hours summary
  * @param employeeId - Employee ID
  * @param year - Year
  * @param month - Month (1-12)
  */
  async getMonthlyWorkHours(employeeId, year, month) {
    return this.getWorkHoursSummary({
      employeeId,
      period: {
        year,
        month
      }
    });
  }
  /**
  * Get yearly work hours summary
  * @param employeeId - Employee ID
  * @param year - Year
  */
  async getYearlyWorkHours(employeeId, year) {
    return this.getWorkHoursSummary({
      employeeId,
      period: {
        year
      }
    });
  }
  /**
  * Create manual attendance entry for multiple employees
  * @param employeeIds - Array of employee IDs
  * @param date - Date
  * @param checkIn - Check-in time (HH:MM)
  * @param checkOut - Check-out time (HH:MM)
  * @param reason - Reason for manual entry
  * @param breakMinutes - Break duration in minutes
  */
  async createBulkManualEntry(employeeIds, date, checkIn, checkOut, reason, breakMinutes = 60) {
    return this.bulkCreate({
      employeeIds,
      date,
      checkInTime: checkIn,
      checkOutTime: checkOut,
      reason,
      breakMinutes
    });
  }
  /**
  * Quick check-in with current location
  * @param location - GPS coordinates
  */
  async quickCheckIn(location) {
    const params = location ? {
      location: {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        accuracy: location.coords.accuracy
      }
    } : void 0;
    return this.checkIn(params);
  }
  /**
  * Quick check-out with current location
  * @param location - GPS coordinates
  */
  async quickCheckOut(location) {
    const params = location ? {
      location: {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        accuracy: location.coords.accuracy
      }
    } : void 0;
    return this.checkOut(params);
  }
  /**
  * Register field work (외근)
  */
  async registerFieldWork(params) {
    return this.client.trpc.attendance.registerFieldWork.mutate(params);
  }
  /**
  * List field work entries (외근) for the current user.
  */
  async listFieldWork(params) {
    return this.client.trpc.attendance.listFieldWork.query(params);
  }
  /**
  * Get attendance status text
  * @param employeeId - Optional employee ID
  */
  async getStatusText(employeeId) {
    const status = await this.getTodayStatus(employeeId);
    if (!status.hasCheckedIn) {
      return "Not checked in";
    }
    if (!status.hasCheckedOut) {
      if (status.checkInTime) {
        return `Checked in at ${new Date(status.checkInTime).toLocaleTimeString()}`;
      }
      return "Checked in";
    }
    return `Completed - ${status.totalHours} hours`;
  }
  /**
  * Calculate total work hours for a period
  * @param attendanceRecords - Array of attendance records
  */
  calculateTotalHours(attendanceRecords) {
    return attendanceRecords.reduce((sum, record) => sum + record.totalHours, 0);
  }
  /**
  * Format work hours for display
  * @param hours - Number of hours
  */
  formatWorkHours(hours) {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}h ${minutes}m`;
  }
  /**
  * Recalculate attendance from secom events for an employee in a date range.
  * Deletes non-manual attendance records and re-runs the secom processor — 근무형태는 앱 출근과 같은 순서로 다시 판정한다.
  */
  async recalculateFromSecom(input) {
    return this.client.trpc.attendance.recalculateFromSecom.mutate(input);
  }
  /** 근태 내역 스냅샷 시트 갱신 — [근태 업데이트] 버튼. */
  async updateAttendanceSummary(sheetViewId) {
    return this.client.trpc.attendance.updateAttendanceSummary.mutate({
      sheetViewId
    });
  }
  /** 근태 내역 스냅샷에 미반영된 소스 변경이 있는지 (알림 아이콘). */
  async attendanceSummaryHasUpdates(sheetViewId) {
    return this.client.trpc.attendance.attendanceSummaryHasUpdates.query({
      sheetViewId
    });
  }
};
__name(_AttendanceService, "AttendanceService");
var AttendanceService = _AttendanceService;

// src/services/AuthService.ts
var _AuthService = class _AuthService {
  constructor(client) {
    __publicField(this, "client");
    __publicField(this, "logger");
    __publicField(this, "publicKeyPem", null);
    this.client = client;
    this.logger = sdkLogger.createServiceLogger("AuthService");
  }
  /**
  * Fetch and cache the server's RSA public key for password transport encryption.
  * Falls back to plaintext (legacy path) if the server doesn't expose `auth.publicKey`
  * or Web Crypto is unavailable — the server still accepts unprefixed plaintext.
  */
  async encrypt(password) {
    try {
      if (!this.publicKeyPem) {
        const result = await this.client.trpc.auth.publicKey.query();
        this.publicKeyPem = result.publicKeyPem;
      }
      return await encryptPassword(password, this.publicKeyPem);
    } catch (error) {
      this.logger.warn({
        error
      }, "Password encryption unavailable, falling back to plaintext");
      return password;
    }
  }
  /**
  * Login with email/phone and password
  */
  async login(params) {
    this.logger.debug({
      identifier: params.identifier
    }, "Attempting login");
    try {
      const encryptedParams = {
        ...params,
        password: await this.encrypt(params.password)
      };
      const result = await this.client.trpc.auth.login.mutate(encryptedParams);
      if ("captchaRequired" in result) {
        return result;
      }
      if ("twoFactorRequired" in result) {
        this.logger.info({
          identifier: params.identifier
        }, "Login needs two-factor code");
        return result;
      }
      this.client.setTokens(result.accessToken, result.refreshToken);
      this.logger.info({
        userId: result.user.id
      }, "Login successful");
      return result;
    } catch (error) {
      this.logger.error({
        error,
        identifier: params.identifier
      }, "Login failed");
      throw error;
    }
  }
  /** 앱 설치 안내 로그인 링크 토큰 → 로그인 ID·임시 비밀번호(없거나 기한 지남·이미 변경이면 null) */
  async loginPrefill(token) {
    return this.client.trpc.auth.loginPrefill.mutate({
      token
    });
  }
  /**
  * Register a new user
  */
  async register(params) {
    const encryptedParams = {
      ...params,
      password: await this.encrypt(params.password)
    };
    const result = await this.client.trpc.auth.register.mutate(encryptedParams);
    this.client.setTokens(result.accessToken, result.refreshToken);
    return result;
  }
  /**
  * Logout and clear tokens
  */
  async logout() {
    this.logger.debug("Logging out");
    try {
      await this.client.trpc.auth.logout.mutate();
      this.logger.info("Logout successful");
    } catch (error) {
      this.logger.warn({
        error
      }, "Logout API call failed, clearing tokens locally");
    } finally {
      this.client.clearTokens();
    }
  }
  /**
  * Refresh access token
  */
  async refreshToken(refreshToken) {
    const token = refreshToken || this.client.getRefreshToken();
    if (!token) {
      throw new Error("No refresh token available");
    }
    const result = await this.client.trpc.auth.refreshToken.mutate({
      refreshToken: token
    });
    this.client.setTokens(result.accessToken, result.refreshToken);
    return result;
  }
  /**
  * Get current user information
  */
  async getCurrentUser() {
    if (!this.client.isAuthenticated()) {
      throw new Error("Not authenticated");
    }
    return this.client.trpc.auth.me.query();
  }
  /**
  * Change password
  */
  async changePassword(currentPassword, newPassword, confirmPassword) {
    if (!this.client.isAuthenticated()) {
      throw new Error("Not authenticated");
    }
    await this.client.trpc.auth.changePassword.mutate({
      currentPassword: await this.encrypt(currentPassword),
      newPassword: await this.encrypt(newPassword),
      confirmPassword: await this.encrypt(confirmPassword)
    });
  }
  /**
  * Check if self-service password reset is available (public, no auth required)
  */
  async passwordResetAvailable() {
    const result = await this.client.trpc.auth.passwordResetAvailable.query();
    return result.available;
  }
  /**
  * Request password reset — server auto-detects delivery (SMS first, email fallback)
  */
  async requestPasswordReset(identifier) {
    await this.client.trpc.auth.requestPasswordReset.mutate({
      identifier
    });
  }
  /**
  * List login methods enabled for an organization (or globally if no code given).
  * Used by the login page to render the correct tabs (password / phone OTP).
  */
  async availableLoginMethods(organizationCode) {
    const result = await this.client.trpc.auth.availableLoginMethods.query(organizationCode ? {
      organizationCode
    } : void 0);
    return result.methods;
  }
  /**
  * Request a 6-digit SMS login code (휴대폰 인증 로그인).
  * Returns generic success regardless of outcome to prevent identifier enumeration.
  */
  async requestLoginCode(identifier, organizationCode) {
    await this.client.trpc.auth.requestLoginCode.mutate({
      identifier,
      organizationCode
    });
  }
  /**
  * Verify the SMS login code and complete login. On success, tokens are set on the client
  * and the same response shape as `login()` is returned.
  */
  async loginWithCode(identifier, code, organizationCode, captchaToken) {
    const result = await this.client.trpc.auth.loginWithCode.mutate({
      identifier,
      code,
      organizationCode,
      captchaToken
    });
    if ("captchaRequired" in result || "twoFactorRequired" in result) {
      return result;
    }
    this.client.setTokens(result.accessToken, result.refreshToken);
    this.logger.info({
      userId: result.user.id
    }, "OTP login successful");
    return result;
  }
  /**
  * Reset password with token
  */
  async resetPassword(token, newPassword, confirmPassword) {
    await this.client.trpc.auth.resetPassword.mutate({
      token,
      newPassword: await this.encrypt(newPassword),
      confirmPassword: await this.encrypt(confirmPassword)
    });
  }
  /**
  * Admin: reset employee password. The temporary password is ALWAYS delivered via SMS
  * to the employee — it is never returned in the response for security reasons.
  * Fails with BAD_REQUEST if the employee has no phone, or INTERNAL_SERVER_ERROR if SMS fails.
  */
  async adminResetPassword(employeeId) {
    return this.client.trpc.auth.adminResetPassword.mutate({
      employeeId
    });
  }
};
__name(_AuthService, "AuthService");
var AuthService = _AuthService;

// src/services/CertificationTypeService.ts
var _CertificationTypeService = class _CertificationTypeService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter) {
    return this.client.trpc.certificationType.list.query(filter);
  }
  async create(input) {
    return this.client.trpc.certificationType.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.certificationType.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.certificationType.delete.mutate(id);
  }
};
__name(_CertificationTypeService, "CertificationTypeService");
var CertificationTypeService = _CertificationTypeService;

// src/services/ChatDeviceLinkService.ts
var _ChatDeviceLinkService = class _ChatDeviceLinkService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async create(ephemeralPublicKey, options) {
    return this.client.trpc.chatDeviceLink.create.mutate({
      ephemeralPublicKey,
      ...options?.requesterPushToken ? {
        requesterPushToken: options.requesterPushToken
      } : {}
    });
  }
  async listPending() {
    return this.client.trpc.chatDeviceLink.listPending.query();
  }
  async approve(input) {
    return this.client.trpc.chatDeviceLink.approve.mutate(input);
  }
  async claim(requestId) {
    return this.client.trpc.chatDeviceLink.claim.mutate({
      requestId
    });
  }
  async cancel(requestId) {
    return this.client.trpc.chatDeviceLink.cancel.mutate({
      requestId
    });
  }
};
__name(_ChatDeviceLinkService, "ChatDeviceLinkService");
var ChatDeviceLinkService = _ChatDeviceLinkService;

// src/services/ChatKeyService.ts
var _ChatKeyService = class _ChatKeyService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async getMine() {
    return this.client.trpc.chatKey.getMine.query();
  }
  /** 복구 코드로 감싼 내 백업 — 새 기기에서 복구 코드로 풀 때. */
  async getMyBackup() {
    return this.client.trpc.chatKey.getMyBackup.query();
  }
  async register(input) {
    return this.client.trpc.chatKey.register.mutate(input);
  }
  async updateBackup(input) {
    return this.client.trpc.chatKey.updateBackup.mutate(input);
  }
  async publicKeys(employeeIds) {
    return this.client.trpc.chatKey.publicKeys.query({
      employeeIds
    });
  }
  /** 한 직원의 공개키 이력(E9) — 옛 version 의 서명 검증·키 변경 시각 */
  async history(employeeId) {
    return this.client.trpc.chatKey.history.query({
      employeeId
    });
  }
  async recoveryPublicKey() {
    return this.client.trpc.chatKey.recoveryPublicKey.query();
  }
};
__name(_ChatKeyService, "ChatKeyService");
var ChatKeyService = _ChatKeyService;

// src/services/ChatRecoveryService.ts
var _ChatRecoveryService = class _ChatRecoveryService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(status, limit) {
    return this.client.trpc.chatRecovery.list.query({
      status,
      limit
    });
  }
  /** 당사자(1~2명)의 대화방 목록 — 요청 화면의 「대화방 고르기」. 조회는 감사에 남는다 */
  async conversationsOf(employeeIds, period = {}) {
    return this.client.trpc.chatRecovery.conversationsOf.query({
      employeeIds,
      ...period
    });
  }
  /** 제출 전 미리보기 — 이 범위로 고정될 방·메시지 수 */
  async previewScope(scope) {
    return this.client.trpc.chatRecovery.previewScope.query({
      scope
    });
  }
  async get(requestId) {
    return this.client.trpc.chatRecovery.get.query({
      requestId
    });
  }
  async audit(requestId) {
    return this.client.trpc.chatRecovery.audit.query({
      requestId
    });
  }
  async create(input) {
    return this.client.trpc.chatRecovery.create.mutate(input);
  }
  async approve(requestId) {
    return this.client.trpc.chatRecovery.approve.mutate({
      requestId
    });
  }
  async reject(requestId, reason) {
    return this.client.trpc.chatRecovery.reject.mutate({
      requestId,
      reason
    });
  }
  async documentUrl(requestId, storagePath) {
    return this.client.trpc.chatRecovery.documentUrl.mutate({
      requestId,
      storagePath
    });
  }
  async claimHandoff(requestId) {
    return this.client.trpc.chatRecovery.claimHandoff.mutate({
      requestId
    });
  }
  async complete(input) {
    return this.client.trpc.chatRecovery.complete.mutate(input);
  }
  /** handoff 를 받은 뒤 — 범위 메시지의 암호문을 쪽 단위로(브라우저가 푼다) */
  async messages(requestId, page = 1, limit = 200) {
    return this.client.trpc.chatRecovery.messages.query({
      requestId,
      page,
      limit
    });
  }
  async attachmentUrl(requestId, storagePath) {
    return this.client.trpc.chatRecovery.attachmentUrl.mutate({
      requestId,
      storagePath
    });
  }
};
__name(_ChatRecoveryService, "ChatRecoveryService");
var ChatRecoveryService = _ChatRecoveryService;

// src/services/ConversationKeyService.ts
var _ConversationKeyService = class _ConversationKeyService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async epochs(conversationId) {
    return this.client.trpc.conversationKey.epochs.query({
      conversationId
    });
  }
  async myEnvelopes(conversationId) {
    return this.client.trpc.conversationKey.myEnvelopes.query({
      conversationId
    });
  }
  async missingRecipients(conversationId) {
    return this.client.trpc.conversationKey.missingRecipients.query({
      conversationId
    });
  }
  /** 이 기기에 현재 세대 키가 없다 — 키를 가진 멤버 기기에 봉투를 달라고 깨운다(2026-10-15). */
  async requestEnvelopes(conversationId) {
    return this.client.trpc.conversationKey.requestEnvelopes.mutate({
      conversationId
    });
  }
  async publish(input) {
    return this.client.trpc.conversationKey.publish.mutate(input);
  }
};
__name(_ConversationKeyService, "ConversationKeyService");
var ConversationKeyService = _ConversationKeyService;

// src/services/ConversationService.ts
var _ConversationService = class _ConversationService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.conversation.list.query({
      filter,
      pagination
    });
  }
  /** 현재 사용자의 모든 활성 대화방에서 안 읽은 메시지 수 합계 */
  async unreadCount() {
    return this.client.trpc.conversation.unreadCount.query();
  }
  async get(id) {
    return this.client.trpc.conversation.get.query(id);
  }
  async create(input) {
    return this.client.trpc.conversation.create.mutate(input);
  }
  /** 「나와의 대화」 — 있으면 그 방, 없으면 만들어서 id 를 돌려준다 (MED-903) */
  async ensureSelf() {
    return this.client.trpc.conversation.ensureSelf.mutate();
  }
  /** Legacy single-member add */
  async addMember(input) {
    return this.client.trpc.conversation.addMember.mutate(input);
  }
  /** Bulk invite — handles direct→group conversion + system messages */
  async addMembers(input) {
    return this.client.trpc.conversation.addMembers.mutate(input);
  }
  /** 대화방 알림 끄기/켜기 — 단체방만, 사용자별 (MED-942) */
  async setMuted(input) {
    return this.client.trpc.conversation.setMuted.mutate(input);
  }
  /** 「지금 이 방을 보고 있다」 heartbeat — 보고 있는 방의 푸시를 서버가 보내지 않는다(null = 보는 방 없음) */
  async setViewing(input) {
    return this.client.trpc.conversation.setViewing.mutate(input);
  }
  /** Leave a conversation (soft-leave, history preserved) */
  async leave(conversationId) {
    return this.client.trpc.conversation.leave.mutate({
      conversationId
    });
  }
  /** Set per-user custom room name (null to clear) */
  async updateMemberSettings(conversationId, customName) {
    return this.client.trpc.conversation.updateMemberSettings.mutate({
      conversationId,
      customName
    });
  }
  /** Get active members of a conversation */
  async getMembers(conversationId) {
    return this.client.trpc.conversation.getMembers.query(conversationId);
  }
  /** Remove a member (owner-only) */
  async removeMember(conversationId, employeeId, attestation) {
    return this.client.trpc.conversation.removeMember.mutate({
      conversationId,
      employeeId,
      attestation
    });
  }
  /** Owner-only: transfer ownership to another active member (방장 위임) */
  async transferOwnership(conversationId, newOwnerId) {
    return this.client.trpc.conversation.transferOwnership.mutate({
      conversationId,
      newOwnerId
    });
  }
  /** Group-room owner-only: dissolve the room (방장이 나가면서 방 해체) */
  async leaveAndDissolve(conversationId) {
    return this.client.trpc.conversation.leaveAndDissolve.mutate({
      conversationId
    });
  }
  /** Owner-only: approve a pending join request (team-mandatory rooms). */
  async approveMember(conversationId, employeeId) {
    return this.client.trpc.conversation.approveMember.mutate({
      conversationId,
      employeeId
    });
  }
  /**
  * 팀장 없는 팀 단톡방의 입장 대기 — 시스템 관리자에게만 목록이 온다(그 밖에는 빈 목록).
  * 승인 · 거절은 approveMember / removeMember 로 한다.
  */
  async teamRoomJoinRequests(teamId) {
    return this.client.trpc.conversation.teamRoomJoinRequests.query({
      teamId
    });
  }
};
__name(_ConversationService, "ConversationService");
var ConversationService = _ConversationService;

// src/services/DeductionImportService.ts
var _DeductionImportService = class _DeductionImportService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** Decode an uploaded eCount xlsx (base64) and return a preview + the org's D-codes. */
  async parse(input) {
    return this.client.trpc.deductionImport.parse.mutate(input);
  }
  /** Build a staging SheetView from matched rows; commit via sheetView.commitToRecords. */
  async createStagingSheet(input) {
    return this.client.trpc.deductionImport.createStagingSheet.mutate(input);
  }
};
__name(_DeductionImportService, "DeductionImportService");
var DeductionImportService = _DeductionImportService;

// src/services/DeductionService.ts
var _DeductionService = class _DeductionService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.deduction.list.query({
      filter,
      pagination
    });
  }
  async get(id) {
    return this.client.trpc.deduction.get.query(id);
  }
  async create(input) {
    return this.client.trpc.deduction.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.deduction.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.deduction.delete.mutate(id);
  }
  async approve(ids) {
    return this.client.trpc.deduction.approve.mutate({
      ids
    });
  }
  async cancel(ids) {
    return this.client.trpc.deduction.cancel.mutate({
      ids
    });
  }
  async review(year, month, employeeId) {
    return this.client.trpc.deduction.review.query({
      year,
      month,
      employeeId
    });
  }
};
__name(_DeductionService, "DeductionService");
var DeductionService = _DeductionService;

// src/services/DeviceCredentialService.ts
var _DeviceCredentialService = class _DeviceCredentialService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 내 기기 자격증명 목록(회수된 것 제외) */
  async list() {
    return this.client.trpc.deviceCredential.list.query();
  }
  async revoke(input) {
    return this.client.trpc.deviceCredential.revoke.mutate(input);
  }
  /* 기기 키 */
  async registerDeviceKey(input) {
    return this.client.trpc.deviceCredential.registerDeviceKey.mutate(input);
  }
  async deviceKeyChallenge(input) {
    return this.client.trpc.deviceCredential.deviceKeyChallenge.mutate(input);
  }
  /** 서명 검증 뒤 세션 발급 — 토큰은 여기서 바로 심는다(비밀번호 로그인과 같은 응답). */
  async deviceKeyLogin(input) {
    const result = await this.client.trpc.deviceCredential.deviceKeyLogin.mutate(input);
    this.client.setTokens(result.accessToken, result.refreshToken);
    return result;
  }
  /* 패스키 */
  async passkeyRegisterOptions() {
    return this.client.trpc.deviceCredential.passkeyRegisterOptions.mutate();
  }
  async passkeyRegisterVerify(input) {
    return this.client.trpc.deviceCredential.passkeyRegisterVerify.mutate(input);
  }
  async passkeyLoginOptions(input) {
    return this.client.trpc.deviceCredential.passkeyLoginOptions.mutate(input ?? void 0);
  }
  async passkeyLoginVerify(input) {
    const result = await this.client.trpc.deviceCredential.passkeyLoginVerify.mutate(input);
    this.client.setTokens(result.accessToken, result.refreshToken);
    return result;
  }
};
__name(_DeviceCredentialService, "DeviceCredentialService");
var DeviceCredentialService = _DeviceCredentialService;

// src/services/DocumentService.ts
var _DocumentService = class _DocumentService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.document.list.query({
      filter,
      pagination
    });
  }
  async get(id) {
    return this.client.trpc.document.get.query(id);
  }
  async upload(input) {
    return this.client.trpc.document.upload.mutate(input);
  }
  async update(input) {
    return this.client.trpc.document.update.mutate(input);
  }
  async cancel(id) {
    return this.client.trpc.document.cancel.mutate(id);
  }
  /** 관리자 기안 — 대상 직원 명의의 인사처리 문서(인사발령품의서) */
  async createHrAction(input) {
    return this.client.trpc.document.createHrAction.mutate(input);
  }
  async review(input) {
    return this.client.trpc.document.review.mutate(input);
  }
};
__name(_DocumentService, "DocumentService");
var DocumentService = _DocumentService;

// src/services/DocumentTemplateService.ts
var _DocumentTemplateService = class _DocumentTemplateService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list() {
    return this.client.trpc.documentTemplate.list.query();
  }
  async get(id) {
    return this.client.trpc.documentTemplate.get.query(id);
  }
  async create(input) {
    return this.client.trpc.documentTemplate.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.documentTemplate.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.documentTemplate.delete.mutate(id);
  }
  async variables() {
    return this.client.trpc.documentTemplate.variables.query();
  }
  async preview(input) {
    return this.client.trpc.documentTemplate.preview.query(input);
  }
};
__name(_DocumentTemplateService, "DocumentTemplateService");
var DocumentTemplateService = _DocumentTemplateService;

// src/services/EducationSubsidyService.ts
var _EducationSubsidyService = class _EducationSubsidyService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.educationSubsidy.list.query({
      filter,
      pagination
    });
  }
  async get(id) {
    return this.client.trpc.educationSubsidy.get.query(id);
  }
  async create(input) {
    return this.client.trpc.educationSubsidy.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.educationSubsidy.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.educationSubsidy.delete.mutate(id);
  }
  async approve(ids) {
    return this.client.trpc.educationSubsidy.approve.mutate({
      ids
    });
  }
};
__name(_EducationSubsidyService, "EducationSubsidyService");
var EducationSubsidyService = _EducationSubsidyService;

// src/services/EmployeeCertificationService.ts
var _EmployeeCertificationService = class _EmployeeCertificationService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter) {
    return this.client.trpc.employeeCertification.list.query(filter);
  }
  async summary() {
    return this.client.trpc.employeeCertification.summary.query();
  }
  async create(input) {
    return this.client.trpc.employeeCertification.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.employeeCertification.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.employeeCertification.delete.mutate(id);
  }
  async export(filter) {
    return this.client.trpc.employeeCertification.export.mutate(filter);
  }
  /** Self-service: employee submits their own certification */
  async submitMy(input) {
    return this.client.trpc.employeeCertification.submitMy.mutate(input);
  }
};
__name(_EmployeeCertificationService, "EmployeeCertificationService");
var EmployeeCertificationService = _EmployeeCertificationService;

// src/client/quiet-forbidden.ts
function quietForbiddenContext(opts) {
  return opts?.background ? {
    context: {
      quietForbidden: true
    }
  } : void 0;
}
__name(quietForbiddenContext, "quietForbiddenContext");

// src/services/EmployeeService.ts
var _EmployeeService = class _EmployeeService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** Verify RRN front 6 digits for payslip access */
  async verifyRrn(rrnFront) {
    return this.client.trpc.employee.verifyRrn.mutate({
      rrnFront
    });
  }
  /**
  * Lightweight contact list for messenger (name, teams).
  * Any authenticated employee can see all contacts in their organization.
  */
  async getContacts(input) {
    return this.client.trpc.employee.contacts.query(input);
  }
  /**
  * 메신저 연락처 프로필 — 사진 · 소속 팀 · 역할 · 직무/직군/직위 · 이메일, 병원이 공개하면 전화번호.
  * 재직 중이 아니거나 다른 병원 직원이면 null.
  */
  async getContactProfile(id) {
    return this.client.trpc.employee.contactProfile.query({
      id
    });
  }
  /**
  * 직원 카드(표의 이름을 누르면 뜨는 팝오버) — 이름 · 상태 · 사번 · 직무 · 소속 팀 · 병동 배정.
  * 보는 사람의 직원 조회 범위 안에서만 내려오고(밖이면 FORBIDDEN), 없는 직원이면 null.
  */
  async getEmployeeCard(id) {
    return this.client.trpc.employee.card.query({
      id
    });
  }
  /**
  * 오늘의 축하 대상(생일 · 입사 N주년) — 직원 앱 홈 카드용.
  * 조직 타임존 기준 오늘, 이름·팀·N주년만 내려온다(생년월일은 포함되지 않는다).
  */
  async getCelebrations() {
    return this.client.trpc.employee.celebrations.query();
  }
  /**
  * Get all employees with optional filters
  */
  async getEmployees(filters) {
    return this.client.trpc.employee.list.query({
      filter: filters,
      pagination: filters
    });
  }
  /**
  * 소속 한 줄의 근무 · 주 소속 저장(직원 수정 화면). 직원 수정 권한으로 저장되고,
  * 주 소속 줄이면 서버가 직원 근무(근태·연차 기준)까지 맞춘다.
  */
  async updateMembershipShift(input) {
    return this.client.trpc.employee.updateMembershipShift.mutate(input);
  }
  /**
  * Get employee by ID
  */
  async getEmployee(employeeId) {
    return this.client.trpc.employee.get.query(employeeId);
  }
  /**
  * 직원 추가 패널 미리보기 — 저장하면 행이 들어갈 시트(조직 설정 「신규 입사자 시트 자동 추가」가 켜져 있을 때).
  * `background: true` — 패널이 스스로 부르는 보조 조회라 권한(employee:create 전체)이 없어도 권한 모달을 띄우지 않는다.
  */
  async previewNewEmployeeSheets(hireDate, opts) {
    return this.client.trpc.employee.previewNewEmployeeSheets.query({
      hireDate: hireDate || void 0
    }, quietForbiddenContext(opts));
  }
  /** 직원 추가 패널의 사번 미리보기 — 같은 이유로 배경 조회 */
  async previewNextEmployeeNumber(opts) {
    return this.client.trpc.employee.previewNextEmployeeNumber.query(void 0, quietForbiddenContext(opts));
  }
  /**
  * Create a new employee
  */
  async createEmployee(params) {
    return this.client.trpc.employee.create.mutate(params);
  }
  /**
  * Update an employee
  */
  async updateEmployee(params) {
    return this.client.trpc.employee.update.mutate({
      ...params
    });
  }
  /**
  * Upsert the employee's employment contract (append-only versioning —
  * supersedes the active contract and creates a merged new version, or
  * bootstraps v1 when none exists). Pass only the changed fields.
  */
  async upsertContract(params) {
    return this.client.trpc.employee.upsertContract.mutate(params);
  }
  /**
  * Delete an employee (soft delete)
  */
  async deleteEmployee(employeeId) {
    return this.client.trpc.employee.delete.mutate(employeeId);
  }
  /** 직원 계정(Account) 초대 메일 발송 — 회원 계정 연결 경로 */
  async sendAccountInvite(employeeId) {
    return this.client.trpc.employee.sendAccountInvite.mutate({
      employeeId
    });
  }
  /**
  * Confirm profile picture after presigned-URL upload
  */
  async confirmProfilePicture(input) {
    return this.client.trpc.employee.confirmProfilePicture.mutate(input);
  }
  /**
  * Remove the caller's own profile picture (resets to the initial avatar)
  */
  async removeProfilePicture() {
    return this.client.trpc.employee.removeProfilePicture.mutate();
  }
  /** 본인 서명/도장 이미지 조회 (내 정보 서명 섹션 미리보기) */
  async mySignature() {
    return this.client.trpc.employee.mySignature.query();
  }
  /** 내 근로형태(포괄임금 월 시간 기준, MED-965) — 지정이 없으면 null */
  async myWorkPattern() {
    return this.client.trpc.employee.myWorkPattern.query();
  }
  /** 본인 서명/도장 이미지 등록(data URI)·삭제(null) — 결재문서 {{서명}} 자리에 삽입 */
  async setMySignature(image) {
    return this.client.trpc.employee.setMySignature.mutate({
      image
    });
  }
  /** 직원이 신청할 수 있는 증명서 양식(재직·경력·퇴직) + 양식별 신청 입력 항목 */
  async certificateTemplates() {
    return this.client.trpc.employee.certificateTemplates.query();
  }
  /** 인사처리 서류(사직서·휴직신청서…) 종류별 첨부 필수 여부 — 조직 결재 서식의 「첨부 파일」 설정 반영(MED-902) */
  async hrActionAttachmentPolicy() {
    return this.client.trpc.employee.hrActionAttachmentPolicy.query();
  }
  /**
  * 증명서 신청 — 문자열이면 용도만(재직증명서 기본), 객체면 양식·용도·부가 항목(제출처·주소·수량·비고)
  */
  async requestCertificate(input) {
    const payload = typeof input === "string" || input === void 0 ? {
      purpose: input || "\uC81C\uCD9C\uC6A9"
    } : input;
    return this.client.trpc.employee.requestCertificate.mutate(payload);
  }
  async myCertificateRequests() {
    return this.client.trpc.employee.myCertificateRequests.query();
  }
  /** 본인 대기 중 증명서 신청 취소 */
  async cancelCertificateRequest(id) {
    return this.client.trpc.employee.cancelCertificateRequest.mutate(id);
  }
  async approveCertificate(input) {
    return this.client.trpc.employee.approveCertificate.mutate(input);
  }
  async previewCertificate(id) {
    return this.client.trpc.employee.previewCertificate.query(id);
  }
  async rejectCertificate(id, reason) {
    return this.client.trpc.employee.rejectCertificate.mutate({
      id,
      reason
    });
  }
  /** 신규 직원 대량 추가 — dryRun=true 검증, false 커밋 (All-or-Nothing). */
  async bulkCreateValidated(input) {
    return this.client.trpc.employee.bulkCreateValidated.mutate(input);
  }
  /** 직원 정보 대량 수정 — 식별키 매칭 + All-or-Nothing. */
  async bulkUpdateValidated(input) {
    return this.client.trpc.employee.bulkUpdateValidated.mutate(input);
  }
  async exportRoster(filter) {
    return this.client.trpc.employee.exportRoster.mutate(filter);
  }
  async statusHistory(employeeId) {
    return this.client.trpc.employee.statusHistory.query(employeeId);
  }
  async createLeaveRecord(input) {
    return this.client.trpc.employee.createLeaveRecord.mutate(input);
  }
  async returnFromLeave(employeeId) {
    return this.client.trpc.employee.returnFromLeave.mutate(employeeId);
  }
  /** 퇴직 취소(재직 복귀) — 실수로 퇴직 처리한 직원을 되돌린다(MED-834). 사유 필수. */
  async cancelTermination(input) {
    return this.client.trpc.employee.cancelTermination.mutate(input);
  }
  async bulkAssignRole(employeeIds, roleId) {
    return this.client.trpc.employee.bulkAssignRole.mutate({
      employeeIds,
      roleId
    });
  }
  /** 근무 그룹·근무 형태 일괄 배정 — shiftId 를 주면 그룹도 그 형태의 그룹으로 맞춰진다. */
  async bulkAssignShift(employeeIds, shiftGroupId, shiftId) {
    return this.client.trpc.employee.bulkAssignShift.mutate({
      employeeIds,
      shiftGroupId,
      shiftId
    });
  }
  /** 「부 그룹 제거」 — 주 소속이 아닌 소속 줄의 근무 그룹·형태를 비운다(MED-924). 주 소속·직원 근무는 그대로. */
  async clearSecondaryShiftGroups(employeeIds) {
    return this.client.trpc.employee.clearSecondaryShiftGroups.mutate({
      employeeIds
    });
  }
  async certificateHistory(filter) {
    return this.client.trpc.employee.certificateHistory.query(filter);
  }
};
__name(_EmployeeService, "EmployeeService");
var EmployeeService = _EmployeeService;

// src/services/EntityPickerService.ts
var _EntityPickerService = class _EntityPickerService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async directory(input) {
    return this.client.trpc.entityPicker.directory.query(input);
  }
};
__name(_EntityPickerService, "EntityPickerService");
var EntityPickerService = _EntityPickerService;

// src/services/ESignService.ts
var _ESignService = class _ESignService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async listHerebyTemplates() {
    return this.client.trpc.eSign.listHerebyTemplates.query();
  }
  async integrationStatus() {
    return this.client.trpc.eSign.integrationStatus.query();
  }
  async provisionIntegration() {
    return this.client.trpc.eSign.provisionIntegration.mutate();
  }
  async send(input) {
    return this.client.trpc.eSign.send.mutate(input);
  }
  async list(input = {}) {
    return this.client.trpc.eSign.list.query(input);
  }
  async getById(id) {
    return this.client.trpc.eSign.getById.query(id);
  }
  async syncStatus(input) {
    return this.client.trpc.eSign.syncStatus.mutate(input);
  }
  // 목록 새로고침 시 미완료 레코드 여러 건의 Hereby 상태를 한 번에 동기화한다.
  async syncStatuses(input) {
    return this.client.trpc.eSign.syncStatuses.mutate(input);
  }
  async integrationHealth() {
    return this.client.trpc.eSign.integrationHealth.query();
  }
  // Phase 3.1: re-issue a fresh login-token redirect URL on every "열기" click.
  async openContract(id) {
    return this.client.trpc.eSign.openContract.mutate(id);
  }
};
__name(_ESignService, "ESignService");
var ESignService = _ESignService;

// src/services/FixedIncentiveService.ts
var _FixedIncentiveService = class _FixedIncentiveService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.fixedIncentive.list.query({
      filter,
      pagination
    });
  }
  /** Active incentive presets — 유형 picker source on 인센티브 세션 sheets. */
  async listPresets() {
    return this.client.trpc.fixedIncentive.listPresets.query();
  }
  /** 병원별 자기 등록 스위치 (`incentive.self_registration`) — 기본 꺼짐(opt-in). */
  async getSelfRegistrationConfig() {
    return this.client.trpc.fixedIncentive.getSelfRegistrationConfig.query();
  }
  async get(id) {
    return this.client.trpc.fixedIncentive.get.query(id);
  }
  async create(input) {
    return this.client.trpc.fixedIncentive.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.fixedIncentive.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.fixedIncentive.delete.mutate(id);
  }
  async approve(ids) {
    return this.client.trpc.fixedIncentive.approve.mutate({
      ids
    });
  }
  /**
  * IR-3 합산 확인 승인 — (year, month, incentiveType) 의 PENDING 전건
  * 일괄 승인. 팀장은 자기 팀원 건만 승인된다(서버 scope 필터).
  */
  async approveByType(year, month, incentiveType) {
    return this.client.trpc.fixedIncentive.approveByType.mutate({
      year,
      month,
      incentiveType
    });
  }
  // ── IR-4 주말 당직 치료 세션 전자결재 ────────────────────────────
  /**
  * 당직 인원 픽커용 내 팀 동료 목록. workDate 를 주면 그 날짜에 출근
  * 기록이 있는 동료가 `onDuty: true` 로 표시된다 (IR-4 프리필).
  */
  async listMyTeammates(input) {
    return this.client.trpc.fixedIncentive.listMyTeammates.query(input ?? {});
  }
  /** 치료 세션 신청 (당직 치료사 본인 상신). */
  async requestTreatmentSession(input) {
    return this.client.trpc.fixedIncentive.requestTreatmentSession.mutate(input);
  }
  /** 신청 목록 — self 는 본인, 팀장/관리자는 스코프 내 전체. */
  async listTreatmentSessionRequests(input = {}) {
    return this.client.trpc.fixedIncentive.listTreatmentSessionRequests.query(input);
  }
  /** 신청 승인 → 세션 생성. */
  async approveTreatmentSessionRequest(id) {
    return this.client.trpc.fixedIncentive.approveTreatmentSessionRequest.mutate({
      id
    });
  }
  /** 신청 반려 (사유 필수). */
  async rejectTreatmentSessionRequest(id, rejectionReason) {
    return this.client.trpc.fixedIncentive.rejectTreatmentSessionRequest.mutate({
      id,
      rejectionReason
    });
  }
  /** 신청 취소 (본인·대기 상태만). */
  async cancelTreatmentSessionRequest(id) {
    return this.client.trpc.fixedIncentive.cancelTreatmentSessionRequest.mutate({
      id
    });
  }
  /**
  * Treatment-session upsert. Creates or updates a session of N
  * therapists in one shot, computing per-person amounts server-side
  * and fanning out to N FixedIncentive rows. Used by team-manager
  * registration and hr-system therapy-daily edit.
  */
  async upsertTreatmentSession(input) {
    return this.client.trpc.fixedIncentive.upsertTreatmentSession.mutate(input);
  }
};
__name(_FixedIncentiveService, "FixedIncentiveService");
var FixedIncentiveService = _FixedIncentiveService;

// src/services/GeneralAffairsService.ts
var _GeneralAffairsService = class _GeneralAffairsService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  // --- Dashboard ---
  async dashboard() {
    return this.client.trpc.generalAffairs.dashboard.query();
  }
  // --- Training ---
  async listTrainings(filter, pagination) {
    return this.client.trpc.generalAffairs.training.list.query({
      filter,
      pagination
    });
  }
  async getTraining(id) {
    return this.client.trpc.generalAffairs.training.get.query(id);
  }
  async createTraining(input) {
    return this.client.trpc.generalAffairs.training.create.mutate(input);
  }
  async updateTraining(input) {
    return this.client.trpc.generalAffairs.training.update.mutate(input);
  }
  async deleteTraining(id) {
    return this.client.trpc.generalAffairs.training.delete.mutate(id);
  }
  async listCompletions(filter, pagination) {
    return this.client.trpc.generalAffairs.training.completions.query({
      filter,
      pagination
    });
  }
  async addCompletion(input) {
    return this.client.trpc.generalAffairs.training.addCompletion.mutate(input);
  }
  async updateCompletion(input) {
    return this.client.trpc.generalAffairs.training.updateCompletion.mutate(input);
  }
  async deleteCompletion(id) {
    return this.client.trpc.generalAffairs.training.deleteCompletion.mutate(id);
  }
  async trainingDashboard() {
    return this.client.trpc.generalAffairs.training.dashboard.query();
  }
  // --- Tasks ---
  async listTasks(filter, pagination) {
    return this.client.trpc.generalAffairs.task.list.query({
      filter,
      pagination
    });
  }
  async getTask(id) {
    return this.client.trpc.generalAffairs.task.get.query(id);
  }
  async createTask(input) {
    return this.client.trpc.generalAffairs.task.create.mutate(input);
  }
  async updateTask(input) {
    return this.client.trpc.generalAffairs.task.update.mutate(input);
  }
  async deleteTask(id) {
    return this.client.trpc.generalAffairs.task.delete.mutate(id);
  }
  async listExecutions(filter, pagination) {
    return this.client.trpc.generalAffairs.task.executions.query({
      filter,
      pagination
    });
  }
  async createExecution(input) {
    return this.client.trpc.generalAffairs.task.createExecution.mutate(input);
  }
  async updateExecution(input) {
    return this.client.trpc.generalAffairs.task.updateExecution.mutate(input);
  }
  // --- Notices ---
  async listNotices(filter, pagination) {
    return this.client.trpc.generalAffairs.notice.list.query({
      filter,
      pagination
    });
  }
  async getNotice(id) {
    return this.client.trpc.generalAffairs.notice.get.query(id);
  }
  async createNotice(input) {
    return this.client.trpc.generalAffairs.notice.create.mutate(input);
  }
  async updateNotice(input) {
    return this.client.trpc.generalAffairs.notice.update.mutate(input);
  }
  async deleteNotice(id) {
    return this.client.trpc.generalAffairs.notice.delete.mutate(id);
  }
  async publishNotice(id) {
    return this.client.trpc.generalAffairs.notice.publish.mutate(id);
  }
  /** 특정 공지를 확인한 직원 목록 (확인 내역 팝업) */
  /** 관리자가 직원 대신 공지 확인 처리 (데이터 조율용, CS-LOC-260909-0009). */
  async acknowledgeNoticeOnBehalf(input) {
    return this.client.trpc.generalAffairs.notice.acknowledgeOnBehalf.mutate(input);
  }
  /** 게시 후 확인 인원 표시값만 변경 (CS-LOC-260909-0009). */
  async setNoticeAcknowledgedCountOverride(input) {
    return this.client.trpc.generalAffairs.notice.setAcknowledgedCountOverride.mutate(input);
  }
  /** 게시된 공지의 수정 이력 — 수정 전 판들, 최신 판이 먼저 (MED-974) */
  async listNoticeRevisions(noticeId) {
    return this.client.trpc.generalAffairs.notice.revisions.query(noticeId);
  }
  async getNoticeAcknowledgedEmployees(noticeId) {
    return this.client.trpc.generalAffairs.notice.getAcknowledgedEmployees.query(noticeId);
  }
  /** 공지 게시 대상 팀 선택용 — 조직의 활성 팀 목록 */
  async listNoticeTeams() {
    return this.client.trpc.generalAffairs.notice.listTeams.query();
  }
  // --- Employee-facing Notices ---
  async listPublishedNotices(filter, pagination) {
    return this.client.trpc.generalAffairs.notice.listPublished.query({
      filter,
      pagination
    });
  }
  async acknowledgeNotice(noticeId) {
    return this.client.trpc.generalAffairs.notice.acknowledge.mutate(noticeId);
  }
  async unreadNoticeCount() {
    return this.client.trpc.generalAffairs.notice.unreadCount.query();
  }
};
__name(_GeneralAffairsService, "GeneralAffairsService");
var GeneralAffairsService = _GeneralAffairsService;

// src/services/GradePromotionService.ts
var _GradePromotionService = class _GradePromotionService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async detect() {
    return this.client.trpc.gradePromotion.detect.mutate();
  }
  // biome-ignore lint/suspicious/noExplicitAny: dynamic filter
  async list(filter) {
    return this.client.trpc.gradePromotion.list.query(filter);
  }
  async approve(ids) {
    return this.client.trpc.gradePromotion.approve.mutate({
      ids
    });
  }
  async reject(ids, reason) {
    return this.client.trpc.gradePromotion.reject.mutate({
      ids,
      reason
    });
  }
  async getSettings() {
    return this.client.trpc.gradePromotion.getSettings.query();
  }
  // biome-ignore lint/suspicious/noExplicitAny: dynamic input
  async updateSettings(input) {
    return this.client.trpc.gradePromotion.updateSettings.mutate(input);
  }
};
__name(_GradePromotionService, "GradePromotionService");
var GradePromotionService = _GradePromotionService;

// src/services/ImpersonationService.ts
var _ImpersonationService = class _ImpersonationService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 시작 모달용 대상 요약 — 이름·사번·역할 칩·유효 권한 수·admin 접근 가능 여부 */
  async preview(employeeId) {
    return this.client.trpc.impersonation.preview.query({
      employeeId
    });
  }
  /** 세션 발급 — 응답은 로그인 응답 모양 + impersonation 메타 */
  async start(input) {
    return this.client.trpc.impersonation.start.mutate(input);
  }
  /** 현재 미리보기 세션 종료 — grant 회수 + 토큰 무효화 */
  async end() {
    return this.client.trpc.impersonation.end.mutate();
  }
};
__name(_ImpersonationService, "ImpersonationService");
var ImpersonationService = _ImpersonationService;

// src/services/InventoryService.ts
var _InventoryService = class _InventoryService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  // ── Categories ──
  async listCategories(filter) {
    return this.client.trpc.inventory.listCategories.query(filter);
  }
  async getCategory(id) {
    return this.client.trpc.inventory.getCategory.query(id);
  }
  async createCategory(input) {
    return this.client.trpc.inventory.createCategory.mutate(input);
  }
  async updateCategory(input) {
    return this.client.trpc.inventory.updateCategory.mutate(input);
  }
  async deleteCategory(id) {
    return this.client.trpc.inventory.deleteCategory.mutate(id);
  }
  // ── Items ──
  async listItems(filter) {
    return this.client.trpc.inventory.listItems.query(filter);
  }
  async getItem(id) {
    return this.client.trpc.inventory.getItem.query(id);
  }
  async createItem(input) {
    return this.client.trpc.inventory.createItem.mutate(input);
  }
  async updateItem(input) {
    return this.client.trpc.inventory.updateItem.mutate(input);
  }
  async deleteItem(id) {
    return this.client.trpc.inventory.deleteItem.mutate(id);
  }
  async adjustQuantity(input) {
    return this.client.trpc.inventory.adjustQuantity.mutate(input);
  }
  async markRefund(input) {
    return this.client.trpc.inventory.markRefund.mutate(input);
  }
  // ── Dashboard ──
  async getExpirationAlerts() {
    return this.client.trpc.inventory.getExpirationAlerts.query();
  }
  async getDashboardSummary() {
    return this.client.trpc.inventory.getDashboardSummary.query();
  }
};
__name(_InventoryService, "InventoryService");
var InventoryService = _InventoryService;

// src/services/InviteService.ts
var _InviteService = class _InviteService {
  constructor(client) {
    __publicField(this, "client");
    __publicField(this, "logger");
    __publicField(this, "publicKeyPem", null);
    this.client = client;
    this.logger = sdkLogger.createServiceLogger("InviteService");
  }
  async encrypt(password) {
    try {
      if (!this.publicKeyPem) {
        const result = await this.client.trpc.auth.publicKey.query();
        this.publicKeyPem = result.publicKeyPem;
      }
      return await encryptPassword(password, this.publicKeyPem);
    } catch (error) {
      this.logger.warn({
        error
      }, "Password encryption unavailable, falling back to plaintext");
      return password;
    }
  }
  /** 초대 정보 조회 (수락 페이지 초기 로드) */
  async info(token) {
    return this.client.trpc.invite.info.query({
      token
    });
  }
  /** 초대 수락 — 성공 시 세션 토큰이 저장된다 (mode: workspace | account) */
  async accept(token, password) {
    const result = await this.client.trpc.invite.accept.mutate({
      token,
      password: await this.encrypt(password)
    });
    this.client.setTokens(result.accessToken, result.refreshToken);
    this.logger.info({
      mode: result.mode
    }, "Invite accepted");
    return result;
  }
};
__name(_InviteService, "InviteService");
var InviteService = _InviteService;

// src/services/ItemCodeService.ts
var _ItemCodeService = class _ItemCodeService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  // ── Allowance Item Codes ──
  async listAllowanceCodes(filter) {
    return this.client.trpc.itemCode.listAllowanceCodes.query(filter);
  }
  async createAllowanceCode(input) {
    return this.client.trpc.itemCode.createAllowanceCode.mutate(input);
  }
  async bulkCreateAllowanceCodes(input) {
    return this.client.trpc.itemCode.bulkCreateAllowanceCodes.mutate(input);
  }
  async updateAllowanceCode(input) {
    return this.client.trpc.itemCode.updateAllowanceCode.mutate(input);
  }
  async deleteAllowanceCode(id) {
    return this.client.trpc.itemCode.deleteAllowanceCode.mutate(id);
  }
  // ── Deduction Item Codes ──
  async listDeductionCodes(filter) {
    return this.client.trpc.itemCode.listDeductionCodes.query(filter);
  }
  async createDeductionCode(input) {
    return this.client.trpc.itemCode.createDeductionCode.mutate(input);
  }
  async bulkCreateDeductionCodes(input) {
    return this.client.trpc.itemCode.bulkCreateDeductionCodes.mutate(input);
  }
  async updateDeductionCode(input) {
    return this.client.trpc.itemCode.updateDeductionCode.mutate(input);
  }
  async deleteDeductionCode(id) {
    return this.client.trpc.itemCode.deleteDeductionCode.mutate(id);
  }
};
__name(_ItemCodeService, "ItemCodeService");
var ItemCodeService = _ItemCodeService;

// src/services/JobDutyService.ts
var _JobDutyService = class _JobDutyService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 직무 목록(기본 근무·허용 근무형태·직원 수·근무형태 분포 포함). activeOnly 로 선택지용 활성 목록만. */
  async list(input) {
    return this.client.trpc.jobDuty.list.query(input);
  }
  async create(input) {
    return this.client.trpc.jobDuty.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.jobDuty.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.jobDuty.delete.mutate(id);
  }
  /** 직무 기본 근무 일괄 재적용 — dryRun(미리보기) → 적용 2단계. 직무·직원을 안 주면 직무가 있는 직원 전체. */
  async reapplyDefaultShifts(input) {
    return this.client.trpc.jobDuty.reapplyDefaultShifts.mutate(input);
  }
  /** 근무 유형 폼 「기본으로 쓰는 직무」 — 이 근무형태를 기본 근무로 쓰는 직무 목록을 통째로 저장한다. */
  async setDefaultShift(shiftId, jobDutyIds) {
    return this.client.trpc.jobDuty.setDefaultShift.mutate({
      shiftId,
      jobDutyIds
    });
  }
};
__name(_JobDutyService, "JobDutyService");
var JobDutyService = _JobDutyService;

// src/services/LeaveImportService.ts
var _LeaveImportService = class _LeaveImportService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async preview(input) {
    return this.client.trpc.leaveImport.preview.mutate(input);
  }
  async run(input) {
    return this.client.trpc.leaveImport.run.mutate(input);
  }
  /** 시트 「연차 신청 리스트」 → 미리보기 (파일 대신 sheetViewId) */
  async previewSheet(sheetViewId) {
    return this.client.trpc.leaveImport.previewSheet.mutate({
      sheetViewId
    });
  }
  /** 시트 「연차 신청 리스트」 → 검증(dryRun)/반영. 이미 이관된 행은 오류 없이 건너뛴다 */
  async runSheet(input) {
    return this.client.trpc.leaveImport.runSheet.mutate(input);
  }
  async listBatches() {
    return this.client.trpc.leaveImport.listBatches.query();
  }
  async revert(batchId) {
    return this.client.trpc.leaveImport.revert.mutate({
      batchId
    });
  }
};
__name(_LeaveImportService, "LeaveImportService");
var LeaveImportService = _LeaveImportService;

// src/services/LeaveRequestService.ts
var _LeaveRequestService = class _LeaveRequestService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.leaveRequest.list.query({
      filter,
      pagination
    });
  }
  /** 휴가 건을 휴가신청서 서식(A4 인쇄 HTML)으로 렌더링 */
  async renderForm(id) {
    return this.client.trpc.leaveRequest.renderForm.query(id);
  }
  async get(id) {
    return this.client.trpc.leaveRequest.get.query(id);
  }
  async create(input) {
    return this.client.trpc.leaveRequest.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.leaveRequest.update.mutate(input);
  }
  /** 승인된 휴가 취소 신청(MED-959) — 시작 전이면 전체, 시작 뒤면 오늘부터 남은 날 */
  async requestCancel(id, reason) {
    return this.client.trpc.leaveRequest.requestCancel.mutate({
      id,
      reason
    });
  }
  /** 결재 전 취소 신청 철회 */
  async withdrawCancelRequest(id) {
    return this.client.trpc.leaveRequest.withdrawCancelRequest.mutate(id);
  }
  /** 취소 신청 결재(관리자) — approve=false 면 거절(휴가 그대로) */
  async resolveCancelRequest(id, approve, rejectionReason) {
    return this.client.trpc.leaveRequest.resolveCancelRequest.mutate({
      id,
      approve,
      rejectionReason
    });
  }
  async cancel(id) {
    return this.client.trpc.leaveRequest.cancel.mutate(id);
  }
  async approve(input) {
    return this.client.trpc.leaveRequest.approve.mutate(input);
  }
  async request(input) {
    return this.client.trpc.leaveRequest.request.mutate(input);
  }
  async reject(input) {
    return this.client.trpc.leaveRequest.reject.mutate(input);
  }
  /** 연차 현황 — basis 를 주면 그 기준으로 본다(적용 기준과 다르면 참고 환산, 응답 basis.reference=true) */
  async listBalances(filter) {
    return this.client.trpc.leaveRequest.listBalances.query(filter || {});
  }
  async myBalance(asOfDate) {
    return this.client.trpc.leaveRequest.myBalance.query(asOfDate ? {
      asOfDate
    } : void 0);
  }
  async getBalance(employeeId, asOfDate) {
    return this.client.trpc.leaveRequest.getBalance.query({
      employeeId,
      asOfDate
    });
  }
  /** [상세] 적용된 규칙 탭 + [조정] 팝업 공통 데이터 — 적용 규칙·기간·잔여·소정근로시간 */
  /** 퇴사 정산 기준값 — 입사일 기준 잔여(적용 기준이 회계년도여도 입사일 기준으로 환산) */
  async settlementBalance(employeeId, asOfDate) {
    return this.client.trpc.leaveRequest.settlementBalance.query({
      employeeId,
      asOfDate
    });
  }
  async balanceRuleDetail(employeeId) {
    return this.client.trpc.leaveRequest.balanceRuleDetail.query({
      employeeId
    });
  }
  /** [상세] 조정 내역 탭 — 수동 ADJUST 원장 목록 */
  async listBalanceAdjustments(employeeId) {
    return this.client.trpc.leaveRequest.listBalanceAdjustments.query({
      employeeId
    });
  }
  /** [조정] — 잔여를 목표값(일)으로 맞추는 수동 조정 (8시간=1일, 1시간 단위) */
  async adjustBalance(input) {
    return this.client.trpc.leaveRequest.adjustBalance.mutate(input);
  }
  async listEntitlementRules() {
    return this.client.trpc.leaveRequest.listEntitlementRules.query();
  }
  // ── 산정 기준(입사일/회계연도) 변경 예약 ──
  async listBasisChanges() {
    return this.client.trpc.leaveRequest.listBasisChanges.query();
  }
  async scheduleBasisChange(input) {
    return this.client.trpc.leaveRequest.scheduleBasisChange.mutate(input);
  }
  async cancelBasisChange(id) {
    return this.client.trpc.leaveRequest.cancelBasisChange.mutate({
      id
    });
  }
  /**
  * 연차 정책을 저장하기 전에 영향을 미리 본다 — 새로 켜지는 차단 검사 때문에
  * 승인 불가가 될 대기 신청 목록을 돌려준다. 아무것도 바꾸지 않는다.
  */
  /** 신청 전 차감 미리보기(MED-958) — 본인 기준 운영 규칙으로 센 일수·제외일 */
  async estimateDeduction(input) {
    return this.client.trpc.leaveRequest.estimateDeduction.query(input);
  }
  async previewPolicyImpact(policy) {
    return this.client.trpc.leaveRequest.previewPolicyImpact.query(policy);
  }
  async upsertEntitlementRule(input) {
    return this.client.trpc.leaveRequest.upsertEntitlementRule.mutate(input);
  }
  async deleteEntitlementRule(id) {
    return this.client.trpc.leaveRequest.deleteEntitlementRule.mutate(id);
  }
  /**
  * 보정 이전 공휴일 소진 이력 — 읽기 전용 리포트(자동 수정 없음).
  * 유급휴일이 연차로 차감된 원장 엔트리 목록 + 추정 과차감 분(분 단위).
  */
  async holidayConsumptionReport() {
    return this.client.trpc.leaveRequest.holidayConsumptionReport.query();
  }
};
__name(_LeaveRequestService, "LeaveRequestService");
var LeaveRequestService = _LeaveRequestService;

// src/services/ManagementDashboardService.ts
var _ManagementDashboardService = class _ManagementDashboardService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async laborCostByTeam(year, months) {
    return this.client.trpc.managementDashboard.laborCostByTeam.query({
      year,
      months
    });
  }
  async dailyLaborCost(year, month) {
    return this.client.trpc.managementDashboard.dailyLaborCost.query({
      year,
      month
    });
  }
  async incentiveSummary(year, month, page = 1, limit = 20) {
    return this.client.trpc.managementDashboard.incentiveSummary.query({
      year,
      month,
      page,
      limit
    });
  }
  /** IR-5 원장 당일 현황 — 오늘/어제 인센티브 발생 요약. */
  async incentiveDailySnapshot() {
    return this.client.trpc.managementDashboard.incentiveDailySnapshot.query();
  }
  async staffingOverview(date) {
    return this.client.trpc.managementDashboard.staffingOverview.query({
      date
    });
  }
  async teamLaborCost(year, month) {
    return this.client.trpc.managementDashboard.teamLaborCost.query({
      year,
      month
    });
  }
  /** 월별 청구 금액 목록 (연도 지정 시 그 해만) */
  async revenueList(year) {
    return this.client.trpc.managementDashboard.revenueList.query({
      year
    });
  }
  /** 월별 청구 금액 저장. claimAmount 0 → 해당 월 입력 취소(소프트 삭제). */
  async revenueUpsert(yearMonth, claimAmount) {
    return this.client.trpc.managementDashboard.revenueUpsert.mutate({
      yearMonth,
      claimAmount
    });
  }
  /** 월별 청구 금액 삭제 */
  async revenueRemove(yearMonth) {
    return this.client.trpc.managementDashboard.revenueRemove.mutate({
      yearMonth
    });
  }
};
__name(_ManagementDashboardService, "ManagementDashboardService");
var ManagementDashboardService = _ManagementDashboardService;

// src/services/MessageService.ts
var _MessageService = class _MessageService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(input) {
    return this.client.trpc.message.list.query(input);
  }
  async send(input) {
    return this.client.trpc.message.send.mutate(input);
  }
  async markRead(input) {
    return this.client.trpc.message.markRead.mutate(input);
  }
  /** 내가 보낸 메시지 삭제 — 참여자 전원 화면에서 「삭제된 메시지입니다」 자리 표시로 바뀐다 (MED-941) */
  async delete(input) {
    return this.client.trpc.message.delete.mutate(input);
  }
  /** 이모지 반응 토글(2026-10-20) — 같은 이모지를 다시 보내면 뺀다. 응답 `active` 가 붙었는지 */
  async react(input) {
    return this.client.trpc.message.react.mutate(input);
  }
  /** 대화방 고정 메시지(2026-10-20) — `messageId: null` 은 해제 */
  async pin(input) {
    return this.client.trpc.message.pin.mutate(input);
  }
  /** 방의 반응 전부 + 고정 메시지 — 방을 열 때 메시지 목록과 따로 한 번 읽는다 */
  async extras(input) {
    return this.client.trpc.message.extras.query(input);
  }
  async search(input) {
    return this.client.trpc.message.search.query(input);
  }
  async sendWithAttachments(input) {
    return this.client.trpc.message.sendWithAttachments.mutate(input);
  }
  /** 방 구독 — 새 메시지(kind 없음)·삭제(`deleted`)·읽음(`read`)·반응(`reaction`)·고정(`pinned`)이 같은 줄로 온다. */
  subscribeToMessages(conversationId, onData, onError, onStarted) {
    const subscription = this.client.trpc.message.onNewMessage.subscribe({
      conversationId
    }, {
      onStarted: /* @__PURE__ */ __name(() => onStarted?.(), "onStarted"),
      onData: /* @__PURE__ */ __name((data) => onData(data), "onData"),
      onError: /* @__PURE__ */ __name((err) => onError?.(err), "onError")
    });
    return subscription;
  }
  subscribeToConversationUpdates(onData, onError) {
    const subscription = this.client.trpc.message.onConversationUpdate.subscribe(void 0, {
      onData: /* @__PURE__ */ __name((data) => onData(data), "onData"),
      onError: /* @__PURE__ */ __name((err) => onError?.(err), "onError")
    });
    return subscription;
  }
  /** 방 키 봉투 이벤트(2026-10-15) — 봉투 도착(envelopes)·봉투 요청(request). 직원 단위. */
  subscribeToChatKeyEvents(onData, onError) {
    const subscription = this.client.trpc.message.onChatKeyEvent.subscribe(void 0, {
      onData: /* @__PURE__ */ __name((data) => onData(data), "onData"),
      onError: /* @__PURE__ */ __name((err) => onError?.(err), "onError")
    });
    return subscription;
  }
  subscribeToReadSync(onData, onError) {
    const subscription = this.client.trpc.message.onReadSync.subscribe(void 0, {
      onData: /* @__PURE__ */ __name((data) => onData(data), "onData"),
      onError: /* @__PURE__ */ __name((err) => onError?.(err), "onError")
    });
    return subscription;
  }
};
__name(_MessageService, "MessageService");
var MessageService = _MessageService;

// src/services/ModuleRegistryService.ts
var _ModuleRegistryService = class _ModuleRegistryService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list() {
    return this.client.trpc.moduleRegistry.list.query();
  }
};
__name(_ModuleRegistryService, "ModuleRegistryService");
var ModuleRegistryService = _ModuleRegistryService;

// src/services/NotificationService.ts
var NOTIFICATION_SETTINGS_CHANGED_EVENT = "hereby:notification-settings-changed";
function announceSettingsChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(NOTIFICATION_SETTINGS_CHANGED_EVENT));
  }
}
__name(announceSettingsChanged, "announceSettingsChanged");
var _NotificationService = class _NotificationService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  // ── Preference Settings (sparse per-type per-channel matrix) ──
  /** Get all preference overrides for current user: { [eventType]: { [channel]: enabled } } */
  async getSettings() {
    return this.client.trpc.notification.getSettings.query();
  }
  /** Upsert a single preference: eventType + channel + enabled */
  async updateSettings(input) {
    const result = await this.client.trpc.notification.updateSettings.mutate(input);
    announceSettingsChanged();
    return result;
  }
  // ── Device Token ──
  /** 메신저 푸시 포맷(MED-893, D4) */
  async getMessengerPushFormat() {
    return this.client.trpc.notification.getMessengerPushFormat.query();
  }
  async updateMessengerPushFormat(format) {
    const result = await this.client.trpc.notification.updateMessengerPushFormat.mutate({
      format
    });
    announceSettingsChanged();
    return result;
  }
  /** 휴대폰 알림 방해 금지 시간(MED-967) — { start, end } HH:MM 또는 null(끔) */
  async getPushQuietHours() {
    return this.client.trpc.notification.getPushQuietHours.query();
  }
  async updatePushQuietHours(quietHours) {
    const result = await this.client.trpc.notification.updatePushQuietHours.mutate({
      quietHours
    });
    announceSettingsChanged();
    return result;
  }
  async registerDevice(input) {
    return this.client.trpc.deviceToken.register.mutate(input);
  }
  async unregisterDevice(input) {
    return this.client.trpc.deviceToken.unregister.mutate(input);
  }
  // ── Inbox (reads from NotificationDelivery joined with NotificationEvent) ──
  async getInbox(filter) {
    return this.client.trpc.notification.inboxList.query(filter);
  }
  async getUnreadCount() {
    const result = await this.client.trpc.notification.inboxUnreadCount.query();
    return result.count;
  }
  async markRead(id) {
    return this.client.trpc.notification.inboxMarkRead.mutate(id);
  }
  async markAllRead() {
    return this.client.trpc.notification.inboxMarkAllRead.mutate();
  }
  async dismiss(id) {
    return this.client.trpc.notification.inboxDismiss.mutate(id);
  }
  /** 알림함 새 행 실시간 구독 — WebSocket(tRPC subscription). 끊기면 onError, 호출부가 다시 건다. */
  subscribeToInbox(onData, onError) {
    return this.client.trpc.notification.onInbox.subscribe(void 0, {
      onData: /* @__PURE__ */ __name((data) => onData(data), "onData"),
      onError: /* @__PURE__ */ __name((err) => onError?.(err), "onError")
    });
  }
  async getActionItems() {
    return this.client.trpc.notification.actionItems.query();
  }
  // ── Mute Settings ──
  async getMuteList() {
    return this.client.trpc.notification.muteList.query();
  }
  async mute(input) {
    const result = await this.client.trpc.notification.muteCreate.mutate(input);
    announceSettingsChanged();
    return result;
  }
  async unmute(id) {
    const result = await this.client.trpc.notification.muteDelete.mutate(id);
    announceSettingsChanged();
    return result;
  }
};
__name(_NotificationService, "NotificationService");
var NotificationService = _NotificationService;

// src/services/NurseSchedulerService.ts
var _NurseSchedulerService = class _NurseSchedulerService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  // --- Ward (physical hospital unit, separate from Team) ---
  /**
  * `background: true` — 다른 화면(팀 수정 · 직원 수정)이 선택지를 채우려 배경으로 읽는 호출. 권한이 없어도
  * 권한 모달을 띄우지 않는다(호출부가 빈 목록으로 넘어간다).
  */
  async listWards(filter, opts) {
    return this.client.trpc.nurseScheduler.ward.list.query(filter, quietForbiddenContext(opts));
  }
  async getWard(id) {
    return this.client.trpc.nurseScheduler.ward.get.query(id);
  }
  async createWard(input) {
    return this.client.trpc.nurseScheduler.ward.create.mutate(input);
  }
  async updateWard(input) {
    return this.client.trpc.nurseScheduler.ward.update.mutate(input);
  }
  async deleteWard(id) {
    return this.client.trpc.nurseScheduler.ward.delete.mutate(id);
  }
  /** 병동 관리 「직원 배정」 미리보기 — 실행과 같은 입력으로 직원마다 바뀌는 것 · 부수 효과를 돌려준다(DB 변경 없음).
  *  읽기지만 mutation 이다 — 직원이 20명쯤 넘으면 GET URL 이 batch 링크 한도(1800자)를 넘어 요청이 아예 나가지 않는다. */
  async previewWardAssign(input) {
    return this.client.trpc.nurseScheduler.ward.assignPreview.mutate(input);
  }
  /** 병동 관리 「직원 배정」 실행 — 연결 팀 · 겸임/이동(지금 · 예약) · 근무 동반을 직원마다 한 번에 적용한다. */
  async assignEmployeesToWard(input) {
    return this.client.trpc.nurseScheduler.ward.assignEmployees.mutate(input);
  }
  /** 병동 배정 해제 — 고른 직원의 그 병동 배정만 끝낸다(팀 소속 · 근무는 그대로). 되살릴 id 를 결과에 담아 준다. */
  async unassignEmployeesFromWard(input) {
    return this.client.trpc.nurseScheduler.ward.unassignEmployees.mutate(input);
  }
  /** 해제 되돌리기 — 방금 끝낸 배정을 그대로 되살린다. */
  async restoreWardAssignments(input) {
    return this.client.trpc.nurseScheduler.ward.restoreAssignments.mutate(input);
  }
  // --- Schedule group (스케줄러 — 병동 안에서 근무표를 나누는 단위) ---
  /** 조직의 스케줄러 목록(병동 이름 포함). wardId 로 한 병동만. */
  async listScheduleGroups(filter, opts) {
    return this.client.trpc.nurseScheduler.scheduleGroup.list.query(filter, quietForbiddenContext(opts));
  }
  async getScheduleGroup(id) {
    return this.client.trpc.nurseScheduler.scheduleGroup.get.query(id);
  }
  async createScheduleGroup(input) {
    return this.client.trpc.nurseScheduler.scheduleGroup.create.mutate(input);
  }
  async updateScheduleGroup(input) {
    return this.client.trpc.nurseScheduler.scheduleGroup.update.mutate(input);
  }
  async deleteScheduleGroup(id) {
    return this.client.trpc.nurseScheduler.scheduleGroup.delete.mutate(id);
  }
  /** 스케줄러 편성 설정 — 교대 매핑(기본 시간대) · 지원 근무형태 · 교육 정책 · 포함 직군 */
  async getScheduleGroupSettings(scheduleGroupId) {
    return this.client.trpc.nurseScheduler.scheduleGroup.getSettings.query({
      scheduleGroupId
    });
  }
  async updateScheduleGroupSettings(input) {
    return this.client.trpc.nurseScheduler.scheduleGroup.updateSettings.mutate(input);
  }
  // --- Hospital-wide config ---
  async getConfig() {
    return this.client.trpc.nurseScheduler.config.get.query();
  }
  async updateConfig(input) {
    return this.client.trpc.nurseScheduler.config.update.mutate(input);
  }
  // --- Profile ---
  async listProfiles(filter, pagination) {
    return this.client.trpc.nurseScheduler.profile.list.query({
      filter,
      pagination
    });
  }
  async getProfile(employeeId, opts) {
    return this.client.trpc.nurseScheduler.profile.get.query(employeeId, quietForbiddenContext(opts));
  }
  /** 자동등록 후보 — 병동 연결 팀 소속원 + 직무 조건에 맞는 재직·휴직 직원 중 이 스케줄러 미등록자. */
  async listAutoRegisterCandidates(input) {
    return this.client.trpc.nurseScheduler.profile.autoRegisterCandidates.query(input);
  }
  async createProfile(input) {
    return this.client.trpc.nurseScheduler.profile.create.mutate(input);
  }
  async updateProfile(input) {
    return this.client.trpc.nurseScheduler.profile.update.mutate(input);
  }
  async deleteProfile(id) {
    return this.client.trpc.nurseScheduler.profile.delete.mutate(id);
  }
  // --- Ward reservation (예약 이동) ---
  async createWardReservation(input) {
    return this.client.trpc.nurseScheduler.reservation.create.mutate(input);
  }
  async updateWardReservation(input) {
    return this.client.trpc.nurseScheduler.reservation.update.mutate(input);
  }
  async cancelWardReservation(id) {
    return this.client.trpc.nurseScheduler.reservation.cancel.mutate(id);
  }
  // --- Schedule ---
  async listSchedules(filter, pagination) {
    return this.client.trpc.nurseScheduler.schedule.list.query({
      filter,
      pagination
    });
  }
  async getSchedule(id) {
    return this.client.trpc.nurseScheduler.schedule.get.query(id);
  }
  /** 스케줄러(없으면 병동 기본)·연·월의 최신 근무표(entries 포함) 1건 — 없으면 null. list+get 2왕복 대체. */
  async findScheduleByPeriod(input) {
    return this.client.trpc.nurseScheduler.schedule.findByPeriod.query(input);
  }
  async createSchedule(input) {
    return this.client.trpc.nurseScheduler.schedule.create.mutate(input);
  }
  async generateSchedule(scheduleId) {
    return this.client.trpc.nurseScheduler.schedule.generate.mutate({
      scheduleId
    });
  }
  async getGenerationStatus(scheduleId) {
    return this.client.trpc.nurseScheduler.schedule.generationStatus.query(scheduleId);
  }
  async cancelGeneration(scheduleId) {
    return this.client.trpc.nurseScheduler.schedule.cancelGeneration.mutate({
      scheduleId
    });
  }
  async confirmSchedule(scheduleId) {
    return this.client.trpc.nurseScheduler.schedule.confirm.mutate({
      scheduleId
    });
  }
  async publishSchedule(scheduleId) {
    return this.client.trpc.nurseScheduler.schedule.publish.mutate({
      scheduleId
    });
  }
  async revertScheduleToEditing(scheduleId) {
    return this.client.trpc.nurseScheduler.schedule.revertToEditing.mutate({
      scheduleId
    });
  }
  async deleteSchedule(id) {
    return this.client.trpc.nurseScheduler.schedule.delete.mutate(id);
  }
  // --- Entry ---
  async updateEntry(input) {
    return this.client.trpc.nurseScheduler.entry.update.mutate(input);
  }
  async batchUpdateEntries(input) {
    return this.client.trpc.nurseScheduler.entry.batchUpdate.mutate(input);
  }
  async batchDeleteEntries(input) {
    return this.client.trpc.nurseScheduler.entry.batchDelete.mutate(input);
  }
  async validateSchedule(scheduleId) {
    return this.client.trpc.nurseScheduler.entry.validate.query({
      scheduleId
    });
  }
  /** 겸임 중복 근무 — 이 근무표 직원이 같은 날 다른 근무표에도 근무로 배정된 셀(표시용, 저장은 막지 않음). */
  async getScheduleConflicts(scheduleId) {
    return this.client.trpc.nurseScheduler.entry.conflicts.query({
      scheduleId
    });
  }
  // --- Preference ---
  async listPreferences(filter, pagination) {
    return this.client.trpc.nurseScheduler.preference.list.query({
      filter,
      pagination
    });
  }
  async getMyPreference(year, month, employeeId) {
    return this.client.trpc.nurseScheduler.preference.get.query({
      year,
      month,
      employeeId
    });
  }
  async createPreference(input) {
    return this.client.trpc.nurseScheduler.preference.create.mutate(input);
  }
  async updatePreference(input) {
    return this.client.trpc.nurseScheduler.preference.update.mutate(input);
  }
  async submitPreference(id) {
    return this.client.trpc.nurseScheduler.preference.submit.mutate({
      id
    });
  }
  /** 수간호사가 종이 취합본을 대신 입력. (employeeId, year, month) upsert + SUBMITTED. */
  async upsertPreferenceByManager(input) {
    return this.client.trpc.nurseScheduler.preference.upsertByManager.mutate(input);
  }
  // --- WorkTypeChange ---
  async listWorkTypeChanges(filter, pagination) {
    return this.client.trpc.nurseScheduler.workTypeChange.list.query({
      filter,
      pagination
    });
  }
  async requestWorkTypeChange(input) {
    return this.client.trpc.nurseScheduler.workTypeChange.request.mutate(input);
  }
  async cancelWorkTypeChange(id) {
    return this.client.trpc.nurseScheduler.workTypeChange.cancel.mutate(id);
  }
  async approveWorkTypeChange(id) {
    return this.client.trpc.nurseScheduler.workTypeChange.approve.mutate({
      id
    });
  }
  async rejectWorkTypeChange(id, rejectionReason) {
    return this.client.trpc.nurseScheduler.workTypeChange.reject.mutate({
      id,
      rejectionReason
    });
  }
  // --- Ward Settings ---
  async getWardSettings(wardId) {
    return this.client.trpc.nurseScheduler.wardSettings.get.query({
      wardId
    });
  }
  async updateWardSettings(input) {
    return this.client.trpc.nurseScheduler.wardSettings.update.mutate(input);
  }
  // --- Daily Record ---
  async upsertDailyPatientCount(input) {
    return this.client.trpc.nurseScheduler.dailyRecord.upsert.mutate(input);
  }
  async getDailyPatientCount(wardId, date) {
    return this.client.trpc.nurseScheduler.dailyRecord.get.query({
      wardId,
      date
    });
  }
  async getDailyPatientCountRange(wardId, startDate, endDate) {
    return this.client.trpc.nurseScheduler.dailyRecord.getRange.query({
      wardId,
      startDate,
      endDate
    });
  }
};
__name(_NurseSchedulerService, "NurseSchedulerService");
var NurseSchedulerService = _NurseSchedulerService;

// src/services/OffsiteWorkService.ts
var _OffsiteWorkService = class _OffsiteWorkService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.offsiteWork.list.query({
      filter,
      pagination
    });
  }
  async get(id) {
    return this.client.trpc.offsiteWork.get.query(id);
  }
  /** 외근 유형 목록 (활성) — 신청·보고서 유형 select 공통 사용 */
  async listTypes() {
    return this.client.trpc.offsiteWork.listTypes.query();
  }
  /** Admin 외근 유형 관리 — 전체 목록 (비활성 포함) */
  async listAllTypes() {
    return this.client.trpc.offsiteWork.listAllTypes.query();
  }
  async createType(input) {
    return this.client.trpc.offsiteWork.createType.mutate(input);
  }
  async updateType(input) {
    return this.client.trpc.offsiteWork.updateType.mutate(input);
  }
  async deleteType(id) {
    return this.client.trpc.offsiteWork.deleteType.mutate(id);
  }
  /** Works 외근 신청 */
  async request(input) {
    return this.client.trpc.offsiteWork.request.mutate(input);
  }
  /** Works: 대기 상태 외근 신청 취소 */
  async cancelRequest(id) {
    return this.client.trpc.offsiteWork.cancelRequest.mutate({
      id
    });
  }
  /** Works 외근 보고서 제출 — 시간·유형은 신청 기본값에서 수정 가능 */
  async submitReport(id, actualHours, summary, opts) {
    return this.client.trpc.offsiteWork.submitReport.mutate({
      id,
      actualHours,
      summary,
      startTime: opts?.startTime,
      endTime: opts?.endTime,
      typeId: opts?.typeId
    });
  }
  async approve(ids) {
    return this.client.trpc.offsiteWork.approve.mutate({
      ids
    });
  }
  /** Admin 일괄 반려 — 사유 필수 (이력 모달 표시용) */
  async reject(ids, reason) {
    return this.client.trpc.offsiteWork.reject.mutate({
      ids,
      reason
    });
  }
  /**
  * Admin: 승인 이후 단계 (APPROVED / REPORT_PENDING / REPORT_APPROVED) 외근 취소.
  * 사유 필수. 보고서 승인분에 부착됐던 attendance row 도 함께 무효화된다.
  */
  async cancelApproved(id, reason) {
    return this.client.trpc.offsiteWork.cancelApproved.mutate({
      id,
      reason
    });
  }
  /** Admin 보고서 일괄 승인 (REPORT_PENDING → REPORT_APPROVED) + 근태 반영 */
  async approveReport(ids) {
    return this.client.trpc.offsiteWork.approveReport.mutate({
      ids
    });
  }
  /** Admin 보고서 반려 (REPORT_PENDING → REPORT_REJECTED, 사유 필수) */
  async rejectReport(id, reason) {
    return this.client.trpc.offsiteWork.rejectReport.mutate({
      id,
      reason
    });
  }
  /** 이력 모달 — 결재자/결재일시/사유 entries 를 반환 */
  async history(id) {
    return this.client.trpc.offsiteWork.history.query(id);
  }
  /** 외근 건을 외근신청서 서식(A4 인쇄 HTML)으로 렌더링 */
  async renderForm(id) {
    return this.client.trpc.offsiteWork.renderForm.query(id);
  }
};
__name(_OffsiteWorkService, "OffsiteWorkService");
var OffsiteWorkService = _OffsiteWorkService;

// src/services/OrganizationHolidayService.ts
var _OrganizationHolidayService = class _OrganizationHolidayService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(year) {
    return this.client.trpc.organizationHoliday.list.query({
      year
    });
  }
  async create(input) {
    return this.client.trpc.organizationHoliday.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.organizationHoliday.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.organizationHoliday.delete.mutate({
      id
    });
  }
  /**
  * 국가 공휴일의 조직별 적용/제외 토글 — 제외하면 이 조직에서 유급휴일이 아니게 된다
  * (연차 차감 제외·신청 차단·휴일 분류에서 모두 빠짐 — 노동절을 일반 근무일로 쓰는 조직 등).
  * recurring=true면 매년 반복 제외(MM-DD 저장).
  */
  async setNationalHolidayApplied(input) {
    return this.client.trpc.organizationHoliday.setNationalHolidayApplied.mutate(input);
  }
  /**
  * 국가 공휴일 동기화 — 공공데이터포털 특일정보 API → national_holidays(판정 정본).
  * 대체공휴일·임시공휴일 발표 반영용. 연도 생략 시 작년·올해·내년을 동기화한다.
  */
  async syncNationalHolidays(years) {
    return this.client.trpc.organizationHoliday.syncNationalHolidays.mutate(years ? {
      years
    } : void 0);
  }
  /**
  * 연도별 준법 상태 — 빈 달력 경고(emptyCalendarWarning)·공휴일 제외 실효 여부·
  * 휴일근무 실효 모드(안전 폴백 반영)를 돌려준다. admin 경고 배너용.
  */
  async complianceStatus(year) {
    return this.client.trpc.organizationHoliday.complianceStatus.query({
      year
    });
  }
  async listSubstitutions(year) {
    return this.client.trpc.organizationHoliday.listSubstitutions.query(year ? {
      year
    } : void 0);
  }
  async upsertSubstitution(input) {
    return this.client.trpc.organizationHoliday.upsertSubstitution.mutate(input);
  }
  async deleteSubstitution(id) {
    return this.client.trpc.organizationHoliday.deleteSubstitution.mutate({
      id
    });
  }
  /** 기간 내 유급휴일 분류(휴일근로/휴일대체, 안전 폴백 반영) — 급여·스케줄 계층 전달용 */
  async classifyHolidayWork(startDate, endDate) {
    return this.client.trpc.organizationHoliday.classifyHolidayWork.query({
      startDate,
      endDate
    });
  }
};
__name(_OrganizationHolidayService, "OrganizationHolidayService");
var OrganizationHolidayService = _OrganizationHolidayService;

// src/services/OrganizationParameterService.ts
var _OrganizationParameterService = class _OrganizationParameterService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(input = {}) {
    return this.client.trpc.organizationParameter.list.query(input);
  }
  async getValue(input) {
    return this.client.trpc.organizationParameter.getValue.query(input);
  }
  async create(input) {
    return this.client.trpc.organizationParameter.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.organizationParameter.update.mutate(input);
  }
  async delete(input) {
    return this.client.trpc.organizationParameter.delete.mutate(input);
  }
  async history(input) {
    return this.client.trpc.organizationParameter.history.query(input);
  }
};
__name(_OrganizationParameterService, "OrganizationParameterService");
var OrganizationParameterService = _OrganizationParameterService;

// src/services/OrganizationService.ts
var _OrganizationService = class _OrganizationService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /**
  * Create a new organization (public endpoint)
  * @param input Organization creation data including admin details
  * @returns Created organization and admin employee
  */
  async create(input) {
    return this.client.trpc.organization.create.mutate(input);
  }
  /**
  * Get organization by ID (super admin only)
  * @param id Organization ID
  * @returns Organization details
  */
  async getById(id) {
    return this.client.trpc.organization.getById.query({
      id
    });
  }
  /**
  * Get current organization
  * @returns Current organization details
  */
  async getCurrent() {
    return this.client.trpc.organization.getCurrent.query();
  }
  /**
  * 직원 앱 첫화면 레이아웃(병원별 카드 노출·순서) — 권한 관문 없음, 해석된 값
  */
  async getEmployeeHomeLayout() {
    return this.client.trpc.organization.getEmployeeHomeLayout.query();
  }
  /** 신청 시간 단위(분) — 연장·외근 신청/보고, 시간 단위 휴가의 시각 선택 간격(15/30/60, 기본 30 · MED-901) */
  async getRequestTimeStep() {
    return this.client.trpc.organization.getRequestTimeStep.query();
  }
  /** 직원 앱 화면 캡처 제한 켬/끔 — 권한 관문 없음, 해석된 값(설정 키가 없으면 켬) */
  async getScreenCaptureProtection() {
    return this.client.trpc.organization.getScreenCaptureProtection.query();
  }
  /**
  * List all organizations (super admin only)
  * @param query Query parameters for filtering and pagination
  * @returns Paginated list of organizations
  */
  async list(query) {
    return this.client.trpc.organization.list.query(query || {});
  }
  /**
  * Update organization
  * @param id Organization ID
  * @param data Update data
  * @returns Updated organization
  */
  async update(id, data) {
    return this.client.trpc.organization.update.mutate({
      id,
      data
    });
  }
  /**
  * Update organization settings
  * @param settings Settings to update
  * @returns Updated settings
  */
  async updateSettings(settings) {
    return this.client.trpc.organization.updateSettings.mutate({
      settings
    });
  }
  /**
  * Suspend organization (super admin only)
  * @param id Organization ID
  * @param reason Suspension reason
  * @returns Updated organization
  */
  async suspend(id, reason) {
    return this.client.trpc.organization.suspend.mutate({
      id,
      reason
    });
  }
  /**
  * Reactivate organization (super admin only)
  * @param id Organization ID
  * @returns Updated organization
  */
  async reactivate(id) {
    return this.client.trpc.organization.reactivate.mutate({
      id
    });
  }
  /**
  * Get organization statistics
  * @returns Organization statistics including employee counts, payroll totals, etc.
  */
  async getStatistics() {
    return this.client.trpc.organization.getStatistics.query();
  }
  /**
  * Delete organization (soft delete, super admin only)
  * @param id Organization ID
  * @param reason Deletion reason
  * @param confirmationCode Confirmation code
  * @returns Success status
  */
  async delete(id, reason, confirmationCode) {
    return this.client.trpc.organization.delete.mutate({
      id,
      reason,
      confirmationCode
    });
  }
};
__name(_OrganizationService, "OrganizationService");
var OrganizationService = _OrganizationService;

// src/services/OvertimeService.ts
var _OvertimeService = class _OvertimeService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.overtime.list.query({
      filter,
      pagination
    });
  }
  /** 활성 연장근무 유형 목록 (Works 신청·보고서 select). 관리형 OvertimeType 마스터. */
  async listTypes() {
    return this.client.trpc.overtime.listTypes.query();
  }
  /** Admin 유형 관리 — 전체 유형(비활성 포함) + 사용 건수 */
  async listAllTypes() {
    return this.client.trpc.overtime.listAllTypes.query();
  }
  async createType(input) {
    return this.client.trpc.overtime.createType.mutate(input);
  }
  async updateType(input) {
    return this.client.trpc.overtime.updateType.mutate(input);
  }
  async deleteType(id) {
    return this.client.trpc.overtime.deleteType.mutate(id);
  }
  async get(id) {
    return this.client.trpc.overtime.get.query(id);
  }
  async create(input) {
    return this.client.trpc.overtime.create.mutate(input);
  }
  /** Employee self-service overtime request */
  async request(input) {
    return this.client.trpc.overtime.request.mutate(input);
  }
  /** Employee self-service: cancel overtime request */
  async cancelRequest(id) {
    return this.client.trpc.overtime.cancelRequest.mutate({
      id
    });
  }
  /** Employee self-service: request cancellation of an APPROVED overtime (기획서 §3.1) */
  async requestCancel(id, reason) {
    return this.client.trpc.overtime.requestCancel.mutate({
      id,
      reason
    });
  }
  /** Employee self-service: submit overtime report — shiftId로 유형 변경 가능 (승인 건이 기본값) */
  async submitReport(id, actualHours, summary, opts) {
    return this.client.trpc.overtime.submitReport.mutate({
      id,
      actualHours,
      summary,
      category: opts?.category
    });
  }
  async update(input) {
    return this.client.trpc.overtime.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.overtime.delete.mutate(id);
  }
  async approve(ids) {
    return this.client.trpc.overtime.approve.mutate({
      ids
    });
  }
};
__name(_OvertimeService, "OvertimeService");
var OvertimeService = _OvertimeService;

// src/services/PayrollService.ts
var _PayrollService = class _PayrollService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.payroll.list.query({
      filter,
      pagination
    });
  }
  async getPayrollRecords(filters) {
    return this.client.trpc.payroll.list.query({
      filter: filters,
      pagination: filters
    });
  }
  async approvePayroll(payrollId) {
    return this.client.trpc.payroll.approve.mutate({
      payrollIds: [
        payrollId
      ]
    });
  }
  async processPayment(payrollId, paidAt) {
    return this.client.trpc.payroll.processPayment.mutate({
      payrollIds: [
        payrollId
      ],
      paymentDate: paidAt || /* @__PURE__ */ new Date(),
      paymentMethod: "bank_transfer"
    });
  }
  async generateForPeriod(year, month) {
    return this.client.trpc.payroll.generateForPeriod.mutate({
      period: {
        year,
        month
      }
    });
  }
  async refreshForPeriod(year, month) {
    return this.client.trpc.payroll.refreshForPeriod.mutate({
      period: {
        year,
        month
      }
    });
  }
  async bulkApprove(payrollIds, approverNotes) {
    return this.client.trpc.payroll.approve.mutate({
      payrollIds,
      approverNotes
    });
  }
  async generateSummary(year, month) {
    return this.client.trpc.payroll.summary.query({
      year,
      month
    });
  }
  async validate(year, month) {
    return this.client.trpc.payroll.validate.query({
      year,
      month
    });
  }
  async confirm(year, month, force) {
    return this.client.trpc.payroll.confirm.mutate({
      year,
      month,
      force
    });
  }
  async confirmByIds(payrollIds) {
    return this.client.trpc.payroll.confirmByIds.mutate({
      payrollIds
    });
  }
  /**
  * 이카운트 내보내기 미리보기(MED-934) — 이 급여대장의 집계 열을 시트 순서대로(열 이름·코드·합계),
  * 처음 체크를 끌 열 key(이 기간 마지막 내보내기 → 저장된 선택 → 기본급 계열).
  */
  async ecountExportPreview(year, month, sheetViewId) {
    return this.client.trpc.payroll.ecountExportPreview.query({
      year,
      month,
      sheetViewId
    });
  }
  async exportEcount(year, month, excludeConfigIds, sheetViewId, rememberSelection, excludeColumnKeys) {
    return this.client.trpc.payroll.exportEcount.mutate({
      year,
      month,
      excludeConfigIds,
      excludeColumnKeys,
      sheetViewId,
      rememberSelection
    });
  }
  async cancelConfirm(year, month) {
    return this.client.trpc.payroll.cancelConfirm.mutate({
      year,
      month
    });
  }
  async cancelConfirmByIds(payrollIds) {
    return this.client.trpc.payroll.cancelConfirmByIds.mutate({
      payrollIds
    });
  }
  /** Confirm one 급여대장 row → upsert a PayrollRecord snapshot from
  *  the row's aggregate cell totals. */
  async confirmLedgerRow(sheetViewId, sheetRowId) {
    return this.client.trpc.payroll.confirmLedgerRow.mutate({
      sheetViewId,
      sheetRowId
    });
  }
  /** Bulk 전체 확정 — confirms every eligible 급여대장 row in one shot. */
  async confirmAllLedgerRows(sheetViewId) {
    return this.client.trpc.payroll.confirmAllLedgerRows.mutate({
      sheetViewId
    });
  }
  /** Bulk 전체 취소 — flips every CONFIRMED row back to DRAFT. PAID rows skipped. */
  async cancelAllLedgerConfirmations(sheetViewId) {
    return this.client.trpc.payroll.cancelAllLedgerConfirmations.mutate({
      sheetViewId
    });
  }
  /** Revert `confirmLedgerRow` — CONFIRMED → DRAFT. PAID records refuse. */
  async cancelLedgerConfirmation(sheetViewId, sheetRowId) {
    return this.client.trpc.payroll.cancelLedgerConfirmation.mutate({
      sheetViewId,
      sheetRowId
    });
  }
  async sendToEmployee(payrollIds) {
    return this.client.trpc.payroll.sendToEmployee.mutate({
      payrollIds
    });
  }
  async adjustmentCheck(year, month) {
    return this.client.trpc.payroll.adjustmentCheck.query({
      year,
      month
    });
  }
  // biome-ignore lint/suspicious/noExplicitAny: Dynamic input from router schema
  async createTaxBracket(input) {
    return this.client.trpc.payroll.createTaxBracket.mutate(input);
  }
  async getActiveTaxBrackets(effectiveYear) {
    return this.client.trpc.payroll.getActiveTaxBrackets.query(effectiveYear);
  }
  // --- Cell Memo ---
  async createCellMemo(input) {
    return this.client.trpc.payrollCellMemo.create.mutate(input);
  }
  async resolveCellMemo(id) {
    return this.client.trpc.payrollCellMemo.resolve.mutate({
      id
    });
  }
  /** Edit an open memo's text (author-only, G-1). */
  async updateCellMemo(id, memo) {
    return this.client.trpc.payrollCellMemo.update.mutate({
      id,
      memo
    });
  }
  /** Soft-delete an open memo (author-only, G-1). */
  async deleteCellMemo(id) {
    return this.client.trpc.payrollCellMemo.delete.mutate({
      id
    });
  }
  async listCellMemos(year, month) {
    return this.client.trpc.payrollCellMemo.listForPeriod.query({
      year,
      month
    });
  }
  async cellMemoHistory(filter) {
    return this.client.trpc.payrollCellMemo.history.query(filter);
  }
  /** Sheet-cell-keyed memo create (workbook-rooted memos). */
  async createCellMemoForSheet(input) {
    return this.client.trpc.payrollCellMemo.createForCell.mutate(input);
  }
  /** All memos on a single sheet (workbook overlay + tooltips). */
  async listCellMemosForSheet(sheetViewId, status = "open") {
    return this.client.trpc.payrollCellMemo.listForSheet.query({
      sheetViewId,
      status
    });
  }
  /** OPEN-memo count for one sheet — toolbar 메모 이력 badge. */
  async unresolvedMemoCountForSheet(sheetViewId) {
    return this.client.trpc.payrollCellMemo.unresolvedCountForSheet.query({
      sheetViewId
    });
  }
};
__name(_PayrollService, "PayrollService");
var PayrollService = _PayrollService;

// src/services/PermissionRequestService.ts
var _PermissionRequestService = class _PermissionRequestService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /**
  * Submit an access permission request. Fires a notification to org admins
  * (users holding `employee:manage` / `employee:approve`) who can review
  * the request in their 알림 inbox.
  */
  async create(input) {
    return this.client.trpc.permissionRequest.create.mutate(input);
  }
};
__name(_PermissionRequestService, "PermissionRequestService");
var PermissionRequestService = _PermissionRequestService;

// src/services/RecurringAllowanceService.ts
var _RecurringAllowanceService = class _RecurringAllowanceService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  list(input) {
    return this.client.trpc.recurringAllowance.list.query(input ?? {});
  }
  create(input) {
    return this.client.trpc.recurringAllowance.create.mutate(input);
  }
  /** 정정(같은 chain v+1) — 금액·라벨·종료일·메모 */
  correct(input) {
    return this.client.trpc.recurringAllowance.correct.mutate(input);
  }
  /** 종료 — validUntil 지정. 다음 달부터 시트에서 빠진다 */
  end(id, validUntil) {
    return this.client.trpc.recurringAllowance.end.mutate({
      id,
      validUntil
    });
  }
};
__name(_RecurringAllowanceService, "RecurringAllowanceService");
var RecurringAllowanceService = _RecurringAllowanceService;

// src/services/ResignationService.ts
var _ResignationService = class _ResignationService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.resignation.list.query({
      filter,
      pagination
    });
  }
  async get(id) {
    return this.client.trpc.resignation.get.query(id);
  }
  async create(input) {
    return this.client.trpc.resignation.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.resignation.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.resignation.delete.mutate(id);
  }
  // biome-ignore lint/suspicious/noExplicitAny: Dynamic input
  async simulateSettlement(input) {
    return this.client.trpc.resignation.simulateSettlement.query(input);
  }
  // biome-ignore lint/suspicious/noExplicitAny: Dynamic input
  async calculateSettlement(input) {
    return this.client.trpc.resignation.calculateSettlement.mutate(input);
  }
  async cancelSettlement(resignationId) {
    return this.client.trpc.resignation.cancelSettlement.mutate({
      resignationId
    });
  }
};
__name(_ResignationService, "ResignationService");
var ResignationService = _ResignationService;

// src/services/RoleService.ts
var _RoleService = class _RoleService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(query) {
    return this.client.trpc.role.list.query(query || {});
  }
  async getById(id) {
    return this.client.trpc.role.getById.query({
      id
    });
  }
  async create(input) {
    return this.client.trpc.role.create.mutate(input);
  }
  async update(id, data) {
    return this.client.trpc.role.update.mutate({
      id,
      data
    });
  }
  async delete(id) {
    return this.client.trpc.role.delete.mutate({
      id
    });
  }
  async listPermissions() {
    return this.client.trpc.role.listPermissions.query();
  }
  async updatePermissions(roleId, permissionIds) {
    return this.client.trpc.role.updatePermissions.mutate({
      roleId,
      permissionIds
    });
  }
};
__name(_RoleService, "RoleService");
var RoleService = _RoleService;

// src/services/SalaryGradeService.ts
var _SalaryGradeService = class _SalaryGradeService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.salaryGrade.list.query({
      filter,
      pagination
    });
  }
  async availableYears() {
    return this.client.trpc.salaryGrade.availableYears.query();
  }
  async get(id) {
    return this.client.trpc.salaryGrade.get.query(id);
  }
  async create(input) {
    return this.client.trpc.salaryGrade.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.salaryGrade.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.salaryGrade.delete.mutate(id);
  }
  async getByCategory(filter) {
    return this.client.trpc.salaryGrade.getByCategory.query({
      filter
    });
  }
  async listEmployeeAssignments() {
    return this.client.trpc.salaryGrade.listEmployeeAssignments.query();
  }
  async assignGradeToEmployee(input) {
    return this.client.trpc.salaryGrade.assignGradeToEmployee.mutate(input);
  }
};
__name(_SalaryGradeService, "SalaryGradeService");
var SalaryGradeService = _SalaryGradeService;

// src/services/ScheduledActionService.ts
var _ScheduledActionService = class _ScheduledActionService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  list(input) {
    return this.client.trpc.scheduledAction.list.query(input ?? {});
  }
  /** 관리 페이지 목록 — 서버가 시행월·소유자·검색까지 거르고 페이지로 자른다(총 건수·소유자 목록 포함). */
  page(input) {
    return this.client.trpc.scheduledAction.page.query(input);
  }
  /** 상세 — 변경 전 값·만든/적용한/취소한 사람·이관 이력. */
  detail(id) {
    return this.client.trpc.scheduledAction.detail.query(id);
  }
  /** 상단 요약 — 필터와 상관없는 실패·확인 대기·예약됨·7일 안 시행·이관 필요 건수. */
  summary() {
    return this.client.trpc.scheduledAction.summary.query();
  }
  /** 자동 처리 기록의 실패를 「확인함」으로 — 진행 중 목록·실패 건수에서 빠진다. */
  acknowledge(id) {
    return this.client.trpc.scheduledAction.acknowledge.mutate(id);
  }
  cancel(id) {
    return this.client.trpc.scheduledAction.cancel.mutate(id);
  }
  /** 시행일을 기다리지 않고 지금 실행 — FAILED 는 다시 실행. 권한은 소유자 기준으로 서버가 판정. */
  run(id) {
    return this.client.trpc.scheduledAction.run.mutate(id);
  }
  /** 「이 달에 시행되는 변경」(T9) — 예약 액션 + 원천 시행일 요약. payroll:read org-wide. */
  monthOverview(year, month) {
    return this.client.trpc.scheduledAction.monthOverview.query({
      year,
      month
    });
  }
  /** 승인(D7) — AI 제안(확인 대기)을 예약으로. 시행일이 오늘 이하면 바로 실행. */
  approve(id) {
    return this.client.trpc.scheduledAction.approve.mutate(id);
  }
  /** dry-run(T8) — 같은 코드를 끝까지 돌리고 롤백한 결과(영향·diff·확인 사유). */
  dryRun(id) {
    return this.client.trpc.scheduledAction.dryRun.mutate(id);
  }
  /** 소유자 이관(T8) — 새 소유자 권한으로 dry-run 이 통과해야 바뀐다. */
  transfer(id, toOwnerId) {
    return this.client.trpc.scheduledAction.transfer.mutate({
      id,
      toOwnerId
    });
  }
  /** 셀 예약 값(MED-825) — 「N월부터 이 칸은 X」. 시행월 시트가 이미 있으면 서버가 바로 실행해 결과 상태를 돌려준다. */
  createSheetCell(input) {
    return this.client.trpc.scheduledAction.createSheetCell.mutate(input);
  }
};
__name(_ScheduledActionService, "ScheduledActionService");
var ScheduledActionService = _ScheduledActionService;

// src/services/ScreenCaptureService.ts
var _ScreenCaptureService = class _ScreenCaptureService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async report(input) {
    return this.client.trpc.screenCapture.report.mutate(input);
  }
  async list(input) {
    return this.client.trpc.screenCapture.list.query(input);
  }
};
__name(_ScreenCaptureService, "ScreenCaptureService");
var ScreenCaptureService = _ScreenCaptureService;

// src/services/SearchHistoryService.ts
var _SearchHistoryService = class _SearchHistoryService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 한 컨텍스트의 이력(사용 횟수·마지막 사용 시각 포함) */
  async list(input) {
    return this.client.trpc.searchHistory.list.query(input);
  }
  /** 검색어 사용 기록 — 민감 패턴이면 서버가 `saved: false` 로 저장하지 않는다 */
  async record(input) {
    return this.client.trpc.searchHistory.record.mutate(input);
  }
  /** 즐겨찾기(별) 켬/끔 — 고정된 검색어는 「자주 찾는 검색어」 맨 위에 남는다 */
  async pin(input) {
    return this.client.trpc.searchHistory.pin.mutate(input);
  }
  /** 검색어 한 줄 삭제 */
  async remove(input) {
    return this.client.trpc.searchHistory.remove.mutate(input);
  }
  /** 컨텍스트의 이력 전부 삭제 */
  async clear(input) {
    return this.client.trpc.searchHistory.clear.mutate(input);
  }
};
__name(_SearchHistoryService, "SearchHistoryService");
var SearchHistoryService = _SearchHistoryService;

// src/services/SecomEventService.ts
var _SecomEventService = class _SecomEventService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async ingestEvents(events) {
    return this.client.trpc.secomEvent.ingestEvents.mutate({
      events
    });
  }
  async processPending() {
    return this.client.trpc.secomEvent.processPending.mutate();
  }
  async listEvents(filter) {
    return this.client.trpc.secomEvent.listEvents.query(filter || {});
  }
  async getEvent(id) {
    return this.client.trpc.secomEvent.getEvent.query({
      id
    });
  }
};
__name(_SecomEventService, "SecomEventService");
var SecomEventService = _SecomEventService;

// src/services/SecurityService.ts
var _SecurityService = class _SecurityService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async overview() {
    return this.client.trpc.security.overview.query();
  }
  async sessions() {
    return this.client.trpc.security.sessions.query();
  }
  async revokeSession(input) {
    return this.client.trpc.security.revokeSession.mutate(input);
  }
  /** 패스키 전용 로그인 켜기/끄기 — 켜려면 등록된 키가 있어야 한다 */
  async setPasskeyOnly(input) {
    return this.client.trpc.security.setPasskeyOnly.mutate(input);
  }
  /** 현재 기기 외 모두 로그아웃 */
  async revokeOtherSessions() {
    return this.client.trpc.security.revokeOtherSessions.mutate();
  }
  async twoFactorSetupStart() {
    return this.client.trpc.security.twoFactorSetupStart.mutate();
  }
  async twoFactorSetupConfirm(input) {
    return this.client.trpc.security.twoFactorSetupConfirm.mutate(input);
  }
  async twoFactorDisable(input) {
    return this.client.trpc.security.twoFactorDisable.mutate(input);
  }
  /** 로그인 2단계 — 코드가 맞으면 세션을 심는다 */
  async verifyTwoFactor(input) {
    const result = await this.client.trpc.auth.verifyTwoFactor.mutate(input);
    this.client.setTokens(result.accessToken, result.refreshToken);
    return result;
  }
};
__name(_SecurityService, "SecurityService");
var SecurityService = _SecurityService;

// src/services/SheetCellService.ts
var _SheetCellService = class _SheetCellService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async set(input) {
    return this.client.trpc.sheetCell.set.mutate(input);
  }
  async clear(input) {
    return this.client.trpc.sheetCell.clear.mutate(input);
  }
  async batchSet(input) {
    return this.client.trpc.sheetCell.batchSet.mutate(input);
  }
  async batchClear(input) {
    return this.client.trpc.sheetCell.batchClear.mutate(input);
  }
  /**
  * Apply a style patch to one or more cells (Phase 3 formatting
  * toolbar). Patch fields with `null` clear that style attribute;
  * fields absent leave the existing value alone. Server creates a
  * style-only SheetCell row if the target doesn't exist yet — the
  * resolver still reads source-record values for the cell content;
  * the row only carries the formatting.
  */
  async setStyle(input) {
    return this.client.trpc.sheetCell.setStyle.mutate(input);
  }
  /**
  * 칸 하나의 값 변경 이력 (SHT-11) — 「누가·언제·얼마에서 얼마로」, 최신순.
  * 칸 상세 패널의 「변경 이력」 탭이 읽는다.
  */
  async history(input) {
    return this.client.trpc.sheetCell.history.query(input);
  }
  /**
  * 이력 한 줄 되돌리기 — 그 칸을 「바뀌기 전 값」으로 돌린다. 되돌린 것도
  * 이력에 남는다.
  */
  async revertChange(input) {
    return this.client.trpc.sheetCell.revertChange.mutate(input);
  }
  /**
  * 한 동작(붙여넣기 한 번)을 통째로 되돌린다. 그 사이 다른 사람이 고친 칸은
  * 건너뛰고 `skipped` 로 돌려준다.
  */
  async revertChangeBatch(input) {
    return this.client.trpc.sheetCell.revertChangeBatch.mutate(input);
  }
  /** 시트 전체의 값 변경 이력 — 「히스토리」 목록. */
  async sheetHistory(input) {
    return this.client.trpc.sheetCell.sheetHistory.query(input);
  }
  /** List every styled cell on a sheet — feeds the client style cache. */
  async listStyles(input) {
    return this.client.trpc.sheetCell.listStyles.query(input);
  }
};
__name(_SheetCellService, "SheetCellService");
var SheetCellService = _SheetCellService;

// src/services/SheetRowService.ts
var _SheetRowService = class _SheetRowService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async create(input) {
    return this.client.trpc.sheetRow.create.mutate(input);
  }
  /**
  * Bulk-create rows in one round-trip. Prefer this over
  * `Promise.all(rows.map(create))` — the parallel path batches into a
  * single tRPC URL that exceeds the path-length limit past ~30 rows.
  */
  /** 행 추가 「부서 / 팀」 후보 — 고른 팀(하위 팀 포함)의 재직 직원, 이미 있는 행·입사일 가드 표시 */
  async teamMemberCandidates(input) {
    return this.client.trpc.sheetRow.teamMemberCandidates.query(input);
  }
  async batchCreate(input) {
    return this.client.trpc.sheetRow.batchCreate.mutate(input);
  }
  async update(input) {
    return this.client.trpc.sheetRow.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.sheetRow.delete.mutate({
      id
    });
  }
  /** 삭제한 행 되살리기 — 실행취소(⌘Z)가 부른다. */
  async restore(id) {
    return this.client.trpc.sheetRow.restore.mutate({
      id
    });
  }
  /** 여러 행 한 번에 삭제(MED-835). 같은 시트의 수동 행만. */
  async batchDelete(input) {
    return this.client.trpc.sheetRow.batchDelete.mutate(input);
  }
  /** 여러 행 되살리기 — 일괄 삭제의 실행취소. */
  async batchRestore(input) {
    return this.client.trpc.sheetRow.batchRestore.mutate(input);
  }
  async reorder(input) {
    return this.client.trpc.sheetRow.reorder.mutate(input);
  }
  /**
  * 시트 및 범위 보호 — toggle row-level protection on a single
  * SheetRow. When `locked: true`, record-update mutations touching
  * this row are rejected by assertSheetRowNotLocked. Cleared by
  * `setLock({ locked: false })`.
  */
  /** text→식별 전환 시 직원 자동 매핑 (B-4) — 일치 행 연결 + 미일치 사유 반환. */
  async linkEmployeesFromColumn(input) {
    return this.client.trpc.sheetRow.linkEmployeesFromColumn.mutate(input);
  }
  async setLock(input) {
    return this.client.trpc.sheetRow.setLock.mutate(input);
  }
  /** List every protected SheetRow on a sheet — drives the protection modal. */
  async listProtected(input) {
    return this.client.trpc.sheetRow.listProtected.query(input);
  }
};
__name(_SheetRowService, "SheetRowService");
var SheetRowService = _SheetRowService;

// src/services/SheetTemplateService.ts
var _SheetTemplateService = class _SheetTemplateService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** List system + org templates, optionally narrowed by kind. */
  async list(input = {}) {
    return this.client.trpc.sheetTemplate.list.query(input);
  }
  async get(id) {
    return this.client.trpc.sheetTemplate.get.query({
      id
    });
  }
  async create(input) {
    return this.client.trpc.sheetTemplate.create.mutate(input);
  }
  /** Clone an existing sheet's settings into a new template. */
  async saveSheetAsTemplate(input) {
    return this.client.trpc.sheetTemplate.saveSheetAsTemplate.mutate(input);
  }
  /**
  * Apply a template — creates a new SheetView for the given period.
  * Calls the `applyToPeriod` procedure because tRPC reserves `apply` as
  * a router key (Function-prototype name collision).
  */
  async apply(input) {
    return this.client.trpc.sheetTemplate.applyToPeriod.mutate(input);
  }
  /**
  * Apply every member of a template set (퇴사 정산 등) for one period.
  * Transactional on the server — partial set = no set. Returns
  * `{ sheets, count }` with created/reused sheets in member order.
  */
  async applySet(input) {
    return this.client.trpc.sheetTemplate.applySet.mutate(input);
  }
  async update(input) {
    return this.client.trpc.sheetTemplate.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.sheetTemplate.delete.mutate({
      id
    });
  }
  // ── 병원 공통 수식 템플릿 (UP-002-S2-b) ──────────────────────────
  /** 우리 병원 수식 템플릿 목록. */
  async listFormulaTemplates() {
    return this.client.trpc.sheetTemplate.listFormulaTemplates.query();
  }
  /** 현재 수식을 병원 공통 템플릿으로 저장 (급여 관리 권한자). */
  async createFormulaTemplate(input) {
    return this.client.trpc.sheetTemplate.createFormulaTemplate.mutate(input);
  }
  /** 병원 공통 수식 템플릿 삭제 (급여 관리 권한자). */
  async deleteFormulaTemplate(id) {
    return this.client.trpc.sheetTemplate.deleteFormulaTemplate.mutate({
      id
    });
  }
  // ── 열 북마크 (MED-845) ───────────────────────────────────────────
  /** 우리 병원 열 북마크 목록 — 수식 편집기 「북마크한 열」 칩. */
  async listColumnBookmarks() {
    return this.client.trpc.sheetTemplate.listColumnBookmarks.query();
  }
  /** 열 북마크 추가 (급여 관리 권한자). 같은 열이 이미 있으면 그 행을 돌려준다. */
  async createColumnBookmark(input) {
    return this.client.trpc.sheetTemplate.createColumnBookmark.mutate(input);
  }
  /** 열 북마크 삭제 (급여 관리 권한자). */
  async deleteColumnBookmark(id) {
    return this.client.trpc.sheetTemplate.deleteColumnBookmark.mutate({
      id
    });
  }
};
__name(_SheetTemplateService, "SheetTemplateService");
var SheetTemplateService = _SheetTemplateService;

// src/services/SheetViewService.ts
var _SheetViewService = class _SheetViewService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /**
  * List sheets for a (year, month). Auto-upserts defaults on first visit.
  * Returns shared sheets + the caller's private sheets.
  */
  async list(input) {
    return this.client.trpc.sheetView.list.query(input);
  }
  /** Get a single sheet by id (with config links). */
  async get(id) {
    return this.client.trpc.sheetView.get.query({
      id
    });
  }
  /** Treatment-catalog item list for format="treatmentcount" pickers. */
  async treatmentCatalog() {
    return this.client.trpc.sheetView.treatmentCatalog.query();
  }
  /**
  * Sync a category 치료일 sheet from its fixed_incentive session
  * records (one row per sessionId). Idempotent — safe on every open.
  */
  async syncTreatmentSessions(id) {
    return this.client.trpc.sheetView.syncTreatmentSessions.mutate({
      id
    });
  }
  /** Export a sheet's layout + ref hints as a portable template. */
  async exportTemplate(id) {
    return this.client.trpc.sheetView.exportTemplate.query({
      id
    });
  }
  /** Create a new sheet from an exported template payload. */
  async importTemplate(input) {
    return this.client.trpc.sheetView.importTemplate.mutate(input);
  }
  /**
  * Upload a CSV / XLSX file to create a sheet populated with the file's
  * rows. Distinct from `importTemplate` (which imports the sheet SHAPE
  * from a JSON template, zero data) — this imports DATA only and
  * infers a minimal column layout from the header row.
  */
  async importData(input) {
    return this.client.trpc.sheetView.importData.mutate(input);
  }
  /** Create a custom sheet. */
  async create(input) {
    return this.client.trpc.sheetView.create.mutate(input);
  }
  /** Update sheet name / visibility / layout / filters / configs. */
  async update(input) {
    return this.client.trpc.sheetView.update.mutate(input);
  }
  /** Soft delete. Defaults re-upsert on next list(). */
  async delete(id) {
    return this.client.trpc.sheetView.delete.mutate({
      id
    });
  }
  /**
  * Trash inbox — every soft-deleted sheet the caller had permission to
  * delete (scoped by visibility the same way `delete` is). Spans every
  * period because users typically remember "I deleted that yesterday",
  * not "I deleted that for January". Newest-deleted first.
  */
  async listTrashed() {
    return this.client.trpc.sheetView.listTrashed.query();
  }
  /** Restore a soft-deleted sheet (clears deletedAt). */
  async restore(id) {
    return this.client.trpc.sheetView.restore.mutate({
      id
    });
  }
  /**
  * Ensure the sheet has a slug (formula-ref anchor). Idempotent —
  * returns the existing slug or assigns a generated custom_* one.
  */
  async ensureSlug(id) {
    return this.client.trpc.sheetView.ensureSlug.mutate({
      id
    });
  }
  /** Idempotent default upsert for a (year, month). */
  async ensureDefaults(input) {
    return this.client.trpc.sheetView.ensureDefaults.mutate(input);
  }
  /** Carry shared customs from one period to another. */
  async carryForward(input) {
    return this.client.trpc.sheetView.carryForward.mutate(input);
  }
  /**
  * Read-only carry-forward preview — what would be created without
  * committing. Powers the confirmation modal before calling carryForward.
  */
  /**
  * 상시 시트 새 버전 — (year, month) 부터 적용되는 사본(행·셀 포함). 그 달부터는
  * 새 버전이 보이고 참조되며, 이전 달은 예전 버전을 그대로 읽는다.
  */
  async createEvergreenVersion(input) {
    return this.client.trpc.sheetView.createEvergreenVersion.mutate(input);
  }
  async carryForwardPreview(input) {
    return this.client.trpc.sheetView.carryForwardPreview.query(input);
  }
  /**
  * Cross-sheet search inside a period — returns the sheets that have rows
  * matching the query, with per-sheet match counts.
  */
  async searchAcrossPeriod(input) {
    return this.client.trpc.sheetView.searchAcrossPeriod.query(input);
  }
  /**
  * Manually lock or unlock a sheet (separate from period freeze).
  * 잠금 해제는 작업 완료 표시도 함께 해제하며, 그 경우 응답의
  * `workDoneCleared` 가 true 로 온다.
  */
  async setLock(input) {
    return this.client.trpc.sheetView.setLock.mutate(input);
  }
  /**
  * 시트 작업 완료 표시 토글. 완료는 잠금을 포함하므로 done=true 는
  * 잠금까지 함께 걸고, done=false 는 둘 다 해제한다. 잠금만 따로
  * 걸려면 setLock 을 쓴다.
  */
  async setWorkDone(input) {
    return this.client.trpc.sheetView.setWorkDone.mutate(input);
  }
  /**
  * [설정 완료] — 시트 수명주기 설정 중 → 사용 중 전이 (UP-008).
  * 완료하면 구조 변경이 서버에서도 잠기고 이력에 기록이 남는다.
  * 이미 사용 중이면 `alreadyActive: true` 로 멱등 성공한다.
  */
  async completeSetup(input) {
    return this.client.trpc.sheetView.completeSetup.mutate(input);
  }
  /**
  * 「시트 재설정」 — 사용 중 → 설정 중 복귀. 완료(workDone)·보호(locked)
  * 시트는 먼저 그 잠금을 풀어야 하며, 실행 이력이 남는다.
  */
  async reopenSetup(input) {
    return this.client.trpc.sheetView.reopenSetup.mutate(input);
  }
  /** 설정 완료/재설정 이력 (히스토리 드로어 리스트, 최신순 + 실행자 이름). */
  async listSetupEvents(input) {
    return this.client.trpc.sheetView.listSetupEvents.query(input);
  }
  /**
  * Set or clear the sheet's auto-populate rule. Pass `rule: null` to
  * turn it off (sheet stays pure manual). Next `queryRows` runs the
  * executor and materializes auto rows from the source table.
  */
  async setAutoPopulate(input) {
    return this.client.trpc.sheetView.setAutoPopulate.mutate(input);
  }
  /**
  * Set or clear the sheet's compute config — which codeModule runs on
  * "🧮 계산 실행", with what parameters, and which column / rate config
  * it commits into. Pass `config: null` to disable.
  */
  async setComputeConfig(input) {
    return this.client.trpc.sheetView.setComputeConfig.mutate(input);
  }
  /**
  * Run the codeModule against the sheet's rows. Writes the output
  * values into cells whose column has `kind="computed"` + a matching
  * `computedKey`. User overrides (literal cells) are preserved.
  */
  async runCompute(input) {
    return this.client.trpc.sheetView.runCompute.mutate(input);
  }
  /**
  * Commit the sheet's resolved values to allowance / deduction records.
  * Upserts by `(sourceSheetId, sourceSheetRowId)` lineage — running
  * twice updates the records in place.
  */
  async commitToRecords(input) {
    return this.client.trpc.sheetView.commitToRecords.mutate(input);
  }
  /**
  * Commit-status summary for the sheet header — last commit time, who
  * committed, and whether any row/cell changed since (the "변경됨"
  * flag). Read-only; safe to poll on sheet open / after edits.
  */
  async commitStatus(input) {
    return this.client.trpc.sheetView.commitStatus.query(input);
  }
  /**
  * Source sheets feeding a 급여대장 + each one's commit status — powers
  * the "전체 커밋" hover detail (어떤 시트가 아직 커밋 안 됐는지).
  */
  async ledgerCommitSources(input) {
    return this.client.trpc.sheetView.ledgerCommitSources.query(input);
  }
  /** "전체 커밋" — commit every source sheet feeding a 급여대장 at once. */
  async commitLedgerSources(input) {
    return this.client.trpc.sheetView.commitLedgerSources.mutate(input);
  }
  /**
  * Commit a master-data sheet's literal-cell overrides back to the
  * source entity table (호봉 정의 → salary_grades, 인센티브 프리셋 →
  * incentive_presets). Updates rows by `sourceRecordId` and clears
  * the literal cells on success so the sheet falls back to live
  * source values on next resolve.
  *
  * No-op for sheets that aren't master-data system templates.
  */
  async commitMasterOverrides(input) {
    return this.client.trpc.sheetView.commitMasterOverrides.mutate(input);
  }
  /**
  * XLSX export. Pass `sheetId` for a single tab, or `year`+`month` for
  * the whole period. Returns `{ filename, base64 }` so the caller can
  * trigger a download.
  */
  async exportXlsx(input) {
    return this.client.trpc.sheetView.exportXlsx.query(input);
  }
  /**
  * The hot path — fetch the row data for a sheet (live join).
  * Returns one row per record, or one empty row per employee with no
  * records this period.
  */
  async queryRows(id) {
    return this.client.trpc.sheetView.queryRows.query({
      id
    });
  }
  /**
  * Paginated row fetch for large sheets. Returns `{ rows, totalCount,
  * offset, limit }`; the workbook adapter uses this to paint a first
  * batch fast (500 rows by default) and stream the rest in the
  * background. The materializers (autoPopulate, column-level
  * sourceTable) only run on the first page (offset 0).
  */
  async queryRowsPage(input) {
    return this.client.trpc.sheetView.queryRowsPage.query(input);
  }
  /* ── 접근 권한 (팀·개인 공유) — docs/internal/design-sheet-access-control.md ── */
  /** 시트의 grant 목록 + 내 역할. 읽을 수 있는 시트면 누구나. */
  async listGrants(sheetViewId) {
    return this.client.trpc.sheetView.listGrants.query({
      sheetViewId
    });
  }
  /** grant 추가·역할 변경(upsert). 시트 editor 필요. */
  async setGrant(input) {
    return this.client.trpc.sheetView.setGrant.mutate(input);
  }
  /** grant 회수. 시트 editor 필요. */
  async removeGrant(grantId) {
    return this.client.trpc.sheetView.removeGrant.mutate({
      grantId
    });
  }
};
__name(_SheetViewService, "SheetViewService");
var SheetViewService = _SheetViewService;

// src/services/ShiftGroupService.ts
var _ShiftGroupService = class _ShiftGroupService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 근무 그룹 목록 (하위 근무 유형 중첩 포함) */
  async list() {
    return this.client.trpc.shiftGroup.list.query();
  }
  async create(input) {
    return this.client.trpc.shiftGroup.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.shiftGroup.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.shiftGroup.delete.mutate(id);
  }
};
__name(_ShiftGroupService, "ShiftGroupService");
var ShiftGroupService = _ShiftGroupService;

// src/services/ShiftService.ts
var _ShiftService = class _ShiftService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 근무 유형 목록. `{ schedulerOnly: true }` 로 스케줄러 연동 그룹의 활성 유형만 받는다. */
  async list(input) {
    return this.client.trpc.shift.list.query(input);
  }
  /** 근무시간 자기 선택용 — org 시프트 목록 + 최근 출근 기록 기반 추천 */
  async recommendForMe() {
    return this.client.trpc.shift.recommendForMe.query();
  }
  /** 본인 근무시간 확정 (self-only — 별도 권한 불필요) */
  async setMyShift(shiftId) {
    return this.client.trpc.shift.setMyShift.mutate({
      shiftId
    });
  }
  async create(input) {
    return this.client.trpc.shift.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.shift.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.shift.delete.mutate(id);
  }
};
__name(_ShiftService, "ShiftService");
var ShiftService = _ShiftService;

// src/services/SignupService.ts
var _SignupService = class _SignupService {
  constructor(client) {
    __publicField(this, "client");
    __publicField(this, "logger");
    __publicField(this, "publicKeyPem", null);
    this.client = client;
    this.logger = sdkLogger.createServiceLogger("SignupService");
  }
  async encrypt(password) {
    try {
      if (!this.publicKeyPem) {
        const result = await this.client.trpc.auth.publicKey.query();
        this.publicKeyPem = result.publicKeyPem;
      }
      return await encryptPassword(password, this.publicKeyPem);
    } catch (error) {
      this.logger.warn({
        error
      }, "Password encryption unavailable, falling back to plaintext");
      return password;
    }
  }
  /** 1단계: 관리자 정보 + 기업명 → PENDING Account 생성 + 인증 메일 발송 */
  async start(params) {
    const encryptedParams = {
      ...params,
      password: await this.encrypt(params.password)
    };
    const result = await this.client.trpc.signup.start.mutate(encryptedParams);
    this.logger.info({
      email: params.email
    }, "Signup started");
    return result;
  }
  /** 인증 메일 재발송 */
  async resend(email) {
    return this.client.trpc.signup.resend.mutate({
      email
    });
  }
  /** 2단계: 이메일 토큰 + 비밀번호 재입력 → 인증 완료 + account 세션 발급 */
  async verify(token, password) {
    const result = await this.client.trpc.signup.verify.mutate({
      token,
      password: await this.encrypt(password)
    });
    this.client.setTokens(result.accessToken, result.refreshToken);
    this.logger.info("Signup email verified");
    return result;
  }
};
__name(_SignupService, "SignupService");
var SignupService = _SignupService;

// src/services/StorageService.ts
var _StorageService = class _StorageService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** Request a presigned upload URL from the API */
  async getUploadUrl(filename, contentType, category, conversationId, size) {
    return this.client.trpc.storage.getUploadUrl.mutate({
      filename,
      contentType,
      category,
      ...conversationId && {
        conversationId
      },
      ...typeof size === "number" ? {
        size
      } : {}
    });
  }
  /** 프로필 사진 URL 묶음(최대 300개) — 목록 아바타용. 없는 키는 빠진다 */
  async getProfilePictureUrls(keys) {
    if (keys.length === 0) {
      return {};
    }
    const { urls } = await this.client.trpc.storage.getProfilePictureUrls.query({
      keys
    });
    return urls;
  }
  /** Get a presigned download URL for a stored file */
  async getDownloadUrl(key) {
    return this.client.trpc.storage.getDownloadUrl.query({
      key
    });
  }
  /**
  * 진행률이 있는 업로드(2026-10-20) — fetch 는 올리는 쪽 진행률을 주지 않아 XMLHttpRequest 로 PUT 한다.
  * `onProgress(loaded, total)` 은 바이트 단위, `signal` 로 취소(AbortError).
  */
  async uploadFileWithProgress(file, category, conversationId, contentType, onProgress, signal) {
    const filename = file.name || "file";
    const { url, key } = await this.getUploadUrl(filename, contentType, category, conversationId, file.size);
    if (signal?.aborted) {
      throw Object.assign(new Error("aborted"), {
        name: "AbortError"
      });
    }
    await new Promise((resolve2, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      xhr.setRequestHeader("Content-Type", contentType);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress?.(e.loaded, e.total);
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(file.size, file.size);
          resolve2();
        } else {
          reject(new Error(`S3 upload failed: ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error("S3 upload failed: network"));
      xhr.onabort = () => reject(Object.assign(new Error("aborted"), {
        name: "AbortError"
      }));
      signal?.addEventListener("abort", () => xhr.abort(), {
        once: true
      });
      xhr.send(file);
    });
    return {
      key
    };
  }
  /**
  * Upload a file directly to S3 via presigned URL.
  * Handles the full flow: get presigned URL → PUT file to S3.
  */
  async uploadFile(file, category, conversationId, contentType) {
    const effectiveType = contentType || file.type;
    const { url, key } = await this.getUploadUrl(file.name, effectiveType, category, conversationId, file.size);
    const response = await fetch(url, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": effectiveType
      }
    });
    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
    }
    return {
      key
    };
  }
};
__name(_StorageService, "StorageService");
var StorageService = _StorageService;

// src/services/TeamDriveService.ts
var _TeamDriveService = class _TeamDriveService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 조직의 전체 폴더 목록 */
  async listFolders() {
    return this.client.trpc.teamDrive.listFolders.query();
  }
  /** 특정 위치(folderId 미지정 = 루트)의 파일 목록 */
  async listFiles(input) {
    return this.client.trpc.teamDrive.listFiles.query(input ?? {});
  }
  /** 폴더 생성 */
  async createFolder(input) {
    return this.client.trpc.teamDrive.createFolder.mutate(input);
  }
  /** 업로드 완료된 파일 메타데이터 등록 (presigned PUT 이후) */
  async createFile(input) {
    return this.client.trpc.teamDrive.createFile.mutate(input);
  }
  /** 폴더 이름·설명 수정 (상위 변경은 moveItems) */
  async updateFolder(input) {
    return this.client.trpc.teamDrive.updateFolder.mutate(input);
  }
  /** 파일 이름 변경 — 확장자는 허용 목록 안이어야 한다 */
  async renameFile(input) {
    return this.client.trpc.teamDrive.renameFile.mutate(input);
  }
  /** 폴더·파일 삭제(소프트) — 폴더는 하위까지. 단건도 길이 1 배열로 */
  async deleteItems(input) {
    return this.client.trpc.teamDrive.deleteItems.mutate(input);
  }
  /* ── 휴지통 ── */
  /** 휴지통 목록 — 묶음의 꼭대기만 (관리자는 전체, 그 외는 본인이 지운 것) */
  async listTrash() {
    return this.client.trpc.teamDrive.listTrash.query();
  }
  /** 복원 — 같은 묶음의 하위 폴더·파일·공유 링크·grant 까지. 원래 위치가 없으면 루트로 */
  async restoreItems(input) {
    return this.client.trpc.teamDrive.restoreItems.mutate(input);
  }
  /** 영구 삭제 — DB 행과 스토리지 오브젝트까지. 되돌릴 수 없다 */
  async purgeItems(input) {
    return this.client.trpc.teamDrive.purgeItems.mutate(input);
  }
  /** 휴지통 비우기 — 내가 볼 수 있는 항목 전부 영구 삭제 */
  async emptyTrash() {
    return this.client.trpc.teamDrive.emptyTrash.mutate();
  }
  /** 폴더·파일 이동 — targetFolderId null = 루트. 단건도 길이 1 배열로 */
  async moveItems(input) {
    return this.client.trpc.teamDrive.moveItems.mutate(input);
  }
  /**
  * 파일 URL 발급 — 서버가 권한(접근 role 또는 공유 토큰)을 검사하고 **2분짜리 티켓**이 붙은
  * 브로커 URL(`/drive/files/:id/content?ticket=…`)을 준다. 헤더를 못 붙이는 자리(새 탭·네이티브
  * 열기)에만 쓰고, 화면 안 미리보기·다운로드는 fetchFileBlob 을 쓴다. presigned URL 은 더 이상 없다.
  */
  async getDownloadUrl(input) {
    const result = await this.client.trpc.teamDrive.getDownloadUrl.query(input);
    return {
      ...result,
      url: `${this.client.getBaseUrl()}${result.path}`
    };
  }
  /**
  * 파일 바이트를 Bearer 인증으로 받아 Blob 으로 돌려준다 — 브로커 라우트가 요청마다 로그인·권한을
  * 검사한다. 미리보기(`URL.createObjectURL`)·다운로드에 쓴다. 공유 링크로 진입했으면 shareToken 을 넘긴다.
  */
  async fetchFileBlob(fileId, shareToken) {
    const token = this.client.getAccessToken();
    const query = shareToken ? `?share=${encodeURIComponent(shareToken)}` : "";
    const response = await fetch(`${this.client.getBaseUrl()}/drive/files/${encodeURIComponent(fileId)}/content${query}`, {
      headers: token ? {
        Authorization: `Bearer ${token}`
      } : {}
    });
    if (!response.ok) {
      let message = "\uD30C\uC77C\uC744 \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC5B4\uC694.";
      try {
        const body = await response.json();
        if (body?.error) {
          message = body.error;
        }
      } catch {
      }
      throw new Error(message);
    }
    return response.blob();
  }
  /* ── 접근 제어 (권한 관리) ── */
  async setAccessMode(input) {
    return this.client.trpc.teamDrive.setAccessMode.mutate(input);
  }
  async listGrants(input) {
    return this.client.trpc.teamDrive.listGrants.query(input);
  }
  async addGrant(input) {
    return this.client.trpc.teamDrive.addGrant.mutate(input);
  }
  async removeGrant(grantId) {
    return this.client.trpc.teamDrive.removeGrant.mutate({
      grantId
    });
  }
  /* ── 공유 링크 ── */
  async createShareLink(input) {
    return this.client.trpc.teamDrive.createShareLink.mutate(input);
  }
  async listShareLinks(fileId) {
    return this.client.trpc.teamDrive.listShareLinks.query({
      fileId
    });
  }
  async revokeShareLink(linkId) {
    return this.client.trpc.teamDrive.revokeShareLink.mutate({
      linkId
    });
  }
  async resolveShareLink(token) {
    return this.client.trpc.teamDrive.resolveShareLink.query({
      token
    });
  }
};
__name(_TeamDriveService, "TeamDriveService");
var TeamDriveService = _TeamDriveService;

// src/services/TeamManagerService.ts
var _TeamManagerService = class _TeamManagerService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async dashboard() {
    return this.client.trpc.teamManager.dashboard.query();
  }
  async recentActivity() {
    return this.client.trpc.teamManager.recentActivity.query();
  }
  async myTeams() {
    return this.client.trpc.teamManager.myTeams.query();
  }
  async teamMembers(teamId) {
    return this.client.trpc.teamManager.teamMembers.query({
      teamId
    });
  }
  // Leave requests
  async listLeaveRequests(input) {
    return this.client.trpc.teamManager.leaveRequest.list.query(input ?? {});
  }
  /** 휴가 승인 — confirmOveruse(MED-968): 초과 사용 확인 정책에서 잔여보다 많은 신청을 확인하고 승인할 때 true */
  async approveLeaveRequests(ids, opts = {}) {
    return this.client.trpc.teamManager.leaveRequest.approve.mutate({
      ids,
      confirmOveruse: opts.confirmOveruse
    });
  }
  async rejectLeaveRequests(ids, rejectionReason) {
    return this.client.trpc.teamManager.leaveRequest.reject.mutate({
      ids,
      rejectionReason
    });
  }
  async revertLeaveRequests(ids) {
    return this.client.trpc.teamManager.leaveRequest.revert.mutate({
      ids
    });
  }
  // Overtime
  async listOvertime(input) {
    return this.client.trpc.teamManager.overtime.list.query(input ?? {});
  }
  async approveOvertime(ids) {
    return this.client.trpc.teamManager.overtime.approve.mutate({
      ids
    });
  }
  async rejectOvertime(ids, rejectionReason) {
    return this.client.trpc.teamManager.overtime.reject.mutate({
      ids,
      rejectionReason
    });
  }
  async approveOvertimeReport(ids) {
    return this.client.trpc.teamManager.overtime.approveReport.mutate({
      ids
    });
  }
  async rejectOvertimeReport(ids, rejectionReason) {
    return this.client.trpc.teamManager.overtime.rejectReport.mutate({
      ids,
      rejectionReason
    });
  }
  /** 승인 후 취소 요청 처리 — approve=true면 취소 확정, false면 요청 거절 */
  /** 휴가 취소 신청 결재(MED-959) — 팀장 앱 · 직원 앱 결재함 */
  async resolveLeaveCancelRequest(id, approve, rejectionReason) {
    return this.client.trpc.teamManager.leaveRequest.resolveCancelRequest.mutate({
      id,
      approve,
      rejectionReason
    });
  }
  async resolveOvertimeCancelRequest(id, approve) {
    return this.client.trpc.teamManager.overtime.resolveCancelRequest.mutate({
      id,
      approve
    });
  }
  // Offsite work (외근)
  async listOffsiteWork(input) {
    return this.client.trpc.teamManager.offsiteWork.list.query(input ?? {});
  }
  async approveOffsiteWork(ids) {
    return this.client.trpc.teamManager.offsiteWork.approve.mutate({
      ids
    });
  }
  async rejectOffsiteWork(ids, rejectionReason) {
    return this.client.trpc.teamManager.offsiteWork.reject.mutate({
      ids,
      rejectionReason
    });
  }
  async approveOffsiteWorkReport(ids) {
    return this.client.trpc.teamManager.offsiteWork.approveReport.mutate({
      ids
    });
  }
  async rejectOffsiteWorkReport(ids, rejectionReason) {
    return this.client.trpc.teamManager.offsiteWork.rejectReport.mutate({
      ids,
      rejectionReason
    });
  }
  // Treatment categories
  async treatmentCategories() {
    return this.client.trpc.teamManager.treatmentCategories.query();
  }
  // Attendance summary
  async attendanceSummary(year, month) {
    return this.client.trpc.teamManager.attendanceSummary.query({
      year,
      month
    });
  }
  // Incentive preset categories
  async incentivePresetCategories() {
    return this.client.trpc.teamManager.incentivePresetCategories.query();
  }
  // Incentives
  async listIncentives(input) {
    return this.client.trpc.teamManager.incentive.list.query(input ?? {});
  }
  async createIncentive(input) {
    return this.client.trpc.teamManager.incentive.create.mutate(input);
  }
  async approveIncentives(ids) {
    return this.client.trpc.teamManager.incentive.approve.mutate({
      ids
    });
  }
  /** 인센티브 반려(결재선 MED-900) — 사유 필수, 네 종류가 같은 버튼을 가진다 */
  async rejectIncentives(ids, rejectionReason) {
    return this.client.trpc.teamManager.incentive.reject.mutate({
      ids,
      rejectionReason
    });
  }
  // Documents
  async listDocuments(input) {
    return this.client.trpc.teamManager.document.list.query(input ?? {});
  }
  async approveDocuments(ids) {
    return this.client.trpc.teamManager.document.approve.mutate({
      ids
    });
  }
  async rejectDocuments(ids, rejectionReason) {
    return this.client.trpc.teamManager.document.reject.mutate({
      ids,
      rejectionReason
    });
  }
  // Certificates
  async listCertificates(input) {
    return this.client.trpc.teamManager.certificate.list.query(input ?? {});
  }
  async approveCertificates(ids) {
    return this.client.trpc.teamManager.certificate.approve.mutate({
      ids
    });
  }
  async rejectCertificates(ids, rejectionReason) {
    return this.client.trpc.teamManager.certificate.reject.mutate({
      ids,
      rejectionReason
    });
  }
  // Unified document management (certificates + documents)
  async listDocumentManagement(status, kind = "all", filters) {
    return this.client.trpc.teamManager.documentManagement.list.query({
      status,
      kind,
      ...filters
    });
  }
  /**
  * 증명서 건을 실제 발급 모양으로 렌더(저장 없음) — 결재 미리보기.
  * 증명서 원장 행은 `certificate`, 구 경로 서류 원장의 파일 없는 요청 행은 `document`.
  */
  async previewCertificateDocument(id, source = "certificate") {
    return this.client.trpc.teamManager.documentManagement.previewCertificate.query({
      id,
      source
    });
  }
  async approveDocumentManagement(id, source) {
    return this.client.trpc.teamManager.documentManagement.approve.mutate({
      id,
      source
    });
  }
  async rejectDocumentManagement(id, source, rejectionReason) {
    return this.client.trpc.teamManager.documentManagement.reject.mutate({
      id,
      source,
      rejectionReason
    });
  }
  /** 전자결재 건을 서식(휴가신청서·시간외근무신청서·외근신청서) HTML로 렌더링 */
  async renderDocumentForm(source, recordId) {
    return this.client.trpc.teamManager.documentForm.render.query({
      source,
      recordId
    });
  }
};
__name(_TeamManagerService, "TeamManagerService");
var TeamManagerService = _TeamManagerService;

// src/services/TeamService.ts
var _TeamService = class _TeamService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /**
  * Create a new team
  * @param input Team creation data
  * @returns Created team
  */
  async create(input) {
    return this.client.trpc.team.create.mutate(input);
  }
  /**
  * Get team by ID
  * @param id Team ID
  * @returns Team details with relations
  */
  async getById(id) {
    return this.client.trpc.team.getById.query({
      id
    });
  }
  /**
  * List teams with filters
  * @param query Query parameters for filtering, sorting, and pagination
  * @returns Paginated list of teams
  */
  async list(query) {
    return this.client.trpc.team.list.query(query || {});
  }
  /**
  * Update team
  * @param id Team ID
  * @param data Update data
  * @returns Updated team
  */
  async update(id, data) {
    return this.client.trpc.team.update.mutate({
      id,
      data
    });
  }
  /**
  * 근무 유형 폼의 「대표로 쓰는 팀」 저장 — teamIds 의 팀 대표 근무를 이 형태로 바꾸고,
  * 빠진 팀은 형태만 비운다(그룹만 대표로 남음).
  */
  async setRepresentativeShift(shiftId, teamIds) {
    return this.client.trpc.team.setRepresentativeShift.mutate({
      shiftId,
      teamIds
    });
  }
  /**
  * Add member to team
  * @param input Add member parameters
  * @returns Created team member
  */
  async addMember(input) {
    return this.client.trpc.team.addMember.mutate(input);
  }
  /**
  * Remove member from team
  * @param teamId Team ID
  * @param memberId Team member ID
  * @param reason Optional removal reason
  * @returns Success status
  */
  async removeMember(teamId, memberId, reason) {
    return this.client.trpc.team.removeMember.mutate({
      teamId,
      memberId,
      reason
    });
  }
  /**
  * Update team member
  * @param teamId Team ID
  * @param memberId Team member ID
  * @param data Update data
  * @returns Updated team member
  */
  async updateMember(teamId, memberId, data) {
    return this.client.trpc.team.updateMember.mutate({
      teamId,
      memberId,
      data
    });
  }
  /**
  * Perform bulk operations on team members
  * @param input Bulk operation parameters
  * @returns Results of each operation
  */
  async bulkMemberOperation(input) {
    return this.client.trpc.team.bulkMemberOperation.mutate(input);
  }
  /**
  * 팀 이동(MED-912) — 직원들을 다른 팀으로 한 번에 옮긴다(또는 겸직으로 추가). 직원마다 자동 승인된
  * 인사발령 문서가 남고, 발령일이 오늘 이전·오늘이면 바로, 미래면 시행일에 반영된다.
  */
  async moveMembers(input) {
    return this.client.trpc.teamMove.execute.mutate(input);
  }
  /**
  * 팀 관리 명단 — 조직 전체 직원(퇴직 · 비활성 포함)과 활성 소속을 가벼운 한 응답으로.
  * admin 팀 관리가 진입 때 한 번 읽어 팀별 명단 · 트리 인원 · 미소속을 모두 파생한다(MED-914).
  */
  /** 형제 팀 노출 순서 — 같은 상위 팀 아래 팀 id 를 보일 순서대로(MED-953) */
  async reorder(input) {
    return this.client.trpc.team.reorder.mutate(input);
  }
  async roster() {
    return this.client.trpc.team.roster.query();
  }
  /** 총괄 팀장(조직 전체) 목록 — 팀 관리 워크스페이스 헤더(MED-946) */
  async orgLeaders() {
    return this.client.trpc.team.orgLeaders.query();
  }
  /** 총괄 팀장 목록을 통째로 바꾼다 — 빈 배열이면 전원 해제 */
  async setOrgLeaders(leaderIds) {
    return this.client.trpc.team.setOrgLeaders.mutate({
      leaderIds
    });
  }
  /**
  * Get team composition with member details and statistics
  * @param teamId Team ID
  * @returns Team composition with members and statistics
  */
  async getComposition(teamId) {
    return this.client.trpc.team.getComposition.query({
      teamId
    });
  }
  /**
  * 팀 관리 병동 요약 — 선택한 팀(과 하위 팀) 중 병동이 연결된 팀마다
  * 인원 · 직군 · 근무 형태 · 수간호사 · 병동 정보. teamId 를 비우면 조직 전체 병동.
  * @param teamId Team ID (생략 시 조직 전체)
  */
  async wardSummary(teamId) {
    return this.client.trpc.team.wardSummary.query({
      teamId
    });
  }
  /**
  * 병동 하나의 요약 — 팀 관리 트리의 병동 노드. 팀 연결 여부와 무관하게 병동 배정(간호 프로필) 기준으로 센다.
  * @param wardId Ward ID
  */
  async wardSummaryForWard(wardId) {
    return this.client.trpc.team.wardSummary.query({
      wardId
    });
  }
  /**
  * Delete team (soft delete)
  * @param id Team ID
  * @returns Success status
  */
  async delete(id) {
    return this.client.trpc.team.delete.mutate({
      id
    });
  }
};
__name(_TeamService, "TeamService");
var TeamService = _TeamService;

// src/services/WorkPatternService.ts
var _WorkPatternService = class _WorkPatternService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 근로형태 목록(임금산정시간·직무·기본 근무·직원 수 포함). activeOnly 로 선택지용 활성 목록만. */
  async list(input) {
    return this.client.trpc.workPattern.list.query(input);
  }
  async create(input) {
    return this.client.trpc.workPattern.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.workPattern.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.workPattern.delete.mutate(id);
  }
  /** 직원 파생값 재계산 — dryRun(기본)은 달라지는 직원·필드만 돌려주고, dryRun=false 가 근로계약 새 버전을 쓴다. */
  /** 근무 그룹에서 가져오기 — dryRun(미리보기) → 적용 2단계. */
  async importFromShiftGroups(input) {
    return this.client.trpc.workPattern.importFromShiftGroups.mutate(input);
  }
  async recompute(input) {
    return this.client.trpc.workPattern.recompute.mutate(input);
  }
};
__name(_WorkPatternService, "WorkPatternService");
var WorkPatternService = _WorkPatternService;

// src/services/WorkTimeClosingService.ts
var _WorkTimeClosingService = class _WorkTimeClosingService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  /** 마감 내역 목록 (마감 건별 확정/전체 인원 + 상태). */
  async list() {
    return this.client.trpc.workTimeClosing.list.query();
  }
  /** 마감 건 생성 (대상 미지정 시 재직 전원). */
  async create(input) {
    return this.client.trpc.workTimeClosing.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.workTimeClosing.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.workTimeClosing.delete.mutate(id);
  }
  /** 마감 건의 대상자 목록 (상태 필터 + 이름/사번 검색). */
  async listTargets(input) {
    return this.client.trpc.workTimeClosing.listTargets.query(input);
  }
  /** 선택 대상 확정(마감). */
  async confirmTargets(input) {
    return this.client.trpc.workTimeClosing.confirmTargets.mutate(input);
  }
  /** 선택 대상 확정 취소(해제). */
  async unconfirmTargets(input) {
    return this.client.trpc.workTimeClosing.unconfirmTargets.mutate(input);
  }
};
__name(_WorkTimeClosingService, "WorkTimeClosingService");
var WorkTimeClosingService = _WorkTimeClosingService;

// src/services/YearEndSettlementService.ts
var _YearEndSettlementService = class _YearEndSettlementService {
  constructor(client) {
    __publicField(this, "client");
    this.client = client;
  }
  async list(filter, pagination) {
    return this.client.trpc.yearEndSettlement.list.query({
      filter,
      pagination
    });
  }
  async get(id) {
    return this.client.trpc.yearEndSettlement.get.query(id);
  }
  async create(input) {
    return this.client.trpc.yearEndSettlement.create.mutate(input);
  }
  async update(input) {
    return this.client.trpc.yearEndSettlement.update.mutate(input);
  }
  async delete(id) {
    return this.client.trpc.yearEndSettlement.delete.mutate(id);
  }
  async approve(id) {
    return this.client.trpc.yearEndSettlement.approve.mutate(id);
  }
  // biome-ignore lint/suspicious/noExplicitAny: Dynamic input
  async applyToPayroll(input) {
    return this.client.trpc.yearEndSettlement.applyToPayroll.mutate(input);
  }
};
__name(_YearEndSettlementService, "YearEndSettlementService");
var YearEndSettlementService = _YearEndSettlementService;

// src/client/config.ts
var defaultConfig = {
  baseUrl: "http://localhost:3000",
  headers: {},
  timeout: 3e4,
  timeoutOverrides: {
    "ai.chat": 18e4
  },
  debug: false
};

// src/client/impersonation-return.ts
var IMPERSONATION_RETURN_KEY = "hereby_impersonation_return";
var IMPERSONATION_NOTICE_KEY = "hereby_impersonation_notice";
function storage() {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}
__name(storage, "storage");
function readImpersonationReturn() {
  const raw = storage()?.getItem(IMPERSONATION_RETURN_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (!(parsed && typeof parsed.accessToken === "string" && parsed.accessToken)) {
      return null;
    }
    return {
      accessToken: parsed.accessToken,
      refreshToken: typeof parsed.refreshToken === "string" ? parsed.refreshToken : null,
      returnUrl: typeof parsed.returnUrl === "string" ? parsed.returnUrl : "",
      grantId: typeof parsed.grantId === "string" ? parsed.grantId : "",
      expiresAt: typeof parsed.expiresAt === "string" ? parsed.expiresAt : null,
      targetName: typeof parsed.targetName === "string" ? parsed.targetName : "",
      actorName: typeof parsed.actorName === "string" ? parsed.actorName : ""
    };
  } catch {
    return null;
  }
}
__name(readImpersonationReturn, "readImpersonationReturn");
function saveImpersonationReturn(value) {
  storage()?.setItem(IMPERSONATION_RETURN_KEY, JSON.stringify(value));
}
__name(saveImpersonationReturn, "saveImpersonationReturn");
function clearImpersonationReturn() {
  storage()?.removeItem(IMPERSONATION_RETURN_KEY);
}
__name(clearImpersonationReturn, "clearImpersonationReturn");
function takeImpersonationNotice() {
  const s = storage();
  const v = s?.getItem(IMPERSONATION_NOTICE_KEY) ?? null;
  if (v) {
    s?.removeItem(IMPERSONATION_NOTICE_KEY);
  }
  return v;
}
__name(takeImpersonationNotice, "takeImpersonationNotice");
var restoredThisPage = false;
function restoreImpersonationReturn(client, notice) {
  const saved = readImpersonationReturn();
  if (!saved) {
    return {
      restored: false,
      returnUrl: ""
    };
  }
  clearImpersonationReturn();
  client.setTokens(saved.accessToken, saved.refreshToken ?? void 0);
  restoredThisPage = true;
  if (notice) {
    storage()?.setItem(IMPERSONATION_NOTICE_KEY, notice);
  }
  return {
    restored: true,
    returnUrl: saved.returnUrl
  };
}
__name(restoreImpersonationReturn, "restoreImpersonationReturn");
function recoverFromImpersonationExpiry(client) {
  if (restoredThisPage) {
    return true;
  }
  if (!restoreImpersonationReturn(client, IMPERSONATION_EXPIRED_NOTICE).restored) {
    return false;
  }
  if (typeof window !== "undefined") {
    window.location.reload();
  }
  return true;
}
__name(recoverFromImpersonationExpiry, "recoverFromImpersonationExpiry");
var IMPERSONATION_EXPIRED_NOTICE = "\uBBF8\uB9AC\uBCF4\uAE30\uAC00 \uB9CC\uB8CC\uB418\uC5B4 \uC6D0\uB798 \uC138\uC158\uC73C\uB85C \uB3CC\uC544\uC654\uC5B4\uC694";
var IMPERSONATION_ENDED_NOTICE = "\uAD8C\uD55C \uBBF8\uB9AC\uBCF4\uAE30\uB97C \uC885\uB8CC\uD588\uC5B4\uC694";
function navigateAfterImpersonationExit(target, loc = window.location) {
  const current2 = new URL(loc.href);
  const next = new URL(target, loc.href);
  const sameDocument = current2.origin === next.origin && current2.pathname === next.pathname && current2.search === next.search;
  if (!sameDocument) {
    loc.href = next.href;
    return "navigate";
  }
  if (loc.hash !== next.hash) {
    loc.hash = next.hash;
  }
  loc.reload();
  return "reload";
}
__name(navigateAfterImpersonationExit, "navigateAfterImpersonationExit");
function restoredPageHasStaleSession(persisted, liveToken, storedToken) {
  return persisted && (liveToken ?? "") !== (storedToken ?? "");
}
__name(restoredPageHasStaleSession, "restoredPageHasStaleSession");
var historyGuardInstalled = false;
function installImpersonationHistoryGuard(client, loc = window.location) {
  if (historyGuardInstalled || typeof window === "undefined") {
    return;
  }
  historyGuardInstalled = true;
  window.addEventListener("pageshow", (event) => {
    const persisted = event.persisted === true;
    const stored = storage()?.getItem("hereby_access_token") ?? null;
    if (stored && restoredPageHasStaleSession(persisted, client.getAccessToken(), stored)) {
      loc.reload();
    }
  });
}
__name(installImpersonationHistoryGuard, "installImpersonationHistoryGuard");

// src/client/mutation-activity.ts
function mutationPathsFromUrl(url) {
  const marker = "/trpc/";
  const at = url.indexOf(marker);
  if (at < 0) {
    return [];
  }
  const raw = url.slice(at + marker.length).split("?")[0] ?? "";
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
  }
  return decoded.split(",").filter((p) => p.length > 0);
}
__name(mutationPathsFromUrl, "mutationPathsFromUrl");
async function readBatchOutcomes(response, count) {
  const byStatus = /* @__PURE__ */ __name(() => Array.from({
    length: count
  }, () => ({
    ok: response.ok
  })), "byStatus");
  let body;
  try {
    body = await response.clone().json();
  } catch {
    return byStatus();
  }
  const items = Array.isArray(body) ? body : [
    body
  ];
  return Array.from({
    length: count
  }, (_, i) => {
    const item = items[i];
    if (item && typeof item === "object" && "error" in item && item.error) {
      const message = item.error.json?.message ?? item.error.message;
      return typeof message === "string" ? {
        ok: false,
        message
      } : {
        ok: false
      };
    }
    return {
      ok: response.ok
    };
  });
}
__name(readBatchOutcomes, "readBatchOutcomes");

// src/client/permission-error.ts
var PERMISSION_CODE_RE = /^[\w-]+:[\w-]+(?:\.[\w-]+)?$/;
function extractPermissionCode(message) {
  const m = (message ?? "").match(/\(([^()\s]+:[^()\s]+)\)\s*$/);
  const code = m ? m[1] : "";
  return PERMISSION_CODE_RE.test(code) ? code : "";
}
__name(extractPermissionCode, "extractPermissionCode");
function shouldSurfaceForbidden(path, quietCounts) {
  if (!path) {
    return true;
  }
  return (quietCounts.get(path) ?? 0) <= 0;
}
__name(shouldSurfaceForbidden, "shouldSurfaceForbidden");

// src/client/HerebyClient.ts
var HTTP_BATCH_MAX_ITEMS = 10;
var HTTP_BATCH_MAX_URL_LENGTH = 1800;
var WS_KEEPALIVE = {
  enabled: true,
  intervalMs: 15e3,
  pongTimeoutMs: 5e3
};
var WS_RETRY_MAX_MS = 1e4;
function wsRetryDelayMs(attemptIndex) {
  return Math.min(1e3 * 2 ** attemptIndex, WS_RETRY_MAX_MS);
}
__name(wsRetryDelayMs, "wsRetryDelayMs");
var WS_REOPENED_EVENT = "hereby:ws-reopened";
function resolveRequestTimeout(path, fallback, overrides) {
  if (!overrides) {
    return fallback;
  }
  const last = path.split("/").pop() ?? "";
  let timeout = fallback;
  for (const procedure of last.split(",")) {
    const value = overrides[decodeURIComponent(procedure)];
    if (typeof value === "number" && value > timeout) {
      timeout = value;
    }
  }
  return timeout;
}
__name(resolveRequestTimeout, "resolveRequestTimeout");
var _HerebyClient = class _HerebyClient {
  constructor(config = {}) {
    __publicField(this, "config");
    __publicField(this, "accessToken");
    __publicField(this, "refreshToken");
    __publicField(this, "onTokenRefresh");
    __publicField(this, "onAuthError");
    __publicField(this, "onMutationActivity");
    __publicField(this, "mutationSeq", 0);
    __publicField(this, "storage");
    __publicField(this, "wsClient");
    __publicField(this, "_refreshPromise");
    /**
    * 「배경 조회」로 표시된(op.context.quietForbidden) tRPC 경로의 진행 중 호출 수 — 그 경로의 FORBIDDEN 은 권한 모달·
    * 토스트를 띄우지 않는다. 호출부(서비스 메서드의 `{ background: true }`)가 표시하고 quietForbiddenLink 가 센다.
    */
    __publicField(this, "quietForbiddenPaths", /* @__PURE__ */ new Map());
    __publicField(this, "trpc");
    // Service instances
    __publicField(this, "account");
    __publicField(this, "ai");
    __publicField(this, "auth");
    __publicField(this, "invite");
    __publicField(this, "signup");
    __publicField(this, "organization");
    __publicField(this, "organizationHoliday");
    __publicField(this, "organizationParameter");
    __publicField(this, "team");
    __publicField(this, "employee");
    __publicField(this, "payroll");
    __publicField(this, "attendance");
    __publicField(this, "attendanceRate");
    __publicField(this, "workTimeClosing");
    __publicField(this, "salaryGrade");
    __publicField(this, "gradePromotion");
    __publicField(this, "scheduledAction");
    __publicField(this, "recurringAllowance");
    __publicField(this, "overtime");
    __publicField(this, "offsiteWork");
    __publicField(this, "approvalFlow");
    __publicField(this, "attendanceCorrection");
    __publicField(this, "allowance");
    __publicField(this, "annualLeavePromotion");
    __publicField(this, "deduction");
    __publicField(this, "deductionImport");
    __publicField(this, "resignation");
    __publicField(this, "impersonation");
    __publicField(this, "yearEndSettlement");
    __publicField(this, "fixedIncentive");
    __publicField(this, "educationSubsidy");
    __publicField(this, "role");
    __publicField(this, "leaveImport");
    __publicField(this, "leaveRequest");
    __publicField(this, "conversation");
    __publicField(this, "message");
    __publicField(this, "chatKey");
    __publicField(this, "chatDeviceLink");
    __publicField(this, "deviceCredential");
    __publicField(this, "security");
    __publicField(this, "chatRecovery");
    __publicField(this, "conversationKey");
    __publicField(this, "notification");
    __publicField(this, "permissionRequest");
    __publicField(this, "nurseScheduler");
    __publicField(this, "document");
    __publicField(this, "teamDrive");
    __publicField(this, "teamManager");
    __publicField(this, "generalAffairs");
    __publicField(this, "appOnboarding");
    __publicField(this, "shift");
    __publicField(this, "shiftGroup");
    __publicField(this, "jobDuty");
    __publicField(this, "workPattern");
    __publicField(this, "managementDashboard");
    __publicField(this, "moduleRegistry");
    __publicField(this, "itemCode");
    __publicField(this, "fileStorage");
    __publicField(this, "employeeCertification");
    __publicField(this, "certificationType");
    __publicField(this, "inventory");
    __publicField(this, "screenCapture");
    __publicField(this, "secomEvent");
    __publicField(this, "sheetView");
    __publicField(this, "sheetRow");
    __publicField(this, "sheetCell");
    __publicField(this, "sheetTemplate");
    __publicField(this, "searchHistory");
    __publicField(this, "entityPicker");
    __publicField(this, "documentTemplate");
    __publicField(this, "eSign");
    const filteredConfig = Object.fromEntries(Object.entries(config).filter(([_, v]) => v !== void 0));
    this.config = {
      ...defaultConfig,
      ...filteredConfig
    };
    this.accessToken = config.accessToken;
    this.refreshToken = config.refreshToken;
    this.onTokenRefresh = config.onTokenRefresh;
    this.onAuthError = config.onAuthError;
    this.onMutationActivity = config.onMutationActivity;
    this.trpc = this.createTRPCClient();
    this.account = new AccountService(this);
    this.ai = new AiService(this);
    this.auth = new AuthService(this);
    this.invite = new InviteService(this);
    this.signup = new SignupService(this);
    this.organization = new OrganizationService(this);
    this.organizationHoliday = new OrganizationHolidayService(this);
    this.organizationParameter = new OrganizationParameterService(this);
    this.team = new TeamService(this);
    this.employee = new EmployeeService(this);
    this.payroll = new PayrollService(this);
    this.attendance = new AttendanceService(this);
    this.attendanceRate = new AttendanceRateService(this);
    this.workTimeClosing = new WorkTimeClosingService(this);
    this.salaryGrade = new SalaryGradeService(this);
    this.gradePromotion = new GradePromotionService(this);
    this.scheduledAction = new ScheduledActionService(this);
    this.recurringAllowance = new RecurringAllowanceService(this);
    this.overtime = new OvertimeService(this);
    this.offsiteWork = new OffsiteWorkService(this);
    this.approvalFlow = new ApprovalFlowService(this);
    this.attendanceCorrection = new AttendanceCorrectionService(this);
    this.allowance = new AllowanceService(this);
    this.annualLeavePromotion = new AnnualLeavePromotionService(this);
    this.deduction = new DeductionService(this);
    this.deductionImport = new DeductionImportService(this);
    this.resignation = new ResignationService(this);
    this.impersonation = new ImpersonationService(this);
    this.yearEndSettlement = new YearEndSettlementService(this);
    this.fixedIncentive = new FixedIncentiveService(this);
    this.educationSubsidy = new EducationSubsidyService(this);
    this.role = new RoleService(this);
    this.leaveImport = new LeaveImportService(this);
    this.leaveRequest = new LeaveRequestService(this);
    this.conversation = new ConversationService(this);
    this.message = new MessageService(this);
    this.chatKey = new ChatKeyService(this);
    this.chatDeviceLink = new ChatDeviceLinkService(this);
    this.deviceCredential = new DeviceCredentialService(this);
    this.security = new SecurityService(this);
    this.chatRecovery = new ChatRecoveryService(this);
    this.conversationKey = new ConversationKeyService(this);
    this.notification = new NotificationService(this);
    this.permissionRequest = new PermissionRequestService(this);
    this.nurseScheduler = new NurseSchedulerService(this);
    this.document = new DocumentService(this);
    this.teamDrive = new TeamDriveService(this);
    this.teamManager = new TeamManagerService(this);
    this.generalAffairs = new GeneralAffairsService(this);
    this.appOnboarding = new AppOnboardingService(this);
    this.shift = new ShiftService(this);
    this.shiftGroup = new ShiftGroupService(this);
    this.jobDuty = new JobDutyService(this);
    this.workPattern = new WorkPatternService(this);
    this.managementDashboard = new ManagementDashboardService(this);
    this.moduleRegistry = new ModuleRegistryService(this);
    this.itemCode = new ItemCodeService(this);
    this.fileStorage = new StorageService(this);
    this.employeeCertification = new EmployeeCertificationService(this);
    this.certificationType = new CertificationTypeService(this);
    this.inventory = new InventoryService(this);
    this.screenCapture = new ScreenCaptureService(this);
    this.secomEvent = new SecomEventService(this);
    this.sheetView = new SheetViewService(this);
    this.sheetRow = new SheetRowService(this);
    this.sheetCell = new SheetCellService(this);
    this.sheetTemplate = new SheetTemplateService(this);
    this.searchHistory = new SearchHistoryService(this);
    this.entityPicker = new EntityPickerService(this);
    this.documentTemplate = new DocumentTemplateService(this);
    this.eSign = new ESignService(this);
  }
  joinUrl(base, path) {
    const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
    return `${normalizedBase}${path}`;
  }
  /** API 기준 URL(끝 슬래시 없음) — tRPC 밖의 REST 경로(파일 브로커 등)를 만들 때 쓴다 */
  getBaseUrl() {
    return this.config.baseUrl.endsWith("/") ? this.config.baseUrl.slice(0, -1) : this.config.baseUrl;
  }
  setBaseUrl(baseUrl) {
    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    if (normalizedBaseUrl === this.config.baseUrl) {
      return;
    }
    this.config = {
      ...this.config,
      baseUrl: normalizedBaseUrl
    };
    this.trpc = this.createTRPCClient();
  }
  getWsUrl() {
    const base = this.config.baseUrl;
    const tokenParam = this.accessToken ? `?token=${this.accessToken}` : "";
    if (base.startsWith("http://") || base.startsWith("https://")) {
      const wsProtocol = base.startsWith("https://") ? "wss" : "ws";
      const host = base.replace(/^https?:\/\//, "");
      return `${wsProtocol}://${host}/trpc-ws${tokenParam}`;
    }
    if (base.startsWith("/") && typeof window !== "undefined") {
      const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
      return `${wsProtocol}://${window.location.host}${this.joinUrl(base, "/trpc-ws")}${tokenParam}`;
    }
    return this.joinUrl(base, `/trpc-ws${tokenParam}`);
  }
  createTRPCClient() {
    this.wsClient?.close();
    let opens = 0;
    this.wsClient = createWSClient({
      url: this.getWsUrl(),
      retryDelayMs: wsRetryDelayMs,
      keepAlive: {
        ...WS_KEEPALIVE
      },
      onOpen: /* @__PURE__ */ __name(() => {
        opens += 1;
        if (opens > 1 && typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent(WS_REOPENED_EVENT));
        }
      }, "onOpen")
    });
    const quietForbiddenLink = /* @__PURE__ */ __name(() => {
      return ({ next, op }) => observable7((observer) => {
        const quiet = op.context?.quietForbidden === true;
        let counted = false;
        if (quiet) {
          this.quietForbiddenPaths.set(op.path, (this.quietForbiddenPaths.get(op.path) ?? 0) + 1);
          counted = true;
        }
        const release = /* @__PURE__ */ __name(() => {
          if (!counted) {
            return;
          }
          counted = false;
          const n = (this.quietForbiddenPaths.get(op.path) ?? 1) - 1;
          if (n <= 0) {
            this.quietForbiddenPaths.delete(op.path);
          } else {
            this.quietForbiddenPaths.set(op.path, n);
          }
        }, "release");
        const sub = next(op).subscribe({
          next: /* @__PURE__ */ __name((value) => observer.next(value), "next"),
          error: /* @__PURE__ */ __name((err) => {
            release();
            observer.error(err);
          }, "error"),
          complete: /* @__PURE__ */ __name(() => {
            release();
            observer.complete();
          }, "complete")
        });
        return () => {
          release();
          sub.unsubscribe();
        };
      });
    }, "quietForbiddenLink");
    return createTRPCClient({
      links: [
        quietForbiddenLink,
        splitLink({
          condition: /* @__PURE__ */ __name((op) => op.type === "subscription", "condition"),
          true: wsLink({
            transformer: SuperJSON,
            client: this.wsClient
          }),
          false: httpBatchLink({
            transformer: SuperJSON,
            url: this.joinUrl(this.config.baseUrl, "/trpc"),
            maxItems: HTTP_BATCH_MAX_ITEMS,
            maxURLLength: HTTP_BATCH_MAX_URL_LENGTH,
            headers: /* @__PURE__ */ __name(() => {
              const headers = {
                ...this.config.headers
              };
              if (this.accessToken) {
                headers.Authorization = `Bearer ${this.accessToken}`;
              }
              return headers;
            }, "headers"),
            // 쓰기 요청 알림(onMutationActivity)으로 한 번 감싼다 — 안쪽 전송(토큰
            // 갱신 재시도·오류 문구·CS 기록)은 그대로다.
            fetch: /* @__PURE__ */ __name((url, options) => this.observeMutations(String(url), options, async () => {
              const csPath = (() => {
                try {
                  return new URL(String(url)).pathname;
                } catch {
                  return String(url).split("?")[0];
                }
              })();
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), resolveRequestTimeout(csPath, this.config.timeout, this.config.timeoutOverrides));
              const csStartedAt = Date.now();
              try {
                const response = await fetch(url, {
                  ...options,
                  signal: controller.signal
                });
                const isRefreshCall = String(url).includes("auth.refreshToken");
                if (response.status === 401 && this.refreshToken && !isRefreshCall) {
                  try {
                    await this.handleTokenRefresh();
                    const retryHeaders = {
                      ...options?.headers,
                      Authorization: `Bearer ${this.accessToken}`
                    };
                    return fetch(url, {
                      ...options,
                      headers: retryHeaders
                    });
                  } catch (refreshError) {
                    if (isDefinitiveAuthFailure(refreshError) && isTopLevelFrame()) {
                      if (recoverFromImpersonationExpiry(this)) {
                        return response;
                      }
                      this.clearTokens();
                    }
                    return response;
                  }
                }
                const contentType = response.headers.get("content-type") || "";
                if (!contentType.includes("application/json") && response.status !== 204) {
                  throw new Error("\uC2E4\uD589 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694. \uC9C0\uC18D\uC801\uC73C\uB85C \uC624\uB958 \uBC1C\uC0DD\uC2DC \uAD00\uB9AC\uC790\uC5D0\uAC8C \uBB38\uC758\uD574 \uC8FC\uC2DC\uAE30 \uBC14\uB78D\uB2C8\uB2E4.");
                }
                if (typeof window !== "undefined" && contentType.includes("application/json")) {
                  try {
                    const cloned = response.clone();
                    const body = await cloned.json();
                    const results = Array.isArray(body) ? body : [
                      body
                    ];
                    const firstError = results.map((r) => r?.error?.json).find((e) => e !== void 0);
                    csCollector.recordApi({
                      path: csPath,
                      ok: response.ok && !firstError,
                      status: response.status,
                      code: firstError?.data?.code,
                      requestId: response.headers.get("x-request-id") ?? firstError?.data?.requestId,
                      ms: Date.now() - csStartedAt
                    });
                    for (const result of results) {
                      const err = result?.error?.json;
                      if (err?.data?.code === "FORBIDDEN") {
                        const message = err.message ?? "";
                        const path = err.data.path;
                        if (!shouldSurfaceForbidden(path, this.quietForbiddenPaths)) {
                          continue;
                        }
                        const permissionCode = extractPermissionCode(message);
                        const event = new CustomEvent("hereby:permission-error", {
                          cancelable: true,
                          detail: {
                            message,
                            path,
                            permission: permissionCode
                          }
                        });
                        const proceeded = window.dispatchEvent(event);
                        if (proceeded) {
                          window.dispatchEvent(new CustomEvent("permission-denied", {
                            detail: {
                              permission: permissionCode,
                              message
                            }
                          }));
                        }
                        break;
                      }
                    }
                  } catch {
                  }
                }
                return response;
              } catch (error) {
                csCollector.recordApi({
                  path: csPath,
                  ok: false,
                  code: error instanceof Error && error.name === "AbortError" ? "TIMEOUT" : "NETWORK",
                  ms: Date.now() - csStartedAt
                });
                if (error instanceof Error && error.name === "AbortError") {
                  throw new Error("Request timeout");
                }
                if (error instanceof TypeError || error instanceof Error && /fetch|network|load failed/i.test(error.message)) {
                  throw new Error("\uC2E4\uD589 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694. \uC9C0\uC18D\uC801\uC73C\uB85C \uC624\uB958 \uBC1C\uC0DD\uC2DC \uAD00\uB9AC\uC790\uC5D0\uAC8C \uBB38\uC758\uD574 \uC8FC\uC2DC\uAE30 \uBC14\uB78D\uB2C8\uB2E4.");
                }
                throw error;
              } finally {
                clearTimeout(timeoutId);
              }
            }), "fetch")
          })
        })
      ]
    });
  }
  /**
  * 쓰기 요청(POST — httpBatchLink 는 query 를 GET 으로 보낸다)의 시작·끝을
  * `onMutationActivity` 로 알린다. 알릴 곳이 없거나 읽기 요청이면 그대로 보낸다.
  * 끝은 요청(op)마다 따로 — 배치 일부만 실패해도(HTTP 207) 그 요청만 실패로.
  * 콜백이 던지는 오류는 삼켜서 저장 흐름을 막지 않는다.
  */
  async observeMutations(url, options, send) {
    const listener = this.onMutationActivity;
    if (!listener || (options?.method ?? "GET").toUpperCase() !== "POST") {
      return send();
    }
    const paths = mutationPathsFromUrl(url);
    if (paths.length === 0) {
      return send();
    }
    const emit = /* @__PURE__ */ __name((event) => {
      try {
        listener(event);
      } catch {
      }
    }, "emit");
    const ops = paths.map((path) => ({
      id: ++this.mutationSeq,
      path
    }));
    for (const op of ops) {
      emit({
        phase: "start",
        ...op
      });
    }
    let response;
    try {
      response = await send();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      for (const op of ops) {
        emit({
          phase: "end",
          ...op,
          ok: false,
          message
        });
      }
      throw error;
    }
    const outcomes = await readBatchOutcomes(response, ops.length);
    ops.forEach((op, i) => {
      emit({
        phase: "end",
        ...op,
        ...outcomes[i] ?? {
          ok: response.ok
        }
      });
    });
    return response;
  }
  /**
  * Close WebSocket connection and clean up resources
  */
  destroy() {
    this.wsClient?.close();
    this.wsClient = void 0;
  }
  async handleTokenRefresh() {
    if (!this.refreshToken) {
      throw new Error("No refresh token available");
    }
    if (this._refreshPromise) {
      return this._refreshPromise;
    }
    if (this._adoptRotatedTokensFromStorage()) {
      return;
    }
    this._refreshPromise = (async () => {
      const rt = this.refreshToken;
      try {
        if (!rt) {
          throw new Error("No refresh token available");
        }
        const result = await this.trpc.auth.refreshToken.mutate({
          refreshToken: rt
        });
        this.setTokens(result.accessToken, result.refreshToken);
        if (this.onTokenRefresh) {
          this.onTokenRefresh({
            accessToken: result.accessToken,
            refreshToken: result.refreshToken
          });
        }
      } catch (error) {
        if (this._adoptRotatedTokensFromStorage(rt)) {
          return;
        }
        for (let i = 0; i < 3; i++) {
          await new Promise((resolve2) => setTimeout(resolve2, 150));
          if (this._adoptRotatedTokensFromStorage(rt)) {
            return;
          }
        }
        if (this.onAuthError) {
          this.onAuthError(error);
        }
        throw error;
      } finally {
        this._refreshPromise = void 0;
      }
    })();
    return this._refreshPromise;
  }
  /**
  * Pull rotated tokens from localStorage if another client/tab has
  * already refreshed. Returns true when storage carried a newer
  * refresh token than `expectedOldRT` (or the current in-memory
  * `refreshToken` when no expected value is passed).
  *
  * Used by `handleTokenRefresh` as both a pre-check (avoid burning a
  * stale RT on the server) and a post-check (recover from a 401 that
  * another client already cleared).
  */
  _adoptRotatedTokensFromStorage(expectedOldRT) {
    if (!this.storage) {
      return false;
    }
    const storedRT = this.storage.getRefreshToken();
    if (!storedRT) {
      return false;
    }
    const baseline = expectedOldRT ?? this.refreshToken;
    if (!baseline || storedRT === baseline) {
      return false;
    }
    const storedAT = this.storage.getAccessToken();
    if (storedAT) {
      this.accessToken = storedAT;
    }
    this.refreshToken = storedRT;
    this.trpc = this.createTRPCClient();
    if (this.onTokenRefresh && storedAT) {
      this.onTokenRefresh({
        accessToken: storedAT,
        refreshToken: storedRT
      });
    }
    return true;
  }
  /**
  * Load tokens from persistent storage (localStorage in browser)
  */
  loadTokens() {
    if (!this.storage) {
      this.initStorage();
    }
    if (this.storage) {
      const at = this.storage.getAccessToken();
      const rt = this.storage.getRefreshToken();
      if (at) {
        this.accessToken = at;
      }
      if (rt) {
        this.refreshToken = rt;
      }
      if (at) {
        this.trpc = this.createTRPCClient();
      }
    }
  }
  initStorage() {
    if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
      this.storage = {
        getAccessToken: /* @__PURE__ */ __name(() => localStorage.getItem("hereby_access_token"), "getAccessToken"),
        getRefreshToken: /* @__PURE__ */ __name(() => localStorage.getItem("hereby_refresh_token"), "getRefreshToken"),
        setTokens: /* @__PURE__ */ __name((at, rt) => {
          localStorage.setItem("hereby_access_token", at);
          if (rt) {
            localStorage.setItem("hereby_refresh_token", rt);
          }
        }, "setTokens"),
        clearTokens: /* @__PURE__ */ __name(() => {
          localStorage.removeItem("hereby_access_token");
          localStorage.removeItem("hereby_refresh_token");
        }, "clearTokens")
      };
      try {
        window.addEventListener("storage", (event) => {
          if (event.key === "hereby_refresh_token" && event.newValue && event.newValue !== this.refreshToken) {
            this._adoptRotatedTokensFromStorage();
          }
        });
      } catch {
      }
    }
  }
  /**
  * Set authentication tokens
  */
  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    if (refreshToken) {
      this.refreshToken = refreshToken;
    }
    if (!this.storage) {
      this.initStorage();
    }
    this.storage?.setTokens(accessToken, refreshToken);
    this.trpc = this.createTRPCClient();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("hereby:ws-reconnected"));
    }
  }
  /**
  * Clear authentication tokens
  */
  clearTokens() {
    this.accessToken = void 0;
    this.refreshToken = void 0;
    if (!this.storage) {
      this.initStorage();
    }
    this.storage?.clearTokens();
    this.trpc = this.createTRPCClient();
  }
  /**
  * Get current access token
  */
  getAccessToken() {
    return this.accessToken;
  }
  /**
  * Get current refresh token
  */
  getRefreshToken() {
    return this.refreshToken;
  }
  /**
  * Check if client is authenticated
  */
  isAuthenticated() {
    return !!this.accessToken;
  }
  /**
  * 저장된 access token의 세션 종류를 판별한다 (서명 검증 없이 페이로드만 디코드).
  * - "workspace": 조직 스코프 세션 (userId 보유 — 기존 로그인)
  * - "account": account 스코프 세션 (무소속 회원 — 허브로 라우팅)
  * - null: 토큰 없음/해석 불가
  */
  getSessionKind() {
    const token = this.accessToken;
    if (!token) {
      return null;
    }
    try {
      const payloadPart = token.split(".")[1];
      if (!payloadPart) {
        return null;
      }
      const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(atob(normalized));
      if (payload.userId || payload.ownerGrant || payload.grantId || payload.systemGrant) {
        return "workspace";
      }
      if (payload.accountId) {
        return "account";
      }
      return null;
    } catch {
      return null;
    }
  }
  /**
  * Update SDK configuration
  */
  updateConfig(config) {
    this.config = {
      ...this.config,
      ...config
    };
    if (config.accessToken !== void 0) {
      this.accessToken = config.accessToken;
    }
    if (config.refreshToken !== void 0) {
      this.refreshToken = config.refreshToken;
    }
    if (config.onTokenRefresh !== void 0) {
      this.onTokenRefresh = config.onTokenRefresh;
    }
    if (config.onAuthError !== void 0) {
      this.onAuthError = config.onAuthError;
    }
    if (config.onMutationActivity !== void 0) {
      this.onMutationActivity = config.onMutationActivity;
    }
    this.trpc = this.createTRPCClient();
  }
  /**
  * Enable debug mode
  */
  enableDebug() {
    this.config.debug = true;
  }
  /**
  * Disable debug mode
  */
  disableDebug() {
    this.config.debug = false;
  }
};
__name(_HerebyClient, "HerebyClient");
var HerebyClient = _HerebyClient;

// src/device-login/passkey.ts
import { browserSupportsWebAuthn, platformAuthenticatorIsAvailable, startAuthentication, startRegistration } from "@simplewebauthn/browser";
async function isPasskeyAvailable() {
  try {
    return browserSupportsWebAuthn() && await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}
__name(isPasskeyAvailable, "isPasskeyAvailable");
async function registerPasskey(service, deviceName) {
  const { challengeId, options } = await service.passkeyRegisterOptions();
  let response;
  try {
    response = await startRegistration({
      optionsJSON: options
    });
  } catch (e) {
    if (e?.name === "NotAllowedError") {
      return false;
    }
    throw e;
  }
  await service.passkeyRegisterVerify({
    deviceName,
    response: {
      ...response,
      challengeId
    }
  });
  return true;
}
__name(registerPasskey, "registerPasskey");
async function loginWithPasskey(service, identifier) {
  const { challengeId, options } = await service.passkeyLoginOptions(identifier ? {
    identifier
  } : void 0);
  let response;
  try {
    response = await startAuthentication({
      optionsJSON: options
    });
  } catch (e) {
    if (e?.name === "NotAllowedError") {
      return null;
    }
    throw e;
  }
  return service.passkeyLoginVerify({
    challengeId,
    response
  });
}
__name(loginWithPasskey, "loginWithPasskey");

// src/device-login/index.ts
function resolve(deps) {
  const store = deps.store ?? createDefaultDeviceKeyStore();
  return {
    store,
    provider: deps.provider ?? new WebWrapKeyProvider(store)
  };
}
__name(resolve, "resolve");
async function getActiveDeviceKey(deps) {
  try {
    return await resolve(deps).store.getActive();
  } catch {
    return null;
  }
}
__name(getActiveDeviceKey, "getActiveDeviceKey");
async function registerDeviceKey(deps, params) {
  const { store, provider } = resolve(deps);
  const existing = await store.getActive();
  if (existing && existing.employeeId === params.employeeId) {
    return existing;
  }
  const generated = await generateDeviceKey(provider);
  const { credentialId } = await deps.service.registerDeviceKey({
    publicKey: generated.publicKey,
    deviceName: params.deviceName,
    platform: params.platform
  });
  const record = {
    credentialId,
    employeeId: params.employeeId,
    organizationId: params.organizationId,
    employeeName: params.employeeName,
    publicKey: generated.publicKey,
    wrappedPrivateKey: generated.wrappedPrivateKey,
    createdAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  await store.setActive(record);
  return record;
}
__name(registerDeviceKey, "registerDeviceKey");
async function loginWithDeviceKey(deps) {
  const { store, provider } = resolve(deps);
  const active = await store.getActive();
  if (!active) {
    return {
      status: "no-key"
    };
  }
  try {
    const { challengeId, challenge } = await deps.service.deviceKeyChallenge({
      credentialId: active.credentialId
    });
    const signature = await signChallenge(provider, active.wrappedPrivateKey, challenge);
    const result = await deps.service.deviceKeyLogin({
      credentialId: active.credentialId,
      challengeId,
      challenge,
      signature
    });
    return {
      status: "ok",
      result
    };
  } catch (e) {
    const code = e?.data?.code;
    if (code === "UNAUTHORIZED") {
      return {
        status: "rejected",
        message: e.message
      };
    }
    throw e;
  }
}
__name(loginWithDeviceKey, "loginWithDeviceKey");
async function forgetDeviceKey(deps) {
  await resolve(deps).store.clearActive();
}
__name(forgetDeviceKey, "forgetDeviceKey");

// src/alpine/plugin.ts
var DEFAULT_BRAND = "hereby Medical";
var DEFAULT_BRAND_FAMILY = "medical";
var SKIP_DEVICE_LOGIN_AUTO_KEY = "hereby:skip-device-login-auto";
function detectEmbedded() {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    if (new URLSearchParams(window.location.search).get("embedded") === "1") {
      return true;
    }
    return window.parent !== window.self;
  } catch {
    return true;
  }
}
__name(detectEmbedded, "detectEmbedded");
async function withBootRetry(fn, attempts = 3) {
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      if (isUnloading() || isAbortError(e) || isDefinitiveAuthFailure(e)) {
        throw e;
      }
      if (i < attempts - 1) {
        await new Promise((resolve2) => setTimeout(resolve2, 300 * (i + 1)));
      }
    }
  }
  throw lastError;
}
__name(withBootRetry, "withBootRetry");
function endSessionOnBootFailure(client, error) {
  if (isUnloading() || isAbortError(error)) {
    return false;
  }
  if (isDefinitiveAuthFailure(error)) {
    if (isTopLevelFrame()) {
      if (recoverFromImpersonationExpiry(client)) {
        return true;
      }
      client.clearTokens();
    }
    return true;
  }
  return true;
}
__name(endSessionOnBootFailure, "endSessionOnBootFailure");
function defaultDeviceName(platform) {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  if (platform === "ios") {
    return /iPad/.test(ua) ? "iPad" : "iPhone";
  }
  if (platform === "android") {
    return /Android.*; ([^;)]+)\)/.exec(ua)?.[1]?.replace(/ Build.*/, "") || "Android";
  }
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : /Firefox\//.test(ua) ? "Firefox" : "\uBE0C\uB77C\uC6B0\uC800";
  const os = /Macintosh/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${os} ${browser}` : browser;
}
__name(defaultDeviceName, "defaultDeviceName");
function createAlpinePlugin(config = {}) {
  return /* @__PURE__ */ __name(function herebyPlugin(Alpine) {
    installUnloadGuard();
    installImeEnterGuard();
    installCsReportHooks();
    const client = new HerebyClient({
      ...config,
      onAuthError: /* @__PURE__ */ __name((error) => {
        if (isUnloading() || !isDefinitiveAuthFailure(error)) {
          return;
        }
        if (isTopLevelFrame() && recoverFromImpersonationExpiry(client)) {
          return;
        }
        const store = Alpine.store("hereby");
        if (store) {
          store.authenticated = false;
          store.currentUser = null;
        }
      }, "onAuthError")
    });
    const rawClient = Alpine.raw ? Alpine.raw(client) : client;
    Object.defineProperty(rawClient, "__v_skip", {
      value: true
    });
    let _loginPassword = "";
    let pendingLogin;
    const reportedSessions = /* @__PURE__ */ new WeakSet();
    const sessionAdopted = /* @__PURE__ */ __name((kind, result, store) => {
      if (reportedSessions.has(result)) {
        return;
      }
      if (store.requiresPasswordChange) {
        if (kind === "login") {
          pendingLogin = result;
        }
        return;
      }
      reportedSessions.add(result);
      try {
        config.onSessionAdopted?.({
          kind,
          context: [
            store.authenticated,
            store.currentUser?.id,
            store.currentUser?.organizationId,
            store.account?.id
          ]
        });
      } catch {
      }
    }, "sessionAdopted");
    const i18n = createI18n("ko");
    i18n.load(getLocaleBundle("ko"));
    function extractTimezone(user) {
      return user.organizationTimezone || "Asia/Seoul";
    }
    __name(extractTimezone, "extractTimezone");
    function extractLocale(user) {
      return user.organizationLocale || "ko-KR";
    }
    __name(extractLocale, "extractLocale");
    function adoptLocaleAndTerms(store, user) {
      store.locale = extractLocale(user);
      i18n.reset(store.locale, getLocaleBundle(store.locale));
      store.setTerminology(user.organizationTerminology);
    }
    __name(adoptLocaleAndTerms, "adoptLocaleAndTerms");
    setDeviceSecretProvider(config.deviceLogin?.secretProvider ?? null);
    const deviceLoginDeps = /* @__PURE__ */ __name(() => ({
      service: client.deviceCredential,
      provider: config.deviceLogin?.secretProvider
    }), "deviceLoginDeps");
    const adoptLoginResult = /* @__PURE__ */ __name((store, result) => {
      pendingLogin = void 0;
      store.authenticated = true;
      store.currentUser = result.user;
      store.requiresPasswordChange = result.user.requiresPasswordChange === true;
      store.timezone = extractTimezone(result.user);
      adoptLocaleAndTerms(store, result.user);
      store.refreshProfilePictureUrl();
      sessionAdopted("login", result, store);
    }, "adoptLoginResult");
    Alpine.store("hereby", {
      sdk: rawClient,
      brand: config.brand ?? DEFAULT_BRAND,
      brandFamily: config.brandFamily ?? DEFAULT_BRAND_FAMILY,
      csAppId: config.appId ?? "",
      // 수집기 싱글턴을 스토어에 직접 담지 않는다(내부 배열을 Alpine 이 reactive 로
      // 감싸면 push 마다 재렌더). 함수만 노출해 컴포넌트가 필요할 때 읽는다.
      csReport: {
        snapshotLogs: /* @__PURE__ */ __name(() => csCollector.snapshot(), "snapshotLogs"),
        envContext: /* @__PURE__ */ __name(() => collectEnvContext(), "envContext"),
        lastError: /* @__PURE__ */ __name(() => csCollector.peekLastError(), "lastError"),
        noteError: /* @__PURE__ */ __name((snapshot) => csCollector.setLastError(snapshot), "noteError")
      },
      embedded: config.embedded ?? detectEmbedded(),
      linkedBrandFamily: config.linkedBrandFamily ?? "",
      authenticated: false,
      twoFactor: null,
      captcha: null,
      unusualLogin: false,
      deviceLogin: {
        ready: false,
        hasDeviceKey: false,
        employeeName: "",
        passkeyAvailable: false,
        platform: config.deviceLogin?.platform ?? "web",
        unsupportedReason: null
      },
      requiresPasswordChange: false,
      timezone: "Asia/Seoul",
      locale: "ko-KR",
      terminology: resolveTerminology(void 0, "ko-KR"),
      currentUser: null,
      currentUserProfilePictureUrl: "",
      isLoading: true,
      accountAuthenticated: false,
      account: null,
      workspaces: [],
      async init() {
        try {
          if (!String(config.baseUrl ?? "").trim()) {
            return;
          }
          client.loadTokens();
          this.refreshDeviceLoginState().catch(() => void 0);
          installImpersonationHistoryGuard(client);
          if (client.isAuthenticated()) {
            if (client.getSessionKind() === "account") {
              try {
                const me = await withBootRetry(() => client.account.me());
                this.accountAuthenticated = true;
                this.account = me.account;
                this.workspaces = me.workspaces;
              } catch (e) {
                this.accountAuthenticated = !endSessionOnBootFailure(client, e);
              }
              return;
            }
            this.authenticated = true;
            try {
              const user = await withBootRetry(() => client.auth.getCurrentUser());
              this.currentUser = user;
              this.requiresPasswordChange = user.mustChangePassword === true;
              this.timezone = extractTimezone(user);
              adoptLocaleAndTerms(this, user);
              this.refreshProfilePictureUrl();
            } catch (e) {
              this.authenticated = !endSessionOnBootFailure(client, e);
            }
          }
        } finally {
          this.isLoading = false;
        }
      },
      async login(identifier, password, captchaToken) {
        pendingLogin = void 0;
        const result = await client.auth.login({
          identifier,
          password,
          captchaToken
        });
        if ("captchaRequired" in result) {
          this.captcha = {
            siteKey: result.siteKey,
            kind: "password",
            identifier,
            secret: password
          };
          return "captcha";
        }
        this.captcha = null;
        if ("twoFactorRequired" in result) {
          this.twoFactor = {
            challengeId: result.challengeId,
            password
          };
          return "two_factor";
        }
        this.authenticated = true;
        this.currentUser = result.user;
        this.requiresPasswordChange = result.user.requiresPasswordChange === true;
        this.timezone = extractTimezone(result.user);
        adoptLocaleAndTerms(this, result.user);
        this.refreshProfilePictureUrl();
        this.unusualLogin = result.unusualLogin === true;
        if (this.requiresPasswordChange) {
          _loginPassword = password;
        }
        sessionAdopted("login", result, this);
        return "ok";
      },
      /** 로그인 2단계 — 인증 앱 코드(또는 백업 코드). 맞으면 세션 채택. 틀리면 throw(폼이 메시지를 보인다). */
      async verifyTwoFactor(code) {
        const pending = this.twoFactor;
        if (!pending) {
          throw new Error("\uB2E4\uC2DC \uB85C\uADF8\uC778\uD574 \uC8FC\uC138\uC694.");
        }
        try {
          const result = await client.security.verifyTwoFactor({
            challengeId: pending.challengeId,
            code
          });
          this.twoFactor = null;
          adoptLoginResult(this, result);
          this.unusualLogin = result.unusualLogin === true;
          if (this.requiresPasswordChange && pending.password) {
            _loginPassword = pending.password;
          }
        } catch (e) {
          const msg = e?.message ?? "";
          const at = msg.lastIndexOf("|");
          if (at > 0) {
            this.twoFactor = {
              ...pending,
              challengeId: msg.slice(at + 1)
            };
            throw new Error(msg.slice(0, at));
          }
          this.twoFactor = null;
          throw e;
        }
      },
      cancelTwoFactor() {
        this.twoFactor = null;
      },
      cancelCaptcha() {
        this.captcha = null;
      },
      async loginWithCode(identifier, code, captchaToken) {
        pendingLogin = void 0;
        const result = await client.auth.loginWithCode(identifier, code, void 0, captchaToken);
        if ("captchaRequired" in result) {
          this.captcha = {
            siteKey: result.siteKey,
            kind: "code",
            identifier,
            secret: code
          };
          return "captcha";
        }
        this.captcha = null;
        if ("twoFactorRequired" in result) {
          this.twoFactor = {
            challengeId: result.challengeId
          };
          return "two_factor";
        }
        this.authenticated = true;
        this.currentUser = result.user;
        this.requiresPasswordChange = result.user.requiresPasswordChange === true;
        this.timezone = extractTimezone(result.user);
        adoptLocaleAndTerms(this, result.user);
        this.refreshProfilePictureUrl();
        this.unusualLogin = result.unusualLogin === true;
        sessionAdopted("login", result, this);
        return "ok";
      },
      _adoptOrgSession(result, password) {
        pendingLogin = void 0;
        this.authenticated = true;
        this.accountAuthenticated = false;
        this.currentUser = result.user;
        this.requiresPasswordChange = result.user.requiresPasswordChange === true;
        this.timezone = extractTimezone(result.user);
        adoptLocaleAndTerms(this, result.user);
        this.refreshProfilePictureUrl();
        if (this.requiresPasswordChange && password) {
          _loginPassword = password;
        }
      },
      async accountLogin(identifier, password, captchaToken) {
        pendingLogin = void 0;
        const result = await client.account.login({
          identifier,
          password,
          captchaToken
        });
        if (result.mode === "captcha") {
          this.captcha = {
            siteKey: result.siteKey,
            kind: "account",
            identifier,
            secret: password
          };
          return "captcha";
        }
        this.captcha = null;
        if (result.mode === "workspace") {
          this.unusualLogin = result.unusualLogin === true;
        }
        if (result.mode === "two_factor") {
          this.twoFactor = {
            challengeId: result.challengeId,
            password
          };
          return "two_factor";
        }
        if (result.mode === "workspace") {
          this._adoptOrgSession(result, password);
          sessionAdopted("login", result, this);
          return "workspace";
        }
        this.accountAuthenticated = true;
        this.account = result.account;
        this.workspaces = result.workspaces;
        sessionAdopted("login", result, this);
        return "account";
      },
      async selectWorkspace(organizationId) {
        const result = await client.account.selectWorkspace(organizationId);
        this._adoptOrgSession(result);
        sessionAdopted("workspace_selected", result, this);
      },
      // owner 암묵 grant 입장 — 멤버십 없는 소유 조직 (허브 ownerEntry 항목)
      async enterOwnedWorkspace(organizationId) {
        const result = await client.account.enterOwnedWorkspace(organizationId);
        this._adoptOrgSession(result);
        sessionAdopted("workspace_selected", result, this);
      },
      // OrganizationGrant 입장 — support 대행/impersonation (허브 지원 섹션)
      async enterGrantedWorkspace(grantId) {
        const result = await client.account.enterGrantedWorkspace(grantId);
        this._adoptOrgSession(result);
        sessionAdopted("workspace_selected", result, this);
      },
      async createWorkspace(input) {
        const result = await client.account.createWorkspace(input);
        this._adoptOrgSession(result);
        this.account = null;
        this.workspaces = [];
      },
      async refreshWorkspaces() {
        if (!this.accountAuthenticated) {
          return;
        }
        this.workspaces = await client.account.workspaces();
      },
      async accountLogout() {
        pendingLogin = void 0;
        try {
          await client.account.logout();
        } catch {
        }
        this.accountAuthenticated = false;
        this.account = null;
        this.workspaces = [];
        window.location.reload();
      },
      /* ── 키 로그인(2026-10-13) ── */
      async refreshDeviceLoginState() {
        const platform = config.deviceLogin?.platform ?? "web";
        const support = config.deviceLogin?.supported?.() ?? {
          ok: true
        };
        if (!support.ok) {
          this.deviceLogin = {
            ready: true,
            hasDeviceKey: false,
            employeeName: "",
            passkeyAvailable: false,
            platform,
            unsupportedReason: support.reason
          };
          return;
        }
        const active = await getActiveDeviceKey(deviceLoginDeps());
        const passkeyAvailable = platform === "web" ? await isPasskeyAvailable() : false;
        this.deviceLogin = {
          ready: true,
          hasDeviceKey: !!active,
          employeeName: active?.employeeName ?? "",
          passkeyAvailable,
          platform,
          unsupportedReason: null
        };
      },
      async loginWithDeviceKey() {
        if (!this.deviceLogin.hasDeviceKey) {
          return "no-key";
        }
        const gate = config.deviceLogin?.gate;
        if (gate && !await gate("\uB85C\uADF8\uC778\uD558\uB824\uBA74 \uBCF8\uC778 \uD655\uC778\uC774 \uD544\uC694\uD574\uC694")) {
          return "cancelled";
        }
        const outcome = await loginWithDeviceKey(deviceLoginDeps());
        if (outcome.status === "no-key") {
          await this.refreshDeviceLoginState();
          return "no-key";
        }
        if (outcome.status === "rejected") {
          await forgetDeviceKey(deviceLoginDeps());
          await this.refreshDeviceLoginState();
          return "rejected";
        }
        adoptLoginResult(this, outcome.result);
        return "ok";
      },
      async loginWithPasskey(identifier) {
        const result = await loginWithPasskey(client.deviceCredential, identifier);
        if (!result) {
          return false;
        }
        adoptLoginResult(this, result);
        return true;
      },
      async registerThisDeviceKey() {
        const user = this.currentUser;
        if (!(user?.id && this.authenticated)) {
          return false;
        }
        if (this.deviceLogin.unsupportedReason) {
          throw new Error(this.t(this.deviceLogin.unsupportedReason));
        }
        const platform = config.deviceLogin?.platform ?? "web";
        const name = `${user.lastName || ""}${user.firstName || ""}`.trim() || user.email || "";
        await registerDeviceKey(deviceLoginDeps(), {
          employeeId: user.id,
          organizationId: user.organizationId || "",
          employeeName: name,
          deviceName: config.deviceLogin?.deviceName?.() || defaultDeviceName(platform),
          platform
        });
        await this.refreshDeviceLoginState();
        return true;
      },
      async forgetThisDeviceKey(revoke) {
        const active = await getActiveDeviceKey(deviceLoginDeps());
        if (active && revoke) {
          try {
            await client.deviceCredential.revoke({
              id: active.credentialId
            });
          } catch {
          }
        }
        await forgetDeviceKey(deviceLoginDeps());
        await this.refreshDeviceLoginState();
      },
      async registerThisPasskey(deviceName) {
        return registerPasskey(client.deviceCredential, deviceName);
      },
      async logout() {
        pendingLogin = void 0;
        try {
          await client.auth.logout();
        } catch {
        }
        this.authenticated = false;
        this.requiresPasswordChange = false;
        this.currentUser = null;
        this.currentUserProfilePictureUrl = "";
        this._fileUrlCache.clear();
        _loginPassword = "";
        try {
          const chatStore = createDefaultChatKeyStore();
          await chatStore.clearMessageCache();
          await chatStore.clearAttachmentCache();
        } catch {
        }
        try {
          sessionStorage.setItem(SKIP_DEVICE_LOGIN_AUTO_KEY, "1");
        } catch {
        }
        window.location.reload();
      },
      async changePassword(newPassword, confirmPassword) {
        await client.auth.changePassword(_loginPassword, newPassword, confirmPassword);
        this.requiresPasswordChange = false;
        _loginPassword = "";
        if (pendingLogin) {
          const completed = pendingLogin;
          pendingLogin = void 0;
          sessionAdopted("login", completed, this);
        }
      },
      hasPermission(permission) {
        if (!this.currentUser) {
          return false;
        }
        if (this.currentUser.isSystemAdmin) {
          return true;
        }
        const perms = this.currentUser.permissions;
        if (!Array.isArray(perms)) {
          return false;
        }
        if (perms.includes(permission)) {
          return true;
        }
        const prefix = `${permission}.`;
        return perms.some((p) => p.startsWith(prefix));
      },
      todayLocal() {
        return todayLocal(this.timezone);
      },
      formatDateISO(date) {
        return formatDateISO(date, this.timezone);
      },
      formatDate(date) {
        return formatDate(date, this.locale, this.timezone);
      },
      formatDateTime(date) {
        return formatDateTime(date, this.locale, this.timezone);
      },
      formatDateTimeLong(date) {
        return formatDateTimeLong(date, this.locale, this.timezone);
      },
      formatTime(date) {
        return formatTime(date, this.locale, this.timezone);
      },
      formatDateCustom(date, options) {
        return formatDateCustom(date, this.locale, this.timezone, options);
      },
      t(key, vars) {
        const self = this ?? Alpine.store("hereby");
        return self.terminology ? i18n.t(key, vars) : key;
      },
      term(key) {
        return this.terminology[key] ?? i18n.term(key);
      },
      setTerminology(config2) {
        this.terminology = resolveTerminology(config2, this.locale);
        i18n.setTerms(this.terminology);
      },
      tEnum(enumName, value) {
        return i18n.tEnum(enumName, value);
      },
      tPermission(code) {
        return i18n.tPermission(code);
      },
      setLocale(newLocale) {
        this.locale = newLocale;
        i18n.reset(newLocale, getLocaleBundle(newLocale));
        this.setTerminology(this.terminology);
      },
      // biome-ignore lint/style/useNamingConvention: internal i18n instance
      _i18n: i18n,
      // biome-ignore lint/style/useNamingConvention: internal cache prefixed with _ to indicate private
      _fileUrlCache: /* @__PURE__ */ new Map(),
      async getFileUrl(key) {
        if (!key) {
          return "";
        }
        const cached = this._fileUrlCache.get(key);
        if (cached && cached.expiresAt > Date.now()) {
          return cached.url;
        }
        const { url } = await rawClient.fileStorage.getDownloadUrl(key);
        this._fileUrlCache.set(key, {
          url,
          expiresAt: Date.now() + 50 * 60 * 1e3
        });
        return url;
      },
      async refreshProfilePictureUrl() {
        const key = this.currentUser?.profilePicture || "";
        if (!key) {
          this.currentUserProfilePictureUrl = "";
          return;
        }
        this._fileUrlCache.delete(key);
        try {
          const url = await this.getFileUrl(key);
          this.currentUserProfilePictureUrl = url;
        } catch {
          this.currentUserProfilePictureUrl = "";
        }
      }
    });
  }, "herebyPlugin");
}
__name(createAlpinePlugin, "createAlpinePlugin");

// src/chat-crypto/chat-key-manager.ts
import { deviceLinkCode, exportAccountPrivateBundle, exportPublicKeySpki, fingerprintPublicKey, generateAccountKeyPair, generateDeviceLinkKeyPair, importAccountPrivateBundle, openDeviceLinkPayload, sealDeviceLinkPayload } from "@hereby/core/chat-crypto";

// src/chat-crypto/push-token.ts
var devicePushToken = null;
function setDevicePushToken(token) {
  devicePushToken = token && token.length > 0 ? token : null;
}
__name(setDevicePushToken, "setDevicePushToken");
function getDevicePushToken() {
  return devicePushToken;
}
__name(getDevicePushToken, "getDevicePushToken");

// src/chat-crypto/chat-key-manager.ts
var _ChatKeyManager = class _ChatKeyManager {
  constructor(deps) {
    __publicField(this, "deps");
    __publicField(this, "pair", null);
    __publicField(this, "stored", null);
    __publicField(this, "state", "unknown");
    this.deps = deps;
  }
  get currentState() {
    return this.state;
  }
  /** 어느 직원의 관리자인지 — 로그인 직원이 바뀌면 호출자가 새로 만든다. */
  get employeeId() {
    return this.deps.employeeId;
  }
  /** 기기 키 + 서버 키를 맞춰 보고 상태를 정한다. 메신저 진입마다 부른다. */
  async ensure() {
    const [local, remote] = await Promise.all([
      this.deps.store.getAccount(this.deps.employeeId),
      this.deps.chatKey.getMine()
    ]);
    if (!remote) {
      this.pair = null;
      this.stored = null;
      this.state = "setup";
      return this.state;
    }
    if (local && local.organizationId === this.deps.organizationId && local.version === remote.version && local.bundle.ecdhPublicKey === remote.ecdhPublicKey) {
      this.stored = local;
      this.pair = await importAccountPrivateBundle(local.bundle);
      this.state = "ready";
      return this.state;
    }
    this.pair = null;
    this.stored = null;
    this.state = "restore";
    return this.state;
  }
  /** 복호에 쓰는 계정 키. ready 가 아니면 null. */
  getKeyPair() {
    return this.pair;
  }
  getKeyVersion() {
    return this.stored?.version ?? null;
  }
  /** 내 공개키(세대 봉투를 자기 자신에게 만들 때). ready 가 아니면 null. */
  getPublicKeys() {
    return this.stored ? {
      ecdhPublicKey: this.stored.bundle.ecdhPublicKey,
      ecdsaPublicKey: this.stored.bundle.ecdsaPublicKey
    } : null;
  }
  // ── 최초 설정 ──
  /**
  * 1단계: 키 쌍을 만든다(아직 서버에 없음). 복구 코드는 더 만들지 않는다(2026-10-15 결정) — 사용자가 복구 수단을
  * 직접 보관하지 않는다. 기기를 모두 잃으면 지난 대화는 다른 멤버 기기가 봉투를 다시 줄 수 있는 방에서만 돌아온다.
  */
  async beginSetup() {
    const pair = await generateAccountKeyPair();
    const bundle = await exportAccountPrivateBundle(pair);
    return {
      pair,
      bundle
    };
  }
  /** 2단계: 공개키를 등록하고 기기에 저장한다. 서버 백업은 없다. */
  async completeSetup(pending) {
    const { version } = await this.deps.chatKey.register({
      ecdhPublicKey: pending.bundle.ecdhPublicKey,
      ecdsaPublicKey: pending.bundle.ecdsaPublicKey
    });
    await this.saveLocal(pending.bundle, version);
    this.pair = pending.pair;
    await this.deps.requestPersistentStorage?.().catch(() => false);
  }
  // ── 복구 ──
  /** 새로 시작 — 새 키를 만든다. 지난 대화는 이 사람 화면에서만 안 보이고 회사 자료는 복구 봉투로 보존된다. */
  async resetWithNewKey() {
    return this.beginSetup();
  }
  // ── 새 기기 승인(이 기기가 새 기기) ──
  async startDeviceLink() {
    const ephemeral = await generateDeviceLinkKeyPair();
    const epk = await exportPublicKeySpki(ephemeral.publicKey);
    const token = getDevicePushToken();
    const created = await this.deps.chatDeviceLink.create(epk, token ? {
      requesterPushToken: token
    } : void 0);
    return {
      requestId: created.requestId,
      code: await deviceLinkCode(epk),
      privateKey: ephemeral.privateKey,
      expiresAt: new Date(created.expiresAt)
    };
  }
  /** 승인됐으면 봉투를 받아 키를 저장하고 true, 아직이면 false, 만료되면 throw. */
  async pollDeviceLink(session) {
    const result = await this.deps.chatDeviceLink.claim(session.requestId);
    if (result.status === "expired") {
      throw new Error("\uC694\uCCAD\uC774 \uB9CC\uB8CC\uB410\uC5B4\uC694. \uB2E4\uC2DC \uC2DC\uC791\uD574 \uC8FC\uC138\uC694.");
    }
    if (!result.payload) {
      return false;
    }
    const bundle = await openDeviceLinkPayload({
      ephemeralPrivateKey: session.privateKey,
      payload: result.payload,
      requestId: session.requestId
    });
    const remote = await this.deps.chatKey.getMine();
    this.pair = await importAccountPrivateBundle(bundle);
    await this.saveLocal(bundle, remote?.version ?? 1);
    return true;
  }
  async cancelDeviceLink(session) {
    await this.deps.chatDeviceLink.cancel(session.requestId).catch(() => void 0);
  }
  // ── 새 기기 승인(이 기기가 옛 기기) ──
  /** 내 계정으로 들어온 대기 요청과 각각의 6자리 코드. 키가 없는(ready 아님) 기기는 승인할 수 없어 빈 배열. */
  async listPendingDeviceLinks() {
    if (this.state !== "ready" || !this.stored) {
      return [];
    }
    const rows = await this.deps.chatDeviceLink.listPending();
    return Promise.all(rows.map(async (r) => ({
      requestId: r.requestId,
      code: await deviceLinkCode(r.ephemeralPublicKey),
      createdAt: new Date(r.createdAt),
      ephemeralPublicKey: r.ephemeralPublicKey
    })));
  }
  /** 사용자가 코드를 대조하고 승인했다 — 계정 키 묶음을 새 기기의 임시 공개키로 감싸 올린다. */
  async approveDeviceLink(requestId, ephemeralPublicKey) {
    if (!this.stored) {
      throw new Error("\uC774 \uAE30\uAE30\uC5D0 \uB300\uD654 \uC7A0\uAE08 \uD0A4\uAC00 \uC5C6\uC5B4\uC694.");
    }
    const payload = await sealDeviceLinkPayload({
      ephemeralPublicKey,
      bundle: this.stored.bundle,
      requestId
    });
    await this.deps.chatDeviceLink.approve({
      requestId,
      payload
    });
  }
  async rejectDeviceLink(requestId) {
    await this.deps.chatDeviceLink.cancel(requestId);
  }
  // ── 관리 ──
  /** 이 기기에서 키 삭제(공용 PC 등). 서버 키는 그대로라 다음 진입 때 restore 상태가 된다. */
  async forgetLocalKey() {
    await this.deps.store.deleteAccount(this.deps.employeeId);
    await this.deps.store.clearEpochKeys();
    this.pair = null;
    this.stored = null;
    this.state = "restore";
  }
  async fingerprint() {
    return this.stored ? fingerprintPublicKey(this.stored.bundle.ecdhPublicKey) : null;
  }
  async saveLocal(bundle, version) {
    const record = {
      employeeId: this.deps.employeeId,
      organizationId: this.deps.organizationId,
      version,
      // 평범한 객체로 다시 만든다 — UI(Alpine) 상태에서 온 묶음은 Proxy 라 IndexedDB 가 DataCloneError 로 거절한다
      // (CS-OKBEST-261009-0001 · MED-978: 서버 등록은 됐는데 이 기기 저장만 실패). 필드는 전부 문자열·숫자다.
      bundle: plainAccountBundle(bundle),
      savedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
    await this.deps.store.putAccount(record);
    await this.deps.store.clearEpochKeys();
    this.stored = record;
    this.state = "ready";
  }
};
__name(_ChatKeyManager, "ChatKeyManager");
var ChatKeyManager = _ChatKeyManager;
function plainAccountBundle(bundle) {
  return {
    v: bundle.v,
    ecdhPublicKey: String(bundle.ecdhPublicKey),
    ecdsaPublicKey: String(bundle.ecdsaPublicKey),
    ecdhPrivateKey: String(bundle.ecdhPrivateKey),
    ecdsaPrivateKey: String(bundle.ecdsaPrivateKey)
  };
}
__name(plainAccountBundle, "plainAccountBundle");

// src/chat-crypto/conversation-key-manager.ts
import { buildEpochAttestationBytes, buildMembershipAttestationBytes, generateRoomKey, importRoomKey, openEpochEnvelope, roomKeyCommitment, sealEpochEnvelope, signBytes, verifyBytes } from "@hereby/core/chat-crypto";
var _ConversationKeyManager = class _ConversationKeyManager {
  constructor(deps) {
    __publicField(this, "deps");
    /** 세대 키 CryptoKey 캐시(메모리) — 원본은 store 에 */
    __publicField(this, "keys", /* @__PURE__ */ new Map());
    /** 호출자가 넣어 주는 내 ECDSA 공개키(spki) — 내가 만든 세대의 서명을 다시 확인할 때 */
    __publicField(this, "myEcdsaPublicKey", null);
    this.deps = deps;
  }
  get employeeId() {
    return this.deps.employeeId;
  }
  cacheKey(conversationId, epoch) {
    return `${conversationId}:${epoch}`;
  }
  /** 세대 키(AES-KW). 캐시·저장소에 없으면 null — 호출자가 `sync` 를 먼저 돌려야 한다. */
  async getRoomKey(conversationId, epoch) {
    const k = this.cacheKey(conversationId, epoch);
    const cached = this.keys.get(k);
    if (cached) {
      return cached;
    }
    const raw = await this.deps.store.getEpochKey(conversationId, epoch);
    if (!raw) {
      return null;
    }
    const key = await importRoomKey(raw);
    this.keys.set(k, key);
    return key;
  }
  async remember(conversationId, epoch, raw) {
    await this.deps.store.putEpochKey(conversationId, epoch, raw);
    this.keys.set(this.cacheKey(conversationId, epoch), await importRoomKey(raw));
  }
  /**
  * 세대 서명 검증(E9) — "trusted" | "server" | "untrusted".
  * 서명이 있으면: 약속값 = sha256(방 키) 이어야 하고, 서명자의 그 version ECDSA 공개키(고정값)로 서명이 맞아야 한다.
  * 서명이 없으면: 서버 이관 세대(reason migration, createdBy 없음)만 "server". 디렉터리가 없는 호출자는 전부 믿는다.
  */
  async verifyEpoch(conversationId, meta, raw) {
    if (!this.deps.directory) {
      return "trusted";
    }
    if (!meta) {
      return "untrusted";
    }
    if (!meta.signature) {
      return meta.reason === "migration" && !meta.createdBy ? "server" : "untrusted";
    }
    if (!meta.createdBy || meta.signerKeyVersion === null || !meta.keyCommitment) {
      return "untrusted";
    }
    if (await roomKeyCommitment(raw) !== meta.keyCommitment) {
      return "untrusted";
    }
    const signerKey = meta.createdBy === this.deps.employeeId ? await this.mySignerKey(meta.signerKeyVersion) : await this.deps.directory.ecdsaKeyForVersion(meta.createdBy, meta.signerKeyVersion);
    if (!signerKey) {
      return "untrusted";
    }
    const ok = await verifyBytes(signerKey, buildEpochAttestationBytes({
      organizationId: this.deps.organizationId,
      conversationId,
      epoch: meta.epoch,
      reason: meta.reason,
      keyCommitment: meta.keyCommitment
    }), meta.signature);
    return ok ? "trusted" : "untrusted";
  }
  /**
  * 내가 서명한 세대는 내 키로 — 지금 version 이면 호출자가 넣어 준 내 ECDSA 공개키, **옛 version 이면 서버 이력**에서.
  * 예전엔 지금 version 만 받아들여, 키를 다시 만든 뒤 **내가 예전 키로 연 세대**(1:1 방은 재설정할 때마다 내가 세대를
  * 올리므로 현재 세대가 거의 늘 내 서명이다)를 못 믿어 그 방이 영영 「서명을 확인할 수 없음」·보내기 막힘이 됐다
  * (2026-10-20 DM 보고, 로컬 재현: untrustedEpochs [4] · rekeyed false). 이력의 옛 키는 폐기된 키라 바꿔치기해 얻을 것이 없다.
  */
  async mySignerKey(version) {
    const account = this.deps.accountKey();
    if (!account) {
      return null;
    }
    if (account.version === version) {
      return this.myEcdsaPublicKey ?? null;
    }
    return this.deps.directory ? await this.deps.directory.ecdsaKeyForVersion(this.deps.employeeId, version) : null;
  }
  /**
  * 방을 열 때 한 번. `allowCreate` 는 조직이 암호화를 켰고 이 방이 암호화 대상(공식 알림 아님)일 때만 true.
  */
  async sync(conversationId, allowCreate) {
    const account = this.deps.accountKey();
    const result = {
      currentEpoch: null,
      haveCurrentKey: false,
      created: false,
      rotated: false,
      reopened: false,
      rekeyed: false,
      filled: 0,
      untrustedEpochs: [],
      serverMintedEpochs: [],
      heldForKeyChange: [],
      heldForMembership: []
    };
    if (!account) {
      return result;
    }
    const mine = await this.deps.conversationKey.myEnvelopes(conversationId);
    result.currentEpoch = mine.currentEpoch;
    const metaByEpoch = new Map((mine.epochs ?? []).map((e) => [
      e.epoch,
      e
    ]));
    for (const e of mine.envelopes) {
      if (e.recipientKeyVersion !== account.version) {
        continue;
      }
      if (await this.getRoomKey(conversationId, e.epoch)) {
        continue;
      }
      let raw;
      try {
        raw = await openEpochEnvelope({
          recipientEcdhPrivateKey: account.pair.ecdh.privateKey,
          envelope: e.envelope,
          header: {
            organizationId: this.deps.organizationId,
            conversationId,
            epoch: e.epoch,
            recipientEmployeeId: this.deps.employeeId,
            recipientKeyVersion: account.version
          }
        });
      } catch {
        continue;
      }
      const verdict = await this.verifyEpoch(conversationId, metaByEpoch.get(e.epoch), raw);
      if (verdict === "untrusted") {
        result.untrustedEpochs.push(e.epoch);
        continue;
      }
      if (verdict === "server") {
        result.serverMintedEpochs.push(e.epoch);
      }
      await this.remember(conversationId, e.epoch, raw);
    }
    if (mine.currentEpoch === null) {
      if (!allowCreate) {
        return result;
      }
      await this.openEpoch(conversationId, 1, "create", account);
      result.currentEpoch = 1;
      result.created = true;
    } else if (mine.stale && await this.getRoomKey(conversationId, mine.currentEpoch)) {
      const next = mine.currentEpoch + 1;
      await this.openEpoch(conversationId, next, "member_removed", account);
      result.currentEpoch = next;
      result.rotated = true;
    } else if (allowCreate && mine.otherHolders !== void 0 && !result.untrustedEpochs.includes(mine.currentEpoch) && !await this.getRoomKey(conversationId, mine.currentEpoch)) {
      const rekeyed = mine.envelopes.some((e) => e.recipientKeyVersion < account.version);
      if (mine.otherHolders === 0 || rekeyed) {
        const next = mine.currentEpoch + 1;
        await this.openEpoch(conversationId, next, "rotate", account);
        result.currentEpoch = next;
        result.rotated = true;
        result.reopened = mine.otherHolders === 0;
        result.rekeyed = rekeyed && mine.otherHolders !== 0;
      }
    }
    const fill = await this.fillMissing(conversationId);
    result.filled = fill.sent;
    result.heldForKeyChange = fill.heldForKeyChange;
    result.heldForMembership = fill.heldForMembership;
    result.haveCurrentKey = result.currentEpoch !== null && await this.getRoomKey(conversationId, result.currentEpoch) !== null;
    let missingPast = false;
    for (const e of mine.epochs ?? []) {
      if (e.epoch !== result.currentEpoch && !await this.getRoomKey(conversationId, e.epoch)) {
        missingPast = true;
        break;
      }
    }
    if (result.currentEpoch !== null && (!result.haveCurrentKey && (mine.otherHolders ?? 0) > 0 || missingPast)) {
      this.deps.conversationKey.requestEnvelopes?.(conversationId).catch(() => void 0);
    }
    return result;
  }
  async openEpoch(conversationId, epoch, reason, account) {
    const raw = generateRoomKey();
    const envelope = await sealEpochEnvelope({
      recipientEcdhPublicKey: account.ecdhPublicKey,
      roomKey: raw,
      header: {
        organizationId: this.deps.organizationId,
        conversationId,
        epoch,
        recipientEmployeeId: this.deps.employeeId,
        recipientKeyVersion: account.version
      }
    });
    const keyCommitment = await roomKeyCommitment(raw);
    const signature = await signBytes(account.pair.ecdsa.privateKey, buildEpochAttestationBytes({
      organizationId: this.deps.organizationId,
      conversationId,
      epoch,
      reason,
      keyCommitment
    }));
    await this.deps.conversationKey.publish({
      conversationId,
      epoch,
      reason,
      envelopes: [
        {
          recipientEmployeeId: this.deps.employeeId,
          recipientKeyVersion: account.version,
          envelope
        }
      ],
      attestation: {
        signerKeyVersion: account.version,
        keyCommitment,
        signature
      }
    });
    await this.remember(conversationId, epoch, raw);
  }
  /**
  * 합류 서명(E9) — **더는 봉투를 막지 않는다**(2026-10-20). 서버를 믿기로 한 결정(10-15, 상대 키 자동 수용)과 같은 선상이다.
  * 예전엔 사람이 넣은 멤버(addedBy 있음)는 넣은 사람 기기의 서명이 있어야 봉투를 줬는데, 서명 없이 만들어진 옛 1:1 방
  * (키가 생기기 전·옛 클라이언트가 만든 방)의 상대는 **모든 세대** 봉투가 영영 보류돼 아무것도 못 읽었다(dev 1:1 보고:
  * missingRecipients 가 1~9 세대 전부, membership.attestation null). 서명이 있으면 확인해 틀린 경우만 기록용으로 남긴다.
  */
  async membershipAllowed(conversationId, r) {
    const m = r.membership;
    const a = m?.attestation;
    if (!(this.deps.directory && m?.addedBy && a)) {
      return true;
    }
    if (!a.employeeIds.includes(r.employeeId)) {
      return true;
    }
    const signerKey = m.addedBy === this.deps.employeeId ? await this.mySignerKey(a.signerKeyVersion) : await this.deps.directory.ecdsaKeyForVersion(m.addedBy, a.signerKeyVersion);
    if (!signerKey) {
      return true;
    }
    const ok = await verifyBytes(signerKey, buildMembershipAttestationBytes({
      organizationId: this.deps.organizationId,
      conversationId: a.action === "create" ? null : conversationId,
      action: a.action,
      employeeIds: a.employeeIds,
      at: a.at
    }), a.signature);
    return ok;
  }
  /**
  * 봉투가 없는 멤버에게 내가 가진 세대 키를 봉투로 보낸다 — 받는 사람의 고정된 키에만(E9).
  * 디렉터리 응답이 어긋나거나 합류 서명이 **틀린** 경우만 보류하고 이름을 돌려준다(서명이 없는 멤버는 준다, 2026-10-20).
  */
  async fillMissing(conversationId) {
    const out = {
      sent: 0,
      heldForKeyChange: [],
      heldForMembership: []
    };
    const { currentEpoch, missing } = await this.deps.conversationKey.missingRecipients(conversationId);
    if (currentEpoch === null || missing.length === 0) {
      return out;
    }
    const others = [
      ...new Set(missing.map((m) => m.employeeId))
    ].filter((id) => id !== this.deps.employeeId);
    const infos = this.deps.directory ? await this.deps.directory.resolve(others) : null;
    const held = /* @__PURE__ */ new Set();
    const heldMembership = /* @__PURE__ */ new Set();
    const allowed = [];
    for (const m of missing) {
      if (infos && m.employeeId !== this.deps.employeeId) {
        const info = infos.get(m.employeeId);
        if (!info?.trusted || info.trusted.ecdhPublicKey !== m.ecdhPublicKey || info.trusted.version !== m.keyVersion) {
          held.add(m.employeeId);
          continue;
        }
      }
      if (!await this.membershipAllowed(conversationId, m)) {
        heldMembership.add(m.employeeId);
        continue;
      }
      allowed.push(m);
    }
    out.heldForKeyChange = [
      ...held
    ];
    out.heldForMembership = [
      ...heldMembership
    ];
    const byEpoch = /* @__PURE__ */ new Map();
    for (const m of allowed) {
      const list = byEpoch.get(m.epoch) ?? [];
      list.push(m);
      byEpoch.set(m.epoch, list);
    }
    for (const [epoch, recipients] of byEpoch) {
      const raw = await this.deps.store.getEpochKey(conversationId, epoch);
      if (!raw) {
        continue;
      }
      const envelopes = await Promise.all(recipients.map(async (r) => ({
        recipientEmployeeId: r.employeeId,
        recipientKeyVersion: r.keyVersion,
        envelope: await sealEpochEnvelope({
          recipientEcdhPublicKey: r.ecdhPublicKey,
          roomKey: raw,
          header: {
            organizationId: this.deps.organizationId,
            conversationId,
            epoch,
            recipientEmployeeId: r.employeeId,
            recipientKeyVersion: r.keyVersion
          }
        })
      })));
      await this.deps.conversationKey.publish({
        conversationId,
        epoch,
        envelopes
      });
      out.sent += envelopes.length;
    }
    return out;
  }
  /** 로그인 직원이 바뀌었을 때 메모리 캐시 비우기(저장소는 호출자가 clearEpochKeys). */
  clear() {
    this.keys.clear();
  }
};
__name(_ConversationKeyManager, "ConversationKeyManager");
var ConversationKeyManager = _ConversationKeyManager;

// src/chat-crypto/membership-attestation.ts
import { buildMembershipAttestationBytes as buildMembershipAttestationBytes2, signBytes as signBytes2 } from "@hereby/core/chat-crypto";
async function signMembershipChange(params) {
  const at = (params.now ?? /* @__PURE__ */ new Date()).toISOString();
  const employeeIds = [
    ...new Set(params.employeeIds)
  ].sort();
  const signature = await signBytes2(params.pair.ecdsa.privateKey, buildMembershipAttestationBytes2({
    organizationId: params.organizationId,
    conversationId: params.conversationId,
    action: params.action,
    employeeIds,
    at
  }));
  return {
    signerKeyVersion: params.keyVersion,
    action: params.action,
    employeeIds,
    at,
    signature
  };
}
__name(signMembershipChange, "signMembershipChange");

// src/chat-crypto/message-crypto.ts
import { buildMessageAad, decryptMessage, encryptMessage, generateMessageKey, importRecoveryPublicKey, sealRecoveryEnvelope, unwrapMessageKey, wrapMessageKey } from "@hereby/core/chat-crypto";
var recoveryKeyCache = /* @__PURE__ */ new Map();
function recoveryKey(info) {
  let p = recoveryKeyCache.get(info.publicKey);
  if (!p) {
    p = importRecoveryPublicKey(info.publicKey);
    recoveryKeyCache.set(info.publicKey, p);
  }
  return p;
}
__name(recoveryKey, "recoveryKey");
function newMessageId() {
  return globalThis.crypto.randomUUID();
}
__name(newMessageId, "newMessageId");
async function encryptOutgoingMessage(params) {
  const roomKey = await params.rooms.getRoomKey(params.conversationId, params.keyEpoch);
  if (!roomKey) {
    throw new Error("\uB300\uD654\uBC29 \uD0A4\uAC00 \uC544\uC9C1 \uC5C6\uC5B4\uC694.");
  }
  const messageId = params.messageId ?? newMessageId();
  const messageKey = generateMessageKey();
  const aad = buildMessageAad({
    organizationId: params.organizationId,
    conversationId: params.conversationId,
    senderId: params.senderId,
    messageId,
    keyEpoch: params.keyEpoch
  });
  const [ciphertext, keyWrap, recoveryEnvelope] = await Promise.all([
    encryptMessage(messageKey, params.payload, aad),
    wrapMessageKey(roomKey, messageKey),
    params.recovery ? sealRecoveryEnvelope({
      recoveryPublicKey: await recoveryKey(params.recovery),
      keyId: params.recovery.keyId,
      messageKey,
      binding: {
        organizationId: params.organizationId,
        conversationId: params.conversationId,
        messageId
      }
    }) : Promise.resolve(null)
  ]);
  return {
    id: messageId,
    ciphertext,
    keyEpoch: params.keyEpoch,
    keyWrap,
    recoveryEnvelope,
    encVersion: 1
  };
}
__name(encryptOutgoingMessage, "encryptOutgoingMessage");
async function decryptIncomingMessage(rooms, organizationId, message) {
  if (message.encVersion !== void 0 && message.encVersion !== null && message.encVersion !== 1) {
    return null;
  }
  const roomKey = await rooms.getRoomKey(message.conversationId, message.keyEpoch);
  if (!roomKey) {
    return null;
  }
  try {
    const messageKey = await unwrapMessageKey(roomKey, message.keyWrap);
    return await decryptMessage(messageKey, message.ciphertext, buildMessageAad({
      organizationId,
      conversationId: message.conversationId,
      senderId: message.senderId,
      messageId: message.id,
      keyEpoch: message.keyEpoch
    }));
  } catch {
    return null;
  }
}
__name(decryptIncomingMessage, "decryptIncomingMessage");
function isEncryptedMessage(m) {
  return Boolean(m.ciphertext && m.keyWrap && typeof m.keyEpoch === "number");
}
__name(isEncryptedMessage, "isEncryptedMessage");

// src/chat-crypto/peer-key-directory.ts
var _PeerKeyDirectory = class _PeerKeyDirectory {
  constructor(deps) {
    __publicField(this, "deps");
    this.deps = deps;
  }
  nowIso() {
    return (this.deps.now?.() ?? /* @__PURE__ */ new Date()).toISOString();
  }
  /** 서버의 지금 키를 받아 고정값과 대조 — 처음이면 고정, 같으면 same, 다르면 changed(pending 기록). */
  async resolve(employeeIds) {
    const out = /* @__PURE__ */ new Map();
    const ids = [
      ...new Set(employeeIds)
    ].filter(Boolean);
    if (ids.length === 0) {
      return out;
    }
    const remote = await this.deps.chatKey.publicKeys(ids);
    const byId = new Map(remote.map((r) => [
      r.employeeId,
      r
    ]));
    for (const id of ids) {
      const r = byId.get(id);
      const pin = await this.deps.store.getPeerPin(id);
      if (!r) {
        out.set(id, {
          employeeId: id,
          version: pin?.version ?? 0,
          ecdhPublicKey: pin?.ecdhPublicKey ?? "",
          ecdsaPublicKey: pin?.ecdsaPublicKey ?? "",
          status: "missing",
          trusted: pin ? pinTrusted(pin) : null
        });
        continue;
      }
      const now = this.nowIso();
      if (!pin) {
        await this.deps.store.putPeerPin({
          employeeId: id,
          version: r.version,
          ecdhPublicKey: r.ecdhPublicKey,
          ecdsaPublicKey: r.ecdsaPublicKey,
          firstSeenAt: now,
          lastSeenAt: now,
          pending: null
        });
        out.set(id, {
          ...r,
          status: "new",
          trusted: {
            ...r
          }
        });
        continue;
      }
      if (sameKey(pin, r)) {
        if (pin.pending) {
          await this.deps.store.putPeerPin({
            ...pin,
            pending: null,
            lastSeenAt: now
          });
        } else {
          await this.deps.store.putPeerPin({
            ...pin,
            lastSeenAt: now
          });
        }
        out.set(id, {
          ...r,
          status: "same",
          trusted: pinTrusted(pin)
        });
        continue;
      }
      await this.deps.store.putPeerPin({
        employeeId: id,
        version: r.version,
        ecdhPublicKey: r.ecdhPublicKey,
        ecdsaPublicKey: r.ecdsaPublicKey,
        firstSeenAt: now,
        lastSeenAt: now,
        pending: null
      });
      out.set(id, {
        ...r,
        status: "changed",
        trusted: {
          ...r
        }
      });
    }
    return out;
  }
  /** (레거시) 보류 중 키를 고정값으로 — 자동 수용 뒤에는 pending 이 남지 않아 false 를 돌려준다. */
  async acknowledge(employeeId) {
    const pin = await this.deps.store.getPeerPin(employeeId);
    if (!pin?.pending) {
      return false;
    }
    const now = this.nowIso();
    await this.deps.store.putPeerPin({
      employeeId,
      version: pin.pending.version,
      ecdhPublicKey: pin.pending.ecdhPublicKey,
      ecdsaPublicKey: pin.pending.ecdsaPublicKey,
      firstSeenAt: now,
      lastSeenAt: now,
      pending: null
    });
    return true;
  }
  async pinned(employeeId) {
    return this.deps.store.getPeerPin(employeeId);
  }
  /**
  * 서명 검증에 쓸 ECDSA 공개키 — 그 version 이 고정된 키면 그것, 확인 전(pending) version 이면 null(믿지 않음),
  * 고정 version 보다 **오래된** version 이면 서버 이력에서 가져온다(이미 폐기된 키라 바꿔치기해 얻을 것이 없다).
  */
  async ecdsaKeyForVersion(employeeId, version) {
    const pin = await this.deps.store.getPeerPin(employeeId);
    if (pin?.version === version) {
      return pin.ecdsaPublicKey;
    }
    const info = (await this.resolve([
      employeeId
    ])).get(employeeId);
    if (info && info.status !== "missing" && info.version === version) {
      return info.ecdsaPublicKey;
    }
    const history = await this.deps.chatKey.history(employeeId);
    return history.find((h) => h.version === version)?.ecdsaPublicKey ?? null;
  }
};
__name(_PeerKeyDirectory, "PeerKeyDirectory");
var PeerKeyDirectory = _PeerKeyDirectory;
function sameKey(a, b) {
  return a.version === b.version && a.ecdhPublicKey === b.ecdhPublicKey && a.ecdsaPublicKey === b.ecdsaPublicKey;
}
__name(sameKey, "sameKey");
function pinTrusted(pin) {
  return {
    version: pin.version,
    ecdhPublicKey: pin.ecdhPublicKey,
    ecdsaPublicKey: pin.ecdsaPublicKey
  };
}
__name(pinTrusted, "pinTrusted");

// src/chat-crypto/push-decrypt.ts
import { buildMessageAad as buildMessageAad2, decryptMessage as decryptMessage2, importRoomKey as importRoomKey2, unwrapMessageKey as unwrapMessageKey2 } from "@hereby/core/chat-crypto";
var ENCRYPTED_PUSH_KIND = "chat_message_encrypted";
var PUSH_BODY_MAX = 200;
function isEncryptedPushData(data) {
  const d = data;
  return Boolean(d && d.kind === ENCRYPTED_PUSH_KIND && d.organizationId && d.conversationId && d.messageId && d.senderId && d.keyEpoch !== void 0 && d.ciphertext && d.keyWrap);
}
__name(isEncryptedPushData, "isEncryptedPushData");
async function decryptPushBody(store, data) {
  if (!isEncryptedPushData(data)) {
    return null;
  }
  try {
    const raw = await store.getEpochKey(data.conversationId, Number(data.keyEpoch));
    if (!raw) {
      return null;
    }
    const roomKey = await importRoomKey2(raw);
    const messageKey = await unwrapMessageKey2(roomKey, data.keyWrap);
    const payload = await decryptMessage2(messageKey, data.ciphertext, buildMessageAad2({
      organizationId: data.organizationId,
      conversationId: data.conversationId,
      senderId: data.senderId,
      messageId: data.messageId,
      keyEpoch: Number(data.keyEpoch)
    }));
    const text = (payload.text ?? "").trim();
    if (text) {
      return text.length > PUSH_BODY_MAX ? `${text.slice(0, PUSH_BODY_MAX)}\u2026` : text;
    }
    const n = payload.attachments?.length ?? 0;
    return n > 0 ? `\uD30C\uC77C ${n}\uAC1C` : null;
  } catch {
    return null;
  }
}
__name(decryptPushBody, "decryptPushBody");

// src/chat-crypto/send-text.ts
async function sendTextToConversation(params) {
  const { client, conversationId, text } = params;
  if (!params.encryptionEnabled) {
    await client.message.send({
      conversationId,
      content: text
    });
    return "sent";
  }
  const store = params.store ?? createDefaultChatKeyStore();
  const keys = new ChatKeyManager({
    employeeId: params.employeeId,
    organizationId: params.organizationId,
    store,
    chatKey: client.chatKey,
    chatDeviceLink: client.chatDeviceLink
  });
  const state = await keys.ensure();
  if (state !== "ready") {
    const view = await client.conversationKey.myEnvelopes(conversationId);
    if (view.currentEpoch === null) {
      await client.message.send({
        conversationId,
        content: text
      });
      return "sent";
    }
    return "missing-key";
  }
  const rooms = new ConversationKeyManager({
    employeeId: params.employeeId,
    organizationId: params.organizationId,
    store,
    conversationKey: client.conversationKey,
    directory: new PeerKeyDirectory({
      store,
      chatKey: client.chatKey
    }),
    accountKey: /* @__PURE__ */ __name(() => {
      const pair = keys.getKeyPair();
      const version = keys.getKeyVersion();
      const ecdhPublicKey = keys.getPublicKeys()?.ecdhPublicKey;
      return pair && version !== null && ecdhPublicKey ? {
        pair,
        version,
        ecdhPublicKey
      } : null;
    }, "accountKey")
  });
  const sync = await rooms.sync(conversationId, true);
  if (sync.currentEpoch === null) {
    await client.message.send({
      conversationId,
      content: text
    });
    return "sent";
  }
  if (!sync.haveCurrentKey) {
    return "missing-key";
  }
  let recovery = null;
  try {
    recovery = await client.chatKey.recoveryPublicKey() ?? null;
  } catch {
    recovery = null;
  }
  const encrypted = await encryptOutgoingMessage({
    rooms,
    organizationId: params.organizationId,
    conversationId,
    senderId: params.employeeId,
    keyEpoch: sync.currentEpoch,
    payload: {
      text
    },
    recovery
  });
  await client.message.send({
    conversationId,
    content: "",
    encrypted
  });
  return "sent";
}
__name(sendTextToConversation, "sendTextToConversation");

// src/utils/async-poller.ts
function pollAsyncOperation(options) {
  const { statusFn, getStatus, pendingStatuses, interval = 3e3, timeout = 18e4, onProgress, onError } = options;
  let cancelled = false;
  let intervalId = null;
  let timeoutId = null;
  const cleanup = /* @__PURE__ */ __name(() => {
    if (intervalId != null) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  }, "cleanup");
  const result = new Promise((resolve2) => {
    let lastData = null;
    const poll = /* @__PURE__ */ __name(async () => {
      if (cancelled) {
        return;
      }
      try {
        const data = await statusFn();
        lastData = data;
        onProgress?.(data);
        const status = getStatus(data);
        if (!pendingStatuses.includes(status)) {
          cleanup();
          resolve2({
            data,
            timedOut: false
          });
        }
      } catch (error) {
        onError?.(error);
      }
    }, "poll");
    timeoutId = setTimeout(() => {
      cleanup();
      cancelled = true;
      if (lastData) {
        resolve2({
          data: lastData,
          timedOut: true
        });
      } else {
        statusFn().then((data) => resolve2({
          data,
          timedOut: true
        })).catch(() => {
          resolve2({
            data: null,
            timedOut: true
          });
        });
      }
    }, timeout);
    intervalId = setInterval(poll, interval);
    poll();
  });
  const cancel = /* @__PURE__ */ __name(() => {
    cancelled = true;
    cleanup();
  }, "cancel");
  return {
    result,
    cancel
  };
}
__name(pollAsyncOperation, "pollAsyncOperation");

// src/utils/errors.ts
var _HerebySDKError = class _HerebySDKError extends Error {
  constructor(message, code, statusCode, details) {
    super(message);
    __publicField(this, "code");
    __publicField(this, "statusCode");
    __publicField(this, "details");
    this.name = "HerebySDKError";
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
  }
};
__name(_HerebySDKError, "HerebySDKError");
var HerebySDKError = _HerebySDKError;
var _AuthenticationError = class _AuthenticationError extends HerebySDKError {
  constructor(message = "Authentication failed", details) {
    super(message, "AUTHENTICATION_ERROR", 401, details);
    this.name = "AuthenticationError";
  }
};
__name(_AuthenticationError, "AuthenticationError");
var AuthenticationError = _AuthenticationError;
var _AuthorizationError = class _AuthorizationError extends HerebySDKError {
  constructor(message = "Access denied", details) {
    super(message, "AUTHORIZATION_ERROR", 403, details);
    this.name = "AuthorizationError";
  }
};
__name(_AuthorizationError, "AuthorizationError");
var AuthorizationError = _AuthorizationError;
var _ValidationError = class _ValidationError extends HerebySDKError {
  constructor(message = "Validation failed", details) {
    super(message, "VALIDATION_ERROR", 400, details);
    this.name = "ValidationError";
  }
};
__name(_ValidationError, "ValidationError");
var ValidationError = _ValidationError;
var _NotFoundError = class _NotFoundError extends HerebySDKError {
  constructor(message = "Resource not found", details) {
    super(message, "NOT_FOUND", 404, details);
    this.name = "NotFoundError";
  }
};
__name(_NotFoundError, "NotFoundError");
var NotFoundError = _NotFoundError;
var _NetworkError = class _NetworkError extends HerebySDKError {
  constructor(message = "Network request failed", details) {
    super(message, "NETWORK_ERROR", 0, details);
    this.name = "NetworkError";
  }
};
__name(_NetworkError, "NetworkError");
var NetworkError = _NetworkError;
var _TimeoutError = class _TimeoutError extends HerebySDKError {
  constructor(message = "Request timeout", details) {
    super(message, "TIMEOUT_ERROR", 408, details);
    this.name = "TimeoutError";
  }
};
__name(_TimeoutError, "TimeoutError");
var TimeoutError = _TimeoutError;
function parseTRPCError(error) {
  if (error instanceof TRPCClientError) {
    const { message, data } = error;
    const code = data?.code || "UNKNOWN_ERROR";
    switch (code) {
      case "UNAUTHORIZED":
        return new AuthenticationError(message, data);
      case "FORBIDDEN":
        return new AuthorizationError(message, data);
      case "BAD_REQUEST":
        return new ValidationError(message, data);
      case "NOT_FOUND":
        return new NotFoundError(message, data);
      default:
        return new HerebySDKError(message, code, data?.httpStatus, data);
    }
  }
  if (error instanceof Error) {
    if (error.message.includes("timeout")) {
      return new TimeoutError(error.message);
    }
    if (error.message.includes("network") || error.message.includes("fetch")) {
      return new NetworkError(error.message);
    }
    return new HerebySDKError(error.message, "UNKNOWN_ERROR");
  }
  return new HerebySDKError("An unknown error occurred", "UNKNOWN_ERROR");
}
__name(parseTRPCError, "parseTRPCError");
async function handleSDKError(promise, errorHandler) {
  try {
    return await promise;
  } catch (error) {
    const sdkError = parseTRPCError(error);
    if (errorHandler) {
      errorHandler(sdkError);
    }
    throw sdkError;
  }
}
__name(handleSDKError, "handleSDKError");

// src/utils/fetch-all-pages.ts
async function fetchAllPages(fetcher, pageSize = 1e3, maxPages = 100, shouldStop) {
  const out = [];
  for (let page = 1; page <= maxPages; page++) {
    if (shouldStop?.()) {
      break;
    }
    const result = await fetcher(page, pageSize);
    const items = Array.isArray(result) ? result : result?.items ?? [];
    out.push(...items);
    if (items.length < pageSize) {
      break;
    }
  }
  return out;
}
__name(fetchAllPages, "fetchAllPages");

// src/utils/storage.ts
var _LocalStorageAdapter = class _LocalStorageAdapter {
  constructor(prefix) {
    __publicField(this, "accessTokenKey", "hereby_access_token");
    __publicField(this, "refreshTokenKey", "hereby_refresh_token");
    if (prefix) {
      this.accessTokenKey = `${prefix}_access_token`;
      this.refreshTokenKey = `${prefix}_refresh_token`;
    }
  }
  getAccessToken() {
    if (typeof window === "undefined") {
      return null;
    }
    return localStorage.getItem(this.accessTokenKey);
  }
  getRefreshToken() {
    if (typeof window === "undefined") {
      return null;
    }
    return localStorage.getItem(this.refreshTokenKey);
  }
  setTokens(accessToken, refreshToken) {
    if (typeof window === "undefined") {
      return;
    }
    localStorage.setItem(this.accessTokenKey, accessToken);
    if (refreshToken) {
      localStorage.setItem(this.refreshTokenKey, refreshToken);
    }
  }
  clearTokens() {
    if (typeof window === "undefined") {
      return;
    }
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }
};
__name(_LocalStorageAdapter, "LocalStorageAdapter");
var LocalStorageAdapter = _LocalStorageAdapter;
var _SessionStorageAdapter = class _SessionStorageAdapter {
  constructor(prefix) {
    __publicField(this, "accessTokenKey", "hereby_access_token");
    __publicField(this, "refreshTokenKey", "hereby_refresh_token");
    if (prefix) {
      this.accessTokenKey = `${prefix}_access_token`;
      this.refreshTokenKey = `${prefix}_refresh_token`;
    }
  }
  getAccessToken() {
    if (typeof window === "undefined") {
      return null;
    }
    return sessionStorage.getItem(this.accessTokenKey);
  }
  getRefreshToken() {
    if (typeof window === "undefined") {
      return null;
    }
    return sessionStorage.getItem(this.refreshTokenKey);
  }
  setTokens(accessToken, refreshToken) {
    if (typeof window === "undefined") {
      return;
    }
    sessionStorage.setItem(this.accessTokenKey, accessToken);
    if (refreshToken) {
      sessionStorage.setItem(this.refreshTokenKey, refreshToken);
    }
  }
  clearTokens() {
    if (typeof window === "undefined") {
      return;
    }
    sessionStorage.removeItem(this.accessTokenKey);
    sessionStorage.removeItem(this.refreshTokenKey);
  }
};
__name(_SessionStorageAdapter, "SessionStorageAdapter");
var SessionStorageAdapter = _SessionStorageAdapter;
var _MemoryStorageAdapter = class _MemoryStorageAdapter {
  constructor() {
    __publicField(this, "accessToken", null);
    __publicField(this, "refreshToken", null);
  }
  getAccessToken() {
    return this.accessToken;
  }
  getRefreshToken() {
    return this.refreshToken;
  }
  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    if (refreshToken) {
      this.refreshToken = refreshToken;
    }
  }
  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
  }
};
__name(_MemoryStorageAdapter, "MemoryStorageAdapter");
var MemoryStorageAdapter = _MemoryStorageAdapter;
var _CookieStorageAdapter = class _CookieStorageAdapter {
  constructor(prefix) {
    __publicField(this, "accessTokenKey", "hereby_access_token");
    __publicField(this, "refreshTokenKey", "hereby_refresh_token");
    if (prefix) {
      this.accessTokenKey = `${prefix}_access_token`;
      this.refreshTokenKey = `${prefix}_refresh_token`;
    }
  }
  getCookie(name) {
    if (typeof document === "undefined") {
      return null;
    }
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift() || null;
    }
    return null;
  }
  setCookie(name, value, days = 7) {
    if (typeof document === "undefined") {
      return;
    }
    const date = /* @__PURE__ */ new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1e3);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
  }
  deleteCookie(name) {
    if (typeof document === "undefined") {
      return;
    }
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }
  getAccessToken() {
    return this.getCookie(this.accessTokenKey);
  }
  getRefreshToken() {
    return this.getCookie(this.refreshTokenKey);
  }
  setTokens(accessToken, refreshToken) {
    this.setCookie(this.accessTokenKey, accessToken);
    if (refreshToken) {
      this.setCookie(this.refreshTokenKey, refreshToken, 30);
    }
  }
  clearTokens() {
    this.deleteCookie(this.accessTokenKey);
    this.deleteCookie(this.refreshTokenKey);
  }
};
__name(_CookieStorageAdapter, "CookieStorageAdapter");
var CookieStorageAdapter = _CookieStorageAdapter;

// src/utils/validators.ts
function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}
__name(isValidEmail, "isValidEmail");
function isValidKoreanPhone(phone) {
  const cleaned = phone.replace(/\D/g, "");
  const mobileRegex = /^01[0-9]{8,9}$/;
  const landlineRegex = /^0[2-6][0-9]{7,8}$/;
  return mobileRegex.test(cleaned) || landlineRegex.test(cleaned);
}
__name(isValidKoreanPhone, "isValidKoreanPhone");
function isValidKoreanRRN(rrn) {
  const cleaned = rrn.replace(/-/g, "");
  if (!/^\d{13}$/.test(cleaned)) {
    return false;
  }
  const weights = [
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    2,
    3,
    4,
    5
  ];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += Number.parseInt(cleaned[i], 10) * weights[i];
  }
  const checkDigit = (11 - sum % 11) % 10;
  return checkDigit === Number.parseInt(cleaned[12], 10);
}
__name(isValidKoreanRRN, "isValidKoreanRRN");
function validatePasswordStrength(password) {
  const feedback = [];
  let score = 0;
  if (password.length < 8) {
    feedback.push("Password must be at least 8 characters long");
  } else if (password.length >= 12) {
    score += 2;
  } else {
    score += 1;
  }
  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push("Include at least one uppercase letter");
  }
  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push("Include at least one lowercase letter");
  }
  if (/\d/.test(password)) {
    score += 1;
  } else {
    feedback.push("Include at least one number");
  }
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    score += 1;
  } else {
    feedback.push("Include at least one special character");
  }
  return {
    isValid: feedback.length === 0 && password.length >= 8,
    score: Math.min(score, 5),
    feedback
  };
}
__name(validatePasswordStrength, "validatePasswordStrength");
function isValidBusinessNumber(number) {
  const cleaned = number.replace(/-/g, "");
  if (!/^\d{10}$/.test(cleaned)) {
    return false;
  }
  const weights = [
    1,
    3,
    7,
    1,
    3,
    7,
    1,
    3,
    5
  ];
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += Number.parseInt(cleaned[i], 10) * weights[i];
  }
  sum += Math.floor(Number.parseInt(cleaned[8], 10) * 5 / 10);
  const checkDigit = (10 - sum % 10) % 10;
  return checkDigit === Number.parseInt(cleaned[9], 10);
}
__name(isValidBusinessNumber, "isValidBusinessNumber");
function formatKoreanPhone(phone) {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    if (cleaned.startsWith("02")) {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    }
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}
__name(formatKoreanPhone, "formatKoreanPhone");
function formatKRW(amount) {
  return new Intl.NumberFormat("ko-KR", {
    style: "currency",
    currency: "KRW"
  }).format(amount);
}
__name(formatKRW, "formatKRW");
function isValidDateRange(startDate, endDate) {
  return startDate <= endDate;
}
__name(isValidDateRange, "isValidDateRange");
function isKoreanHoliday(date) {
  const _year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const fixedHolidays = [
    "01-01",
    "03-01",
    "05-05",
    "06-06",
    "08-15",
    "10-03",
    "10-09",
    "12-25"
  ];
  const dateStr = `${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  return fixedHolidays.includes(dateStr);
}
__name(isKoreanHoliday, "isKoreanHoliday");
function calculateAgeFromRRN(rrn) {
  const cleaned = rrn.replace(/-/g, "");
  if (cleaned.length !== 13) {
    return null;
  }
  const yearPrefix = Number.parseInt(cleaned[0] + cleaned[1], 10);
  const genderDigit = Number.parseInt(cleaned[6], 10);
  let fullYear;
  if (genderDigit === 1 || genderDigit === 2) {
    fullYear = 1900 + yearPrefix;
  } else if (genderDigit === 3 || genderDigit === 4) {
    fullYear = 2e3 + yearPrefix;
  } else {
    return null;
  }
  const today = /* @__PURE__ */ new Date();
  const birthDate = new Date(fullYear, Number.parseInt(cleaned[2] + cleaned[3], 10) - 1, Number.parseInt(cleaned[4] + cleaned[5], 10));
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || monthDiff === 0 && today.getDate() < birthDate.getDate()) {
    age--;
  }
  return age;
}
__name(calculateAgeFromRRN, "calculateAgeFromRRN");

// src/index.ts
function createHerebyClient(config) {
  return new HerebyClient(config);
}
__name(createHerebyClient, "createHerebyClient");
var src_default = HerebyClient;
export {
  ATTACHMENT_CACHE_MAX_FILE,
  ATTACHMENT_CACHE_MAX_TOTAL,
  AccountService,
  AiService,
  AllowanceService,
  AppOnboardingService,
  ApprovalFlowService,
  AttendanceCorrectionService,
  AttendanceRateService,
  AttendanceService,
  AuthService,
  AuthenticationError,
  AuthorizationError,
  ChatDeviceLinkService,
  ChatKeyManager,
  ChatKeyService,
  ChatRecoveryService,
  ConversationKeyManager,
  ConversationKeyService,
  ConversationService,
  CookieStorageAdapter,
  DeductionImportService,
  DeductionService,
  DeviceCredentialService,
  DocumentService,
  ENCRYPTED_PUSH_KIND,
  ESignService,
  EducationSubsidyService,
  EmployeeCertificationService,
  EmployeeService,
  FixedIncentiveService,
  GeneralAffairsService,
  HerebyClient,
  HerebySDKError,
  IMPERSONATION_ENDED_NOTICE,
  IMPERSONATION_EXPIRED_NOTICE,
  ImpersonationService,
  IndexedDbChatKeyStore,
  IndexedDbDeviceKeyStore,
  InviteService,
  JobDutyService,
  LeaveImportService,
  LeaveRequestService,
  LocalStorageAdapter,
  MESSAGE_CACHE_PER_ROOM,
  MemoryChatKeyStore,
  MemoryDeviceKeyStore,
  MemoryStorageAdapter,
  MessageService,
  NetworkError,
  NotFoundError,
  NotificationService,
  NurseSchedulerService,
  OffsiteWorkService,
  OrganizationHolidayService,
  OrganizationService,
  OvertimeService,
  PUSH_BODY_MAX,
  PayrollService,
  PeerKeyDirectory,
  ResignationService,
  RoleService,
  SalaryGradeService,
  SecretBytesProvider,
  SecurityService,
  SessionStorageAdapter,
  ShiftGroupService,
  ShiftService,
  SignupService,
  TeamDriveService,
  TeamManagerService,
  TeamService,
  TimeoutError,
  ValidationError,
  WS_KEEPALIVE,
  WS_REOPENED_EVENT,
  WS_RETRY_MAX_MS,
  WebWrapKeyProvider,
  WorkPatternService,
  YearEndSettlementService,
  calculateAgeFromRRN,
  clearImpersonationReturn,
  collectEnvContext,
  createAlpinePlugin,
  createDefaultChatKeyStore,
  createDefaultDeviceKeyStore,
  createHerebyClient,
  csCollector,
  decryptIncomingMessage,
  decryptPushBody,
  src_default as default,
  encryptOutgoingMessage,
  fetchAllPages,
  forgetDeviceKey,
  formatKRW,
  formatKoreanPhone,
  fromBase64Url,
  generateDeviceKey,
  getActiveDeviceKey,
  getDevicePushToken,
  getDeviceSecretProvider,
  handleSDKError,
  installCsReportHooks,
  installImeEnterGuard,
  installImpersonationHistoryGuard,
  isEncryptedMessage,
  isEncryptedPushData,
  isIndexedDbAvailable,
  isKoreanHoliday,
  isPasskeyAvailable,
  isValidBusinessNumber,
  isValidDateRange,
  isValidEmail,
  isValidKoreanPhone,
  isValidKoreanRRN,
  loginWithDeviceKey,
  loginWithPasskey,
  navigateAfterImpersonationExit,
  newMessageId,
  parseTRPCError,
  plainAccountBundle,
  pollAsyncOperation,
  readImpersonationReturn,
  recoverFromImpersonationExpiry,
  registerDeviceKey,
  registerPasskey,
  restoreImpersonationReturn,
  restoredPageHasStaleSession,
  saveImpersonationReturn,
  sendTextToConversation,
  setDevicePushToken,
  setDeviceSecretProvider,
  setEpochKeySink,
  signChallenge,
  signMembershipChange,
  takeImpersonationNotice,
  toBase64Url,
  validatePasswordStrength,
  wsRetryDelayMs
};
/*! Bundled license information:

@trpc/client/dist/httpBatchLink-fQ1NAi-e.mjs:
  (* istanbul ignore if -- @preserve *)
*/
//# sourceMappingURL=index.mjs.map